/**
 * Catalog tiếng Việt — **nguồn sự thật** của mọi chuỗi người dùng thấy.
 *
 * Luật của tệp này:
 *
 * - Thêm chuỗi thì thêm **ở đây trước**, rồi mới sang `en.ts`. `check:i18n`
 *   đối chiếu hai bên nên thiếu một bên là `pnpm test` gãy.
 * - Kiểu `en.ts` được suy ra từ chính tệp này (`typeof vi`), nên khoá sai
 *   chính tả là lỗi `tsc`, không phải lỗi lúc chạy.
 * - Chính tả theo quy ước kho: kiểu dấu thanh mới (`xoá`, `huỷ`, `khoá`,
 *   `tuỳ`), trừ thuật ngữ nghiệp vụ đã chốt kiểu cũ (`Hóa đơn VAT`,
 *   `hàng hóa`).
 * - Thuật ngữ kỹ thuật giữ nguyên tiếng Anh (`tenant`, `slug`, `model`,
 *   `workflow`, `OCR`, `schema`).
 * - Chuỗi mang tham số dùng `{{ten}}`, không ghép chuỗi ở chỗ dùng: tiếng Anh
 *   đảo thứ tự thành phần câu, mà ghép chuỗi thì không đảo được.
 *
 * Nhóm khoá theo **nơi dùng**, không theo loại từ: tìm chuỗi của một màn thì
 * mở đúng một nhánh, không phải lần qua `nut.*` rồi `tieuDe.*`.
 */
// TODO(ADR-0086): gộp các khối Trợ lý AI dưới đây vào thẳng tệp này khi xong.
import { viChung } from './tro-ly-ai/chung.vi.ts';
import { viHoi } from './tro-ly-ai/hoi.vi.ts';
import { viKetNoi } from './tro-ly-ai/ket-noi.vi.ts';
import { viKho } from './tro-ly-ai/kho.vi.ts';
import { viKhuon } from './tro-ly-ai/khuon.vi.ts';
import { viBot } from './tro-ly-ai/bot.vi.ts';
import { viVanHanh } from './tro-ly-ai/van-hanh.vi.ts';
// TODO(ADR-0095): gộp các khối Nhật ký & Truy vết dưới đây vào thẳng tệp này khi xong.
import { viNktvChung } from './nhat-ky-truy-vet/chung.vi.ts';
import { viNktvSuDungAi } from './nhat-ky-truy-vet/su-dung-ai.vi.ts';
import { viGiaAi } from './gia-ai.vi.ts';
import { viCanhBao } from './canh-bao.vi.ts';
import { viBanToken } from './ban-token.vi.ts';
import { viManTokenAi } from './token-ai.vi.ts';
import { viBienBan } from './bien-ban.vi.ts';
import { viHanMucAi } from './han-muc-ai.vi.ts';
import { viBanGiao } from './ban-giao.vi.ts';
// TODO(ADR-0103): gộp các khối Quản lý RPA dưới đây vào thẳng tệp này khi xong.
import { viRpaChung } from './rpa/chung.vi.ts';
import { viRpaLoi } from './rpa/loi.vi.ts';
import { viRpaKetNoi } from './rpa/ket-noi.vi.ts';
import { viRpaWorkflow } from './rpa/workflow.vi.ts';
import { viRpaLichSu } from './rpa/lich-su.vi.ts';
import { viRpaTongQuan } from './rpa/tong-quan.vi.ts';
import { viRpaLicense } from './rpa/license.vi.ts';
import { viRpaDanhBa } from './rpa/danh-ba.vi.ts';
import { viRpaThongBao } from './rpa/thong-bao.vi.ts';
import { viRpaAgent } from './rpa/agent.vi.ts';

export const vi = {
  // ───────────────────────── Dùng chung ─────────────────────────
  chung: {
    // Nút và hành động lặp lại ở nhiều màn.
    lamMoi: 'Làm mới',
    thuLai: 'Thử lại',
    huy: 'Huỷ',
    luu: 'Lưu',
    dangLuu: 'Đang lưu...',
    xoa: 'Xoá',
    quayLai: 'Quay lại',
    xemTatCa: 'Xem tất cả',
    xoaBoLoc: 'Xoá bộ lọc',
    thaoTac: 'Thao tác',
    thaoTacKhac: 'Thao tác khác',
    thaoTacKhacVoi: 'Thao tác khác với {{ten}}',
    stt: 'STT',
    trangThai: 'Trạng thái',
    khongCo: '—',
    // Nhãn mở/đóng dùng cho nút hai trạng thái.
    moRong: 'Mở rộng',
    thuGon: 'Thu gọn',
    dong: 'Đóng',
    taiThem: 'Tải thêm',
  },

  // Lưới chọn quyền dùng chung — `components/shared/LuoiChonQuyen`.
  chonQuyen: {
    timPlaceholder: 'Tìm quyền hoặc nhóm quyền',
    timNhan: 'Tìm quyền',
    khongKhop: 'Không có quyền nào khớp từ khoá',
    chonCaNhom: 'Chọn mọi quyền của {{nhom}}',
    daChonTrongNhom: '{{chon}}/{{tong}} quyền',
    boLoc: 'Lọc theo trạng thái chọn',
    locTatCa: 'Tất cả',
    locDaChon: 'Đã chọn',
    locChuaChon: 'Chưa chọn',
    chuaLuu: 'Chưa lưu: thêm {{them}}, bỏ {{bo}}',
    chuaCoThayDoi: 'Chưa có thay đổi',
    danhSachNhom: 'Danh sách module',
    nhomDaDoi: 'Có thay đổi chưa lưu',
  },

  // Lỗi tầng dữ liệu — `lib/errors.ts`.
  loi: {
    mang: 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.',
    khongCoQuyen: 'Bạn không có quyền thực hiện thao tác này.',
    khongTimThay: 'Không tìm thấy dữ liệu.',
    saiTaiKhoan: 'Tài khoản hoặc mật khẩu không đúng.',
    chung: 'Đã có lỗi xảy ra. Vui lòng thử lại.',
    // Mã chung của Platform API (ADR-0020) và Gateway — `lib/errors.ts`.
    hetPhien: 'Phiên đăng nhập đã hết hạn. Đăng nhập lại rồi thử lại.',
    mayChu:
      'Máy chủ gặp lỗi ngoài dự kiến. Thử lại sau ít phút; lỗi còn lặp lại thì báo đội vận hành nền tảng.',
    quaNhieuYeuCau: 'Có quá nhiều yêu cầu trong thời gian ngắn. Đợi vài giây rồi thử lại.',
    tamKhongPhucVu: 'Dịch vụ tạm thời không phục vụ được. Thử lại sau ít phút.',
    dichVuChuaSanSang:
      'Dịch vụ chưa sẵn sàng trên máy chủ này. Thử lại sau ít phút; lỗi còn lặp lại thì báo đội vận hành nền tảng.',
    dangXuLy: 'Yêu cầu trước vẫn đang được xử lý. Đợi vài giây rồi tải lại.',
    saiDiaChiTruyCap: 'Địa chỉ này không thuộc tổ chức của bạn. Mở cổng bằng địa chỉ chung của nền tảng.',
    ipBiChan: 'Truy cập từ địa chỉ IP này đã bị chặn. Chuyển sang mạng được phép hoặc liên hệ quản trị.',
    ipNgoaiDanhSach:
      'Mạng bạn đang dùng nằm ngoài danh sách IP được phép của tổ chức. Chuyển sang mạng được phép hoặc liên hệ quản trị tổ chức.',
    // ADR-0090 mục 2: ngoài dải tin cậy mà tổ chức đòi OTP (401 CAN_XAC_THUC_TANG_CUONG)
    canXacThucTangCuong:
      'Bạn đang truy cập từ ngoài mạng tin cậy của tổ chức nên cần xác thực thêm bằng mã OTP.',
  },

  // ───────────────────────── Khung ứng dụng ─────────────────────────
  khung: {
    // Thanh điều hướng — ADR-UI-0009.
    dieuHuongChinh: 'Điều hướng chính',
    veDashboard: 'FOXAI Native — về Dashboard',
    moThanhDieuHuong: 'Mở rộng thanh điều hướng',
    gapThanhDieuHuong: 'Thu gọn thanh điều hướng',
    timManHinh: 'Tìm màn hình',
    timManHinhPhimTat: 'Tìm màn hình (/)',
    timTrongDieuHuong: 'Tìm trong điều hướng',
    xoaTuKhoa: 'Xoá từ khoá',
    xoaTuKhoaTimKiem: 'Xoá từ khoá tìm kiếm',
    soKetQua: '{{so}} màn hình khớp từ khoá',
    khongCoManHinhKhop: 'Không có màn hình nào khớp',
    gapHetCacNhom: 'Gập hết các nhóm',
    moHetCacNhom: 'Mở hết các nhóm',
    chuaManDangXem: '(chứa màn đang xem)',
    // Khối người dùng ở đáy thanh.
    nguoiDung: 'Người dùng',
    chuaXacDinh: 'Chưa xác định',
    caiDatTaiKhoan: 'Tài khoản của tôi',
    dangXuat: 'Đăng xuất',
    ngonNgu: 'Ngôn ngữ',
    /** Nhãn trợ năng của nút ⋮, nhắc tên người đang đăng nhập. */
    menuTaiKhoan: 'Tài khoản và thiết lập',
    menuTaiKhoanCua: 'Tài khoản và thiết lập của {{ten}}',
    giaoDien: 'Giao diện',
    xemGiaoDienTenant: 'Xem giao diện tenant',
    veCongQuanTri: 'Về cổng quản trị',
  },

  // Ngăn tuỳ chỉnh giao diện — ADR-UI-0007.
  tuyChinh: {
    tieuDe: 'Tuỳ chỉnh giao diện',
    nhomCaNhan: 'Giao diện cá nhân',
    chiLuuTrenMayNay: 'Chỉ lưu trên máy này',
    khoiPhucMacDinh: 'Khôi phục mặc định',
    datLaiTatCa: 'Đặt lại tất cả',
    daDatLai: 'Đã đặt lại giao diện cá nhân về mặc định.',
    cheDoNen: 'Chế độ nền',
    cheDoNenMo: 'Tự động đi theo hệ điều hành.',
    nenTuDong: 'Tự động',
    nenSang: 'Sáng',
    nenToi: 'Tối',
    mucDangChon: 'Mục đang chọn',
    mucKhac: 'Mục khác',
    mauNhan: 'Màu nhấn',
    mauNhanMo:
      'Dùng cho nút chính, liên kết và mục đang chọn. Màu trạng thái không đổi.',
    boGoc: 'Độ bo góc',
    boGocMo: 'Áp cho nút, ô nhập, thẻ và hộp thoại.',
    mauNhanh: 'Xem trước',
    nutChinh: 'Nút chính',
    nutPhu: 'Nút phụ',
    nhanMauNhan: 'Màu nhấn',
    nhanXong: 'Xong',
    nhanCho: 'Chờ',
    nhanLoi: 'Lỗi',
    oNhap: 'Ô nhập',
    mauTuyY: 'Màu tự chọn',
    mauTuyYNhan: 'Chọn màu nhấn tự chọn',
    daLamDam: 'Đã làm đậm nhẹ thành {{ma}} để chữ trên nút đọc được.',
  },


  // Hồ sơ nhận diện của tổ chức (tab "Tổ chức" ở ngăn tuỳ chỉnh) — ADR-UI-0016.
  hoSoToChuc: {
    tieuDe: 'Nhận diện tổ chức',
    moTa: 'Logo và tên hiện với mọi thành viên của tổ chức.',
    apDungToanTo: 'Áp dụng cho mọi thành viên của tổ chức.',
    anhApDungNgay: 'Ảnh được áp dụng ngay khi tải lên. Tên chỉ áp dụng sau khi bấm Lưu.',
    tenHienThi: 'Tên hiển thị',
    tenHienThiGoiY: 'Hiện ở thanh điều hướng, email và báo cáo.',
    tenVietTat: 'Tên viết tắt',
    tenVietTatGoiY: 'Hiện khi thanh điều hướng thu gọn và ở tiêu đề tab. Tối đa 12 ký tự.',
    logoSang: 'Logo nền sáng',
    logoSangMo: 'PNG hoặc JPEG, tối đa 2 MB, cạnh dài nhất 2048 px. PNG nền trong suốt cho kết quả đẹp nhất.',
    logoToi: 'Logo nền tối',
    logoToiMo: 'Dùng khi giao diện ở chế độ tối. Bỏ trống thì dùng logo nền sáng.',
    favicon: 'Favicon',
    faviconMo: 'Biểu tượng nhỏ ở tab trình duyệt. Ảnh vuông, nên từ 64 × 64 px.',
    macDinh: 'Đang dùng logo Native mặc định',
    taiAnhLen: 'Tải ảnh lên',
    thayAnh: 'Thay ảnh',
    goAnh: 'Gỡ ảnh',
    dangTaiAnh: 'Đang tải ảnh...',
    luu: 'Lưu thay đổi',
    dangLuu: 'Đang lưu...',
    boThayDoi: 'Huỷ thay đổi',
    lichSuNut: 'Lịch sử thay đổi',
    phienBanHienTai: 'Phiên bản {{so}}, cập nhật {{luc}}',
    phienBanHienTaiBoi: 'Phiên bản {{so}}, cập nhật {{luc}} bởi {{nguoi}}',
    daLuu: 'Đã lưu thay đổi.',
    daDoiAnh: 'Đã cập nhật ảnh.',
    daGoAnh: 'Đã gỡ ảnh.',
    loiTai: 'Không tải được thông tin tổ chức',
    loiTaiMo: 'Kiểm tra kết nối rồi thử lại.',
    xemTruoc: 'Xem trước',
    chuaLuu: {
      tieuDe: 'Rời trang khi chưa lưu?',
      than: 'Tên bạn vừa sửa chưa được lưu và sẽ mất nếu rời khỏi trang này.',
      boThayDoi: 'Bỏ thay đổi',
    },
    truong: {
      tenHienThi: 'Tên hiển thị',
      tenVietTat: 'Tên viết tắt',
      logoSang: 'Logo nền sáng',
      logoToi: 'Logo nền tối',
      favicon: 'Favicon',
    },
    loi: {
      tenRong: 'Nhập tên hiển thị.',
      tenQuaDai: 'Tên hiển thị tối đa 100 ký tự. Rút gọn rồi lưu lại.',
      vietTatRong: 'Nhập tên viết tắt.',
      vietTatQuaDai: 'Tên viết tắt tối đa 12 ký tự. Rút gọn rồi lưu lại.',
      tepDinhDang: 'Chỉ nhận ảnh PNG hoặc JPEG. SVG và WebP không được hỗ trợ, hãy xuất ảnh sang PNG.',
      tepQuaLon: 'Ảnh lớn hơn 2 MB. Chọn ảnh nhỏ hơn hoặc nén lại rồi tải lên.',
      phienBanCu: 'Thông tin vừa được người khác thay đổi. Đã tải lại bản mới nhất, hãy kiểm tra rồi lưu lại.',
    },
    lichSu: {
      tieuDe: 'Lịch sử thay đổi',
      rong: 'Chưa có thay đổi nào',
      loiTai: 'Không tải được lịch sử',
      phienBan: 'Phiên bản {{so}}',
      hienHanh: 'Đang dùng',
      khoiPhucTu: 'Khôi phục từ phiên bản {{so}}',
      boi: 'bởi {{nguoi}}',
      chu: '{{truong}}: {{truoc}} → {{sau}}',
      trong: '(trống)',
      anhMoi: '{{truong}}: đã tải lên',
      anhDoi: '{{truong}}: đã thay ảnh',
      anhGo: '{{truong}}: đã gỡ',
      khoiPhuc: 'Khôi phục',
      xnTieuDe: 'Khôi phục phiên bản {{so}}?',
      xnThan:
        'Tên và ảnh sẽ về đúng nội dung phiên bản {{so}} cho mọi thành viên. Lịch sử không bị xoá: việc này tạo thêm một phiên bản mới và có thể khôi phục lại.',
      nutKhoiPhuc: 'Khôi phục phiên bản {{so}}',
      dangKhoiPhuc: 'Đang khôi phục...',
      daKhoiPhuc: 'Đã khôi phục phiên bản {{so}}.',
    },
  },

  // Tên màu nhấn — `styles/tuy-chinh.ts`.
  mau: {
    indigo: 'Chàm',
    lam: 'Lam',
    'luc-lam': 'Lục lam',
    luc: 'Lục',
    tim: 'Tím',
    hong: 'Hồng sen',
    cam: 'Cam đất',
    than: 'Than chì',
  },

  // Mức bo góc — `styles/tuy-chinh.ts`.
  boGoc: {
    vuong: 'Vuông',
    nhe: 'Bo nhẹ',
    vua: 'Bo vừa',
    'dac-ta': 'Theo thiết kế',
    'rat-tron': 'Rất tròn',
  },

  /**
   * Hậu tố `(mặc định)` ghép vào nhãn màu/bo góc đang là mặc định.
   *
   * Ghép ở chỗ dùng thay vì viết vào từng nhãn: nếu không, đổi mặc định sang
   * màu khác phải sửa hai chuỗi trong hai catalog, và rất dễ còn sót chữ
   * `(mặc định)` trên màu cũ.
   */
  hauTo: {
    macDinh: '{{nhan}} (mặc định)',
  },

  // Màn không có quyền, chưa dựng và 404.
  chan: {
    khongCoQuyenTieuDe: 'Không có quyền truy cập',
    khongCoQuyenMo:
      'Tài khoản của bạn không có quyền xem trang này. Vui lòng liên hệ quản trị viên để được cấp quyền.',
    chuaDungMo: 'Màn này thuộc Phase 6 của kế hoạch dựng lại giao diện, chưa triển khai.',
    khongTimThayTrang: 'Không tìm thấy trang',
    khongTimThayTrangMo:
      'Đường dẫn này không tồn tại. Kiểm tra lại liên kết hoặc chọn một mục ở thanh điều hướng bên trái.',
    saiCongTieuDe: 'Trang này thuộc cổng khác',
    saiCongMo:
      'Tài khoản của bạn không dùng khu vực này của hệ thống. Xin thêm quyền cũng không mở được — hãy quay về khu vực của bạn.',
    veManDauTien: 'Về trang chính',
  },

  // Bảng dữ liệu — `components/shared/DataTable.tsx`.
  bang: {
    phanTrang: 'Phân trang: {{caption}}',
  },

  // ───────────────────────── Điều hướng ─────────────────────────
  /**
   * Nhãn điều hướng (ADR-UI-0010 nới quy ước "giữ nguyên văn" của ADR-UI-0003).
   *
   * Khoá đặt theo **đường dẫn**, không theo nhãn: nhãn là thứ đang dịch, lấy nó
   * làm khoá thì đổi chữ là đổi khoá. Nhãn trùng nhau giữa các nhóm (`Tổng
   * quan` có ở 5 nhóm) nên khoá theo nhãn cũng không phân biệt được.
   */
  nav: {
    nhom: {
      tongQuan: 'TỔNG QUAN',
      cauHinhHeThong: 'CẤU HÌNH HỆ THỐNG',
      triThucAi: 'TRI THỨC AI',
      xuLyTaiLieu: 'XỬ LÝ TÀI LIỆU',
      xuLyVanBan: 'XỬ LÝ VĂN BẢN',
      troLiAi: 'TRỢ LÍ AI',
      thamDinhAi: 'THẨM ĐỊNH AI',
      dinhDanhEkyc: 'ĐỊNH DANH EKYC',
      baoCaoDoanhNghiep: 'BÁO CÁO DOANH NGHIỆP',
      quanLyRpa: 'QUẢN LÝ RPA',
      bienBan: 'BIÊN BẢN CUỘC HỌP',
      module: 'MODULE',
      quanTriNenTang: 'QUẢN TRỊ NỀN TẢNG',
    },
    muc: {
      danhSachModule: 'Danh sách module',
      dashboard: 'Dashboard',
      baoCaoThongKe: 'Báo cáo & Thống kê',
      thongBao: 'Thông báo',

      cauHinhHeThong: 'Cấu hình hệ thống',
      cauHinhVaiTro: 'Cấu hình vai trò',
      cauHinhNguoiDung: 'Cấu hình người dùng',
      coCauToChuc: 'Cơ cấu tổ chức',

      cauHinhXuLyVanBan: 'Cấu hình xử lý văn bản',
      cauHinhDauVaoVb: 'Cấu hình đầu vào xử lý VB',
      danhSachMauVanBan: 'Danh sách mẫu văn bản',
      vaiTroCacCap: 'Danh sách, vai trò các cấp',

      cauHinhOcr: 'Cấu hình OCR',
      thietLapChungTuOcr: 'Thiết lập Chứng từ OCR',

      quanLyTriThuc: 'Quản lý tri thức',
      danhSachBienBan: 'Danh sách biên bản',
      kiemDuyetPheDuyet: 'Kiểm duyệt & Phê duyệt',
      cauHinhDauVaoTriThuc: 'Cấu hình đầu vào tri thức',
      uploadTaiLieu: 'Upload tài liệu',
      ketNoiDuLieuTuDong: 'Kết nối dữ liệu tự động',
      ocrChuanHoa: 'OCR & Chuẩn hoá nội dung',

      nhanDangOcr: 'Nhận dạng OCR',
      quanLyTaiLieuOcr: 'Quản lý tài liệu OCR',
      quanLyChungTu: 'Quản lý Chứng từ',

      xuLyVanBan: 'Xử lý văn bản',
      tomTatVanBan: 'Tóm tắt văn bản',
      soanThaoVanBan: 'Soạn thảo văn bản',
      phanCongNhiemVu: 'Phân công nhiệm vụ',
      quanLyNhiemVu: 'Quản lý nhiệm vụ',

      tongQuan: 'Tổng quan',
      hoiDapAi: 'Hỏi đáp AI',
      troChuyenVoiBot: 'Trò chuyện với bot',
      nguonDuLieu: 'Nguồn dữ liệu',
      ketNoiNguon: 'Kết nối nguồn',
      khoDuLieu: 'Kho dữ liệu',
      congCuApi: 'Công cụ API',
      truyVanMau: 'Truy vấn mẫu',
      phanQuyenDuLieu: 'Phân quyền dữ liệu',
      cauHinhChatbot: 'Cấu hình chatbot',
      vanHanh: 'Vận hành',
      nhatKyTruyVet: 'Nhật ký truy vết',
      thuNghiem: 'Thử nghiệm',

      hoSoThamDinh: 'Hồ sơ thẩm định',
      pheDuyet: 'Phê duyệt',
      thuVienTieuChi: 'Thư viện tiêu chí',
      tichHopApi: 'Tích hợp API',

      dinhDanhNguoiDung: 'Định danh người dùng',
      hoSoDinhDanh: 'Hồ sơ định danh',
      quyTrinhDinhDanh: 'Quy trình định danh',
      ungDungKhoaApi: 'Ứng dụng & khoá API',

      phanHe: 'Phân hệ',
      taiChinh: 'Tài chính',
      duAn: 'Dự án',
      kinhDoanh: 'Kinh doanh',
      nhanSu: 'Nhân sự',
      taiLieuBaoCao: 'Tài liệu báo cáo',
      nguonBaoCao: 'Nguồn dữ liệu',

      dashboardRpa: 'Dashboard RPA',
      troLyAgentRpa: 'Trợ lý Agent RPA',
      quanLyProjectWorkflow: 'Quản lý Project & Workflow',
      cauHinhLoiKetNoi: 'Cấu hình lỗi & thông báo',
      danhSachProject: 'Danh sách Project',
      danhSachLuong: 'Danh sách luồng',
      lichSuLuongChay: 'Lịch sử luồng chạy',
      baoCaoLoiSuCo: 'Báo cáo lỗi & Sự cố',
      quanLyLicenseRpa: 'Quản lý License RPA',
      quanLyUserOpenCore: 'Quản lý User OpenCore',

      // Dự phòng cho mục module động (`Sidebar.tsx`): `nhanThang` luôn thắng nên
      // chuỗi này không hiện ra trong điều kiện bình thường.
      moduleDaCai: 'Module',

      caiDatBaoMat: 'Cài đặt bảo mật',
      khoaApiToChuc: 'Khoá API',
      diaChiRieng: 'Địa chỉ truy cập riêng',
      // ADR-0059 mục 8 "Phía khách": chỉ hiện ở bản on-premise.
      licenseCucBo: 'License bản cài',
    },
  },

  /**
   * Tiêu đề của màn **chưa dựng** vào được bằng URL nhưng không có mục nav.
   *
   * Tách riêng khỏi `nav.muc` vì chúng không phải nhãn điều hướng: không mục
   * nào trong thanh trỏ tới, nên gộp vào `nav.muc` sẽ làm bảng nhãn nav trông
   * như còn mục bị thiếu chỗ đứng.
   */
  route: {
    cauHinhPhanHe: 'Cấu hình phân hệ',
    phanQuyenTaiNguyen: 'Phân quyền tài nguyên',
    hoaDonVatDauVao: 'Hóa đơn VAT đầu vào',
    quenMatKhau: 'Quên mật khẩu',
    datLaiMatKhau: 'Đặt lại mật khẩu',
    chiTietChatbot: 'Chi tiết chatbot',
  },

  // Registry quản trị nền tảng — `app/modules.ts`.
  module: {
    tenant: { nhan: 'Tenant', mo: 'Các tổ chức đang dùng nền tảng' },
    goiThanhToan: { nhan: 'Gói & thanh toán', mo: 'Gói dịch vụ, gói hạn mức, giá vốn model và yêu cầu nạp token' },
    quanLyModule: { nhan: 'Quản lý module', mo: 'Nộp, kiểm và duyệt module mở rộng (plugin)' },
    license: { nhan: 'License', mo: 'License on-premise, hàng chờ duyệt, khoá ký' },
    hangDoi: { nhan: 'Hàng đợi việc', mo: 'Job nền: đang chạy, thất bại' },
    nhatKy: { nhan: 'Nhật ký hệ thống', mo: 'Ai làm gì, lúc nào, trên tenant nào' },
    chinhSachNenTang: {
      nhan: 'Chính sách nền tảng',
      mo: 'Chính sách theo gói, mật khẩu, IP bị chặn, người nhận thông báo',
    },
    nguoiDung: { nhan: 'Người dùng', mo: 'Tài khoản vận hành cổng quản trị' },
    vaiTro: { nhan: 'Vai trò & phân quyền', mo: 'Lưới phân hệ × hành động' },
  khoaApi: { nhan: 'Khoá API', mo: 'Khoá tích hợp cho đối tác' },
    modelAi: { nhan: 'Model AI', mo: 'Danh mục model AI dùng chung cho mọi tenant' },
    mucLog: { nhan: 'Mức log', mo: 'Mức log tạm thời của từng service, tự hết hạn' },
  },

  // Màn giữ chỗ của registry — `modules/sap-co/SapCo.tsx`.
  sapCo: {
    chuaDung: 'Phân hệ này chưa được dựng',
    cotWebhookKetQua: 'Lần gửi webhook cuối',
    chuaGuiWebhook: 'Chưa gửi',
    thuWebhook: 'Thử webhook',
    chuaDungMo:
      'Thuộc đầu việc {{dauViec}}. Màn hình sẽ thay chỗ này mà không phải sửa điều hướng hay route.',
    hangDoiMo: 'Job nền: đang chạy, thất bại, chi tiết từng bước',
    nguoiDungMo: 'Tài khoản vận hành cổng quản trị',
    vaiTroMo: 'Lưới phân hệ × hành động',
    khoaApiMo: 'Khoá tích hợp cấp cho đối tác',
  },

  /*
   * Nhãn vai trò hiện dưới tên người dùng ở thanh điều hướng.
   *
   * `lib/auth/oidc.ts` ánh xạ role lớp 1 của Keycloak sang **khoá** ở đây chứ
   * không sang nhãn: hàm đó là hàm thuần, gọi lúc nhận token, không ở trong cây
   * React nên không có `useT()`. Sidebar dịch lúc vẽ, nhờ vậy nhãn đổi theo
   * ngôn ngữ ngay khi người dùng chuyển mà không phải lấy lại token.
   */
  vaiTro: {
    quanTriNenTang: 'Quản trị nền tảng',
    kiemToan: 'Kiểm toán',
    doiTacPhatTrienModule: 'Đối tác phát triển module',
    quanTriToChuc: 'Quản trị tổ chức',
    phuTrachLicense: 'Phụ trách license',
    nguoiDungToChuc: 'Người dùng tổ chức',
  },
  taiKhoan: {
    // Màn "Tài khoản của tôi" (ADR-0027, ADR-0028), dùng chung hai cổng ở /cai-dat.
    tieuDe: 'Tài khoản của tôi',
    moTa: 'Hồ sơ, mật khẩu, xác thực hai lớp và các phiên đăng nhập của bạn.',
    canKeycloak:
      'Chế độ dữ liệu mẫu không có trang đăng nhập thật để chuyển sang. Đăng nhập bằng Keycloak để dùng chức năng này.',
    hoSo: 'Hồ sơ',
    hoTen: 'Họ tên',
    tenDangNhap: 'Tên đăng nhập',
    emailNhanThu: 'Email nhận thư',
    vaiTro: 'Vai trò',
    hoSoGhiChu: 'Cần sửa họ tên hoặc email, hãy liên hệ người quản trị tài khoản của bạn.',
    dangNhapVaBaoMat: 'Đăng nhập và bảo mật',
    dangNhapVaBaoMatMo: 'Các thao tác dưới đây mở trang đăng nhập, xong sẽ quay lại màn này.',
    matKhau: 'Mật khẩu',
    matKhauDatLuc: 'Đặt lần cuối ngày {{ngay}}.',
    matKhauChuaCo: 'Tài khoản chưa có mật khẩu.',
    doiMatKhau: 'Đổi mật khẩu',
    xacThucHaiLop: 'Xác thực hai lớp',
    dangBat: 'Đang bật',
    chuaBat: 'Chưa bật',
    toChucBatBuoc: 'Tổ chức bắt buộc',
    otpMoDaBat:
      'Mỗi lần đăng nhập, sau mật khẩu sẽ hỏi thêm mã 6 số từ ứng dụng xác thực trên điện thoại.',
    otpMoChuaBat:
      'Bật để mỗi lần đăng nhập hỏi thêm mã 6 số từ ứng dụng xác thực trên điện thoại (Google Authenticator, Microsoft Authenticator...). Lộ mật khẩu thì người khác vẫn không vào được. Cài xong sẽ tạo được mã khôi phục, dùng khi mất điện thoại.',
    ungDungKhongTen: 'Ứng dụng xác thực',
    caiNgay: 'Cài lúc {{ngay}}',
    goUngDung: 'Gỡ ứng dụng',
    caiUngDung: 'Cài ứng dụng xác thực',
    motUngDung:
      'Mỗi tài khoản dùng một ứng dụng xác thực. Đổi điện thoại thì gỡ ứng dụng này, rồi cài lại trên máy mới.',
    motUngDungBatBuoc:
      'Mỗi tài khoản dùng một ứng dụng xác thực. Đổi điện thoại thì gỡ ứng dụng này; tổ chức của bạn bắt buộc xác thực hai lớp nên lần đăng nhập sau sẽ yêu cầu cài lại.',
    batBuocSeHoi:
      'Tổ chức của bạn bắt buộc xác thực hai lớp: lần đăng nhập sau sẽ yêu cầu cài ứng dụng xác thực.',
    // Mã khôi phục — chỉ hiện khi đã cài ứng dụng xác thực.
    maKhoiPhuc: 'Mã khôi phục',
    maKhoiPhucDaTao: 'Đã tạo',
    maKhoiPhucChuaTao: 'Chưa tạo',
    maKhoiPhucMo:
      'Dùng khi mất điện thoại hoặc không mở được ứng dụng xác thực: nhập một mã thay cho mã 6 số. Mỗi lần tạo có 12 mã, mỗi mã chỉ dùng được một lần. Cất ở nơi an toàn, ngoài điện thoại. Trang đăng nhập sẽ hỏi lại mật khẩu trước khi tạo.',
    maKhoiPhucTaoLuc: 'Tạo lúc {{ngay}}.',
    maKhoiPhucTaoLaiMo: 'Tạo lại thì các mã cũ hết hiệu lực.',
    taoMaKhoiPhuc: 'Tạo mã khôi phục',
    taoLaiMaKhoiPhuc: 'Tạo lại mã khôi phục',
    xacNhanGoTieuDe: 'Gỡ ứng dụng xác thực "{{ten}}"?',
    xacNhanGoThan:
      'Mã từ ứng dụng này sẽ không dùng được nữa; muốn dùng lại phải quét mã QR mới. Gỡ ứng dụng cuối cùng thì đăng nhập chỉ còn hỏi mật khẩu, và bộ mã khôi phục cũng bị xoá; cài lại ứng dụng thì tạo bộ mã mới. Trang đăng nhập sẽ hỏi xác nhận lần cuối trước khi gỡ.',
    loiXacThuc: 'Không tải được trạng thái đăng nhập: {{loi}}',
    phienDangNhap: 'Phiên đăng nhập',
    phienDangNhapMo:
      'Các thiết bị đang đăng nhập bằng tài khoản này. Thấy thiết bị lạ thì đăng xuất phiên đó rồi đổi mật khẩu.',
    kenhDangKetNoi: 'Đang kết nối...',
    kenhTrucTiep: 'Cập nhật trực tiếp',
    kenhMatKetNoi: 'Mất kết nối, đang thử lại...',
    phienCaption: 'Danh sách phiên đăng nhập của tài khoản',
    phienRong: 'Không có phiên đăng nhập nào',
    phienNay: 'Phiên này',
    thietBiKhongRo: 'Thiết bị không rõ',
    cotThietBi: 'Thiết bị',
    cotIp: 'Địa chỉ IP',
    cotBatDau: 'Bắt đầu',
    cotTruyCapCuoi: 'Truy cập gần nhất',
    dangXuatPhien: 'Đăng xuất phiên',
    dangXuatPhienKhac: 'Đăng xuất {{so}} phiên khác',
    xacNhanDangXuatTieuDe: 'Đăng xuất phiên trên {{ten}}?',
    xacNhanDangXuatThan:
      'Thiết bị đó sẽ phải đăng nhập lại. Công việc chưa lưu trên thiết bị đó có thể mất.',
    xacNhanDangXuatKhacTieuDe: 'Đăng xuất {{so}} phiên khác?',
    xacNhanDangXuatKhacThan:
      'Mọi thiết bị khác sẽ phải đăng nhập lại; phiên bạn đang dùng giữ nguyên. Công việc chưa lưu trên các thiết bị đó có thể mất.',
    daDangXuatPhien: 'Đã đăng xuất phiên.',
    daDangXuatPhienKhac: 'Đã đăng xuất các phiên khác.',
    loiPhien: 'Không tải được danh sách phiên: {{loi}}',
    // Kết quả action vừa quay về từ trang đăng nhập (`kc_action_status`).
    ketQua: {
      daCaiOtp: 'Đã cài ứng dụng xác thực. Từ lần đăng nhập sau sẽ hỏi mã từ ứng dụng.',
      daGoOtp: 'Đã gỡ ứng dụng xác thực.',
      daDoiMatKhau: 'Đã đổi mật khẩu.',
      huyCaiOtp: 'Đã huỷ cài ứng dụng xác thực. Chưa có gì thay đổi.',
      huyGoOtp: 'Đã huỷ gỡ ứng dụng xác thực. Chưa có gì thay đổi.',
      huyDoiMatKhau: 'Đã huỷ đổi mật khẩu. Mật khẩu cũ vẫn dùng được.',
      daTaoMaKhoiPhuc: 'Đã tạo mã khôi phục. Cất các mã ở nơi an toàn; mỗi mã chỉ dùng được một lần.',
      huyTaoMaKhoiPhuc: 'Đã huỷ tạo mã khôi phục. Chưa có gì thay đổi, mã cũ (nếu có) vẫn dùng được.',
      loi: 'Trang đăng nhập báo lỗi nên chưa làm được. Thử lại; vẫn lỗi thì liên hệ người quản trị.',
      anThongBao: 'Ẩn thông báo',
    },
  },

  /*
   * Cổng ngữ cảnh quyền — `components/layout/NguCanhGate.tsx` (ADR-0034).
   * Phần thân dùng lại câu của `dangNhap.sso` và `loi`.
   */
  nguCanh: {
    dangTai: 'Đang tải quyền của tài khoản...',
    loiTieuDe: 'Chưa tải được quyền của tài khoản',
    // 403 PERMISSION_DENIED: câu trả lời dứt khoát, không mời thử lại.
    khongThuocCongTieuDe: 'Tài khoản không dùng được cổng này',
    khongThuocCongMoTa:
      'Tài khoản này không thuộc nhóm nhân sự nền tảng nên không mở được cổng quản trị. Đăng nhập bằng tài khoản khác, hoặc liên hệ quản trị nền tảng nếu bạn cho rằng đây là nhầm lẫn.',
    biKhoaTieuDe: 'Tài khoản đang bị khoá',
    // Gateway chặn mọi tài khoản của tổ chức, kể cả quản trị tổ chức (403 TENANT_SUSPENDED, ADR-0077).
    tamNgungTieuDe: 'Tổ chức đang tạm ngưng',
    tamNgungMoTa:
      'Tổ chức của bạn đang tạm ngưng sử dụng, nên tài khoản này tạm thời không vào được cổng. Liên hệ quản trị tổ chức để biết khi nào truy cập lại được.',
    // Quản trị tổ chức cũng bị chặn; chỉ nhà cung cấp dịch vụ mở lại được.
    tamNgungMoTaQuanTri: 'Tổ chức đang tạm ngưng. Liên hệ nhà cung cấp dịch vụ để mở lại.',
    // Gateway chặn theo IP (403 IP_BLOCKED / IP_NOT_ALLOWED, ADR-0059 mục 6.1). Thân lỗi không có IP nên không nêu IP.
    ipTieuDe: 'Truy cập từ địa chỉ IP này đã bị chặn',
    ipBiChanMoTa:
      'Tổ chức của bạn không cho phép truy cập từ mạng bạn đang dùng. Chuyển sang mạng được phép (ví dụ mạng văn phòng hoặc VPN của tổ chức) rồi mở lại trang, hoặc liên hệ quản trị tổ chức.',
    ipNgoaiDanhSachMoTa:
      'Mạng bạn đang dùng nằm ngoài danh sách IP được phép của tổ chức. Chuyển sang mạng được phép (ví dụ mạng văn phòng hoặc VPN của tổ chức) rồi mở lại trang, hoặc liên hệ quản trị tổ chức để thêm mạng này vào danh sách.',
    // Cổng nền tảng: chỉ blocklist nền tảng chặn được nhóm route nền tảng.
    ipBiChanMoTaNenTang:
      'Nền tảng không cho phép truy cập từ mạng bạn đang dùng. Chuyển sang mạng được phép rồi mở lại trang, hoặc liên hệ quản trị nền tảng.',
    // Xác thực tăng cường (401 CAN_XAC_THUC_TANG_CUONG, ADR-0090 mục 2)
    canOtpTieuDe: 'Cần xác thực thêm bằng mã OTP',
    canOtpMoTa:
      'Bạn đang truy cập từ ngoài mạng tin cậy của tổ chức, nên tổ chức yêu cầu nhập mã OTP cho phiên này. Chưa cài ứng dụng xác thực thì bạn sẽ được hướng dẫn cài.',
    canOtpLaiMoTa:
      'Bạn vừa xác thực nhưng hệ thống vẫn chưa nhận mã OTP cho phiên này. Thử lại một lần; còn lặp lại thì kết nối mạng công ty hoặc liên hệ quản trị tổ chức.',
    saiDiaChiTieuDe: 'Địa chỉ này không thuộc tổ chức của bạn',
    saiDiaChiMoTa:
      '{{host}} là địa chỉ riêng của một tổ chức khác, hoặc đã ngừng phục vụ. Mở cổng tổ chức bằng địa chỉ chung của nền tảng để tiếp tục làm việc.',
    saiDiaChiMoTaKhongLink:
      '{{host}} là địa chỉ riêng của một tổ chức khác, hoặc đã ngừng phục vụ. Hỏi quản trị tổ chức địa chỉ đúng để vào cổng.',
    nutVeDiaChiDung: 'Mở {{host}}',
    nutXacThucOtp: 'Xác thực bằng mã OTP',
    // Băng báo đầu vùng nội dung, khi tổ chức vừa bị tạm ngưng mà cổng chưa tải lại quyền.
    bangTamNgung: 'Tổ chức đang tạm ngưng. Liên hệ nhà cung cấp dịch vụ để mở lại.',
    bangLuuTru:
      'Tổ chức đã ngừng sử dụng và đang được lưu trữ. Dữ liệu bị xoá vĩnh viễn sau 30 ngày lưu trữ; liên hệ nhà cung cấp dịch vụ để xuất dữ liệu hoặc mở lại.',
  },

  // ───────────────────────── Đăng nhập ─────────────────────────
  dangNhap: {
    tieuDe: 'Đăng nhập',
    moTa: 'Đăng nhập để sử dụng cổng FOXAI Native.',
    bangTaiKhoanFoxai: 'Đăng nhập bằng tài khoản FOXAI',
    chuyenSangTrangChung: 'Bạn sẽ được chuyển sang trang đăng nhập chung',
    foxaiId: 'FoxAI ID',
    roiQuayLaiDay: ', rồi quay lại đây.',
    hoac: 'hoặc',
    tenDangNhap: 'Tên đăng nhập / Email',
    tenDangNhapGoiY: 'admin, vanhanh, tenant hoặc tên bất kỳ',
    matKhau: 'Mật khẩu',
    ghiNho: 'Ghi nhớ đăng nhập',
    quenMatKhau: 'Quên mật khẩu?',
    dangDangNhap: 'Đang đăng nhập...',
    dangMoTrangChung: 'Đang mở trang đăng nhập FoxAI ID...',
    khongMoDuocTrangDangNhap: 'Không mở được trang đăng nhập. Vui lòng thử lại sau ít phút.',
    banQuyen: '© {{nam}} FOXAI. All rights reserved.',
    // Lý do đăng nhập một lần thất bại (§10.16).
    sso: {
      thieu_ma: 'Phản hồi từ hệ thống đăng nhập không đầy đủ. Vui lòng thử lại.',
      state_khong_khop: 'Phiên đăng nhập không khớp hoặc đã quá hạn. Vui lòng thử lại.',
      doi_ma_that_bai: 'Không hoàn tất được đăng nhập. Vui lòng thử lại.',
      phien_qua_lon: 'Phiên đăng nhập vượt giới hạn cho phép. Vui lòng báo quản trị viên.',
      access_denied: 'Bạn đã huỷ đăng nhập.',
      chua_lien_ket:
        'Tài khoản của bạn chưa được liên kết với đăng nhập một lần. Vui lòng liên hệ quản trị viên, hoặc đăng nhập bằng mật khẩu.',
      khong_tra_duoc_quyen:
        'Chưa xác định được quyền của tài khoản lúc này. Vui lòng thử lại sau ít phút.',
      cho_duyet:
        'Tài khoản của bạn đã được tạo và đang chờ quản trị viên cấp quyền. Bạn sẽ đăng nhập được ngay sau khi được duyệt.',
      trung_email:
        'Tài khoản của bạn đã tồn tại trên hệ thống nhưng chưa được liên kết với đăng nhập một lần. Vui lòng liên hệ quản trị viên để liên kết.',
      trung_ten_dang_nhap:
        'Tên đăng nhập của bạn đang trùng với một tài khoản khác trên hệ thống. Vui lòng liên hệ quản trị viên.',
      bi_khoa: 'Tài khoản của bạn đang bị vô hiệu hoặc bị khoá. Vui lòng liên hệ quản trị viên.',
      token_khong_hop_le: 'Phiên đăng nhập không hợp lệ. Vui lòng thử lại.',
      chua_kiem_duoc: 'Hệ thống chưa phản hồi được lúc này. Vui lòng thử lại sau ít phút.',
      phien_ket_thuc:
        'Phiên đăng nhập đã kết thúc, có thể do bạn đăng xuất ở thiết bị khác. Đăng nhập lại để tiếp tục.',
      macDinh: 'Đăng nhập một lần không thành công ({{ma}}).',
    },
  },

  // ───────────────────────── Dashboard ─────────────────────────
  baoCao: {
    theTongQuan: 'Tổng quan',
    theTokenAi: 'Token AI',
    tieuDe: 'Báo cáo & Thống kê',
    phuDe: 'Tổng hợp số liệu OCR, kho tri thức, chatbot và hệ thống',
    loiMotPhan: 'Có {{so}} nguồn số liệu chưa tải được; các phần còn lại vẫn hiển thị.',
    loiTatCa: 'Không tải được số liệu: {{loi}}',
    // Panel 1 — OCR.
    ocrTieuDe: 'Xử lý OCR',
    mauOcr: 'Mẫu OCR',
    tongChungTu: 'Tổng chứng từ',
    daXacNhan: 'Đã xác nhận',
    vaoKho: 'Vào kho tri thức',
    loaiChungTu: 'Loại chứng từ OCR',
    chuaCoChungTu: 'Chưa có chứng từ nào.',
    // Panel 2 — Kho tri thức.
    khoTieuDe: 'Kho tri thức',
    boTriThuc: 'Bộ tri thức',
    tongTep: 'Tổng tệp',
    phongBan: 'Phòng ban',
    tepPdf: 'Tệp PDF',
    tepTheoBo: 'Tệp theo bộ tri thức',
    chuaCoBo: 'Chưa có bộ tri thức nào.',
    // Panel 3 — Chatbot.
    botTieuDe: 'Chatbot AI',
    tongBot: 'Tổng chatbot',
    botHoatDong: 'Đang hoạt động',
    coKichBan: 'Có kịch bản',
    traLoiDuoc: 'Trả lời được',
    botTheoPhamVi: 'Bot theo phạm vi trả lời',
    chuaCoBot: 'Chưa cấu hình chatbot nào.',
    pv: {
      knowledge_only: 'Chỉ trong tri thức',
      open: 'Mở rộng ngoài tri thức',
    },
    // Panel 4 — Hệ thống.
    heThongTieuDe: 'Cấu hình hệ thống',
    nguoiDung: 'Người dùng',
    nguoiDungHoatDong: 'Đang hoạt động',
    vaiTro: 'Vai trò',
    nguoiDungTheoPhongBan: 'Người dùng theo phòng ban',
    chuaCoNguoiDung: 'Chưa có dữ liệu người dùng.',
    chuaPhanCong: 'Chưa phân công',
    // Xuất báo cáo
    xuatBaoCao: 'Xuất báo cáo',
    xuatExcel: 'Excel (.xlsx)',
    xuatPdf: 'PDF (.pdf)',
    xuatTieuDe: 'Báo cáo & Thống kê — FOXAI Native',
    xuatNgay: 'Xuất lúc {{ngay}}',
    // Cột xuất báo cáo
    xuatTen: 'Tên',
    xuatSoLuong: 'Số lượng',
    xuatNhom: 'Nhóm',
    xuatChiSo: 'Chỉ số',
    xuatGiaTri: 'Giá trị',
    xuatTongQuan: 'Tổng quan',
    xuatChiTiet: 'Chi tiết',
    xuatNhomOcr: 'OCR',
    xuatNhomKho: 'Kho tri thức',
    xuatNhomBot: 'Chatbot AI',
    xuatNhomHeThong: 'Hệ thống',
    xuatSheetOcr: 'OCR — Loại chứng từ',
    xuatSheetKho: 'Kho tri thức — Tệp theo bộ',
    xuatSheetBot: 'Chatbot — Bot theo phạm vi',
    xuatSheetHeThong: 'Hệ thống — Người dùng theo phòng ban',
  },

  dashboard: {
    tieuDe: 'Dashboard Tổng Quan',
    aiDangHoatDong: 'AI đang hoạt động',
    // Bốn ô chỉ số.
    tongChungTu: 'Tổng chứng từ',
    soMauOcr: '{{so}} mẫu OCR',
    boTriThuc: 'Bộ tri thức',
    tepTaiLieu: '{{so}} tệp tài liệu',
    chatbotHoatDong: 'Chatbot hoạt động',
    botDaCauHinh: '{{so}} bot đã cấu hình',
    nguoiDungHoatDong: 'Người dùng hoạt động',
    tongTaiKhoan: '{{so}} tổng tài khoản',
    dangTai: 'Đang tải...',
    // Dải lỗi.
    loiSoLieu: 'Không tải được số liệu tổng quan: {{loi}}',
    // Ô hoạt động gần đây.
    hoatDongGanDay: 'Hoạt động gần đây',
    chuaCoHoatDong: 'Chưa có hoạt động nào.',
    hdOcrHoanTat: 'OCR hoàn tất {{so}} chứng từ trong hệ thống',
    hdKhoTriThuc: 'Kho tri thức đang có {{so}} tệp tài liệu',
    hdChatbot: '{{so}} chatbot AI đang hoạt động',
    hdNguoiDung: '{{dangHoatDong}}/{{tong}} người dùng đang hoạt động',
    hdDaXacNhan: '{{so}} chứng từ đã được xác nhận',
    hdDaChuyenKho: '{{so}} chứng từ đã chuyển vào kho tri thức',
    // Ô chứng từ theo trạng thái.
    chungTuTheoTrangThai: 'Chứng từ theo trạng thái',
    chuaCoChungTu: 'Chưa có chứng từ nào.',
    ctNhap: 'Nhập',
    ctDaXacNhan: 'Đã xác nhận',
    ctDaChuyenKho: 'Đã chuyển kho tri thức',
    // Ô xử lý tài liệu.
    xuLyTaiLieu: 'Xử lý tài liệu',
    mauOcr: 'Mẫu OCR',
    chungTuNhap: 'Chứng từ nhập',
    daXacNhan: 'Đã xác nhận',
    daVaoKho: 'Đã vào kho',
    // Ô tri thức theo phòng ban.
    triThucTheoPhongBan: 'Bộ tri thức theo phòng ban',
    chuaCoDuLieu: 'Chưa có dữ liệu.',
    phongBanKhac: 'Khác',
    // Ô trạng thái chatbot.
    trangThaiChatbot: 'Trạng thái Chatbot AI',
    cauHinh: 'Cấu hình',
    chuaCauHinhChatbot: 'Chưa cấu hình chatbot nào.',
    botTat: 'Tắt',
    botChuaSanSang: 'Chưa sẵn sàng',
    botHoatDong: 'Hoạt động',
    // Ô cấu hình hệ thống.
    cauHinhHeThong: 'Cấu hình hệ thống',
    tongNguoiDung: 'Tổng người dùng',
    dangHoatDong: 'Đang hoạt động',
    soVaiTro: 'Vai trò',
    soPhongBan: 'Phòng ban',
    quanLy: 'Quản lý',
    // Ô kho tri thức.
    khoTriThuc: 'Kho tri thức AI',
    chuaCoBoTriThuc: 'Chưa có bộ tri thức nào.',
    soTep: '{{so}} tệp',
    // Ô phê duyệt tri thức.
    pheDuyetTriThuc: 'Phê duyệt tri thức',
    pheDuyetMo:
      'Hệ thống kiểm duyệt & phê duyệt tri thức đang sẵn sàng tiếp nhận hồ sơ mới.',
    sanSang: 'Sẵn sàng',
    mo: 'Mở',
  },

  // ───────────────────────── Tenant ─────────────────────────
  tenant: {
    // Màn danh sách.
    danhSachTieuDe: 'Danh sách tenant',
    danhSachMoTa:
      'Các tổ chức dùng nền tảng: trạng thái vòng đời, gói hạn mức và cách triển khai',
    taoTenant: 'Tạo tenant',
    bangCaption: 'Danh sách tenant trên nền tảng',
    // Bốn ô thống kê.
    tkTongSo: 'Tổng số tenant',
    tkTongSoSub: 'không tính tenant đã xoá',
    tkDangPhucVu: 'Đang phục vụ',
    tkDangPhucVuSub: 'dùng thử, chính thức hoặc đang ân hạn',
    tkChoTrienKhai: 'Chờ triển khai',
    tkChoTrienKhaiSub: 'hồ sơ nháp hoặc đang triển khai',
    tkTamNgung: 'Tạm ngưng',
    tkTamNgungSub: 'người dùng đang bị chặn truy cập',
    // Bộ lọc.
    timTheo: 'Tìm theo tên, slug hoặc mã số thuế...',
    tatCaTrangThai: 'Tất cả trạng thái',
    tatCaGoi: 'Mọi gói hạn mức',
    tatCaCachTrienKhai: 'Mọi cách triển khai',
    nhanGoiDichVu: 'Gói hạn mức',
    nhanCachTrienKhai: 'Cách triển khai',
    // Cột bảng.
    cotTenGoi: 'Tên gọi',
    cotGoi: 'Gói hạn mức',
    cotTrienKhai: 'Triển khai',
    cotVung: 'Vùng',
    cotTaoLuc: 'Tạo lúc',
    tongSoTenant: 'Tổng {{so}} tenant',
    // Thao tác trên tenant.
    xemChiTiet: 'Xem chi tiết',
    suaHoSo: 'Sửa hồ sơ',
    luuHoSo: 'Lưu hồ sơ',
    duyet: 'Duyệt và triển khai',
    tamNgung: 'Tạm ngưng',
    tamNgungMo: 'Chặn người dùng của tenant truy cập',
    khoiPhuc: 'Khôi phục',
    // Trạng thái rỗng và lỗi.
    loiDanhSach: 'Không tải được danh sách tenant: {{loi}}',
    rongLoc: 'Không có tenant nào khớp bộ lọc hiện tại',
    rongThat: 'Chưa có tenant nào trên nền tảng',
    rongThatMo: 'Tạo tenant đầu tiên để bắt đầu vận hành nền tảng.',
    // Màn chi tiết.
    loiChiTiet: 'Không tải được thông tin tenant: {{loi}}',
    khongTimThay: 'Không tìm thấy tenant',
    khongTimThayMo:
      'Không có tenant nào mang mã "{{ma}}". Kiểm tra lại đường dẫn, hoặc mở tenant từ danh sách.',
    veDanhSach: 'Về danh sách tenant',
    // Dải báo đầu màn chi tiết.
    bannerTamNgung:
      'Tạm ngưng từ {{luc}}. Mọi người dùng của tenant, kể cả quản trị tổ chức, đang bị chặn truy cập.',
    bannerDangTrienKhai:
      'Workflow onboarding đang chạy. Hồ sơ chưa sửa được cho tới khi triển khai xong; tiến độ tự cập nhật ở thẻ Tiến độ triển khai.',
    bannerTrienKhaiLoi:
      'Lần triển khai trước thất bại nên tenant đã trở về hồ sơ nháp. Xem bước lỗi ở thẻ Tiến độ triển khai.',
    bannerLuuTru:
      'Tenant đã lưu trữ chỉ còn xuất được dữ liệu, và bị xoá vĩnh viễn sau 30 ngày lưu trữ.',
    bannerDaXoa: 'Tenant đã xoá lúc {{luc}}. Slug và domain của tenant này không dùng lại được.',
    // Tab.
    tabTongQuan: 'Tổng quan',
    tabDomainVaDiaChi: 'Domain',
    tabDomain: 'Domain email',
    tabTienTrinh: 'Tiến độ triển khai',
    tabBaoMat: 'Bảo mật',
    tabLienHe: 'Liên hệ',
    tabNguoiDung: 'Người dùng',
    // Tab Người dùng (ADR-0101).
    nguoiDung: {
      tieuDe: 'Người dùng của tenant',
      tim: 'Tìm theo tên',
      caption: 'Người dùng của tenant',
      rong: 'Tenant chưa có người dùng nào',
      rongLoc: 'Không có người dùng nào khớp tên này',
      loi: 'Không tải được người dùng: {{loi}}',
    },
    // Ô hồ sơ.
    hoSo: 'Hồ sơ',
    dongTenHienThi: 'Tên hiển thị',
    dongTenPhapLy: 'Tên pháp lý',
    dongMaSoThue: 'Mã số thuế',
    dongSlug: 'Slug',
    dongTrangThai: 'Trạng thái',
    dongGoiDichVu: 'Gói hạn mức',
    dongCachTrienKhai: 'Cách triển khai',
    dongTang: 'Tầng',
    dongVung: 'Vùng',
    dongTaoLuc: 'Tạo lúc',
    dongCapNhatLuc: 'Cập nhật lúc',
    // Ô hạ tầng.
    haTang: 'Hạ tầng',
    haTangOnPremise:
      'Tenant on-premise do khách tự vận hành. Nền tảng chỉ quản lý hồ sơ, hợp đồng và license; không tạo database, group Keycloak hay domain cho tenant này.',
    dongCumDb: 'Cụm cơ sở dữ liệu',
    dongTenDb: 'Database lõi',
    dongPhienBanSchema: 'Phiên bản schema',
    dongGroupKeycloak: 'Group Keycloak',
    // Ô người liên hệ.
    nguoiLienHe: 'Người liên hệ quản trị',
    nguoiLienHeCaption: 'Người liên hệ quản trị của tenant',
    nguoiLienHeRong: 'Chưa có người liên hệ quản trị nào',
    cotHoTen: 'Họ tên',
    cotEmail: 'Email',
    cotDienThoai: 'Điện thoại',
    nhanChinh: 'Chính',
    // Ô hạn mức.
    hanMuc: 'Hạn mức tài nguyên',
    hanMucCaption: 'Hạn mức tài nguyên của tenant',
    hanMucRong: 'Hạn mức này chưa khai giới hạn tài nguyên nào',
    cotTaiNguyen: 'Tài nguyên',
    cotGioiHan: 'Giới hạn',
    cotDaDung: 'Đã dùng',
    chuaDo: 'Chưa đo',
    // Tab domain.
    domainCaption: 'Domain email của tenant',
    domainRong: 'Tenant chưa khai domain email nào',
    cotDomain: 'Domain',
    cotXacMinh: 'Xác minh',
    cotCachXacMinh: 'Cách xác minh',
    cotThemLuc: 'Thêm lúc',
    daXacMinh: 'Đã xác minh',
    chuaXacMinh: 'Chưa xác minh',
    // Bản ghi DNS và xác minh thủ công (ADR-0054).
    nguoiDuyet: 'Người duyệt: {{nguoi}}',
    nguoiDuyetLaBan: 'bạn',
    lyDoDuyet: 'Lý do: {{lyDo}}',
    // Thêm domain — chỉ Platform Admin (ADR-0082).
    tdThem: 'Thêm domain',
    tdDangThem: 'Đang thêm...',
    tdRongMo: 'Thêm domain email của khách rồi gửi bản ghi DNS cho khách để xác minh.',
    tdRongTuyChon: 'Tổ chức dùng email cá nhân nên không cần domain. Thêm domain nếu tổ chức có email công ty.',
    tdTieuDe: 'Thêm domain email cho tenant',
    tdThan:
      'Thêm xong, bản ghi DNS cần tạo hiện ngay bên dưới để gửi cho khách. Người dùng chỉ đăng nhập bằng domain này được sau khi domain được xác minh.',
    tdDomain: 'Domain',
    tdDomainGoiY: 'Chỉ phần sau dấu @ của email, không có http:// hay www.',
    tdDomainViDu: 'chi-nhanh.congty.vn',
    tdDomainTrong: 'Nhập domain cần thêm.',
    tdDomainSai: 'Domain chưa đúng dạng, ví dụ congty.vn. Domain có dấu thì nhập dạng punycode (xn--...).',
    tdDomainDai: 'Domain dài quá {{so}} ký tự.',
    tdDaThem: 'Đã thêm domain {{domain}}. Gửi bản ghi DNS bên dưới cho khách để xác minh.',
    dnsKhoiNhan: 'Bản ghi DNS của {{domain}}',
    dnsTieuDe: 'Bản ghi DNS cần tạo cho {{domain}}',
    dnsMo: 'Gửi bản ghi này cho người quản lý DNS của khách. Hệ thống tự kiểm 5 phút một lần; DNS có thể mất tới vài giờ để cập nhật.',
    dnsLoai: 'Loại',
    dnsTen: 'Tên',
    dnsGiaTri: 'Giá trị',
    dnsSaoChep: 'Sao chép',
    dnsDaSaoChep: 'Đã sao chép',
    dnsKhongSaoChepDuoc: 'Trình duyệt chưa cho sao chép. Chọn giá trị trong ô rồi sao chép bằng bàn phím.',
    dnsSaoChepNhan: 'Sao chép {{truong}} của bản ghi DNS',
    dnsChuaCoBanGhi: 'Máy chủ chưa trả bản ghi DNS cho domain này. Tải lại trang để lấy bản ghi.',
    dnsKiemNgay: 'Kiểm tra lại ngay',
    dnsDangKiem: 'Đang kiểm...',
    dnsDaKhop: 'Domain {{domain}} đã xác minh bằng DNS TXT.',
    dnsChuaKhop: 'Chưa thấy bản ghi TXT khớp cho {{domain}}. DNS có thể mất tới vài giờ để cập nhật; hệ thống vẫn tự kiểm 5 phút một lần.',
    domainDaXacMinhTruoc: 'Domain {{domain}} đã được xác minh từ trước.',
    kiemDnsLoi: 'Chưa kiểm được DNS: {{loi}}',
    // Gỡ domain email (GG-FX-39): chỉ Platform Admin.
    gdMo: 'Gỡ domain',
    gdTieuDe: 'Gỡ domain {{domain}}?',
    gdThan: 'Người dùng có email @{{domain}} không còn được điều hướng theo domain này. Không hoàn tác được; muốn dùng lại phải thêm và xác minh lại.',
    gdThanChinhMoi: '{{domain}} đang là domain chính; {{moi}} sẽ thành domain chính.',
    gdThanChinhHet: '{{domain}} là domain cuối cùng; tenant sẽ không còn domain chính.',
    gdDangGo: 'Đang gỡ...',
    gdDaGo: 'Đã gỡ domain {{domain}}.',
    xmMo: 'Xác minh thủ công',
    xmTieuDe: 'Xác minh thủ công domain {{domain}}?',
    xmThan: 'Sau khi xác minh, mọi người có email @{{domain}} đăng nhập sẽ được đưa vào tenant này, và tenant đang chờ ở bước xác minh domain sẽ triển khai tiếp.',
    xmCanhBao: 'Chỉ xác minh khi đã xác nhận khách sở hữu domain qua kênh khác, như hợp đồng hay email gửi từ chính domain. Thao tác được ghi nhật ký kèm lý do và không hoàn tác được trên giao diện.',
    xmLyDo: 'Lý do xác minh',
    xmLyDoGoiY: 'Ghi căn cứ xác nhận; lý do hiện ở bảng domain và trong nhật ký.',
    xmLyDoViDu: 'Ví dụ: Xác nhận qua hợp đồng số 12/2026/HĐ',
    xmLyDoTrong: 'Nhập lý do xác minh, ví dụ số hợp đồng đã đối chiếu.',
    xmLyDoDai: 'Lý do dài tối đa {{so}} ký tự.',
    xmNut: 'Xác minh {{domain}}',
    daXacMinhThuCong: 'Đã xác minh domain {{domain}}.',
    // Tab tiến độ.
    workflowOnboarding: 'Workflow onboarding',
    tuCapNhat: 'Tự cập nhật mỗi 5 giây',
    chuaTrienKhai: 'Tenant này chưa được triển khai lần nào.',
    soBuoc: 'Xong {{xong}}/{{tong}} bước',
    capNhatLuc: 'Cập nhật lúc {{luc}}',
    daThuLai: 'đã thử lại {{so}} lần',
    // Hộp thoại tạo.
    dialogTao: 'Tạo tenant',
    dangTao: 'Đang tạo...',
    nhomHoSo: 'Hồ sơ tổ chức',
    nhomGoi: 'Gói',
    nhomTrienKhai: 'Cách triển khai',
    nhomDomain: 'Domain email',
    fEmailCaNhan: 'Tổ chức dùng email cá nhân (không có domain riêng)',
    fEmailCaNhanMo: 'Cả tổ chức sẽ bắt buộc xác thực hai lớp.',
    nhomLienHe: 'Người liên hệ quản trị',
    fTenHienThi: 'Tên hiển thị',
    fTenHienThiGoiY: 'Ví dụ: FoxAI',
    fTenHienThiTrong: 'Nhập tên hiển thị.',
    fSlug: 'Slug',
    fSlugMo: 'Khoá lại khi hồ sơ được duyệt.',
    fSlugTrong: 'Nhập slug, hoặc nhập tên hiển thị để có gợi ý.',
    fSlugNgan: 'Slug cần ít nhất {{so}} ký tự.',
    fSlugDai: 'Slug dài tối đa {{so}} ký tự.',
    fSlugDinhDang:
      'Chỉ dùng chữ thường không dấu, chữ số và gạch ngang; bắt đầu bằng chữ, không có hai gạch ngang liền nhau, không kết thúc bằng gạch ngang.',
    fSlugDaKhoa: 'Slug đã khoá từ khi hồ sơ được duyệt.',
    fTenPhapLy: 'Tên pháp lý',
    fTenPhapLyGoiY: 'Tên trên giấy chứng nhận đăng ký doanh nghiệp',
    fMaSoThue: 'Mã số thuế',
    fQuaDai: 'Dài tối đa {{so}} ký tự.',
    fKhongXoaDuoc:
      'Không xoá trắng được trường đã có giá trị. Nhập giá trị mới, hoặc giữ nguyên.',
    fCachTrienKhai: 'Cách triển khai',
    fTang: 'Tầng',
    fDomainSo: 'Domain {{so}}',
    fDomainGoiY: 'congty.com.vn',
    fDomainDinhDang:
      'Không đúng định dạng domain, ví dụ congty.com.vn. Domain có dấu nhập ở dạng punycode (xn--…).',
    fDomainDai: 'Domain dài tối đa {{so}} ký tự.',
    fDomainLap: 'Domain này đã có ở dòng trên.',
    themDomain: 'Thêm domain',
    boDomain: 'Bỏ domain {{so}}',
    lienHeChinh: 'Người liên hệ chính',
    lienHeSo: 'Người liên hệ {{so}}',
    fHoTen: 'Họ tên',
    fHoTenTrong: 'Nhập họ tên.',
    fEmail: 'Email',
    fEmailTrong: 'Nhập email.',
    fEmailSai: 'Email chưa đúng định dạng, ví dụ an.nguyen@congty.com.vn.',
    // ADR-0101: hộp thư nhận thư mời và thông báo, khi khác email đăng nhập.
    fEmailNhanThu: 'Email nhận thư (nếu khác)',
    fDienThoai: 'Điện thoại',
    themLienHe: 'Thêm người liên hệ',
    boLienHe: 'Bỏ người liên hệ {{so}}',
    // Hộp thoại sửa.
    dialogSua: 'Sửa hồ sơ {{ten}}',
    // Hộp xác nhận: tiêu đề hỏi, thân nói hậu quả, nút nhắc lại việc sắp làm.
    xnDuyetTieuDe: 'Duyệt và triển khai tenant {{ten}}?',
    xnDuyetThanSaas:
      'Slug {{slug}} khoá vĩnh viễn từ lúc duyệt vì nằm trong tên database và đường dẫn group Keycloak. Workflow onboarding chạy từ tạo database, chạy migration, tạo group và Tenant Admin đầu tiên, tới khi tenant sang Dùng thử.',
    xnDuyetThanOnPremise:
      'Tenant on-premise không chạy onboarding: hồ sơ sang Đang hoạt động, nền tảng không tạo database, group Keycloak hay domain. Slug khoá vĩnh viễn từ lúc duyệt.',
    xnDuyetChoXacMinh:
      'Domain {{domain}} chưa xác minh, nên workflow sẽ dừng ở bước tạo group Keycloak và chờ xác minh, tối đa 14 ngày. Các bước trước đó vẫn chạy ngay.',
    xnDuyetNut: 'Duyệt và triển khai',
    canTroTieuDe: 'Hồ sơ chưa đủ để triển khai:',
    canTroDomain: 'Chưa có domain email, mà onboarding cần domain chính để Tenant Admin đăng nhập.',
    canTroLienHe:
      'Chưa có người liên hệ quản trị, mà onboarding tạo Tenant Admin đầu tiên từ người liên hệ chính.',
    canTroEmail:
      'Email của người liên hệ chính không thuộc domain chính {{domain}}, nên Tenant Admin đầu tiên sẽ không đăng nhập được.',
    xnTamNgungTieuDe: 'Tạm ngưng tenant {{ten}}?',
    xnTamNgungThan:
      'Mọi người dùng của tenant, kể cả quản trị tổ chức, sẽ bị chặn truy cập. Dữ liệu giữ nguyên, và khôi phục được bất cứ lúc nào.',
    xnTamNgungNut: 'Tạm ngưng tenant',
    xnKhoiPhucTieuDe: 'Khôi phục tenant {{ten}}?',
    xnKhoiPhucThanSaas:
      'Người dùng của tenant truy cập lại được ngay. Tenant về Đang hoạt động nếu có thuê bao còn hiệu lực, ngược lại về Dùng thử.',
    xnKhoiPhucThanOnPremise: 'Tenant về trạng thái Đang hoạt động.',
    xnKhoiPhucNut: 'Khôi phục tenant',
    // Toast, thể đã hoàn tất.
    daTao: 'Đã tạo tenant {{ten}}.',
    daLuu: 'Đã lưu hồ sơ tenant {{ten}}.',
    daDuyetSaas: 'Đã duyệt tenant {{ten}}. Workflow onboarding đang chạy, xem ở thẻ Tiến độ triển khai.',
    daDuyetOnPremise: 'Đã duyệt tenant {{ten}}. Tenant chuyển sang Đang hoạt động.',
    daTamNgung: 'Đã tạm ngưng tenant {{ten}}.',
    daKhoiPhuc: 'Đã khôi phục tenant {{ten}}, trạng thái hiện tại: {{trangThai}}.',
    // Giá trị liệt kê của backend (openapi/platform-api.yaml).
    trangThai: {
      DRAFT: 'Hồ sơ nháp',
      PROVISIONING: 'Đang triển khai',
      TRIAL: 'Dùng thử',
      ACTIVE: 'Đang hoạt động',
      GRACE_PERIOD: 'Ân hạn',
      SUSPENDED: 'Tạm ngưng',
      ARCHIVED: 'Đã lưu trữ',
      DELETED: 'Đã xoá',
    },
    // `plan_code`: hạn mức tài nguyên (`plan_quota`), không phải gói dịch vụ của
    // ADR-0019. Hai thứ khác nhau nên nhãn không được gọi đây là "gói dịch vụ".
    goi: {
      TRIAL: 'Dùng thử',
      STANDARD: 'Tiêu chuẩn',
      PROFESSIONAL: 'Chuyên nghiệp',
      ENTERPRISE: 'Doanh nghiệp',
    },
    cachTrienKhai: {
      SAAS: 'SaaS',
      ON_PREMISE: 'On-premise',
    },
    // Tên tầng giữ như đặc tả Chương 3: "tầng Pool", "tầng Dedicated", "tầng Isolated".
    tang: {
      POOL: 'Pool',
      DEDICATED: 'Dedicated',
      ISOLATED: 'Isolated',
    },
    trangThaiBuoc: {
      PENDING: 'Chờ',
      RUNNING: 'Đang chạy',
      SUCCEEDED: 'Xong',
      FAILED: 'Lỗi',
    },
    cachXacMinh: {
      DNS_TXT: 'DNS TXT',
      ADMIN_APPROVAL: 'Quản trị nền tảng duyệt',
    },
    taiNguyen: {
      MAX_USERS: 'Người dùng',
      DB_BYTES: 'Dung lượng database',
      S3_BYTES: 'Dung lượng lưu trữ tệp',
      NOTIFY_MESSAGES_MONTH: 'Tin thông báo mỗi tháng',
    },
    taiNguyenLa: 'Tài nguyên khác',
    fGoiTaiNguyen: 'Gói hạn mức',
    fGoiTaiNguyenMo: '{{so}} token AI mỗi tháng · gói nền {{goi}}',
    // Ô "Gói hạn mức" ở tab Gói & thuê bao của chi tiết tenant.
    goiHanMucTieuDe: 'Gói hạn mức',
    doiGoiHanMuc: 'Đổi gói hạn mức',
    hopDoiGoiHanMuc: 'Đổi gói hạn mức của {{ten}}',
    dongGoi: 'Gói',
    dongTokenThang: 'Token AI mỗi tháng',
    dongGoiNen: 'Gói nền',
    doiGoiNenCanhBao:
      'Gói nền của tenant đổi từ {{cu}} sang {{moi}}: chính sách bảo mật và license theo gói nền đổi theo.',
  },

  // ───────────────────────── Hạn mức token AI theo tháng (ADR-0088) ─────────────────────────
  // Chi tiết tenant và thẻ "Theo gói" ở cổng nền tảng, ô Tổng quan ở cổng tenant.
  // ───────────────────────── Domain thương hiệu (ADR-0097) ─────────────────────────
  diaChiRieng: {
    tieuDe: 'Địa chỉ truy cập riêng',
    caption: 'Địa chỉ truy cập riêng của tenant',
    them: 'Thêm địa chỉ',
    dangThem: 'Đang thêm...',
    daDu: 'Đã đủ {{toiDa}} địa chỉ. Gỡ bớt một địa chỉ để thêm địa chỉ mới.',
    rong: 'Chưa có địa chỉ truy cập riêng',
    rongMoNenTang:
      'Thêm một domain của khách, ví dụ app.congtyabc.com.vn, để người dùng của tenant vào cổng bằng địa chỉ đó.',
    rongMoToChuc:
      'Thêm domain của tổ chức, ví dụ app.congtyabc.com.vn, để người dùng vào cổng bằng địa chỉ đó thay cho địa chỉ chung của nền tảng.',
    loiTai: 'Không tải được địa chỉ truy cập riêng: {{loi}}',
    cotHost: 'Địa chỉ',
    cotTrangThai: 'Trạng thái',
    cotDns: 'Kiểm tra DNS',
    cotChungChi: 'Chứng chỉ TLS',
    nhanGoc: 'Domain gốc',
    khoaNenTang: 'Đã khoá: tổ chức không tự kích hoạt lại hay gỡ được',
    khoaToChuc: 'Quản trị nền tảng đã khoá. Liên hệ quản trị nền tảng để mở lại hoặc gỡ.',
    chiQuanTriToChuc: 'Chỉ quản trị tổ chức thêm và quản lý được địa chỉ truy cập riêng.',
    trangThai: {
      CHO_DNS: 'Chờ DNS',
      DA_XAC_MINH: 'Đã xác minh',
      DANG_DUNG: 'Đang dùng',
      TAM_NGUNG: 'Tạm ngưng',
      DA_GO: 'Đã gỡ',
    },
    dnsChuaKiem: 'Chưa kiểm',
    dnsDatNgan: 'Đạt',
    dnsChuaDatNgan: 'Chưa đạt',
    chungChiHan: 'Hạn đến {{ngay}}',
    chungChiSapHet: 'Sắp hết hạn: {{ngay}}',
    chungChiChoCap: 'Chưa có thông tin',
    loiDns: {
      TXT_MISSING: 'Chưa thấy bản ghi TXT. Tạo bản ghi TXT bên dưới; DNS có thể mất tới vài giờ để cập nhật.',
      TXT_MISMATCH: 'Bản ghi TXT có giá trị khác mã xác minh. Sửa giá trị cho khớp với mã bên dưới.',
      TARGET_MISMATCH:
        'Địa chỉ chưa trỏ về nền tảng. Sửa bản ghi CNAME (hoặc bản ghi A với domain gốc) theo hướng dẫn bên dưới.',
      DNS_UNAVAILABLE: 'Máy chủ DNS chưa trả lời. Hệ thống sẽ tự kiểm lại sau ít phút.',
    },
    dnsKhoiNhan: 'Bản ghi DNS cần tạo cho {{host}}',
    dnsTieuDe: 'Bản ghi DNS cho {{host}}',
    dnsMo: 'Tạo các bản ghi dưới đây ở nơi quản lý DNS của domain. Hệ thống tự kiểm định kỳ; đạt rồi thì kích hoạt địa chỉ.',
    dnsMoGoc:
      'Tạo bản ghi TXT và bản ghi A dưới đây ở nơi quản lý DNS của domain. Hệ thống tự kiểm định kỳ; đạt rồi thì kích hoạt địa chỉ.',
    dnsGhiChuAlias:
      'Nếu nhà cung cấp DNS hỗ trợ ALIAS, ANAME hoặc CNAME flattening (ví dụ Cloudflare), có thể trỏ domain về {{dich}} thay cho bản ghi A; cách này không phải sửa khi nền tảng đổi IP.',
    banGhiLoai: 'Bản ghi {{loai}}',
    kiemLai: 'Kiểm tra lại DNS',
    xemBanGhiDns: 'Xem bản ghi DNS',
    anBanGhiDns: 'Ẩn bản ghi DNS',
    dnsThieuIp:
      'Máy chủ chưa khai IP công khai cho domain gốc nên chưa có bản ghi A. Đội vận hành đặt CUSTOM_HOST_TARGET_IPS trong .env của máy chủ; trong lúc chờ, dùng ALIAS/ANAME nếu nhà cung cấp DNS hỗ trợ.',
    dangKiem: 'Đang kiểm...',
    kiemLoi: 'Không kiểm được DNS: {{loi}}',
    dnsDaDat: 'DNS của {{host}} đã đạt. Có thể kích hoạt địa chỉ.',
    dnsChuaDat: 'DNS của {{host}} chưa đạt. Xem lý do ở hướng dẫn DNS.',
    dnsVanDat: 'Đã kiểm lại DNS của {{host}}.',
    kichHoat: 'Kích hoạt',
    tamNgung: 'Tạm ngưng',
    khoa: 'Khoá địa chỉ',
    go: 'Gỡ địa chỉ',
    daThem: 'Đã thêm {{host}}. Bản ghi DNS cần tạo nằm ở cuối khung.',
    daKichHoat: 'Đã kích hoạt {{host}}.',
    daTamNgung: 'Đã tạm ngưng {{host}}.',
    daKhoa: 'Đã khoá {{host}}.',
    daGo: 'Đã gỡ {{host}}.',
    xnKichHoatTieuDe: 'Kích hoạt {{host}}?',
    xnKichHoatThan: 'Người dùng vào được cổng tổ chức qua https://{{host}}. Tạm ngưng được bất cứ lúc nào.',
    xnKichHoatNut: 'Kích hoạt địa chỉ',
    xnTamNgungTieuDe: 'Tạm ngưng {{host}}?',
    xnTamNgungThan:
      'Trong tối đa 60 giây, địa chỉ này ngừng phục vụ; người đang dùng phải vào bằng địa chỉ chung của nền tảng. Kích hoạt lại được bất cứ lúc nào.',
    xnTamNgungThanNenTang:
      'Trong tối đa 60 giây, địa chỉ này ngừng phục vụ; người đang dùng phải vào bằng địa chỉ chung của nền tảng. Tổ chức không tự kích hoạt lại hay gỡ được, chỉ quản trị nền tảng mở lại.',
    xnTamNgungNut: 'Tạm ngưng địa chỉ',
    xnKhoaTieuDe: 'Khoá {{host}}?',
    xnKhoaThan:
      'Tổ chức đã tự tạm ngưng địa chỉ này. Sau khi khoá, tổ chức không tự kích hoạt lại hay gỡ được; chỉ quản trị nền tảng mở lại.',
    xnKhoaNut: 'Khoá địa chỉ',
    xnGoTieuDe: 'Gỡ {{host}}?',
    xnGoThan:
      'Địa chỉ ngừng phục vụ ngay. Trong 30 ngày sau đó chỉ tổ chức này thêm lại được địa chỉ này. Không hoàn tác được.',
    xnGoNut: 'Gỡ địa chỉ',
    hopTieuDe: 'Thêm địa chỉ truy cập riêng',
    hopThan:
      'Nhập domain của khách. Sau khi thêm, khách tạo các bản ghi DNS theo hướng dẫn; DNS đạt thì kích hoạt.',
    hopThanToChuc:
      'Nhập domain của tổ chức. Sau khi thêm, tạo các bản ghi DNS theo hướng dẫn ở nơi quản lý domain; DNS đạt thì kích hoạt.',
    oHostname: 'Địa chỉ',
    oHostnameGoiY: 'Domain gốc (congtyabc.com.vn) hoặc subdomain (app.congtyabc.com.vn) đều được.',
    oHostnameViDu: 'app.congtyabc.com.vn',
    seLuuLa: 'Sẽ lưu là',
    goiYWww:
      'www.{{host}} là một địa chỉ riêng: muốn người dùng vào được bằng cả hai thì thêm cả www.{{host}}. Mỗi tenant tối đa {{toiDa}} địa chỉ.',
    loiTrong: 'Nhập địa chỉ, ví dụ app.congtyabc.com.vn.',
    loiDai: 'Địa chỉ dài quá {{so}} ký tự.',
    loiIp: 'Không dùng địa chỉ IP. Nhập tên miền, ví dụ app.congtyabc.com.vn.',
    loiSai: 'Địa chỉ chỉ gồm chữ, số, dấu gạch ngang và dấu chấm, ví dụ app.congtyabc.com.vn.',
    loiThieuCham: 'Nhập đầy đủ tên miền, ví dụ app.congtyabc.com.vn.',
  },

  hanMucToken: {
    tenTaiNguyen: 'Token AI mỗi tháng',
    donVi: 'token',
    soToken: '{{so}} token',
    trenHanMuc: '/ {{so}} token',
    khongGioiHan: 'Không giới hạn',
    khongGioiHanDong: 'Đã dùng tháng này · không giới hạn · tính lại từ 0 ngày {{ngay}}',
    thanhNhan: 'Token AI đã dùng tháng này',
    thanhGiaTri: '{{daDung}} trên {{hanMuc}} token',
    conLai: 'Còn {{so}} token',
    veKhong: 'Tính lại từ 0 ngày {{ngay}}',
    trangThai: {
      canhBao: 'Sắp hết',
      daChan: 'Đã chặn',
    },
    // Ô ở chi tiết tenant.
    tieuDe: 'Token AI tháng này',
    dangTai: 'Đang tải hạn mức token AI...',
    loi: 'Không tải được hạn mức token AI. {{loi}}',
    chanNenTang:
      'Tenant đã dùng hết hạn mức tháng này: lời gọi AI mới bị từ chối tới ngày {{ngay}}. Nạp thêm token hoặc đặt hạn mức riêng cho tenant cao hơn để mở lại ngay.',
    datRieng: 'Đặt hạn mức riêng cho tenant',
    suaRieng: 'Sửa hạn mức đặt riêng',
    luuRieng: 'Lưu hạn mức đặt riêng',
    boRieng: 'Bỏ hạn mức đặt riêng',
    daDat: 'Đã lưu hạn mức token AI đặt riêng cho {{ten}}.',
    daBo: 'Đã bỏ hạn mức đặt riêng, {{ten}} quay về hạn mức của gói đang gán.',
    // Hộp đặt hoặc bỏ hạn mức đặt riêng.
    hopDatTieuDe: 'Đặt hạn mức token AI riêng cho {{ten}}',
    hopSuaTieuDe: 'Sửa hạn mức token AI đặt riêng của {{ten}}',
    hopThan:
      'Hạn mức đặt riêng thay cho hạn mức của gói, chỉ áp cho tenant này, có hiệu lực ngay với tháng hiện tại. Token cấp thêm vẫn cộng thêm.',
    hopHanMucGoi: 'Hạn mức của gói {{nguon}}',
    hopDaDung: 'Đã dùng tháng này',
    hopSeChan:
      'Hạn mức này không lớn hơn số đã dùng tháng này ({{so}} token): lời gọi AI của tenant bị chặn ngay, tới ngày {{ngay}}.',
    hopBoThanKhongGioiHan:
      'Tenant quay về hạn mức của gói {{nguon}}: không giới hạn, có hiệu lực ngay với tháng này.',
    hopBoTieuDe: 'Bỏ hạn mức đặt riêng của {{ten}}?',
    hopBoThan:
      'Tenant quay về hạn mức của gói {{nguon}} ({{hanMuc}} mỗi tháng), có hiệu lực ngay với tháng này. Token cấp thêm vẫn giữ.',
    hopBoSeChan:
      'Tháng này tenant đã dùng {{so}} token, chạm hạn mức của gói: lời gọi AI bị chặn ngay, tới ngày {{ngay}}.',
    oNhan: 'Số token mỗi tháng',
    oGoiY: 'Nhập 0 để chặn mọi lời gọi AI của tenant này.',
    oXemTruoc: 'Hạn mức: {{so}} token mỗi tháng',
    oTrong: 'Nhập số token mỗi tháng.',
    oSai: 'Nhập số nguyên không âm, ví dụ 30000000.',
    oQuaLon: 'Tối đa {{so}} token mỗi tháng.',
    // Thẻ "Theo gói" ở Chính sách nền tảng.
    goiTieuDe: 'Token AI của gói {{goi}}',
    goiDangTai: 'Đang tải hạn mức token AI theo gói...',
    goiLoi: 'Không tải được hạn mức token AI theo gói. {{loi}}',
    goiDonViThang: 'token mỗi tháng',
    // Ô Tổng quan ở cổng tenant.
    toChucTieuDe: 'Token AI tháng này',
    toChucCanhBao:
      'Tổ chức đã dùng {{so}}% hạn mức token AI tháng này. Khi dùng hết, các tính năng AI tạm dừng tới ngày {{ngay}}; cần thêm thì gửi yêu cầu ở thẻ Nạp token.',
    toChucDaChanTieuDe: 'Đã dùng hết hạn mức token AI tháng này',
    toChucDaChan:
      'Các tính năng AI tạm dừng tới ngày {{ngay}}, hoặc tới khi quản trị nền tảng cấp thêm token — gửi yêu cầu ở thẻ Nạp token. Các chức năng khác vẫn dùng bình thường.',
    goiChiTro: 'Theo gói hạn mức mặc định {{ten}}.',
    goiMoManGoiTaiNguyen: 'Mở Gói hạn mức',
  },

  // Token AI cấp thêm, yêu cầu của Tenant Admin và hàng chờ duyệt (ADR-0091).
  tokenAi: {
    loai: {
      NAP_THEM: 'Nạp thêm',
      GOI_TAM_THOI: 'Gói tạm thời',
      GIA_HAN: 'Gia hạn',
    },
    loaiMo: {
      NAP_THEM: 'Số token dùng dần trong 1, 3, 6 hoặc 12 tháng; phần chưa dùng chuyển sang tháng sau.',
      GOI_TAM_THOI: 'Trọn gói token dùng bất kỳ lúc nào trong 1–4 tuần hoặc 1–12 tháng.',
      GIA_HAN: 'Dời ngày kết thúc một gói tạm thời đang chạy hoặc sắp chạy; không cộng thêm token.',
    },
    trangThaiCapThem: {
      ACTIVE: 'Đang hiệu lực',
      SCHEDULED: 'Chưa tới ngày',
      EXPIRED: 'Đã hết hạn',
      REVOKED: 'Đã thu hồi',
    },
    trangThaiYeuCau: {
      PENDING: 'Đang chờ',
      APPROVED: 'Đã duyệt',
      REJECTED: 'Bị từ chối',
      CANCELLED: 'Đã huỷ',
    },
    // Chi tiết hạn mức.
    hmSo: 'Hạn mức: {{so}} token/tháng',
    hmKhongGioiHan: 'Hạn mức: không giới hạn',
    hmRiengTenant: 'đặt riêng cho tenant này',
    hmRiengToChuc: 'đặt riêng cho tổ chức',
    hmGoiChuaDat: 'gói hạn mức {{ten}} không giới hạn',
    hmCapThemMot: '+ {{so}} token cấp thêm còn lại (đến {{ngay}})',
    hmCapThemNhieu: '+ {{so}} token cấp thêm còn lại trong {{lan}} túi (túi sớm nhất hết ngày {{ngay}})',
    ctTieuDe: 'Cách tính hạn mức',
    ctRiengTenantMo: 'Thay cho hạn mức của gói, chỉ áp cho tenant này.',
    // Ô token cấp thêm ở chi tiết tenant.
    capThemTieuDe: 'Token cấp thêm',
    capThemCaption: 'Token AI cấp thêm cho {{ten}}',
    capThemRong: 'Chưa cấp thêm token nào',
    capThemRongMo: 'Khi tenant hết token giữa tháng, nạp thêm hoặc thêm gói tạm thời để mở lại ngay.',
    loiCapThem: 'Không tải được token cấp thêm. {{loi}}',
    napThem: 'Nạp thêm token',
    themGoi: 'Thêm gói tạm thời',
    giaHanGoi: 'Gia hạn gói',
    thuHoi: 'Thu hồi token',
    tenDong: '{{so}} token cấp thêm',
    cotLoai: 'Loại',
    cotToken: 'Token',
    cotHieuLuc: 'Hiệu lực',
    cotGhiChu: 'Ghi chú',
    khoangNgay: '{{tu}} – {{den}}',
    tuYeuCau: 'Từ yêu cầu của tổ chức',
    capTrucTiep: 'Cấp trực tiếp',
    dangChoCuaTenant: 'Tổ chức có {{so}} yêu cầu nạp token đang chờ duyệt.',
    moHangCho: 'Mở hàng chờ',
    daNapThem: 'Đã nạp thêm {{so}} token cho {{ten}}.',
    daThemGoi: 'Đã thêm gói tạm thời {{so}} token mỗi tháng cho {{ten}}.',
    daGiaHan: 'Đã gia hạn gói tạm thời.',
    daThuHoi: 'Đã thu hồi token cấp thêm.',
    xacNhanThuHoi: 'Thu hồi {{so}} token cấp thêm?',
    xacNhanThuHoiMo:
      'Hạn mức của {{ten}} giảm ngay {{so}} token. Nếu số đã dùng chạm hạn mức mới, lời gọi AI bị chặn lại sau vài giây. Không hoàn tác được; cần cấp lại thì tạo dòng mới.',
    xacNhanThuHoiChuaToi:
      'Gói chưa tới ngày bắt đầu sẽ không được áp dụng cho {{ten}} ({{so}} token). Không hoàn tác được; cần cấp lại thì tạo dòng mới.',
    // Hộp cấp thêm, gia hạn.
    hopNapTieuDe: 'Nạp thêm token cho {{ten}}',
    hopGoiTieuDe: 'Thêm gói tạm thời cho {{ten}}',
    hopNapThan:
      'Token dùng dần tới hết thời hạn; phần chưa dùng trong tháng chuyển sang tháng sau, hết thời hạn thì mất.',
    hopGoiThan:
      'Trọn gói: toàn bộ số token dùng bất kỳ lúc nào trong thời hạn, không chia theo tháng.',
    hopGiaHanTieuDe: 'Gia hạn gói tạm thời của {{ten}}',
    hopHieuLucHienTai: 'Đang hiệu lực',
    oSoToken: 'Số token',
    oSoTokenGoiY: 'Tổng token prompt và completion, ví dụ 2000000.',
    oTuNgay: 'Từ ngày',
    oTuNgayGoiY: 'Bỏ trống là hôm nay.',
    oDenNgayMoi: 'Ngày kết thúc mới',
    oDenNgayMoiGoiY: 'Số token của gói giữ nguyên.',
    oGhiChu: 'Ghi chú',
    oGhiChuGoiY: 'Số hợp đồng, lý do cấp — hiện trong danh sách và nhật ký.',
    loiSoTrong: 'Nhập số token.',
    loiSoSai: 'Nhập số nguyên dương, ví dụ 2000000.',
    loiSoKhong: 'Cấp thêm ít nhất 1 token.',
    loiNgayTrong: 'Chọn ngày.',
    loiNgayTruocHomNay: 'Chọn từ hôm nay trở đi.',
    loiNgayTruocBatDau: 'Ngày kết thúc phải từ ngày bắt đầu trở đi.',
    loiNgayKhongSau: 'Chọn ngày sau ngày kết thúc hiện tại ({{ngay}}).',
    loiGhiChuDai: 'Tối đa {{so}} ký tự.',
    // Hàng chờ của Platform Admin.
    hangChoCaption: 'Yêu cầu nạp token của các tổ chức',
    soDangCho: '{{so}} đang chờ',
    locTrangThai: 'Lọc theo trạng thái',
    tatCaTrangThai: 'Mọi trạng thái',
    cotToChuc: 'Tổ chức',
    cotNoiDung: 'Nội dung',
    cotLyDo: 'Lý do',
    cotGuiLuc: 'Gửi lúc',
    guiLuc: 'Gửi {{luc}}',
    phanHoi: 'Phản hồi: {{noiDung}}',
    xemTenant: 'Xem tenant',
    duyetYeuCau: 'Duyệt yêu cầu',
    tuChoiYeuCau: 'Từ chối yêu cầu',
    hangChoRongCho: 'Không có yêu cầu nào đang chờ',
    hangChoRongChoMo: 'Yêu cầu mới của quản trị tổ chức sẽ hiện ở đây.',
    hangChoRongThat: 'Chưa có yêu cầu nào',
    hangChoRongThatMo: 'Quản trị tổ chức gửi yêu cầu từ thẻ Token AI ở màn Báo cáo của tổ chức.',
    hangChoRongLoc: 'Không có yêu cầu nào ở trạng thái này',
    loiHangCho: 'Không tải được hàng chờ. {{loi}}',
    dangHien: 'Đang hiện {{so}} yêu cầu',
    ndNapThem: '{{so}} token · {{thoiHan}}',
    ndGoiTamThoi: '{{so}} token trọn gói · {{thoiHan}}',
    ndGiaHanGoiTamThoi: 'Gói tạm thời tới {{den}}',
    ndDaDuyetNgay: 'Đã duyệt: tới {{den}}',
    hopDuyetTieuDe: 'Duyệt yêu cầu của {{ten}}',
    lyDoCuaToChuc: 'Lý do:',
    goiHienTai: 'Gói hiện tại: còn {{conLai}} / {{so}} token, {{tu}} – {{den}}',
    oPhanHoi: 'Ghi chú gửi tổ chức',
    oPhanHoiGoiY: 'Không bắt buộc. Nên ghi khi duyệt khác số đã xin.',
    duyetNap: 'Duyệt và nạp thêm',
    duyetGoi: 'Duyệt và thêm gói',
    duyetGiaHan: 'Duyệt và gia hạn',
    daDuyet: 'Đã duyệt yêu cầu của {{ten}}.',
    hopTuChoiTieuDe: 'Từ chối yêu cầu của {{ten}}?',
    hopTuChoiThan: 'Tổ chức nhận lý do qua thông báo. Không hoàn tác được; tổ chức có thể gửi yêu cầu mới.',
    oLyDoTuChoi: 'Lý do từ chối',
    loiLyDoTuChoiTrong: 'Nhập lý do để tổ chức biết cần làm gì tiếp.',
    daTuChoi: 'Đã từ chối yêu cầu của {{ten}}.',
    // Cổng tenant.
    yeuCauCuaToChuc: 'Yêu cầu nạp token',
    yeuCauCaption: 'Yêu cầu nạp token của tổ chức',
    yeuCauRong: 'Chưa gửi yêu cầu nào',
    yeuCauRongMo: 'Cần thêm token trong tháng, một gói tạm thời hay gia hạn thì gửi yêu cầu; quản trị nền tảng duyệt và tổ chức nhận thông báo.',
    loiYeuCau: 'Không tải được yêu cầu. {{loi}}',
    guiYeuCau: 'Gửi yêu cầu nạp token',
    dangGui: 'Đang gửi...',
    daGuiYeuCau: 'Đã gửi yêu cầu, chờ quản trị nền tảng duyệt.',
    huyYeuCau: 'Huỷ yêu cầu',
    daHuyYeuCau: 'Đã huỷ yêu cầu.',
    xacNhanHuy: 'Huỷ yêu cầu này?',
    xacNhanHuyMo: 'Quản trị nền tảng không xét yêu cầu nữa. Không hoàn tác được; cần thì gửi yêu cầu mới.',
    hopGuiTieuDe: 'Gửi yêu cầu nạp token',
    hopGuiThan: 'Quản trị nền tảng xem và duyệt yêu cầu; tổ chức nhận thông báo khi có quyết định.',
    daDuCho: 'Tổ chức đã có {{so}} yêu cầu đang chờ duyệt. Huỷ bớt một yêu cầu hoặc chờ quyết định trước khi gửi thêm.',
    oLoai: 'Loại yêu cầu',
    oGoiCanGiaHan: 'Gia hạn cho',
    goiTamThoiNhan: 'Gói tạm thời {{so}} token, tới {{den}}',
    oLyDo: 'Lý do',
    oLyDoGoiY: 'Dùng vào việc gì, khối lượng ước tính — giúp duyệt nhanh hơn.',
    loiLyDoTrong: 'Nhập lý do cần thêm token.',
    hmTheoGoiTaiNguyen: 'theo gói hạn mức {{ten}}',
    chonGoiTamThoi: 'Chọn gói tạm thời',
    khongCoGoiTamThoi: 'Tổ chức chưa có gói tạm thời nào đang chạy hoặc sắp chạy để gia hạn.',
    loiChonGoiTamThoi: 'Chọn gói tạm thời cần gia hạn.',
    tomConLaiTren: 'token còn lại trên tổng {{so}}',
    tomDaDung: 'Đã dùng {{so}}',
    tomVeKhong: 'Token của gói hạn mức về 0 ngày {{ngay}}',
    tomThangVaoRa: 'Tháng này: {{vao}} token đầu vào · {{ra}} token đầu ra',
    ctThuTuHet: 'Trừ lần lượt từ trên xuống: túi sắp hết hạn trước.',
    ctThuTuMua: 'Trừ lần lượt từ trên xuống: token mua thêm trước, gói hạn mức sau.',
    ctTuiGoi: 'Gói hạn mức {{ten}} · tháng này',
    ctTuiRiengTenant: 'Hạn mức đặt riêng cho tenant · tháng này',
    ctTuiRiengToChuc: 'Hạn mức đặt riêng cho tổ chức · tháng này',
    ctTuiNap: 'Nạp thêm',
    ctTuiTamThoi: 'Gói tạm thời',
    ctVeKhong: 'Về 0 ngày {{ngay}}',
    ctDungToi: 'Dùng tới {{ngay}}',
    ctConLai: 'Còn {{so}}',
    ctDaDungTren: 'Đã dùng {{daDung}} / {{tong}}',
    sapHetHan: 'Sắp hết hạn',
    ctTongConLai: 'Tổng còn lại',
    ctTongGiaTri: '{{conLai}} / {{tong}} token',
    uuTienTieuDe: 'Thứ tự dùng token',
    uuTienCanhBao: 'Token của gói hạn mức không dùng hết trong tháng sẽ mất vào cuối tháng.',
    uuTienCanhBaoSo:
      'Hiện còn {{so}} token của gói hạn mức tháng này; phần chưa dùng tới hết ngày {{ngay}} sẽ mất.',
    xacNhanUuTien: 'Ưu tiên dùng token mua thêm trước?',
    xacNhanUuTienMo:
      'Token cấp thêm sẽ được trừ trước. Token của gói hạn mức không dùng hết trong tháng sẽ mất vào cuối tháng. Đổi lại được bất cứ lúc nào.',
    nutUuTienMua: 'Dùng token mua thêm trước',
    daDatUuTien: 'Đã đổi thứ tự dùng token.',
    loiUuTien: 'Chưa đổi được thứ tự dùng token. {{loi}}',
    thTuan: '{{so}} tuần',
    thThang: '{{so}} tháng',
    thDonVi: 'Đơn vị thời hạn',
    thDonViTuan: 'Tuần',
    thDonViThang: 'Tháng',
    oThoiHan: 'Thời hạn',
    oThoiHanNapGoiY: 'Token chưa dùng chuyển sang tháng sau, tới hết thời hạn.',
    oThoiHanGoiGoiY: 'Trọn gói dùng bất kỳ lúc nào trong thời hạn, không chia theo tháng.',
    oThoiHanNapYeuCauGoiY: 'Tính từ ngày được duyệt; token chưa dùng chuyển sang tháng sau tới hết thời hạn.',
    oThoiHanGoiYeuCauGoiY: 'Tính từ ngày được duyệt; trọn gói dùng bất kỳ lúc nào trong thời hạn.',
    loiThoiHan: 'Chọn thời hạn trong danh sách.',
    oTokenTronGoi: 'Token trọn gói',
    oDenNgayTuyChon: 'Ngày kết thúc (tuỳ chọn)',
    oDenNgayTuyChonGoiY: 'Bỏ trống là tính theo thời hạn: tới hết ngày {{ngay}}.',
    oTuNgayDuyetGoiY: 'Bỏ trống là ngày duyệt (hôm nay).',
    xemTruocKhoang: 'Hiệu lực {{tu}} – {{den}}',
    xemTruocTong: 'Tổng khả dụng sau khi cấp: {{so}} token.',
    hopConLaiHienTai: 'Còn lại / tổng khả dụng',
    hopDaDungThang: 'Đã dùng tháng này',
    hopConLaiGoi: 'Còn lại / tổng của gói',
    giaHanKhongCongToken: 'Gia hạn chỉ dời ngày kết thúc, không cộng thêm token. Cần thêm token thì nạp thêm.',
    cotDaDungConLai: 'Đã dùng / còn lại',
    daDungConLai: 'Đã dùng {{daDung}} · còn {{conLai}}',
    daDungChi: 'Đã dùng {{daDung}}',
    ndDaDuyet: 'Đã duyệt: {{so}} token · {{tu}} – {{den}}',
    uuTien: {
      HET_HAN_TRUOC: 'Sắp hết hạn dùng trước',
      MUA_THEM_TRUOC: 'Ưu tiên dùng token mua thêm trước',
    },
    thMotTuan: '1 tuần',
    thMotThang: '1 tháng',
  },
  // Màn Gói & thanh toán của cổng nền tảng (`modules/goi-thanh-toan`).
  goiThanhToan: {
    tab: {
      goiDichVu: 'Gói dịch vụ',
      danhMucDichVu: 'Danh mục dịch vụ',
      goiHanMuc: 'Gói hạn mức',
      goiNapToken: 'Gói nạp token',
      bangGiaModel: 'Giá vốn model',
      yeuCauNapToken: 'Yêu cầu nạp token',
    },
  },
  // Gói hạn mức (mã, API: gói tài nguyên, `resource_package`): token AI mỗi tháng trên một gói nền (ADR-0091 mục 1).
  goiTaiNguyen: {
    taoGoi: 'Tạo gói hạn mức',
    luuGoi: 'Lưu gói',
    suaGoi: 'Sửa gói',
    batGoi: 'Bật lại gói',
    ngungGoi: 'Ngừng gói',
    daTao: 'Đã tạo gói hạn mức {{ten}}.',
    daLuu: 'Đã lưu gói hạn mức {{ten}}.',
    daBat: 'Đã bật lại gói {{ten}}.',
    daNgung: 'Đã ngừng gói {{ten}}.',
    trangThaiBat: 'Đang dùng',
    trangThaiNgung: 'Đã ngừng',
    macDinh: 'Hệ thống',
    cotTen: 'Gói',
    cotGoiNen: 'Gói nền',
    cotToken: 'Token AI / tháng',
    cotSoTenant: 'Số tenant',
    timTheo: 'Tìm theo tên hoặc mã gói',
    timNhan: 'Tìm gói hạn mức',
    locTrangThai: 'Lọc theo trạng thái',
    tatCaTrangThai: 'Mọi trạng thái',
    loiDanhSach: 'Không tải được gói hạn mức. {{loi}}',
    bangCaption: 'Danh mục gói hạn mức',
    rongLoc: 'Không có gói nào khớp bộ lọc',
    rongThat: 'Chưa có gói hạn mức nào',
    rongThatMo:
      'Bốn gói mặc định do migration tạo; tạo thêm gói riêng khi một nhóm tenant cần hạn mức khác.',
    dangHien: 'Đang hiện {{so}} gói',
    dangHienLoc: 'Đang hiện {{so}} trên {{tong}} gói',
    xacNhanNgung: 'Ngừng gói {{ten}}?',
    xacNhanNgungMo:
      'Gói không còn chọn được cho tenant mới. Không tenant nào đang dùng gói này. Bật lại được bất cứ lúc nào.',
    xacNhanBat: 'Bật lại gói {{ten}}?',
    xacNhanBatMo: 'Gói chọn được lại ở form tạo và sửa tenant.',
    hopTaoTieuDe: 'Tạo gói hạn mức',
    hopSuaTieuDe: 'Sửa gói {{ten}}',
    suaCanhBao:
      '{{so}} tenant đang dùng gói này: hạn mức mới có hiệu lực ngay với họ (trừ hạn mức đặt riêng cho từng tenant).',
    oTen: 'Tên gói',
    oTenGoiY: 'Ví dụ: Chuyên nghiệp mở rộng.',
    oMa: 'Mã gói',
    oMaChoTen: 'Nhập tên để sinh mã',
    oMaKhongDoi: 'Không đổi được sau khi tạo.',
    oMaTuSinh: 'Tự sinh từ tên, không đổi được sau khi tạo.',
    oGoiNen: 'Gói nền',
    heThongGoiY: 'Gói hệ thống: tên, mã và gói nền cố định; sửa được các hạn mức.',
    oGoiNenGoiY: 'Tenant dùng gói này có gói nền này (chính sách bảo mật, license theo gói nền).',
    oGoiNenChon: 'Chọn gói nền',
    oToken: 'Token AI mỗi tháng',
    oTokenXemTruoc: '{{so}} token mỗi tháng',
    oTokenGoiY: 'Tổng token prompt và completion; 0 là chặn AI.',
    oNguoiDung: 'Người dùng tối đa',
    oDungLuong: 'Dung lượng database',
    oTinThongBao: 'Tin thông báo mỗi tháng',
    donViNguoi: 'người',
    donViGb: 'GB',
    donViTin: 'tin',
    chuaDat: 'Chưa đặt',
    oChuaDatGoiY: 'Để trống là giữ chưa đặt, không chặn.',
    loiNguoiDungTrong: 'Nhập số người dùng tối đa.',
    loiDungLuongTrong: 'Nhập dung lượng database.',
    loiTinTrong: 'Nhập số tin thông báo mỗi tháng.',
    loiSoNguyen: 'Nhập số nguyên từ 1 đến {{so}}.',
    keoTenant:
      '{{so}} tenant đang dùng gói này sẽ chuyển gói nền từ {{cu}} sang {{moi}}: chính sách bảo mật và license theo gói nền của họ đổi theo.',
    loiTenTrong: 'Nhập tên gói.',
    loiTenDai: 'Tên gói tối đa {{so}} ký tự.',
    loiTenKhongSinhMa: 'Tên cần có chữ hoặc số để sinh mã gói.',
    loiTrungMa: 'Mã {{ma}} đã có. Đổi tên gói để sinh mã khác.',
    loiGoiNenTrong: 'Chọn gói nền.',
    chonGoi: 'Chọn gói hạn mức',
    nhanLuaChon: '{{ten}} · {{so}} token/tháng',
    goiNenLa: 'Gói nền: {{goi}}',
  },


  // ───────────────────────── Module OCR: nhãn dùng chung ─────────────────────────
  // Nhãn giá trị liệt kê của module `ocr` (modules/ocr/backend), dùng ở cấu hình loại
  // chứng từ, nhận dạng, chứng từ và kho bản OCR. `Hóa đơn` giữ kiểu dấu cũ theo đặc tả.
  ocr: {
    heThong: 'Hệ thống',
    ban: 'Bạn',
    nguoiDungMa: 'Người dùng {{ma}}',
    modelMacDinh: 'Model mặc định của tổ chức',
    tepGoc: 'Tệp gốc',
    loiTaiTep: 'Không tải được tệp gốc: {{loi}}',
    khongCoTep: 'Không có tệp gốc để xem',
    khongXemTruocDuoc: 'Không xem trước được định dạng này',
    khongXemTruocDuocMo: 'Word và Excel không hiện được trong trình duyệt. Mở tệp bằng ứng dụng trên máy.',
    moTep: 'Mở tệp',
    loiBanChep: 'Không tải được bản chép OCR: {{loi}}',
    chuaCoBanChep: 'Chưa có bản chép OCR',
    chuaCoBanChepMo: 'Bản chép từng trang có sau bước đọc chữ.',
    tepSo: 'Tệp {{so}}',
    soTrang: '{{so}} trang',
    trangSo: 'Trang {{so}}',
    docBang: 'đọc bằng {{ten}}',
    lopVanBanPdf: 'Lớp văn bản PDF',
    dungLaiTuKho: 'Dùng lại từ kho bản OCR',
    loai: {
      INVOICE: 'Hóa đơn',
      RECEIPT: 'Biên lai',
      CONTRACT: 'Hợp đồng',
      STATEMENT: 'Sao kê',
      MINUTES: 'Biên bản',
      WAREHOUSE_RECEIPT: 'Phiếu nhập kho',
      OTHERS: 'Khác',
    },
    kieuDuLieu: {
      TEXT: 'Văn bản',
      DATE: 'Ngày',
      NUMBER: 'Số',
      CURRENCY: 'Tiền',
      BOOLEAN: 'Có / không',
      LIST: 'Danh sách chọn',
    },
    viTri: {
      HEADER: 'Đầu chứng từ',
      BODY: 'Thân chứng từ',
      FOOTER: 'Cuối chứng từ',
    },
    // Cột lọc nhanh của chứng từ mà một trường đổ giá trị vào.
    cotLoc: {
      INVOICE_NUMBER: 'Số chứng từ',
      ISSUE_DATE: 'Ngày chứng từ',
      SELLER_TAX_CODE: 'Mã số thuế người bán',
      SELLER_NAME: 'Tên người bán',
      TOTAL_AMOUNT: 'Tiền trước thuế',
      VAT_AMOUNT: 'Tiền thuế GTGT',
      GRAND_TOTAL: 'Tổng thanh toán',
    },
    cheDo: {
      SINGLE_MODEL: 'Một model',
      OCR_THEN_MAP: 'Hai bước: đọc chữ rồi điền dữ liệu',
    },
    ngonNgu: {
      vi: 'Tiếng Việt',
      en: 'Tiếng Anh',
      viEn: 'Tiếng Việt và tiếng Anh',
    },
  },

  // ───────────────────────── Cấu hình loại chứng từ OCR ─────────────────────────
  // Màn `/xu-ly/cau-hinh-ocr` và `/xu-ly/cau-hinh-ocr/:schemaId` (modules/cau-hinh-ocr).
  cauHinhOcr: {
    daTao: 'Đã tạo loại chứng từ "{{ten}}".',
    daLuu: 'Đã lưu thay đổi.',
    daXoa: 'Đã xoá loại chứng từ "{{ten}}".',
    daThemTruong: 'Đã thêm trường.',
    daXoaTruong: 'Đã xoá trường.',
    daThemBang: 'Đã thêm bảng.',
    daXoaBang: 'Đã xoá bảng.',
    daThemCot: 'Đã thêm cột.',
    daXoaCot: 'Đã xoá cột.',
    dangDung: 'Đang dùng',
    daTat: 'Đã tắt',
    moTa: 'Loại chứng từ quyết định model đọc gì và điền vào trường, bảng nào khi nhận dạng',
    taoLoai: 'Tạo loại chứng từ',
    taoLoaiDauTien: 'Tạo loại chứng từ đầu tiên',
    taoTieuDe: 'Tạo loại chứng từ',
    dangTao: 'Đang tạo...',
    loiTheoO: 'Thông tin chưa đúng ở các ô được đánh dấu.',
    // Số liệu.
    tkTong: 'Loại chứng từ',
    tkTongMo: 'Tổng số đã khai báo',
    tkDangDung: 'Đang dùng',
    tkDangDungMo: 'Chọn được khi nhận dạng',
    tkTruong: 'Trường dữ liệu',
    tkTruongMo: 'Cộng ở mọi loại chứng từ',
    tkBang: 'Bảng',
    tkBangMo: 'Cộng ở mọi loại chứng từ',
    // Bộ lọc.
    timPlaceholder: 'Tìm theo mã, tên, mô tả...',
    timNhan: 'Tìm loại chứng từ',
    tatCaLoai: 'Tất cả phân loại',
    locLoai: 'Lọc theo phân loại',
    tatCaTrangThai: 'Tất cả trạng thái',
    locTrangThai: 'Lọc theo trạng thái',
    // Bảng danh sách.
    bangCaption: 'Danh sách loại chứng từ OCR',
    cotLoai: 'Loại chứng từ',
    cotPhanLoai: 'Phân loại',
    cotModel: 'Cách trích xuất',
    buocDoc: 'Đọc chữ: {{ten}}',
    buocDien: 'Điền dữ liệu: {{ten}}',
    cotCauTruc: 'Cấu trúc',
    soTruong: '{{so}} trường',
    soBang: '{{so}} bảng',
    soCot: '{{so}} cột',
    cotChungTu: 'Chứng từ',
    cotTao: 'Người tạo',
    dangHien: 'Đang hiện {{so}} loại chứng từ',
    dangHienLoc: 'Đang hiện {{so}} / {{tong}} loại chứng từ',
    loiDanhSach: 'Không tải được danh sách loại chứng từ: {{loi}}',
    rongLoc: 'Không có loại chứng từ nào khớp bộ lọc',
    rongThat: 'Chưa có loại chứng từ nào',
    rongThatMo: 'Khai báo loại chứng từ để chọn được nó khi nhận dạng OCR.',
    // Menu thao tác.
    xemCauHinh: 'Xem cấu hình',
    batLoai: 'Bật loại chứng từ',
    batLoaiMo: 'Chọn lại được khi nhận dạng',
    tatLoai: 'Tắt loại chứng từ',
    tatLoaiMo: 'Không chọn được khi nhận dạng; chứng từ cũ giữ nguyên',
    xoaLoai: 'Xoá loại chứng từ',
    xoaChanMo: 'Đang có {{so}} chứng từ — xoá các chứng từ đó trước',
    xoaLoaiHoi: 'Xoá loại chứng từ "{{ten}}"?',
    xoaLoaiThan: 'Loại chứng từ cùng mọi trường, bảng, cột của nó bị xoá. Không hoàn tác được.',
    // Hộp tạo và khối thông tin chung.
    fMa: 'Mã loại chứng từ',
    fMaGoiY: 'Chữ hoa, số, gạch dưới, gạch ngang. Không đổi được sau khi tạo.',
    fMaTrong: 'Nhập mã loại chứng từ.',
    fMaSai: 'Mã chỉ gồm chữ hoa, số, _ và -.',
    fTen: 'Tên loại chứng từ',
    fTenGoiY: 'Ví dụ: Hóa đơn GTGT đầu vào',
    fTenTrong: 'Nhập tên loại chứng từ.',
    fLoai: 'Phân loại',
    fMoTa: 'Mô tả cho model',
    fMoTaGoiY: 'Câu này đi vào prompt của bước điền dữ liệu: nói chứng từ là gì, quy ước ghi số, ngày.',
    fCheDo: 'Cách trích xuất',
    fModelMot: 'Model nhận dạng',
    fModelDien: 'Model điền dữ liệu (bước 2)',
    fModelDoc: 'Model đọc chữ (bước 1)',
    fModelDocTrong: 'Chọn model đọc chữ cho bước 1.',
    fChonModelDoc: 'Chọn model đọc chữ',
    modelKhongDung: '{{khoa}} (đang tắt hoặc không còn)',
    cheDoMo: {
      SINGLE_MODEL: 'Một model vừa đọc chữ vừa điền dữ liệu trong một lượt.',
      OCR_THEN_MAP: 'Model chuyên OCR đọc chữ từng trang, rồi model suy luận điền dữ liệu. Bản chép được lưu để quét lại nhanh.',
    },
    fDangDung: 'Đang dùng',
    fDangDungMo: 'Tắt thì không chọn được khi nhận dạng, chứng từ cũ giữ nguyên.',
    coXuLy: 'Tuỳ chọn xử lý',
    co: {
      split_pdf_pages: 'Tách PDF theo trang',
      split_pdf_pagesMo: 'Gửi từng trang cho model. Bỏ chọn để gửi cả tài liệu trong một lời gọi.',
      enable_word_boxes: 'Đo vị trí từng giá trị',
      enable_word_boxesMo: 'Để tô sáng giá trị trên tệp gốc khi đối soát.',
      transform_by_prompt: 'Chuẩn hoá theo mô tả',
      transform_by_promptMo: 'Model đổi định dạng giá trị theo quy ước trong mô tả.',
      model_self_map: 'Model tự ghép cột',
      model_self_mapMo: 'Model tự ghép cột của bảng theo nhãn in trên chứng từ.',
    },
    // Màn chi tiết.
    chiTietTieuDe: 'Cấu hình loại chứng từ',
    chiTietMo: 'Thông tin chung, trường dữ liệu và bảng model cần điền',
    khongThay: 'Không tìm thấy loại chứng từ',
    khongThayMo: 'Loại chứng từ này đã bị xoá hoặc đường dẫn không đúng.',
    veDanhSach: 'Về danh sách loại chứng từ',
    loiChiTiet: 'Không tải được loại chứng từ: {{loi}}',
    khoiChung: 'Thông tin chung',
    khoiChungMo: 'Thay đổi áp dụng cho lượt nhận dạng sau, không đổi chứng từ đã quét.',
    boThayDoi: 'Bỏ thay đổi',
    luuThayDoi: 'Lưu thay đổi',
    khoiTruong: 'Trường dữ liệu',
    khoiTruongMo: '{{so}} trường model cần điền',
    bangTruongCaption: 'Trường dữ liệu của {{ten}}',
    cotTruong: 'Trường',
    cotKieu: 'Kiểu dữ liệu',
    cotViTri: 'Vị trí',
    cotRangBuoc: 'Ràng buộc',
    batBuoc: 'Bắt buộc',
    duyNhat: 'Duy nhất',
    cotCotLoc: 'Cột lọc nhanh',
    cotNhanIn: 'Nhãn in trên chứng từ',
    chuaCoTruong: 'Chưa có trường nào',
    chuaCoTruongMo: 'Thêm trường để model biết cần lấy giá trị gì.',
    themTruong: 'Thêm trường',
    themTruongDauTien: 'Thêm trường đầu tiên',
    suaTruong: 'Sửa trường',
    xoaTruong: 'Xoá trường',
    xoaTruongHoi: 'Xoá trường "{{ten}}"?',
    xoaTruongThan: 'Model thôi điền trường này từ lượt nhận dạng sau. Trường đã có giá trị ở chứng từ thì không xoá được. Không hoàn tác được.',
    khoiBang: 'Bảng',
    khoiBangMo: '{{so}} bảng, mỗi bảng là một danh sách dòng trên chứng từ',
    chuaCoBang: 'Chưa có bảng nào',
    chuaCoBangMo: 'Thêm bảng khi chứng từ có danh sách dòng, ví dụ hàng hóa, dịch vụ.',
    themBang: 'Thêm bảng',
    themBangDauTien: 'Thêm bảng đầu tiên',
    suaBang: 'Sửa bảng',
    xoaBang: 'Xoá bảng',
    xoaBangHoi: 'Xoá bảng "{{ten}}"?',
    xoaBangThan: 'Bảng cùng {{so}} cột của nó bị xoá. Bảng đã có dòng ở chứng từ thì không xoá được. Không hoàn tác được.',
    coHangTieuDe: 'có hàng tiêu đề',
    khongHangTieuDe: 'không có hàng tiêu đề',
    bangCotCaption: 'Các cột của bảng {{ten}}',
    cotCot: 'Cột',
    chuaCoCot: 'Bảng chưa có cột nào',
    chuaCoCotMo: 'Thêm cột để model biết mỗi dòng gồm những giá trị nào.',
    themCot: 'Thêm cột',
    suaCot: 'Sửa cột',
    xoaCot: 'Xoá cột',
    xoaCotHoi: 'Xoá cột "{{ten}}"?',
    xoaCotThan: 'Model thôi điền cột này từ lượt nhận dạng sau. Không hoàn tác được.',
    // Hộp trường, bảng, cột.
    truongThemTieuDe: 'Thêm trường dữ liệu',
    truongSuaTieuDe: 'Sửa trường dữ liệu',
    luuTruong: 'Lưu trường',
    fKhoa: 'Khoá',
    fKhoaGoiY: 'Chữ cái, số, gạch dưới; bắt đầu bằng chữ cái. Dùng trong kết quả JSON.',
    fKhoaBangGoiY: 'Bảng đã có dòng ở chứng từ thì không đổi được khoá.',
    fKhoaTrong: 'Nhập khoá.',
    fKhoaSai: 'Khoá chỉ gồm chữ cái, số, dấu gạch dưới và bắt đầu bằng chữ cái.',
    fNhan: 'Nhãn',
    fNhanTrong: 'Nhập nhãn.',
    fKieu: 'Kiểu dữ liệu',
    fViTri: 'Vị trí trên chứng từ',
    fBatBuoc: 'Bắt buộc có giá trị',
    fDuyNhat: 'Không trùng giữa các chứng từ',
    fCotLoc: 'Cột lọc nhanh',
    fCotLocGoiY: 'Giá trị được chép sang cột lọc của danh sách chứng từ. Mỗi cột lọc nhận một trường.',
    khongGanCotLoc: 'Không gán',
    cotLocDaDung: '{{cot}} — đã gán cho "{{ten}}"',
    fNhanIn: 'Nhãn in trên chứng từ',
    fNhanInGoiY: 'Nhiều cách in thì ngăn bằng dấu |, ví dụ: Số|Số hóa đơn',
    fRegex: 'Biểu thức kiểm tra',
    fRegexGoiY: 'Biểu thức chính quy giá trị phải khớp. Để trống nếu không kiểm.',
    fRegexSai: 'Biểu thức chính quy không hợp lệ.',
    fMoTaTruong: 'Ghi chú cho model',
    fMoTaTruongGoiY: 'Ví dụ cách đọc, giá trị cho phép của danh sách chọn.',
    bangThemTieuDe: 'Thêm bảng',
    bangSuaTieuDe: 'Sửa bảng',
    luuBang: 'Lưu bảng',
    fTenBang: 'Tên bảng',
    fTenBangTrong: 'Nhập tên bảng.',
    fMoTaBang: 'Ghi chú cho model',
    fCoTieuDe: 'Bảng có hàng tiêu đề',
    fCoTieuDeMo: 'Bỏ chọn khi bảng in không có hàng tiêu đề: cột được ghép theo thứ tự khai báo.',
    cotThemTieuDe: 'Thêm cột vào bảng "{{bang}}"',
    cotSuaTieuDe: 'Sửa cột của bảng "{{bang}}"',
    luuCot: 'Lưu cột',
    // NEO-CAU-HINH-OCR
  },

  // ───────────────────────── Kho bản OCR ─────────────────────────
  // Màn `/xu-ly/tai-lieu-ocr` (modules/kho-ocr).
  khoOcr: {
    moTa: 'Tài liệu đã đọc chữ, lưu lại để tạo chứng từ mà không phải OCR lại',
    taiLen: 'Tải tài liệu lên',
    timNhan: 'Tìm tài liệu',
    timPlaceholder: 'Tìm theo tên tệp...',
    locTrangThai: 'Lọc theo trạng thái',
    tatCaTrangThai: 'Tất cả trạng thái',
    trangThai: {
      READY: 'Có bản dùng được',
      RUNNING: 'Đang đọc',
      FAILED: 'Đọc lỗi',
      EMPTY: 'Chưa có bản OCR',
    },
    trangThaiBan: {
      QUEUED: 'Đang chờ',
      READING: 'Đang đọc',
      DONE: 'Đã đọc xong',
      FAILED: 'Đọc lỗi',
    },
    nguon: {
      PRE_OCR: 'Đọc trước trong kho',
      DOCUMENT: 'Đọc khi nhận dạng chứng từ',
      BACKFILL: 'Bổ sung từ dữ liệu cũ',
    },
    cot: {
      tep: 'Tài liệu',
      trangThai: 'Trạng thái',
      banDung: 'Bản đang dùng',
      soBan: 'Số bản',
      soChungTu: 'Chứng từ dùng',
      nguoiTai: 'Người tải lên',
      ngayTai: 'Ngày tải',
    },
    bangCaption: 'Danh sách tài liệu trong kho bản OCR',
    tienDo: 'Trang {{xong}}/{{tong}}',
    tienDoChuaRo: 'Đang chuẩn bị...',
    daGhim: 'Đã ghim',
    moiNhat: 'Bản mới nhất',
    chuaCoBanDung: 'Chưa có',
    xemChiTiet: 'Xem chi tiết',
    docLai: 'Đọc lại bằng model khác',
    xoaTaiLieu: 'Xoá tài liệu',
    loiDanhSach: 'Không tải được kho bản OCR: {{loi}}',
    rongLoc: 'Không có tài liệu nào khớp bộ lọc',
    rongThat: 'Kho bản OCR chưa có tài liệu nào',
    rongThatMo: 'Tải PDF hoặc ảnh lên để đọc chữ trước, rồi tạo chứng từ từ bản OCR mà không phải đọc lại.',
    taiLenDauTien: 'Tải tài liệu đầu tiên',
    dangHien: 'Đang hiện {{so}} tài liệu',
    dangHienCon: 'Đang hiện {{so}} tài liệu, còn tài liệu cũ hơn',
    // Hộp tải lên.
    hopTaiTieuDe: 'Tải tài liệu lên để đọc chữ',
    keoTha: 'Kéo thả tệp vào đây, hoặc bấm để chọn',
    keoThaMo: 'PDF hoặc ảnh, tối đa 20 tệp, 25 MB mỗi tệp',
    tepDaChon: 'Tệp đã chọn',
    boTep: 'Bỏ tệp {{ten}}',
    loiSaiDinhDang: '{{ten}}: chỉ nhận PDF hoặc ảnh. Bỏ tệp này rồi gửi lại.',
    loiQuaLon: '{{ten}}: lớn hơn 25 MB. Tách tệp hoặc giảm độ phân giải rồi chọn lại.',
    loiQuaNhieu: 'Mỗi lần tối đa 20 tệp. Bỏ bớt {{so}} tệp rồi gửi lại.',
    loiChuaChon: 'Chọn ít nhất một tệp để đọc.',
    modelDoc: 'Model đọc chữ',
    modelDocMo: 'Chỉ hiện model đọc riêng từng trang được.',
    chonModelDoc: 'Chọn model đọc chữ',
    chuaCoModelDoc: 'Chưa có model đọc chữ nào đang bật. Bật một model ở Thiết lập Model AI rồi quay lại.',
    loiChuaChonModel: 'Chọn model đọc chữ.',
    boQuaDaCo: 'Bỏ qua tệp đã có bản OCR',
    boQuaDaCoMo: 'Tệp trùng nội dung với tài liệu đã đọc xong thì không đọc lại.',
    guiSo: 'Đọc {{so}} tệp',
    dangGui: 'Đang tải lên...',
    xong: 'Xong',
    ketQuaTieuDe: 'Kết quả tải lên',
    ketQuaTomTat: '{{xep}} tệp vào hàng đợi đọc, {{bo}} tệp bỏ qua, {{loi}} tệp không nhận.',
    ketQua: {
      QUEUED: 'Đã vào hàng đợi đọc',
      SKIPPED_DONE: 'Bỏ qua: đã có bản OCR',
      SKIPPED_RUNNING: 'Bỏ qua: đang được đọc',
      REJECTED: 'Không nhận',
      FAILED: 'Lỗi',
    },
    // Đọc lại.
    hopDocLaiTieuDe: 'Đọc lại "{{ten}}"?',
    hopDocLaiMo: 'Hệ thống tạo một bản OCR mới bằng model đã chọn. Các bản cũ vẫn giữ, chứng từ đang dùng bản cũ không đổi.',
    docLaiNut: 'Đọc lại tài liệu',
    daDocLai: 'Đã đưa "{{ten}}" vào hàng đợi đọc lại.',
    // Xoá.
    xoaTepTieuDe: 'Xoá tài liệu "{{ten}}" khỏi kho?',
    xoaTepMo: 'Tài liệu và mọi bản OCR của nó bị xoá khỏi kho. Không hoàn tác được.',
    daXoaTep: 'Đã xoá tài liệu "{{ten}}".',
    xoaBanTieuDe: 'Xoá bản OCR này?',
    xoaBanMo: 'Bản OCR đọc bằng {{ten}} bị xoá. Không hoàn tác được.',
    xoaBanNut: 'Xoá bản OCR',
    daXoaBan: 'Đã xoá bản OCR.',
    // Ngăn chi tiết.
    chiTietTieuDe: 'Chi tiết tài liệu',
    thongTin: 'Thông tin tệp',
    tenTep: 'Tên tệp',
    kichThuoc: 'Kích thước',
    kieuTep: 'Kiểu tệp',
    xemTruoc: 'Xem trước',
    cacBan: 'Các bản OCR ({{so}})',
    chuaCoBan: 'Chưa có bản OCR nào',
    chuaCoBanMo: 'Đọc lại tài liệu để tạo bản OCR đầu tiên.',
    dangDung: 'Đang dùng',
    ghim: 'Ghim bản này',
    boGhim: 'Bỏ ghim',
    huyDoc: 'Huỷ lượt đọc',
    xemBanChep: 'Xem bản chép',
    daGhimToast: 'Đã ghim bản OCR. Chứng từ tạo sau sẽ dùng bản này.',
    daBoGhim: 'Đã bỏ ghim bản OCR.',
    daHuyDoc: 'Đã huỷ lượt đọc.',
    hopHuyDocTieuDe: 'Huỷ lượt đọc đang chạy?',
    hopHuyDocMo: 'Bản OCR này dừng lại và được ghi là lỗi. Đọc lại tài liệu được bất cứ lúc nào.',
    banTao: '{{ngay}} · {{nguoi}}',
    soChungTuDung: '{{so}} chứng từ đang dùng',
    banChepCua: 'Bản chép: {{ten}}',
    dongBanChep: 'Đóng bản chép',
    thaoTacBan: 'Thao tác với bản OCR {{ten}}',
    loiChiTiet: 'Không tải được tài liệu: {{loi}}',
    // NEO-KHO-OCR
  },

  // ───────────────────────── Model AI ─────────────────────────
  aiModel: {
    themModel: 'Thêm model AI',
    bangCaption: 'Danh mục model AI của nền tảng',
    timTheo: 'Tìm theo tên, khoá, model id...',
    timNhan: 'Tìm model AI',
    tatCaEngine: 'Tất cả engine',
    tatCaTrangThai: 'Bật và tắt',
    locTrangThai: 'Lọc theo trạng thái',
    // Cột bảng.
    cotTenHienThi: 'Tên hiển thị',
    cotEngine: 'Engine',
    cotModelId: 'Model id',
    cotLamDuoc: 'Làm được',
    cotSuyLuan: 'Suy luận',
    cotGhiChu: 'Ghi chú',
    macDinh: 'Mặc định',
    modelCuaNhaCungCap: 'Mặc định của engine',
    anh: 'ảnh: {{id}}',
    vaiDocChu: 'Đọc chữ',
    vaiDienDuLieu: 'Điền dữ liệu',
    vaiGiong: 'Giọng đọc',
    mucDich: { VAN_BAN: 'Văn bản', GIONG: 'Giọng' },
    dangBat: 'Đang bật',
    daTatNhan: 'Đã tắt',
    dangHien: 'Đang hiện {{so}} model',
    dangHienLoc: 'Đang hiện {{so}} / {{tong}} model',
    // Menu thao tác.
    datLamMacDinh: 'Đặt làm model mặc định',
    dangLaMacDinh: 'Đang là model mặc định',
    batTruocKhiDatMacDinh: 'Bật model trước khi đặt làm mặc định',
    khongDienDuocDeMacDinh: 'Model đọc chữ chuyên dụng không làm mặc định được',
    batModel: 'Bật model',
    tatModel: 'Tắt model',
    doiMacDinhTruocKhiTat: 'Model mặc định — đặt model khác làm mặc định trước khi tắt',
    chinhSua: 'Chỉnh sửa',
    // Hộp xác nhận tắt: ảnh hưởng mọi tenant.
    xacNhanTat: 'Tắt model "{{ten}}" cho mọi tenant?',
    xacNhanTatMo:
      'Người dùng ở mọi tenant không chọn được model này khi cấu hình và nhận dạng chứng từ; loại chứng từ đang dùng nó sẽ báo model không dùng được. Model không bị xoá, bật lại được bất cứ lúc nào.',
    xacNhanTatGiongMo: 'Trợ lý AI ở mọi tenant mất giọng đọc dùng model này. Model không bị xoá, bật lại được bất cứ lúc nào.',
    // Trạng thái rỗng và lỗi.
    loiDanhSach: 'Không tải được danh mục model AI: {{loi}}',
    rongLoc: 'Không có model nào khớp bộ lọc',
    rongThat: 'Danh mục chưa có model AI nào',
    rongThatMo: 'Thêm model đầu tiên để các tenant nhận dạng được chứng từ.',
    rongTab: 'Chưa có model nào ở nhóm này',
    // Bốn tab theo loại model.
    tabTatCa: 'Tất cả',
    tabSuyLuan: 'Suy luận',
    tabOcr: 'OCR',
    tabGiong: 'Giọng nói',
    donViHeSo: 'Theo hệ số',
    nhomChuyenOcr: 'Chuyên OCR',
    nhomDaNang: 'Đa năng đọc được ảnh',
    cotChucNang: 'Chức năng',
    cotDonViTinh: 'Đơn vị tính',
    chucNang: { nhanDang: 'Nhận dạng', docThanhTieng: 'Đọc thành tiếng', hoiThoai: 'Hội thoại', chung: 'Giọng nói' },
    // Hộp thoại thêm / sửa.
    dialogThem: 'Thêm model AI',
    dialogSua: 'Chỉnh sửa model AI',
    themVaLuu: 'Thêm model',
    fTenHienThi: 'Tên hiển thị',
    fTenHienThiGoiY: 'Ví dụ: Gemini 2.5 Pro',
    fTenHienThiTrong: 'Nhập tên hiển thị của model.',
    fQuaDai: 'Tối đa {{so}} ký tự.',
    fKhongSinhDuocKhoa: 'Tên cần có ít nhất một chữ cái hoặc chữ số để sinh khoá, hoặc điền model id.',
    fKhoaTrung: 'Không sinh được khoá không trùng từ tên này. Đổi tên hoặc điền model id khác.',
    fKhoa: 'Khoá model',
    fKhoaChoTen: 'Điền tên hoặc model id để sinh khoá',
    fKhoaTuSinh: 'Tự sinh từ model id, không có thì từ tên; trùng thì thêm -2, -3. Không đổi được sau khi tạo.',
    fEngine: 'Engine',
    fEngineMo: 'Không đổi được sau khi tạo.',
    fKhongDoiSauTao: 'Khoá và engine không đổi được sau khi tạo: cấu hình chứng từ của các tenant tham chiếu theo chúng.',
    fModelId: 'Model id',
    fModelIdMo: 'Để trống thì dùng model mặc định của engine do nền tảng cấu hình.',
    fVisionModelId: 'Model id đọc ảnh',
    fVisionModelIdMo: 'Chỉ cần khi engine dùng model riêng để đọc ảnh.',
    fMucDich: 'Mục đích',
    fMucDichMo: 'Model giọng dùng cho nhận dạng giọng nói và giọng đọc: không hiện ở ô chọn model, không làm mặc định được.',
    fMucDichMacDinh: 'Model mặc định luôn là văn bản. Muốn đổi thì đặt model khác làm mặc định trước.',
    fSuyLuan: 'Suy luận',
    fSuyLuanMo: 'Model có chạy bước suy luận trước khi trả lời hay không.',
    fGhiChu: 'Ghi chú',
    fGhiChuGoiY: 'Model này hợp với loại chứng từ nào',
    fTokenMoiPhut: 'Token Fox cho 1 phút âm thanh',
    fTokenMoiPhutVd: '1 giờ = {{so}} token Fox',
    fTokenMoiPhutLoi: 'Nhập số nguyên từ 1 tới 1.000.000.',
    tokenMoiPhut: '{{so}} token/phút',
    fApDung: 'Bật model này',
    fMacDinh: 'Đặt làm model mặc định của nền tảng',
    fMacDinhPhaiBat: 'Model mặc định phải đang bật. Bật model này hoặc bỏ chọn mặc định.',
    fMacDinhLuonBat: 'Model mặc định luôn bật. Muốn tắt thì đặt model khác làm mặc định trước.',
    // Toast sau khi ghi.
    daThem: 'Đã thêm model "{{ten}}", khoá {{khoa}}.',
    daCapNhat: 'Đã lưu model "{{ten}}".',
    daDatMacDinh: 'Đã đặt "{{ten}}" làm model mặc định.',
    daBat: 'Đã bật model "{{ten}}".',
    daTat: 'Đã tắt model "{{ten}}".',
    // Tab "Model AI" của chi tiết tenant (ADR-0083 mục 3).
    tab: 'Model AI',
    tenantTieuDe: 'Model AI tenant dùng được',
    tenantDem: '{{so}}/{{tong}} model dùng được',
    tenantCaption: 'Model AI của tenant {{ten}}',
    tenantDangTai: 'Đang tải model AI của tenant',
    tenantLoi: 'Không tải được model AI của tenant: {{loi}}',
    tenantLoiLuu: 'Chưa lưu được: {{loi}}',
    tenantRong: 'Danh mục chưa có model nào đang bật',
    tenantRongMo: 'Bật hoặc thêm model ở màn Model AI, rồi quay lại đây để giới hạn cho tenant.',
    moDanhMuc: 'Mở danh mục Model AI',
    cotDungDuoc: 'Tenant dùng được',
    dungDuoc: 'Dùng được',
    khongDungDuoc: 'Đã tắt',
    congTacNhan: 'Cho tenant dùng model {{model}}',
    macDinhCuaTenant: 'Mặc định của tenant',
    tenantTatHet: 'Tenant sẽ không nhận dạng được chứng từ cho tới khi bật lại ít nhất một model.',
    tenantChuaLuu: 'Thay đổi chưa lưu: {{so}}',
    tenantDaKhop: 'Không có thay đổi chưa lưu',
    huyThayDoi: 'Huỷ thay đổi',
    luuThayDoi: 'Lưu thay đổi',
    tenantDaLuu: 'Đã lưu model AI cho tenant {{ten}}.',
    /**
     * Tên bộ adapter. Hầu hết là **danh từ riêng** nên giống nhau ở hai ngôn ngữ — vẫn đưa qua
     * catalog để phần bổ nghĩa (`tự vận hành`) dịch được.
     */
    engine: {
      GEMINI: 'Google Gemini',
      CLAUDE: 'Anthropic Claude',
      FOXAI: 'FoxAI (tự vận hành)',
      GLM: 'Zhipu GLM',
      CHANDRA: 'Chandra OCR (tự vận hành)',
      DEEPSEEK: 'DeepSeek OCR (tự vận hành)',
      CHANDRA2: 'Chandra 2 (tự vận hành)',
      PADDLE: 'PaddleOCR-VL (tự vận hành)',
      CHANDRA2_L40: 'Chandra 2 trên cụm L40',
      QWEN38: 'Qwen 3.8 (tự vận hành)',
      CHANDRA2_H200: 'Chandra 2 trên cụm H200',
      QWEN38_H200: 'Qwen 3.8 trên cụm H200',
      GEMMA4: 'Gemma 4 (tự vận hành)',
      WHISPER: 'Whisper (tự vận hành)',
    },
    // Chế độ suy luận.
    suyLuan: {
      OPTIONAL: 'Tuỳ chọn',
      FORCED: 'Luôn bật',
      DISABLED: 'Không dùng',
    },
  },

  // ───────────────────────── Xử lý chứng từ ─────────────────────────
  chungTu: {
    tieuDe: 'Quản lý Chứng từ',
    moTa: 'Chứng từ đã nhận dạng OCR: xem kết quả, sửa, xác nhận rồi chuyển vào kho tri thức',
    bangCaption: 'Danh sách chứng từ đã tải lên',
    nhanDangMoi: 'Nhận dạng chứng từ',
    // Sáu thẻ số liệu bấm được.
    thTongChungTu: 'Tổng chứng từ',
    thDangXuLy: 'Đang xử lý',
    thChoXacNhan: 'Chờ xác nhận',
    thDaXacNhan: 'Đã xác nhận',
    thDaChuyenKho: 'Đã chuyển kho',
    thLoiOcr: 'Lỗi OCR',
    goiYGiaiDoan: '{{cho}} chờ · {{doc}} đọc chữ · {{suyLuan}} suy luận',
    // Bộ lọc.
    timTheo: 'Tìm theo tên tệp, mã loại, số chứng từ, người bán...',
    timNhan: 'Tìm chứng từ',
    tatCaTrangThai: 'Tất cả trạng thái',
    tatCaLoai: 'Tất cả loại',
    nhanLoaiChungTu: 'Loại chứng từ',
    locTatCa: 'Đang xử lý (tất cả)',
    // Cột bảng.
    cotChungTu: 'Chứng từ',
    cotMa: 'Mã loại',
    cotLoai: 'Loại',
    cotSoChungTu: 'Số chứng từ, người bán',
    cotTongTien: 'Tổng tiền',
    cotNgayTao: 'Ngày tải lên',
    cotNguoiThucHien: 'Người tải lên',
    khongTen: 'Tệp không tên',
    phuLuc: '+{{so}} tệp kèm',
    dangHien: 'Đang hiện {{so}} chứng từ',
    dangHienConNua: 'Đang hiện {{so}} chứng từ, còn chứng từ cũ hơn',
    // Hành động hàng loạt.
    daChon: 'Đã chọn {{so}} chứng từ',
    xacNhanSo: 'Xác nhận {{so}} chứng từ',
    chuyenSo: 'Chuyển {{so}} chứng từ',
    xoaSo: 'Xoá {{so}} chứng từ',
    boChon: 'Bỏ chọn',
    xacNhanXoaSo: 'Xoá {{so}} chứng từ?',
    xacNhanXoaSoMo:
      'Các chứng từ đã chọn và kết quả OCR của chúng bị xoá, không hoàn tác được. Chứng từ đã xác nhận hoặc đã chuyển được giữ lại. Tệp trong kho bản OCR không bị xoá.',
    xacNhanChuyenSo: 'Chuyển {{so}} chứng từ vào kho tri thức?',
    xacNhanChuyenSoMo:
      'Chỉ chứng từ đã xác nhận được chuyển; sau khi chuyển không sửa được nữa. Phiên đăng nhập cũ có thể phải đăng nhập lại để làm thao tác nhạy cảm này.',
    ketQuaHangLoat: 'Đã {{viec}} {{xong}} chứng từ.',
    ketQuaHangLoatBoQua: 'Đã {{viec}} {{xong}} chứng từ; {{boQua}} chứng từ bỏ qua vì không đúng trạng thái.',
    viecXacNhan: 'xác nhận',
    viecChuyen: 'chuyển',
    viecXoa: 'xoá',
    // Menu thao tác của dòng và của màn chi tiết.
    xemChiTiet: 'Xem chi tiết',
    xacNhanChungTu: 'Xác nhận chứng từ',
    chuyenVaoKho: 'Chuyển vào kho tri thức',
    thuLaiSuyLuan: 'Thử lại bước suy luận',
    thuLaiSuyLuanMo: 'Dùng lại bản chép OCR đã có',
    quetLaiTuDau: 'Quét lại từ đầu',
    quetLaiMo: 'Đọc lại từ tệp gốc',
    huyLuotQuet: 'Huỷ lượt quét',
    xoaChungTu: 'Xoá chứng từ',
    // Hộp xác nhận.
    xacNhanXoaMot: 'Xoá chứng từ "{{ten}}"?',
    xacNhanXoaMotMo: 'Chứng từ và kết quả OCR của nó bị xoá, không hoàn tác được. Tệp trong kho bản OCR không bị xoá.',
    xacNhanQuetLai: 'Quét lại "{{ten}}" từ đầu?',
    xacNhanQuetLaiMo:
      'Hệ thống đọc lại tệp gốc và điền lại mọi trường; giá trị đã sửa tay bị ghi đè, chỉ dòng tự thêm được giữ. Lượt quét chạy nền vài phút.',
    xacNhanHuyQuet: 'Huỷ lượt quét của "{{ten}}"?',
    xacNhanHuyQuetMo: 'Chứng từ chuyển sang Lỗi ở bước đang dở. Quét lại được bất cứ lúc nào.',
    xacNhanChuyenMot: 'Chuyển "{{ten}}" vào kho tri thức?',
    xacNhanChuyenMotMo:
      'Sau khi chuyển, chứng từ không sửa được nữa. Phiên đăng nhập cũ có thể phải đăng nhập lại để làm thao tác nhạy cảm này.',
    // Toast.
    daXoaMot: 'Đã xoá chứng từ.',
    daXacNhanMot: 'Đã xác nhận chứng từ.',
    daChuyenMot: 'Đã chuyển chứng từ vào kho tri thức.',
    daBatDauQuetLai: 'Đã đưa chứng từ vào hàng đợi quét lại.',
    daHuyQuet: 'Đã huỷ lượt quét.',
    daLuuGiaTri: 'Đã lưu giá trị đã sửa.',
    // Trạng thái rỗng và lỗi.
    loiDanhSach: 'Không tải được danh sách chứng từ: {{loi}}',
    rongLoc: 'Không có chứng từ nào khớp bộ lọc',
    rongThat: 'Chưa có chứng từ nào',
    rongThatMo: 'Chứng từ xuất hiện ở đây sau khi bạn nhận dạng OCR một tệp.',
    // Màn chi tiết.
    keoDeDoiDoRong: 'Kéo để đổi độ rộng hai khung',
    chiTietTieuDe: 'Chi tiết chứng từ',
    loiChiTiet: 'Không tải được chứng từ: {{loi}}',
    khongTimThay: 'Không tìm thấy chứng từ',
    khongTimThayMo: 'Chứng từ đã bị xoá hoặc thuộc tổ chức khác.',
    veDanhSach: 'Về danh sách chứng từ',
    theTepGoc: 'Tệp gốc',
    theBanChep: 'Bản chép OCR',
    tepChinh: 'Tệp chính',
    dangQuet: 'Đang nhận dạng — màn tự cập nhật khi xong',
    luotThu: 'lượt {{so}}',
    loiQuet: 'Lượt quét gần nhất lỗi ở bước {{buoc}}',
    buocDoc: 'đọc chữ',
    buocSuyLuan: 'suy luận',
    thongTinChung: 'Thông tin chứng từ',
    ttLoaiChungTu: 'Loại chứng từ',
    ttSoChungTu: 'Số chứng từ',
    ttNgayChungTu: 'Ngày chứng từ',
    ttNguoiBan: 'Người bán',
    ttMstNguoiBan: 'MST người bán',
    ttTongThanhToan: 'Tổng thanh toán',
    ttDoTinCay: 'Độ tin cậy',
    ttTaiLenBoi: 'Tải lên',
    ttXacNhanBoi: 'Xác nhận',
    ttChuyenBoi: 'Chuyển kho',
    nguoiLuc: '{{ten}}, {{luc}}',
    truongDuLieu: 'Trường dữ liệu',
    soTruong: '{{so}} trường',
    chuaCoTruong: 'Loại chứng từ chưa khai trường nào',
    chuaNhanDang: 'Chưa nhận dạng được',
    daSuaTay: 'Đã sửa tay',
    co: 'Có',
    khong: 'Không',
    chiDoc: 'Chỉ sửa được chứng từ ở trạng thái Nháp hoặc Chờ xác nhận.',
    luuGiaTri: 'Lưu giá trị đã sửa',
    boThayDoi: 'Bỏ thay đổi',
    soDong: '{{so}} dòng',
    bangCaptionDong: 'Các dòng của bảng {{ten}} nhận dạng từ chứng từ',
    bangRong: 'Bảng chưa có dòng nào',
    // Nhãn trạng thái chứng từ.
    trangThai: {
      DRAFT: 'Nháp',
      PROCESSING: 'Đang xử lý',
      PROCESSED: 'Chờ xác nhận',
      CONFIRMED: 'Đã xác nhận',
      TRANSFERRED: 'Đã chuyển kho',
      ERROR: 'Lỗi',
    },
    // Giai đoạn của luồng OCR.
    giaiDoan: {
      QUEUED: 'Đang chờ',
      READING: 'Đang đọc chữ (OCR)',
      READ_DONE: 'Đã OCR, chờ suy luận',
      MAPPING: 'Đang suy luận',
      RETRY_WAIT: 'Lỗi, chờ thử lại',
    },
    tooltipGiaiDoan: 'Ở giai đoạn này: {{nhan}}',
    tooltipChoThuLai: 'Lỗi lượt trước, đang chờ thử lại.',
    loiOBuoc: 'Lỗi ở bước {{buoc}}',
    // Nhãn tiêu đề các màn giữ chỗ cũ (`routes/index.tsx`). `Hóa đơn VAT` giữ kiểu dấu cũ theo đặc tả.
    loai: {
      HDVAT: 'Hóa đơn VAT',
      HDBL: 'Hợp đồng mua bán',
      PNK: 'Phiếu nhập kho',
      BBBG: 'Biên bản bàn giao',
    },
  },

  // ───────────────────────── Nhận dạng OCR ─────────────────────────
  nhanDang: {
    // Màn chọn loại chứng từ.
    chonKhuonTieuDe: 'Nhận dạng OCR',
    chonKhuonMoTa: 'Chọn loại chứng từ để bắt đầu nhận dạng',
    timKhuon: 'Tìm loại chứng từ theo tên hoặc mã...',
    timKhuonNhan: 'Tìm loại chứng từ',
    khongCoKhuonKhop: 'Không có loại chứng từ nào khớp',
    chuaCoKhuon: 'Chưa có loại chứng từ nào đang dùng',
    chuaCoKhuonMo: 'Khai loại chứng từ ở Thiết lập Chứng từ OCR trước khi nhận dạng.',
    moThietLap: 'Mở Thiết lập Chứng từ OCR',
    loiKhuon: 'Không tải được loại chứng từ: {{loi}}',
    soTruong: '{{so}} trường',
    soBang: '{{so}} bảng',
    // Màn tải lên.
    khongTimThayKhuon: 'Không tìm thấy loại chứng từ',
    khongTimThayKhuonMo: 'Loại chứng từ có mã "{{ma}}" không tồn tại hoặc đã bị gỡ.',
    khuonDaTat: 'Loại chứng từ đang tắt — bật lại ở Thiết lập Chứng từ OCR rồi mới nhận dạng được.',
    chonKhuonKhac: 'Chọn loại chứng từ khác',
    doiKhuon: 'Đổi loại chứng từ',
    keoThaTep: 'Kéo thả tệp vào đây, hoặc bấm để chọn',
    gioiHanTep: 'PDF, ảnh, Word, Excel, CSV hoặc TXT · tối đa 20 tệp, 25 MB mỗi tệp, 60 MB tổng',
    tepDaChon: 'Tệp đã chọn',
    tepChinhMo: 'Tệp đầu là tệp chính; các tệp sau ghép vào cùng chứng từ.',
    boTep: 'Bỏ tệp {{ten}}',
    loiDinhDang: 'Định dạng này không nhận dạng được.',
    loiQuaLon: 'Tệp quá 25 MB.',
    loiQuaNhieu: 'Mỗi lần tối đa 20 tệp.',
    loiTongQuaLon: 'Tổng dung lượng quá 60 MB — bớt tệp rồi gửi lại.',
    caiDat: 'Cách nhận dạng',
    cheDo: 'Chế độ',
    modelDien: 'Model điền dữ liệu',
    modelDienMo: 'Để trống thì dùng model của loại chứng từ, không có thì model mặc định của tổ chức.',
    theoLoaiChungTu: 'Theo cấu hình loại chứng từ',
    modelDoc: 'Model đọc chữ',
    modelDocMo: 'Bước 1 của chế độ hai bước: chép nguyên văn từng trang.',
    ngonNgu: 'Ngôn ngữ chứng từ',
    batDauNhanDang: 'Bắt đầu nhận dạng',
    batDauNhanDangSo: 'Nhận dạng {{so}} tệp',
    dangGui: 'Đang tải lên...',
    chiXem: 'Bạn chỉ có quyền xem — cần quyền nhận dạng để tải tệp lên.',
    daTaiLen: 'Đã tải tệp lên, chứng từ đang được nhận dạng.',
  },
  // ───────────────────────── Gói dịch vụ ─────────────────────────
  // Màn quản trị gói bán cho tenant. Căn cứ: docs/goi-dich-vu/, ADR-0019.
  goiDichVu: {
    // Thống kê đầu màn.
    tkTongGoi: 'Tổng số gói',
    tkTongGoiSub: 'kể cả gói đã ngừng bán',
    tkDangBan: 'Đang bán',
    tkDangBanSub: 'tạo được kỳ thuê bao mới',
    tkTenantDangDung: 'Tenant đang dùng',
    tkTenantDangDungSub: 'tính cả tenant đang trong ân hạn',
    tkDichVu: 'Dịch vụ',
    tkDichVuSub: 'thêm dịch vụ mới cần một lần deploy',

    // Bảng gói.
    bangCaption: 'Danh sách gói dịch vụ bán cho tenant',
    cotTenGoi: 'Gói',
    cotDichVu: 'Dịch vụ trong gói',
    cotAnHan: 'Ân hạn',
    cotTenantDangDung: 'Tenant đang dùng',
    cotSoKy: 'Số kỳ',
    anHanNgay: '{{so}} ngày',
    anHanKhong: 'Không ân hạn',
    soDichVu: '{{so}} dịch vụ',
    themNDichVu: '+{{so}}',
    khongCoDichVu: 'Chưa chọn dịch vụ nào',
    nhanHeThong: 'Hệ thống',
    nhanDangBan: 'Đang bán',
    nhanNgungBan: 'Ngừng bán',
    tongSoGoi: '{{so}} gói',

    // Hành động.
    themGoi: 'Thêm gói',
    suaGoi: 'Sửa gói',
    xoaGoi: 'Xoá gói',
    // Lý do không xoá được, hiện TRƯỚC khi người dùng bấm (BR-108).
    khongXoaHeThong: 'Gói hệ thống, không xoá được',
    khongXoaDaDung: 'Đã dùng cho {{so}} kỳ thuê bao',

    // Hộp thoại thêm/sửa.
    dialogThem: 'Thêm gói dịch vụ',
    dialogSua: 'Sửa gói {{ma}}',
    // Cảnh báo BẮT BUỘC giữ khi cài lại (BR-110): bỏ một dịch vụ khỏi gói là
    // cắt dịch vụ của mọi tenant đang thuê, ngay lập tức.
    dialogSuaCanhBao: 'Sửa gói có hiệu lực ngay với mọi tenant đang dùng gói này.',
    fTenGoi: 'Tên gói',
    fTenGoiGoiY: 'Cơ bản',
    fTenGoiTrong: 'Vui lòng nhập tên gói.',
    fMaGoi: 'Mã gói',
    fMaGoiGoiY: 'Để trống để sinh từ tên gói',
    fMaGoiMo: 'Không đổi được sau khi tạo, vì kỳ thuê bao và nhật ký tham chiếu gói bằng mã.',
    fMaGoiSai:
      'Mã gồm chữ in hoa không dấu, số, gạch dưới; mở đầu bằng chữ; dài 2–40 ký tự.',
    fMoTa: 'Mô tả',
    fMoTaGoiY: 'Gói này bán cho ai, gồm những gì',
    fAnHan: 'Ân hạn mặc định (ngày)',
    fAnHanMo:
      'Sau ngày kết thúc, tenant còn dùng được thêm số ngày này. Kỳ mới chép giá trị lúc tạo nên sửa ở đây không đổi điều khoản của kỳ đã ký.',
    fAnHanSai: 'Ân hạn là số ngày nguyên từ 0 đến 365.',
    fDangBan: 'Đang bán',
    fDangBanMo: 'Bỏ chọn thì không tạo kỳ mới được, kỳ đang chạy vẫn chạy tới hết.',
    fDichVu: 'Dịch vụ trong gói',
    fDichVuLuonBat: '— luôn bật',
    nutTaoGoi: 'Tạo gói',

    // Hộp xác nhận xoá.
    xacNhanXoaTieuDe: 'Xoá gói {{ten}}?',
    xacNhanXoaThan:
      'Gói chưa từng được dùng cho kỳ thuê bao nào nên không tenant nào bị ảnh hưởng. Thao tác này không hoàn tác được.',
    nhanXacNhanXoa: 'Xoá gói {{ten}}',

    // Trạng thái rỗng và lỗi.
    rongThat: 'Chưa có gói dịch vụ nào',
    rongThatMo: 'Tạo gói đầu tiên để bán cho tenant.',
    rongLoc: 'Không có gói nào khớp bộ lọc',
    loiDanhSach: 'Không tải được danh sách gói. {{loi}}',
    daThem: 'Đã thêm gói dịch vụ.',
    daCapNhat: 'Đã lưu thay đổi.',
    daXoa: 'Đã xoá gói dịch vụ.',

    // Tab danh mục dịch vụ — CHỈ ĐỌC, cố ý không có CRUD.
    danhMucCaption: 'Danh mục dịch vụ của nền tảng và mã module tương ứng',
    danhMucMo:
      'Danh mục do migration tạo, màn này chỉ đọc. Thêm một dịch vụ mới cần một lần deploy: thêm nhóm điều hướng ở Portal, thêm mã module ở Gateway, và một migration.',
    cotDichVuTen: 'Dịch vụ',
    cotMaModule: 'Mã module ở Gateway',
    cotLoai: 'Loại',
    loaiBatBuoc: 'Bắt buộc, luôn bật',
    loaiTheoGoi: 'Theo gói',
    khongBaoGioChan: 'Không bao giờ chặn',
    chuaGoiApi: 'Chưa gọi API — chỉ ẩn trên điều hướng',
    // Chú thích BẮT BUỘC (BR-311): vùng dùng chung chỉ bị chặn khi mọi chủ tắt.
    ghiChuVungDungChung:
      'Hai dịch vụ cùng cần một mã module (ví dụ Trợ lý AI và Tri thức AI cùng dùng {{ma}}) thì module đó chỉ bị chặn khi cả hai cùng tắt.',
    ghiChuAnMenu:
      'Tắt một dịch vụ không chỉ ẩn menu: Gateway chặn luôn mã module tương ứng, trước khi yêu cầu chạm tới ứng dụng.',

    // Tình trạng thuê bao — dẫn xuất, không lưu (BR-3xx).
    tinhTrang: {
      chuaCo: 'Chưa có kỳ',
      chuaBatDau: 'Chưa bắt đầu',
      hieuLuc: 'Hiệu lực',
      sapHetHan: 'Sắp hết hạn',
      anHan: 'Trong ân hạn',
      hetHan: 'Hết hạn',
    },
  },

  // ──────────────── Tab Gói & thuê bao ở màn chi tiết tenant ────────────────
  // Đây là nơi GÁN gói cho một tenant; màn Gói dịch vụ chỉ CRUD gói bán.
  thueBao: {
    tab: 'Gói & thuê bao',
    tieuDeThueBao: 'Thuê bao hiện hành',
    tieuDeDichVu: 'Dịch vụ của tenant',
    tieuDeLichSu: 'Lịch sử kỳ thuê bao',
    moDichVu:
      'Dịch vụ tắt thì Gateway chặn luôn mã module tương ứng, không chỉ ẩn nhóm điều hướng.',

    // Thẻ thuê bao hiện hành.
    dongGoi: 'Gói',
    dongKhoangNgay: 'Thời hạn',
    dongConLai: 'Còn lại',
    dongAnHan: 'Ân hạn',
    dongHetAnHan: 'Hết ân hạn',
    dongSoHopDong: 'Số hợp đồng',
    dongGhiChu: 'Ghi chú',
    khongThoiHan: 'Không thời hạn',
    conLaiNgay: '{{so}} ngày',
    anHanNgay: '{{so}} ngày',
    khongAnHan: 'Không ân hạn',
    tuDen: '{{tu}} – {{den}}',
    // Ngày phép tính dùng: nói rõ để người đọc biết con số tính cho hôm nào.
    tinhChoNgay: 'Tính cho ngày {{ngay}}',
    chuaCoKy: 'Tenant này chưa có kỳ thuê bao nào',
    chuaCoKyMo: 'Tạo kỳ đầu tiên để tenant dùng được dịch vụ ngoài nhóm bắt buộc.',

    // Bảng dịch vụ.
    bangDichVuCaption: 'Dịch vụ của tenant, kèm lý do bật hoặc tắt',
    cotDichVu: 'Dịch vụ',
    cotTrangThai: 'Trạng thái',
    cotLyDo: 'Vì sao',
    cotGhiDe: 'Ghi đè',
    dangBat: 'Bật',
    dangTat: 'Tắt',
    khongGhiDe: 'Theo gói',
    ghiDeBatDen: 'Bật tới {{den}}',
    ghiDeBatTu: 'Bật từ {{tu}}',
    ghiDeTatDen: 'Tắt tới {{den}}',
    ghiDeTatTu: 'Tắt từ {{tu}}',
    ghiDeBatVoHan: 'Bật, không giới hạn',
    ghiDeTatVoHan: 'Tắt, không giới hạn',
    ghiDeChuaHieuLuc: 'Chưa hiệu lực',
    // Mã module Gateway chặn: lớp chặn thật, không phải ẩn menu.
    vungBiChan: 'Module bị chặn ở Gateway',
    vungBiChanRong: 'Không module nào bị chặn',

    // Lý do một dịch vụ bật hay tắt (BR-301, dừng ở điều kiện khớp đầu tiên).
    lyDo: {
      batBuoc: 'Bắt buộc',
      ghiDeBat: 'Ghi đè: bật',
      ghiDeTat: 'Ghi đè: tắt',
      theoGoi: 'Theo gói',
      anHan: 'Đang ân hạn',
      ngoaiGoi: 'Ngoài gói',
      hetHan: 'Thuê bao hết hạn',
      chuaBatDau: 'Thuê bao chưa bắt đầu',
      chuaCo: 'Chưa có thuê bao',
    },

    // Bảng lịch sử kỳ.
    bangLichSuCaption: 'Mọi kỳ thuê bao của tenant, kể cả kỳ đã huỷ',
    cotKy: 'Kỳ',
    cotTacNhan: 'Người thao tác',
    nhanDaHuy: 'Đã huỷ',
    nhanHieuLuc: 'Hiệu lực',
    nhanHienHanh: 'Đang áp dụng',
    huyLuc: 'Huỷ lúc {{luc}}',
    lichSuRong: 'Chưa có kỳ nào',

    // Hành động.
    taoKy: 'Tạo kỳ thuê bao',
    giaHan: 'Gia hạn',
    doiGoi: 'Đổi gói',
    suaKy: 'Sửa điều khoản',
    huyKy: 'Huỷ kỳ',
    datGhiDe: 'Ghi đè dịch vụ',
    suaGhiDe: 'Sửa ghi đè',
    boGhiDe: 'Bỏ ghi đè',
    // Lý do không thao tác được, hiện TRƯỚC khi người dùng bấm (BR-108).
    khongSuaKyDaHuy: 'Kỳ đã huỷ, không sửa được',
    khongHuyKyDaHuy: 'Kỳ này đã huỷ từ trước',
    khongGhiDeBatBuoc: 'Dịch vụ bắt buộc, luôn bật — không ghi đè được',
    khongThaoTacLuuTru: 'Tenant đã lưu trữ, không sửa thuê bao được nữa',

    // Hộp thoại kỳ thuê bao.
    dialogTaoKy: 'Tạo kỳ thuê bao',
    dialogDoiGoi: 'Đổi gói giữa kỳ',
    dialogSuaKy: 'Sửa điều khoản kỳ #{{id}}',
    fGoi: 'Gói dịch vụ',
    fGoiChon: 'Chọn gói',
    fGoiMo: 'Chỉ gói đang bán mới tạo được kỳ mới.',
    fGoiTrong: 'Vui lòng chọn gói.',
    fBatDau: 'Ngày bắt đầu',
    fBatDauTrong: 'Vui lòng nhập ngày bắt đầu.',
    fKetThuc: 'Ngày kết thúc',
    fKetThucMo: 'Để trống nghĩa là không thời hạn.',
    fKetThucSai: 'Ngày kết thúc phải từ ngày bắt đầu trở đi.',
    fAnHan: 'Ân hạn (ngày)',
    fAnHanMo:
      'Để trống thì chép ân hạn mặc định của gói. Giá trị chép vào kỳ nên sửa ân hạn của gói sau này không đổi điều khoản của kỳ này.',
    fAnHanSai: 'Ân hạn là số ngày nguyên từ 0 đến 365.',
    fSoHopDong: 'Số hợp đồng',
    fSoHopDongGoiY: 'HD-2026-017',
    fSoHopDongMo: 'Chuỗi tham chiếu, không phải số tiền.',
    fGhiChu: 'Ghi chú',
    loiHuyKyChong: 'Huỷ kỳ đang chồng',
    loiDoiNgayBatDau: 'Đặt ngày bắt đầu từ hôm sau',
    fThayThe: 'Cắt kỳ đang chạy để đổi gói',
    fThayTheMo:
      'Kỳ đang chạy sẽ kết thúc vào ngày trước ngày bắt đầu của kỳ mới. Không chọn thì khoảng ngày chồng nhau sẽ bị từ chối.',
    // Cảnh báo BẮT BUỘC giữ: đổi gói cắt dịch vụ của tenant ngay lập tức.
    canhBaoDoiGoi:
      'Kỳ mới có hiệu lực ngay từ ngày bắt đầu. Dịch vụ không có trong gói mới sẽ bị cắt.',
    nutTaoKy: 'Tạo kỳ',
    nutLuuKy: 'Lưu thay đổi',

    // Hộp xác nhận huỷ kỳ.
    xacNhanHuyTieuDe: 'Huỷ kỳ thuê bao #{{id}}?',
    xacNhanHuyThan:
      'Tenant mất mọi dịch vụ theo gói của kỳ này ngay sau khi huỷ. Kỳ vẫn nằm trong lịch sử. Thao tác này không hoàn tác được.',
    fLyDoHuy: 'Lý do huỷ',
    fLyDoHuyGoiY: 'Doanh nghiệp chấm dứt hợp đồng từ 01/01/2027',
    fLyDoHuyTrong: 'Vui lòng nêu lý do huỷ.',
    fLyDoHuyDai: 'Lý do dài tối đa 500 ký tự.',
    nhanXacNhanHuy: 'Huỷ kỳ #{{id}}',

    // Hộp thoại ghi đè.
    dialogGhiDe: 'Ghi đè dịch vụ {{ten}}',
    fCheDo: 'Chế độ',
    fCheDoBat: 'Bật dịch vụ, bất kể gói',
    fCheDoTat: 'Tắt dịch vụ, bất kể gói',
    fTu: 'Hiệu lực từ',
    fTuMo: 'Để trống nghĩa là không giới hạn đầu.',
    fDen: 'Hiệu lực đến',
    fDenMo: 'Để trống nghĩa là không giới hạn cuối.',
    fDenSai: 'Ngày kết thúc phải từ ngày bắt đầu trở đi.',
    fGhiChuGhiDe: 'Ghi chú',
    fGhiChuGhiDeGoiY: 'Dùng thử ba tháng theo thoả thuận',
    // Ghi đè bật GIỮ được cả sau khi thuê bao hết hạn (BR-301 bước 3).
    moGhiDeBat:
      'Ghi đè bật giữ dịch vụ chạy cả khi thuê bao đã hết hạn, vì nó được xét trước gói.',
    nutLuuGhiDe: 'Lưu ghi đè',

    // Hộp xác nhận bỏ ghi đè.
    xacNhanBoTieuDe: 'Bỏ ghi đè cho {{ten}}?',
    xacNhanBoThan:
      'Dịch vụ trở lại theo gói của kỳ thuê bao hiện hành, nên có thể bật hoặc tắt khác hiện tại.',
    nhanXacNhanBo: 'Bỏ ghi đè',

    // Toast, ở thể đã hoàn tất.
    daTaoKy: 'Đã tạo kỳ thuê bao.',
    daDoiGoi: 'Đã đổi gói.',
    daCapNhatKy: 'Đã lưu thay đổi.',
    daHuyKy: 'Đã huỷ kỳ thuê bao.',
    daGhiDeBat: 'Đã ghi đè: bật dịch vụ.',
    daGhiDeTat: 'Đã ghi đè: tắt dịch vụ.',
    daBoGhiDe: 'Đã bỏ ghi đè.',

    // Trạng thái rỗng và lỗi.
    loiTai: 'Không tải được dịch vụ và thuê bao của tenant. {{loi}}',

    // Gói ban đầu ở form tạo tenant.
    fGoiBanDau: 'Gói dịch vụ',
    fGoiBanDauKhong: 'Chưa chọn — chỉ dịch vụ bắt buộc',
    loiTaiGoi: 'Không tải được danh sách gói. {{loi}}',
    daGanGoiBanDau: 'Đã gán gói {{goi}} từ {{ngay}}.',
    ganGoiBanDauLoi:
      'Đã tạo tenant {{ten}} nhưng chưa gán được gói {{goi}}: {{loi}} Gán lại ở tab "Gói & thuê bao".',
  },

  // ───────────────────── License on-premise — ADR-0059 ─────────────────────
  license: {
    tieuDe: 'License on-premise',
    moTa: 'License đã ký cho các bản cài on-premise, yêu cầu chờ duyệt, keyring và danh sách thu hồi.',
    tab: 'License',
    tabDanhSach: 'License',
    tabChoDuyet: 'Chờ duyệt ({{so}})',
    tabKhoaKy: 'Khoá ký và thu hồi',

    tkDangChay: 'Đang chạy',
    tkDangChaySub: 'Còn hiệu lực hoặc trong ân hạn',
    tkSapHetHan: 'Sắp hết hạn',
    tkSapHetHanSub: 'Trong {{so}} ngày tới',
    tkAnHan: 'Đang ân hạn',
    tkAnHanSub: 'Đã quá hạn, hết ân hạn thì chỉ đọc',
    tkChoDuyet: 'Chờ duyệt',
    tkChoDuyetSub: 'Cần người thứ hai duyệt',

    // Trạng thái tính sẵn `computed_status`.
    trangThai: {
      NOT_YET_VALID: 'Chưa tới ngày hiệu lực',
      ACTIVE: 'Hiệu lực',
      EXPIRING: 'Sắp hết hạn',
      GRACE: 'Đang ân hạn',
      EXPIRED: 'Hết hạn',
      REVOKED: 'Đã thu hồi',
      SUPERSEDED: 'Đã được thay',
    },
    trangThaiYeuCau: {
      DRAFT: 'Bản nháp',
      PENDING_APPROVAL: 'Chờ duyệt',
      APPROVED: 'Đã duyệt',
      REJECTED: 'Bị từ chối',
      ISSUED: 'Đã cấp',
      CANCELLED: 'Đã huỷ',
    },
    trangThaiCaiDat: {
      PENDING: 'Chờ license',
      ACTIVE: 'Đang dùng',
      REPLACED: 'Đã thay',
      DECOMMISSIONED: 'Đã ngừng',
    },
    trangThaiKhoa: {
      PREPUBLISHED: 'Công bố trước',
      ACTIVE: 'Đang ký',
      RETIRED: 'Đã nghỉ',
      COMPROMISED: 'Bị lộ',
    },
    kieu: {
      ISSUE: 'Cấp mới',
      RENEW: 'Gia hạn',
      REPLACE: 'Thay cài đặt',
    },
    lyDoDuyet: {
      TERM_OVER_MAX: 'Thời hạn trên 12 tháng',
      SPECIAL_FEATURES: 'Bật tính năng đặc biệt',
      OVER_PLAN_QUOTA: 'Vượt hạn mức hoặc thêm module ngoài gói',
      RENEW_BEFORE_WINDOW: 'Gia hạn sớm hơn cửa sổ gia hạn',
    },
    loi: {
      trungCaiDat:
        'installation_id này đã đăng ký với nội dung khác hoặc thuộc tenant khác. Kiểm lại tệp activation-request.json khách hàng gửi.',
      canDuyet: 'Bản này cần người thứ hai duyệt. Chọn Gửi duyệt thay vì Cấp license.',
      tuDuyet: 'Người tạo yêu cầu không duyệt hay từ chối được yêu cầu của chính mình. Nhờ người duyệt khác.',
      khongKyDuoc: 'Dịch vụ ký đang không sẵn sàng. Chưa có gì được ghi; thử lại sau ít phút.',
    },

    // Bảng license — màn module.
    bangCaption: 'Mọi license on-premise của nền tảng',
    locTatCa: 'Tất cả',
    locSapHetHan: 'Sắp hết hạn trong {{so}} ngày',
    locAnHan: 'Đang ân hạn',
    timTenant: 'Tìm theo tên tenant',
    cotTenant: 'Tenant',
    cotHetHan: 'Hết hạn',
    cotAnHanToi: 'Ân hạn tới',
    cotPlan: 'Gói',
    cotCaiDat: 'Cài đặt',
    cotLicense: 'License',
    cotHieuLuc: 'Hiệu lực',
    cotProductCode: 'Sản phẩm',
    cotDangKyLuc: 'Đăng ký',
    rongThat: 'Chưa có license nào',
    rongThatMo: 'License được cấp từ tab License của một tenant on-premise.',
    rongLoc: 'Không có license nào khớp bộ lọc',
    xemTenant: 'Xem danh sách tenant',
    moTenant: 'Mở tenant',
    loiTai: 'Không tải được dữ liệu license: {{loi}}',
    conNgay: 'Còn {{so}} ngày',
    quaHanNgay: 'Quá hạn {{so}} ngày',
    khoangNgay: '{{tu}} – {{den}}',

    // Thao tác trên một license.
    xemNoiDung: 'Xem nội dung',
    taiTep: 'Tải tệp',
    thuHoi: 'Thu hồi',
    dong: 'Đóng',
    noiDungTieuDe: 'Nội dung license',
    noiDungYeuCauTieuDe: 'Nội dung yêu cầu',
    noiDungKy: 'Nội dung được ký',
    dongPlan: 'Gói (plan_code)',
    dongHieuLuc: 'Hiệu lực',
    dongAnHanToi: 'Ân hạn tới',
    dongCaiDat: 'installation_id',
    dongJti: 'jti',
    dongKid: 'Khoá ký (kid)',
    dongDigest: 'license_digest',
    dongCapLuc: 'Cấp lúc',
    dongThuHoiLuc: 'Thu hồi lúc',
    dongTenant: 'Tenant',
    dongLoai: 'Loại',
    dongLyDoCap: 'Lý do cấp',
    oModules: 'Module',
    oMaxUsers: 'Số người dùng tối đa',
    oMaxNodes: 'Số node tối đa',
    oGioiHan: 'Giới hạn {{ten}}',
    oFeatures: 'Tính năng đặc biệt',
    khongCoFeature: 'Không bật tính năng nào',
    thuHoiTieuDe: 'Thu hồi license {{ma}}?',
    thuHoiThan:
      'Control Plane đánh dấu license này đã thu hồi và đưa nó vào danh sách thu hồi kế tiếp. Máy khách chỉ ngừng dùng license sau khi nạp danh sách đó. Không hoàn tác được: muốn dùng lại phải cấp license mới.',
    lyDoThuHoi: 'Lý do thu hồi',
    lyDoThuHoiViDu: 'Ví dụ: cấp nhầm số người dùng',
    nhanXacNhanThuHoi: 'Thu hồi license',
    loiThieuLyDoThuHoi: 'Nhập lý do thu hồi; lý do được ghi vào nhật ký.',
    daThuHoi: 'Đã thu hồi license.',
    veTaiTieuDe: 'Vé tải đã sẵn sàng',
    veTaiThan: 'Liên kết dùng được một lần và hết hạn lúc {{luc}}. Mỗi lần tải đều được ghi nhật ký.',
    taiLicense: 'Tải license',

    // Tab License của tenant.
    hieuLucTieuDe: 'License hiệu lực',
    hieuLucMo: 'Bản máy khách đang dùng: bản còn chạy có ngày hết hạn muộn nhất',
    chuaCoHieuLuc: 'Tenant chưa có license đang hiệu lực',
    chuaCoHieuLucMo: 'Cấp license sau khi nhận tệp activation-request.json từ bộ cài của khách hàng.',
    giaHan: 'Gia hạn',
    capLicense: 'Cấp license',
    dangChoDuyet: 'Có {{so}} yêu cầu của tenant này đang chờ duyệt.',
    thieuDauMoi:
      'Tenant chưa có đầu mối chủ hợp đồng (CONTRACT_OWNER): thông báo license sắp hết hạn sẽ không tới ai. Thêm đầu mối này cho tenant.',
    caiDatTieuDe: 'Cài đặt',
    caiDatMo: 'Bản cài on-premise đã đăng ký bằng activation-request.json',
    caiDatCaption: 'Các bản cài on-premise của {{ten}}',
    caiDatRong: 'Chưa có cài đặt nào',
    caiDatRongMo: 'Cài đặt được ghi khi nhập activation-request.json ở trình cấp license.',
    dongThoiGianTieuDe: 'Dòng thời gian license',
    dongThoiGianMo: 'Mỗi chuỗi gia hạn đi từ bản mới nhất về bản gốc',
    dongThoiGianCaption: 'Lịch sử license của {{ten}}',
    dongThoiGianRong: 'Chưa cấp license nào',
    thayCho: 'thay cho {{ma}}',

    // Trình cấp license.
    trinhCapTieuDe: 'Cấp license cho {{ten}}',
    trinhGiaHanTieuDe: 'Gia hạn license cho {{ten}}',
    buocCaiDat: 'Cài đặt',
    buocNoiDung: 'Nội dung',
    buocThoiHan: 'Thời hạn',
    buocXemLai: 'Xem lại',
    tiepTuc: 'Tiếp tục',
    nguonTep: 'Bản cài mới',
    nguonCoSan: 'Cài đặt đã có',
    keoThaTep: 'Kéo thả activation-request.json vào đây, hoặc bấm để chọn tệp',
    keoThaTepMo: 'Tệp được đọc ngay trên trình duyệt; chưa gửi gì lên máy chủ cho tới khi bấm Tiếp tục.',
    dongTep: 'Tệp',
    dongProductCode: 'product_code',
    dongPhienBan: 'Phiên bản',
    dongDigestKichHoat: 'SHA-256 (JSON chuẩn hoá)',
    tenCaiDat: 'Tên cài đặt',
    tenCaiDatGoiY: 'Tên để nhận ra bản cài, ví dụ vị trí máy chủ.',
    chonCaiDat: 'Chọn cài đặt',
    seThayThe: 'Cài đặt này khác cài đặt của license đang hiệu lực, nên license mới sẽ thay thế bản cũ (REPLACE).',
    tepJsonHong: 'Tệp không phải JSON hợp lệ. Chọn đúng tệp activation-request.json do bộ cài sinh ra.',
    tepKhongPhaiDoiTuong: 'Tệp phải là một đối tượng JSON. Chọn đúng tệp activation-request.json do bộ cài sinh ra.',
    tepThieuTruong: 'Tệp thiếu trường {{truong}}. Nhờ khách hàng chạy lại bộ cài để sinh tệp mới.',
    tepSaiUuid: 'Trường {{truong}} không phải UUID. Nhờ khách hàng chạy lại bộ cài để sinh tệp mới.',
    tepKhacTenant: 'Tệp này của tenant khác ({{ma}}). Kiểm lại tệp khách hàng gửi.',
    tepKhongDocDuoc: 'Không đọc được tệp. Chọn lại tệp rồi thử lại.',
    loiChuaCoTep: 'Chọn tệp activation-request.json để tiếp tục.',
    cachNhapNhan: 'Cách nhận yêu cầu kích hoạt',
    cachNhapTep: 'Tải tệp lên',
    cachNhapMa: 'Dán mã kích hoạt',
    maKichHoat: 'Mã kích hoạt',
    maKichHoatGoiY:
      'Bộ cài in mã này cạnh tệp activation-request.json, bắt đầu bằng FXA1. Mã được đọc ngay trên trình duyệt; chưa gửi gì lên máy chủ cho tới khi bấm Tiếp tục.',
    maKichHoatViDu: 'FXA1.eyJjcmVhdGVkX2F0Ijoi...',
    loiChuaCoMa: 'Dán mã kích hoạt khách hàng gửi để tiếp tục.',
    maSaiTienTo: 'Mã kích hoạt phải bắt đầu bằng FXA1. Chép lại nguyên dòng mã bộ cài in ra.',
    maBase64Hong: 'Mã kích hoạt bị thiếu hoặc lẫn ký tự lạ. Chép lại nguyên dòng mã, không bỏ sót ký tự nào.',
    maKhongPhaiYeuCau: 'Mã này không chứa yêu cầu kích hoạt. Nhờ khách hàng chạy lại bộ cài để lấy mã mới.',
    maThieuTruong: 'Mã kích hoạt thiếu trường {{truong}}. Nhờ khách hàng chạy lại bộ cài để lấy mã mới.',
    maSaiUuid: 'Trường {{truong}} trong mã kích hoạt không phải UUID. Nhờ khách hàng chạy lại bộ cài để lấy mã mới.',
    maKhacTenant: 'Mã kích hoạt này của tenant khác ({{ma}}). Kiểm lại mã khách hàng gửi, hoặc mở trình cấp ở đúng tenant.',
    maKhongDocDuoc: 'Không đọc được mã kích hoạt. Dán lại mã rồi thử lại.',
    loiChuaChonCaiDat: 'Chọn một cài đặt để tiếp tục.',
    loiChuaChonPlan: 'Chọn plan_code để dựng nội dung license.',
    goiXuatPhat: 'Gói dịch vụ',
    goiXuatPhatGoiY: 'Module dựng sẵn từ các dịch vụ trong gói.',
    khongTheoGoi: 'Không theo gói',
    planCode: 'plan_code',
    chonPlan: 'Chọn plan_code',
    loiMau: 'Không dựng được nội dung từ gói: {{loi}}',
    noiDungMo: 'Nội dung dựng sẵn từ gói. Chỉ sửa phần khác hợp đồng; ô bị sửa được đánh dấu để người duyệt thấy.',
    khacGoi: 'Khác gói',
    giaTriGoi: 'Gói đặt {{gt}}',
    loiOTrong: 'Nhập theo hợp đồng: gói này không có giá trị mặc định.',
    featuresGoiY: 'Gõ tên tính năng rồi nhấn Enter.',
    thoiHan: 'Thời hạn',
    soThang: '{{so}} tháng',
    ngayCuThe: 'Ngày cụ thể',
    ngayHetHan: 'Ngày hết hạn',
    tomTatThoiHan: 'Hiệu lực {{tu}} – {{den}}, ân hạn tới {{anHan}}.',
    giaHanMo: 'Nội dung chép nguyên từ license đang dùng; license mới bắt đầu đúng lúc bản cũ hết hạn. Chỉ cần chọn thời hạn.',
    canDuyetTieuDe: 'Bản này cần người thứ hai duyệt trước khi ký:',
    khongCanDuyet: 'Không cần duyệt: license được ký ngay khi bấm Cấp license.',
    lyDoCap: 'Lý do cấp',
    lyDoCapGoiY: 'Ghi số hợp đồng hoặc phụ lục để người duyệt đối chiếu.',
    lyDoCapViDu: 'Ví dụ: Hợp đồng số 12/2026/HĐ-FOXAI',
    loiThieuLyDoCap: 'Nhập lý do cấp; lý do được ghi vào nhật ký.',
    guiDuyet: 'Gửi duyệt',
    daCap: 'Đã cấp license.',
    daGuiDuyet: 'Đã gửi yêu cầu duyệt.',

    // Hàng chờ duyệt.
    hangChoCaption: 'Yêu cầu license đang chờ duyệt',
    hangChoRong: 'Không có yêu cầu nào chờ duyệt',
    hangChoRongMo: 'Yêu cầu vào đây khi thời hạn trên 12 tháng, bật tính năng đặc biệt hoặc vượt hạn mức gói.',
    cotThoiHan: 'Thời hạn',
    cotVaoHangCho: 'Cần duyệt vì',
    cotLyDoCap: 'Lý do cấp',
    cotNguoiGui: 'Người gửi',
    ban: 'Bạn',
    duyetVaKy: 'Duyệt và ký',
    tuChoi: 'Từ chối',
    huyYeuCau: 'Huỷ yêu cầu',
    duyetTieuDe: 'Duyệt và ký license cho {{ten}}?',
    duyetThan: 'License được ký ngay sau khi duyệt và người cấp tải được tệp. Muốn bỏ license đã ký thì phải thu hồi.',
    tuChoiTieuDe: 'Từ chối yêu cầu của {{ten}}?',
    tuChoiThan: 'Người gửi thấy lý do từ chối. Yêu cầu bị từ chối không mở lại được; muốn cấp phải gửi yêu cầu mới.',
    lyDoTuChoi: 'Lý do từ chối',
    nhanXacNhanTuChoi: 'Từ chối yêu cầu',
    loiThieuLyDoTuChoi: 'Nhập lý do để người gửi biết cần sửa gì.',
    daDuyet: 'Đã duyệt và ký license.',
    daTuChoi: 'Đã từ chối yêu cầu.',
    daHuyYeuCau: 'Đã huỷ yêu cầu.',
    huyYeuCauTieuDe: 'Huỷ yêu cầu license của {{ten}}?',
    huyYeuCauThan:
      'Yêu cầu chuyển sang Đã huỷ và rời hàng chờ duyệt; không ký được license từ yêu cầu này nữa. Không hoàn tác được: muốn cấp lại phải tạo yêu cầu mới.',
    nhanXacNhanHuy: 'Huỷ yêu cầu',

    // Keyring và danh sách thu hồi.
    keyringTieuDe: 'Keyring hiện hành',
    keyringMo: 'Khoá công khai máy khách dùng để kiểm chữ ký license',
    keyringCaption: 'Các khoá ký trong keyring hiện hành',
    keyringRong: 'Keyring không có khoá nào',
    thuHoiDsTieuDe: 'Danh sách thu hồi mới nhất',
    thuHoiDsMo: 'Máy khách từ chối license có jti nằm trong danh sách này',
    thuHoiDsRong: 'Chưa thu hồi license nào.',
    taiKeyring: 'Tải keyring',
    taiDsThuHoi: 'Tải danh sách thu hồi',
    taiKeyringLen: 'Tải keyring lên',
    taiKeyringLenTieuDe: 'Tải keyring mới lên?',
    taiKeyringLenMo:
      'Chọn tệp keyring đã ký bằng khoá gốc trên máy ngoại tuyến (nghi thức khoá gốc). license-service kiểm chữ ký bằng khoá gốc và khoá công khai của từng kid rồi mới nhận.',
    tepKeyring: 'Tệp keyring (.jws)',
    keyringKeoTha: 'Kéo tệp keyring vào đây hoặc bấm để chọn',
    keyringKeoThaMo: 'Tệp chỉ được đọc trên trình duyệt để xem trước; chưa gửi đi tới khi bấm tải lên.',
    dongKyBoi: 'Ký bởi khoá gốc',
    dongCacKhoa: 'Các khoá',
    sequenceDoi: '{{cu}} → {{moi}}',
    sequenceDauTien: '{{moi}} (keyring đầu tiên)',
    taiKeyringLenHauQua:
      'Sau khi nhận, trạng thái khoá phát hành đổi theo keyring này (khoá ACTIVE mới ký được license) và không tải lại được keyring có sequence nhỏ hơn hoặc bằng {{sequence}}. Không hoàn tác được.',
    nhanTaiKeyringLen: 'Tải keyring sequence {{sequence}} lên',
    dangTaiKeyringLen: 'Đang tải lên...',
    daTaiKeyringLen: 'Đã tải keyring sequence {{sequence}} lên.',
    loiKeyringChuaChon: 'Chọn tệp keyring trước khi tải lên.',
    loiKeyringTepLon: 'Tệp lớn hơn 64 KB nên không phải keyring. Chọn tệp .jws ký ở nghi thức khoá gốc.',
    loiKeyringKhongPhai: 'Tệp này không phải keyring (JWS typ=KEYRING). Chọn tệp keyring-<sequence>.jws ký ở nghi thức khoá gốc.',
    loiKeyringKhongCoKhoa: 'Keyring không có khoá nào. Soạn lại keyring.json với đủ các khoá còn cần kiểm rồi ký lại.',
    loiKeyringSequence:
      'sequence của tệp không lớn hơn bản hiện hành ({{hienHanh}}). Ký lại keyring với sequence lớn hơn {{hienHanh}}.',
    keyringChuaCo: 'Chưa có keyring',
    thuHoiDsChuaCo: 'Chưa có danh sách thu hồi',
    thuHoiDsChuaCoMo: 'Danh sách được ký lần đầu khi thu hồi license đầu tiên.',
    keyringChuaCoMo:
      'Chưa ký được license nào tới khi có keyring đầu tiên. Người duyệt license ký keyring bằng khoá gốc theo nghi thức rồi tải lên bằng nút Tải keyring lên.',
    dongSequence: 'sequence',
    dongPhatHanhLuc: 'Phát hành',
    dongSoThuHoi: 'Số license bị thu hồi',
    cotKid: 'kid',
    cotDungTu: 'Dùng từ',
    cotNghiSau: 'Nghỉ sau',
    khongDocDuocTep: 'Không đọc được tệp đã ký',
    khongDocDuocTepMo: 'Tệp trả về không đúng định dạng JWS. Báo đội vận hành license-service.',
    khongRoLicense: 'Không có trong danh sách license đang xem',
  },

  // ─────────── Quản lý module (plugin platform) — ADR-0045, 0046, 0086 ───────────
  quanLyModule: {
    danhSachTieuDe: 'Quản lý module',
    danhSachMoTa: 'Module mở rộng do FoxAI hoặc đối tác nộp lên, kiểm và duyệt trước khi tenant cài được',
    bangCaption: 'Danh sách module đã nộp lên nền tảng',
    cotTen: 'Tên module',
    cotNhaPhatTrien: 'Nhà phát triển',
    cotPhienBan: 'Phiên bản',
    cotCapNhat: 'Cập nhật',
    cotNopLuc: 'Nộp lúc',
    cotDuyetLuc: 'Duyệt lúc',
    cotGhiChu: 'Ghi chú',
    timTheo: 'Tìm theo tên, mã hoặc nhà phát triển',
    xemChiTiet: 'Xem chi tiết',
    xemChiTietCua: 'Xem chi tiết {{ten}}',
    nopGoi: 'Nộp module',
    duyetNut: 'Duyệt',
    loiDanhSach: 'Không tải được danh sách module: {{loi}}',
    loiChiTiet: 'Không tải được module này',
    rongThat: 'Chưa có module nào được nộp',
    rongThatMo: 'Nộp module đầu tiên để bắt đầu',
    rongLoc: 'Không có module khớp bộ lọc',
    tabDanhSach: 'Module',
    moTaiLieu: 'Hướng dẫn',
    moTaiLieuMo: 'Mở tài liệu hướng dẫn nộp module ở một tab mới',

    trangThai: {
      DRAFT: 'Nháp',
      IN_REVIEW: 'Đang chờ duyệt',
      PUBLISHED: 'Đã duyệt',
      REJECTED: 'Đã từ chối',
      DEPRECATED: 'Đã ngừng dùng',
    },

    khoiMetadata: 'Thông tin module',
    khoiPhienBanMoiNhat: 'Phiên bản mới nhất',
    khoiLichSu: 'Lịch sử phiên bản',
    lichSuCaption: 'Lịch sử các phiên bản đã nộp của module này',
    phienBanDaDuyet: 'Phiên bản đã duyệt',
    giaoDien: {
      diemVao: 'Giao diện trong module',
      khongCo: 'Không có, module chỉ chạy phía máy chủ',
      manHinh: 'Màn hình (menus)',
    },
    xemTruoc: {
      tieuDe: 'Xem trước giao diện · bản {{phienBan}}',
      mo: 'Chạy đúng mã của module đã duyệt, giống như tenant sẽ thấy sau khi cài.',
      chonMan: 'Chọn màn của module',
      canhBao:
        'Màn chạy bằng phiên đăng nhập của bạn. Tài khoản nền tảng không thuộc tenant nào nên API dữ liệu tenant mà module gọi có thể báo lỗi; thao tác của module vẫn bấm thử được.',
    },
    khongCoManifestChiTiet:
      'Chưa có API trả nội dung manifest (quyền yêu cầu, phiên bản SDK) của một phiên bản chưa phát hành — xem trực tiếp trong module đã nộp.',

    maLoiKiem: {
      MODULE_PACKAGE_INVALID: 'Module sai bố cục: thiếu module.yaml, hoặc tệp zip hỏng',
      MODULE_MANIFEST_INVALID: 'Manifest không hợp lệ',
      MODULE_VERSION_CONFLICT:
        'Phiên bản phải cao hơn bản đang phát hành (ví dụ 0.1.0 lên 0.2.0), hoặc mã module đã thuộc bên khác. Đổi phiên bản hoặc mã trong module.yaml rồi nộp lại',
      MODULE_SCAN_FAILED: 'Image còn lỗ hổng bảo mật mức nghiêm trọng',
      MODULE_MIGRATION_INVALID: 'Migration của module chạy thử thất bại',
    },

    daDuyet: 'Đã duyệt module (phiên bản {{phienBan}})',
    daTuChoi: 'Đã từ chối module (phiên bản {{phienBan}})',
    xoaMuc: 'Xoá module',
    xoaTieuDe: 'Xoá module {{ten}}?',
    xoaThan:
      'Mọi phiên bản đã nộp của module sẽ bị xoá và module biến mất khỏi danh sách Module đã duyệt của tenant. Không hoàn tác được. Nếu còn tenant đang cài, hãy yêu cầu họ gỡ trước.',
    xoaNhan: 'Xoá module {{ten}}',
    daXoa: 'Đã xoá module {{ten}}.',

    upload: {
      tieuDe: 'Nộp module',
      mo: 'Chọn tệp .zip gồm module.yaml và thư mục frontend/ do platform-module pack tạo. Mọi thông tin module đọc từ manifest, không nhập lại ở đây.',
      canhBaoBaoMat:
        'Nền tảng kiểm manifest trước khi duyệt, nhưng module vẫn chạy mã của bên nộp. Đọc mã trong frontend/ trước khi duyệt.',
      chonTep: 'Chọn tệp .zip hoặc kéo thả vào đây',
      dieuKien: 'Tệp .zip, tối đa {{toiDa}}',
      saiDinhDang: 'Chỉ nhận tệp .zip',
      quaLon: 'Tệp vượt quá {{toiDa}}',
      nopGoi: 'Nộp module',
      ketQua: 'Đã nhận module {{code}}, phiên bản {{phienBan}}',
      daVaoHangDoi: 'Module đã vào hàng chờ duyệt. Platform Team sẽ xem code và quyết định duyệt hoặc từ chối.',
      moTenant: 'Chọn tệp .zip của module. Tên, phiên bản và quyền được đọc từ tệp, không cần nhập lại.',
      canhBaoTenant: 'Module chạy bằng tài khoản của người dùng trong tổ chức. Chỉ nộp module từ nguồn bạn tin cậy.',
      daDuyetNgay: 'Module đã được duyệt, có thể cài ở tab Module đã duyệt.',
      daVaoHangDoiTenant:
        'Đã gửi quản trị tổ chức duyệt. Bản đang chạy (nếu có) vẫn dùng bình thường cho tới khi bản mới được duyệt. Theo dõi ở tab Module tôi đã nộp.',
    },

    tuChoi: {
      tieuDe: 'Từ chối phiên bản {{phienBan}}',
      mo: 'Lý do sẽ gửi cho người đã nộp để sửa và nộp lại.',
      oNhap: 'Lý do từ chối',
      nut: 'Từ chối',
    },

    duyetXacNhan: {
      tieuDe: 'Duyệt phiên bản {{phienBan}}?',
      than:
        'Module lên Marketplace ngay, mọi tenant thấy và cài được. Bản PUBLISHED trước đó (nếu có) chuyển thành DEPRECATED.',
      nut: 'Duyệt phiên bản {{phienBan}}',
    },
  },

  // ─────────── Màn Module của cổng tenant — ADR-0096 mục 8 ───────────
  moduleToChuc: {
    tieuDe: 'Danh sách module',
    moTa: 'Nộp, xem và cài module cho tổ chức.',
    nopGoi: 'Nộp module',
    tabDaDuyet: 'Module đã duyệt',
    tabGoiCuaToi: 'Module tôi đã nộp',
    boKhoiTao: {
      tab: 'Bộ phát triển',
      bang: 'Danh sách bộ khởi tạo module',
      gioiThieu:
        'Tải bộ khởi tạo của một module hệ thống để tự phát triển module mở rộng: dự án giao diện mẫu cùng mô tả quyền và API của module đó. Bộ khởi tạo không chứa mã nguồn của module.',
      cacBuoc:
        'Giải nén, làm theo README trong bộ (cài thư viện, đặt mã module, đóng gói), rồi chọn "Nộp module" để gửi bản của bạn.',
      phienBanSdk: 'SDK {{phienBan}}',
      cotQuyenApi: 'Quyền và API',
      soQuyenApi: '{{quyen}} quyền · {{api}} API',
      cotDungLuong: 'Dung lượng',
      tai: 'Tải bộ khởi tạo',
      daTai: 'Đã tải bộ khởi tạo {{ten}}.',
      rong: 'Chưa có bộ khởi tạo nào',
      rongMo: 'Bản này chưa phát hành bộ khởi tạo. Liên hệ quản trị nền tảng.',
      loiTai: 'Không tải được danh sách bộ khởi tạo: {{loi}}',
    },
    bangDaDuyet: 'Danh sách module đã duyệt',
    bangGoiCuaToi: 'Danh sách module bạn đã nộp',
    cotModule: 'Module',
    cotNhaPhatTrien: 'Nhà phát triển',
    cotPhienBan: 'Phiên bản',
    cotTrangThai: 'Trạng thái',
    cotNopLuc: 'Nộp lúc',
    cotGhiChu: 'Ghi chú',
    trangThaiCai: {
      daCai: 'Đã cài',
      dangCai: 'Đang cài...',
      dangGo: 'Đang gỡ...',
      loi: 'Cài lỗi',
      chuaCai: 'Chưa cài',
    },
    thaoTac: {
      mo: 'Mở module',
      cai: 'Cài module',
      caiLai: 'Cài lại module',
      go: 'Gỡ module',
      xoa: 'Xoá module',
    },
    daGuiCai: 'Đang cài {{ten}}. Module sẽ hiện trên menu khi cài xong.',
    loiCai: 'Không cài được {{ten}}: {{loi}}',
    xnGoTieuDe: 'Gỡ module {{ten}}?',
    xnGoThan: 'Module sẽ biến khỏi menu của mọi người trong tổ chức. Dữ liệu của module được giữ lại, không bị xoá.',
    daGuiGo: 'Đang gỡ {{ten}}. Module sẽ biến khỏi menu khi gỡ xong.',
    loiGo: 'Không gỡ được {{ten}}: {{loi}}',
    xnXoaTieuDe: 'Xoá module {{ten}} khỏi Marketplace?',
    xnXoaThan:
      'Mọi phiên bản bạn đã nộp của module này bị xoá, không tổ chức nào cài được nữa. Không hoàn tác được.',
    daXoa: 'Đã xoá {{ten}} khỏi Marketplace.',
    loiXoa: 'Không xoá được {{ten}}: {{loi}}',
    loiTai: 'Không tải được danh sách: {{loi}}',
    rongDaDuyet: 'Chưa có module nào được duyệt',
    rongDaDuyetMo: 'Module đã duyệt sẽ hiện ở đây để cài.',
    rongGoiCuaToi: 'Bạn chưa nộp module nào',
    rongGoiCuaToiMo: 'Nén module thành tệp .zip theo tài liệu hướng dẫn, rồi bấm Nộp module.',
    choDuyet: {
      tab: 'Chờ duyệt',
      tabSoLuong: 'Chờ duyệt ({{so}})',
      bang: 'Phiên bản module của tổ chức và lịch sử cập nhật',
      moTa:
        'Module do người trong tổ chức nộp chỉ dùng được sau khi bạn duyệt. Duyệt bản mới thì bản cũ chuyển sang "Đã ngừng dùng" và vẫn nằm trong lịch sử; tổ chức đã cài bản cũ nâng cấp ở tab Module đã duyệt.',
      cotNguoiNop: 'Người nộp',
      cotNguoiDuyet: 'Người duyệt',
      cotDuyetLuc: 'Duyệt lúc',
      thaoTacDuyet: 'Duyệt phiên bản',
      thaoTacTuChoi: 'Từ chối phiên bản',
      xnDuyetTieuDe: 'Duyệt {{ten}} bản {{phienBan}}?',
      xnDuyetThan:
        'Bản này thành bản đang phát hành của tổ chức. Bản đang phát hành trước đó chuyển sang "Đã ngừng dùng" và vẫn nằm trong lịch sử. Ai có quyền cài module sẽ cài hoặc nâng cấp được bản này.',
      nutDuyet: 'Duyệt bản {{phienBan}}',
      daDuyet: 'Đã duyệt {{ten}} bản {{phienBan}}.',
      daTuChoi: 'Đã từ chối {{ten}} bản {{phienBan}}.',
      rong: 'Chưa có phiên bản nào',
      rongMo: 'Khi người trong tổ chức nộp module, các phiên bản chờ duyệt sẽ hiện ở đây.',
      loiTai: 'Không tải được danh sách chờ duyệt: {{loi}}',
    },
  },

  // ─────────── Màn của module đã cài, nạp qua Module Federation — ADR-0096 ───────────
  moduleDaCai: {
    dangTai: 'Đang tải giao diện module...',
    thuLai: 'Thử lại',
    loiDanhSach: {
      tieuDe: 'Không tải được danh sách module đã cài',
    },
    chuaCai: {
      tieuDe: 'Module này chưa được cài cho tổ chức',
      mo: 'Cài module {{code}} ở mục Danh sách module, rồi mở lại màn này.',
    },
    khongGiaoDien: {
      tieuDe: '{{ten}} không có giao diện',
      mo: 'Module này không có màn hình để hiển thị.',
    },
    loiNap: {
      tieuDe: 'Không nạp được giao diện {{ten}}',
      mo: 'Thử lại sau ít phút. Nếu vẫn lỗi, báo cho nhà cung cấp module.',
    },
    loiChay: {
      tieuDe: '{{ten}} gặp lỗi khi hiển thị',
      mo: 'Các màn khác vẫn dùng bình thường. Thử lại; nếu vẫn lỗi, báo cho nhà cung cấp module.',
    },
  },

  // ─────────── License của bản cài on-premise — ADR-0059 mục 6, 8 "Phía khách" ───────────
  licenseCucBo: {
    tieuDe: 'License của bản cài',
    moTa: 'Trạng thái license, module được dùng và mức dùng từng giới hạn của bản cài on-premise này.',
    chiOnPremise: 'Màn này chỉ có ở bản cài on-premise',
    chiOnPremiseMo: 'Bản SaaS do nền tảng quản lý license, không cần nhập tệp ở đây.',
    taiYeuCau: 'Tải activation-request.json',
    dangTaiYeuCau: 'Đang tải...',
    loiTai: 'Không tải được trạng thái license: {{loi}}',
    trangThai: {
      ACTIVE: 'Đang hiệu lực',
      EXPIRING: 'Sắp hết hạn',
      GRACE: 'Đang ân hạn',
      READ_ONLY: 'Chỉ đọc',
      NOT_YET_VALID: 'Chưa hiệu lực',
      INVALID: 'Không hợp lệ',
    },
    // Câu dưới badge trạng thái; {{so}} là số ngày.
    conLai: {
      ACTIVE: 'Còn {{so}} ngày tới ngày hết hạn.',
      EXPIRING: 'Còn {{so}} ngày tới ngày hết hạn. Gửi yêu cầu gia hạn cho nhà cung cấp ngay.',
      GRACE: 'Đã quá hạn, còn {{so}} ngày ân hạn trước khi hệ thống chuyển chỉ đọc.',
      READ_ONLY: 'Đã hết ân hạn: chỉ đọc, đăng nhập, sao lưu và xuất dữ liệu được.',
      NOT_YET_VALID: 'Còn {{so}} ngày tới ngày license có hiệu lực.',
      INVALID: 'Chưa có license hợp lệ: chỉ mở màn license và khôi phục dữ liệu được.',
    },
    // Thẻ license ở chân thanh điều hướng; {{so}} là số ngày, {{goi}} là tên gói (dữ liệu, không dịch).
    thanhBen: {
      goi: 'Gói {{goi}}',
      conNgay: 'Còn {{so}} ngày',
      conMotNgay: 'Còn 1 ngày',
      anHanNgay: 'Còn {{so}} ngày ân hạn',
      anHanMotNgay: 'Còn 1 ngày ân hạn',
      hieuLucSau: 'Hiệu lực sau {{so}} ngày',
      hieuLucSauMotNgay: 'Hiệu lực sau 1 ngày',
      chiDoc: 'Đã hết hạn, chỉ đọc',
      khongHopLe: 'License không hợp lệ',
      chuaKichHoat: 'Chưa kích hoạt license',
      loiTai: 'Không tải được license',
      tomTat: 'License gói {{goi}}: {{trangThai}}, {{demNguoc}}',
    },
    chuaCoLicense: 'Chưa nhập license nào',
    lyDo: 'Mã lý do: {{ma}}',
    dongHoTieuDe: 'Đồng hồ máy chủ đã lùi quá 5 phút',
    dongHoThan:
      'License vẫn tính theo mốc thời gian lớn nhất từng thấy, nên lùi giờ không kéo dài license. Kiểm tra đồng bộ giờ (NTP) của máy chủ.',

    thongTinTieuDe: 'Thông tin license',
    dongGoi: 'Gói',
    dongHieuLuc: 'Hiệu lực',
    dongAnHanToi: 'Ân hạn tới',
    dongCaiDat: 'installation_id',
    dongTenant: 'tenant_id',
    dongMa: 'Mã license (jti)',
    dongKeyring: 'Keyring',
    dongThuHoi: 'Danh sách thu hồi',
    soThuTu: 'sequence {{so}}',
    chuaNhap: 'Chưa nhập',

    moduleTieuDe: 'Module được dùng',
    moduleMo: 'Module không có trong license thì không được nạp.',
    moduleRong: 'Chưa có module nào',
    tinhNang: 'Tính năng',

    mucDungTieuDe: 'Mức dùng',
    mucDungMo: 'Giới hạn ghi trong license và mức bản cài đang dùng.',
    mucDungCaption: 'Mức dùng từng giới hạn trong license',
    mucDungRong: 'Chưa có giới hạn nào',
    mucDungRongMo: 'Giới hạn hiện ở đây sau khi nhập license.',
    cotGioiHan: 'Giới hạn',
    cotMucDung: 'Đang dùng',
    cotTiLe: 'Tỉ lệ',
    mucDungNguoiDung: '{{dung}}/{{toiDa}} người dùng',
    mucDungChung: '{{dung}}/{{toiDa}}',
    chuaDo: 'Chưa đo (tối đa {{toiDa}})',
    gioiHan: {
      max_users: 'Người dùng',
      max_nodes: 'Node',
      storage_gb: 'Dung lượng lưu trữ (GB)',
    },

    nhapTieuDe: 'Nhập tệp',
    nhapMo: 'License, keyring hoặc danh sách thu hồi nhận từ nhà cung cấp. Tệp được kiểm chữ ký trước khi lưu.',
    keoTha: 'Kéo thả tệp vào đây hoặc bấm để chọn',
    keoThaMo: 'Tệp do nhà cung cấp ký, tối đa 64 KB',
    dongTep: 'Tệp',
    loaiTepNhan: 'Loại tệp',
    loaiTepGoiY: 'Nhận ra từ nội dung tệp; chỉ đổi khi nhà cung cấp dặn khác.',
    loaiTep: {
      license: 'License',
      keyring: 'Keyring',
      revocation: 'Danh sách thu hồi',
    },
    nhanNhap: 'Nhập tệp',
    dangNhap: 'Đang nhập...',
    loiChuaChonTep: 'Chọn tệp nhận từ nhà cung cấp trước khi nhập.',
    loiTepLon: 'Tệp lớn hơn 64 KB nên không phải tệp license. Dùng đúng tệp nhận từ nhà cung cấp.',
    loiKhongNhanRa: 'Không nhận ra loại tệp. Chọn loại tệp rồi nhập; nếu vẫn lỗi, xin nhà cung cấp gửi lại tệp.',
    cachNhapNhan: 'Cách nhập',
    cachNhapTep: 'Tải tệp lên',
    cachNhapDan: 'Dán nội dung',
    noiDungDan: 'Nội dung license',
    noiDungDanGoiY:
      'Dán nguyên chuỗi nhà cung cấp gửi: license, keyring hoặc danh sách thu hồi. Khoảng trắng và xuống dòng được bỏ qua.',
    noiDungDanViDu: 'eyJhbGciOiJFZERTQSIsInR5cCI6IkxJQyJ9...',
    nhanNhapDan: 'Nhập nội dung',
    loiChuaDan: 'Dán nội dung nhận từ nhà cung cấp trước khi nhập.',
    loiDanLon: 'Nội dung lớn hơn 64 KB nên không phải license. Dán đúng chuỗi nhà cung cấp gửi.',
    loiDanSaiDang:
      'Nội dung không phải license đã ký (cần ba đoạn nối bằng dấu chấm). Chép lại nguyên chuỗi nhà cung cấp gửi, không bỏ sót ký tự nào.',
    daNhap: {
      license: 'Đã nhập license.',
      keyring: 'Đã nhập keyring.',
      revocation: 'Đã nhập danh sách thu hồi.',
    },

    giaHanTieuDe: 'Gia hạn hoặc đổi license',
    giaHanMo: 'Gia hạn, thêm module hay nâng giới hạn đều là license mới do nhà cung cấp phát.',
    giaHanBuoc1: 'Tải activation-request.json của bản cài.',
    giaHanBuoc2:
      'Gửi tệp cho nhà cung cấp kèm số hợp đồng. Tệp chỉ có mã bản cài, tenant, sản phẩm, phiên bản và ngày tạo, không có dữ liệu nghiệp vụ.',
    giaHanBuoc3: 'Nhập tệp license nhận lại ở khối Nhập tệp. License mới có hiệu lực ngay khi nhập.',
    giaHanNhac: 'Gửi trước ngày hết hạn ít nhất 45 ngày để nhà cung cấp kịp cấp.',

    // Băng cảnh báo đầu mọi màn quản trị (ADR-0059 mục 6.3); {{so}} là số ngày.
    bang: {
      EXPIRING: 'License của bản cài còn {{so}} ngày là hết hạn. Gửi activation-request.json cho nhà cung cấp để gia hạn.',
      GRACE: 'License đã hết hạn, còn {{so}} ngày ân hạn. Hết ân hạn thì hệ thống chỉ cho đọc.',
      READ_ONLY: 'License đã hết ân hạn nên hệ thống chỉ cho đọc. Nhập license gia hạn để ghi dữ liệu trở lại.',
      NOT_YET_VALID: 'License chưa tới ngày hiệu lực (còn {{so}} ngày) nên chưa ghi được dữ liệu.',
      INVALID: 'Bản cài chưa có license hợp lệ nên chỉ mở màn license và khôi phục dữ liệu được.',
    },
    moManLicense: 'Mở màn license',
  },

  // ─────────── Form cài đặt dựng từ danh mục tham số — ADR-0059 mục 1, 8 ───────────
  /*
   * Nhãn và mô tả của từng tham số: `caiDat.<key>.nhan`, `caiDat.<key>.moTa`, đúng
   * `label_key`/`description_key` của `settings-registry.yaml`. Khoá có dấu chấm
   * (`auth.mfa_required`) là **một** khoá phẳng; i18next tra được nhờ tìm khoá phẳng.
   * Nhãn giá trị `enum` nằm cạnh nhãn của khoá (`ai_quota.uu_tien.HET_HAN_TRUOC`).
   */
  caiDat: {
    'auth.chi_email_domain_xac_minh': {
      nhan: 'Chỉ nhận email thuộc domain đã xác minh',
    },
    'auth.mfa_required': {
      nhan: 'Bắt buộc xác thực hai lớp (MFA)',
      moTa: 'Ai chưa cài ứng dụng xác thực phải cài trước khi đăng nhập được.',
    },
    'auth.password_min_length': {
      nhan: 'Độ dài mật khẩu tối thiểu',
    },
    'auth.password_max_length': {
      nhan: 'Độ dài mật khẩu tối đa',
    },
    'auth.password_history': {
      nhan: 'Không dùng lại mật khẩu gần nhất',
    },
    'auth.bruteforce_max_failures': {
      nhan: 'Số lần sai trước khi khoá tài khoản',
    },
    'auth.bruteforce_lock_minutes': {
      nhan: 'Thời gian khoá tài khoản',
    },
    'auth.password_blacklist_enabled': {
      nhan: 'Chặn mật khẩu đã bị lộ',
    },
    'network.ip_allowlist': {
      nhan: 'Dải tin cậy',
    },
    'network.mfa_ngoai_dai': {
      nhan: 'Bắt nhập mã OTP khi ở ngoài dải tin cậy',
      moTa: 'Áp dụng cả khi tổ chức không bắt buộc xác thực hai lớp.',
    },
    'network.auto_block_minutes': {
      nhan: 'Thời gian chặn tạm IP đăng nhập sai nhiều lần',
    },
    'audit.allow_sample_rate': {
      nhan: 'Tỉ lệ ghi nhật ký request được cho phép',
    },
    'audit.read_sample_rate': {
      nhan: 'Tỉ lệ ghi nhật ký thao tác đọc',
    },
    'audit.login_error_burst_per_ip': {
      nhan: 'Ngưỡng đăng nhập sai từ một IP',
      moTa: 'Vượt ngưỡng thì gửi cảnh báo bảo mật và chặn tạm IP đó.',
    },
    'audit.alert_rules': {
      nhan: 'Quy tắc cảnh báo',
    },
    'log.debug_until': {
      nhan: 'Bật log chi tiết (DEBUG) đến',
    },
    'notify.security_critical': {
      nhan: 'Cảnh báo bảo mật nghiêm trọng',
    },
    'notify.ops_warning': {
      nhan: 'Cảnh báo vận hành',
    },
    'notify.tenant_security': {
      nhan: 'Cảnh báo bảo mật của tenant',
    },
    'notify.audit_alert': {
      nhan: 'Cảnh báo audit theo quy tắc',
    },
    'notify.license_expiry': {
      nhan: 'License sắp hết hạn',
    },
    'ai_quota.uu_tien': {
      nhan: 'Thứ tự dùng token AI',
      moTa: 'Token gói hạn mức không dùng hết trong tháng sẽ mất vào cuối tháng.',
      HET_HAN_TRUOC: 'Sắp hết hạn dùng trước',
      MUA_THEM_TRUOC: 'Ưu tiên dùng token mua thêm trước',
    },
    'notify.ai_quota': {
      nhan: 'Token AI sắp hết hoặc đã hết',
    },
    'notify.ai_token_request': {
      nhan: 'Yêu cầu nạp token',
    },
    'notify.ai_token_decision': {
      nhan: 'Kết quả yêu cầu nạp token',
    },
    'contact.types': {
      nhan: 'Loại đầu mối liên hệ',
      moTa: 'Mã chữ hoa và gạch dưới, ví dụ ADMIN, SECURITY.',
    },
    'license.default_term_months': {
      nhan: 'Thời hạn license mặc định',
    },
    'license.grace_days': {
      nhan: 'Thời gian ân hạn',
    },
    'license.renewal_window_days': {
      nhan: 'Mở gia hạn trước',
    },
    'license.notify_days': {
      nhan: 'Các mốc báo license sắp hết hạn',
    },
    'license.approval.max_term_months': {
      nhan: 'Thời hạn tối đa không cần duyệt',
    },
    'license.approval.over_plan_quota': {
      nhan: 'Duyệt license vượt hạn mức gói',
    },
    'license.approval.special_features': {
      nhan: 'Tính năng cần duyệt',
      moTa: 'Dấu * là mọi tính năng.',
    },

    // Nguồn của giá trị hiện cạnh mỗi dòng.
    nguon: {
      macDinh: 'Mặc định',
      nenTang: 'Nền tảng đặt',
      // Ở tầng tenant: giá trị đến từ nền tảng (mức sàn hoặc tham số chỉ nền tảng quản lý, ADR-0078)
      theoNenTang: 'Theo nền tảng',
      goi: 'Theo gói',
      theoGoi: 'Theo gói {{goi}}',
      tenant: 'Tổ chức đặt',
      dangSua: 'Đang sửa',
      veKeThua: 'Về mặc định',
      seLay: 'Sau khi lưu: {{nguon}}',
    },
    // Biên hiện dưới ô nhập.
    bien: {
      tuDen: 'Từ {{min}} đến {{max}}',
      // Biên dưới là mức sàn của nền tảng (ADR-0078)
      tuSanDen: 'Từ {{min}} (mức sàn của nền tảng) đến {{max}}',
      tuDenTheoGoi: 'Từ {{min}} đến {{max}} theo gói hiện tại',
      tu: 'Từ {{min}} trở lên',
      den: 'Tối đa {{max}}',
      denTheoGoi: 'Tối đa {{max}} theo gói hiện tại',
      tuDenToiDaPhanTu: 'Mỗi mốc từ {{min}} đến {{max}}, tối đa {{so}} mốc',
      toiDaDong: 'Tối đa {{so}} dòng',
      toiDaPhut: 'Tối đa {{so}} phút tính từ bây giờ',
      toiDaNguoiNhan: 'Tối đa {{so}} người nhận',
      toiDaQuyTac: 'Tối đa {{so}} quy tắc',
    },
    donVi: {
      phut: 'phút',
      lan: 'lần',
      kyTu: 'ký tự',
      ngay: 'ngày',
      thang: 'tháng',
      suKienS: 'sự kiện/s',
    },
    // Lỗi phía giao diện: nói cách sửa, đặt ngay dưới ô.
    loi: {
      kieu_sai: 'Nhập đúng kiểu giá trị cho ô này.',
      khong_phai_so_nguyen: 'Nhập một số nguyên, không có phần thập phân.',
      nho_hon_min: 'Nhập giá trị từ {{min}} trở lên.',
      lon_hon_max: 'Nhập giá trị không quá {{max}}.',
      ngoai_danh_sach: 'Chọn một giá trị trong danh sách.',
      qua_so_dong: 'Bỏ bớt dòng: danh sách tối đa {{max}} dòng.',
      qua_so_nguoi_nhan: 'Bỏ bớt người nhận: tối đa {{max}} địa chỉ. Dùng hộp thư nhóm nếu cần gửi nhiều người.',
      cidr_sai: 'Dòng {{dong}}: sửa "{{cidr}}" thành địa chỉ IP hoặc dải CIDR, ví dụ 10.0.0.0/24.',
      cidr_trung: 'Dòng {{dong}}: {{cidr}} đã có ở dòng khác. Xoá một dòng.',
      cidr_tat_ca: 'Dòng {{dong}}: không chặn được toàn bộ địa chỉ (/0). Nhập một dải hẹp hơn.',
      dong_ip_sai: 'Còn {{so}} dòng chưa hợp lệ. Sửa các dòng được nêu ngay dưới ô trước khi lưu.',
      thoi_diem_qua_khu: 'Chọn một thời điểm trong tương lai.',
      thoi_diem_qua_xa: 'Chọn thời điểm trong vòng {{phut}} phút tới.',
      email_sai: 'Sửa "{{email}}" thành địa chỉ email đầy đủ, ví dụ secops@congty.vn.',
      sai_dinh_dang: 'Sửa "{{giaTri}}" cho đúng định dạng của ô này.',
      thieu_kenh: 'Chọn ít nhất một kênh gửi.',
      chuoi_rong: 'Bỏ phần tử trống trong danh sách.',
      chi_duoc_siet: 'Tenant chỉ được hạ hoặc giữ nguyên mức kế thừa ({{gioiHan}}).',
      chi_duoc_siet_bat: 'Gói hoặc nền tảng đã bật tham số này; tenant không tắt được.',
      qua_so_quy_tac: 'Xoá bớt quy tắc: tối đa {{max}} quy tắc.',
      quy_tac_sai: 'Quy tắc {{dong}} ("{{ten}}"): chọn Sửa rồi kiểm lại ô {{truong}}.',
    },
    bat: 'Bật',
    tat: 'Tắt',
    khongDat: 'Không đặt',
    danhSachRong: 'Danh sách trống',
    soDong: '{{so}} dòng',
    soQuyTac: '{{so}} quy tắc',
    tomTatTuyen: '{{kenh}} · {{so}} người nhận · từ mức {{muc}}',
    danhSachSoViDu: 'Ví dụ: 45, 30, 7, 1',
    danhSachChuoiViDu: 'Gõ rồi nhấn Enter',
    chuaLuu: 'Chưa lưu',
    chiXem: 'Chỉ xem',
    hoanTac: 'Hoàn tác',
    veMacDinh: 'Về mặc định',
    veMacDinhMo: 'Bỏ giá trị đặt ở đây, dùng lại {{nguon}}: {{giaTri}}',
    veKeThuaGhiChu: 'Bỏ giá trị đặt ở tầng này, dùng lại giá trị kế thừa.',
    khoaLyDo: {
      khongQuyen: 'Tài khoản của bạn chỉ có quyền xem cài đặt bảo mật.',
      tangKhac: 'Tham số này không mở cho vai trò của bạn sửa ở tầng này.',
      nenTangQuanLy: 'Nền tảng quản lý tham số này cho mọi tổ chức; tổ chức chỉ xem.',
      biEpEmailNgoaiDomain: 'Bắt buộc vì tổ chức cho phép email ngoài domain đã xác minh.',
    },
    // Tiêu đề nhóm con trong một khối tham số.
    nhom: {
      xacThuc: 'Xác thực',
      ghiNhatKy: 'Ghi nhật ký',
      phatHienDo: 'Phát hiện dò mật khẩu',
      yeuCauMatKhau: 'Yêu cầu mật khẩu',
      khoaKhiSai: 'Khoá khi đăng nhập sai',
      goLoi: 'Gỡ lỗi',
    },
    kenh: { EMAIL: 'Email', CHAT: 'Chat', PORTAL: 'Trong cổng' },
    mucDo: { INFO: 'Thông tin', WARNING: 'Cảnh báo', CRITICAL: 'Nghiêm trọng' },
    tuyen: {
      nguoiNhan: 'Người nhận',
      nguoiNhanViDu: 'secops@congty.vn — gõ rồi nhấn Enter',
      mucToiThieu: 'Gửi từ mức',
      kenh: 'Kênh gửi',
      chuaCoNguoiNhan: 'Chưa có người nhận: thông báo chỉ hiện trong cổng quản trị.',
    },
    cidr: {
      // Chữ phụ dưới mỗi CIDR trong bảng.
      phamViMot: '1 địa chỉ',
      phamViKhoang: '{{dau}} – {{cuoi}}',
      phamViSo: '{{so}} địa chỉ',
      phamViDaiLon: 'Dải IPv6 /{{tienTo}}',
      cotCidr: 'CIDR',
      daHetHan: 'Đã hết hạn',
      chuaIpCuaBan: 'Chứa IP của bạn',
    },
    thayDoi: {
      them: 'Thêm:',
      bo: 'Bỏ:',
      chiDoiGhiChu: 'Chỉ đổi ghi chú hoặc ngày hết hạn.',
      sua: 'Sửa:',
      chiDoiThuTu: 'Chỉ đổi thứ tự quy tắc.',
      thanh: 'thành',
    },
    // Thanh lưu và hộp xác nhận.
    chuaCoThayDoi: 'Chưa có thay đổi nào.',
    coThayDoi: 'Có {{so}} thay đổi chưa lưu.',
    suaLoiTruoc: 'Sửa {{so}} ô đang báo lỗi trước khi lưu.',
    boThayDoi: 'Bỏ thay đổi',
    luuSoThayDoi: 'Lưu {{so}} thay đổi',
    xacNhanTieuDe: 'Lưu {{so}} thay đổi?',
    lyDo: 'Lý do',
    lyDoGoiY: 'Ghi vào lịch sử thay đổi để người sau hiểu vì sao. Không bắt buộc.',
    daLuu: 'Đã lưu {{so}} thay đổi.',
    xungDot: 'Có người vừa lưu thay đổi khác trên cài đặt này. Tải bản mới để xem; phần bạn đang sửa vẫn giữ nguyên trên form.',
    taiLaiBanMoi: 'Tải bản mới',
    loiTai: 'Không tải được cài đặt: {{loi}}',
    ruiRo: {
      mfaNgoaiDaiBat: 'Người dùng đang ở ngoài dải tin cậy sẽ phải nhập mã OTP ở lần thao tác tiếp theo; ai chưa cài ứng dụng xác thực sẽ phải cài.',
      mfaBat: 'Người dùng chưa thiết lập MFA sẽ phải thiết lập trước khi dùng tiếp.',
      emailNgoaiDomain:
        'Người dùng dùng được email cá nhân và cả tổ chức bắt buộc xác thực hai lớp: ai chưa thiết lập sẽ phải thiết lập trước khi dùng tiếp.',
    },
    mfa: {
      apDungKhi: 'Áp dụng MFA khi nào?',
      lanDangNhapSau: 'Từ lần đăng nhập sau',
      lanDangNhapSauMo: 'Phiên đang mở chạy tiếp; mỗi người thiết lập MFA ở lần đăng nhập kế tiếp.',
      ngay: 'Áp dụng ngay',
      ngayMo: 'Mọi phiên đang mở phải xác thực lại bằng MFA. Người chưa thiết lập sẽ bị đăng xuất để thiết lập.',
    },
    lichSu: {
      tieuDe: 'Lịch sử thay đổi',
      caption: 'Lịch sử thay đổi cài đặt bảo mật',
      cotPhienBan: 'Phiên bản',
      cotThoiDiem: 'Thời điểm',
      cotNguoiSua: 'Người sửa',
      cotThayDoi: 'Thay đổi',
      cotLyDo: 'Lý do',
      hienTai: 'Hiện tại',
      heThong: 'Hệ thống',
      themN: '+{{so}} mục',
      tenPhienBan: 'phiên bản {{so}}',
      xemThayDoi: 'Xem thay đổi',
      khoiPhuc: 'Khôi phục phiên bản này',
      xemTieuDe: 'Thay đổi ở phiên bản {{so}}',
      xemMo: '{{nguoi}} lưu lúc {{luc}}.',
      khoiPhucTieuDe: 'Khôi phục phiên bản {{so}}?',
      khoiPhucThan: 'Cài đặt trở về đúng như sau phiên bản này. Thay đổi đi qua đủ các bước kiểm như một lần lưu; lịch sử cũ giữ nguyên.',
      nhanKhoiPhuc: 'Khôi phục phiên bản {{so}}',
      rong: 'Chưa có thay đổi nào',
      rongMo: 'Cài đặt đang dùng toàn bộ giá trị kế thừa. Lần lưu đầu tiên sẽ hiện ở đây.',
      loi: 'Không tải được lịch sử: {{loi}}',
    },
  },

  // ─────────── Cài đặt bảo mật của tenant — ADR-0059 mục 8 ───────────
  baoMat: {
    khongCoTenant: 'Màn này đọc dữ liệu của một tenant',
    khongCoTenantMo: 'Tài khoản nền tảng không thuộc tenant nào. Mở Tenant ở cổng quản trị để xem cài đặt và nhật ký của từng tenant.',
    goi: {
      TRIAL: 'Dùng thử',
      STANDARD: 'Standard',
      PROFESSIONAL: 'Professional',
      ENTERPRISE: 'Enterprise',
    },
    the: {
      truyCapIp: 'Truy cập IP',
      dangNhap: 'Đăng nhập',
      nhatKy: 'Nhật ký thao tác',
      lichSu: 'Lịch sử thay đổi',
    },
    // Bốn thẻ con của thẻ Truy cập IP (ADR-0098).
    theIp: {
      daiTinCay: 'Dải tin cậy',
      ngoaiDai: 'IP ngoài dải tin cậy',
      biChan: 'IP bị chặn',
      tuDong: 'Chặn tạm tự động',
    },
    ip: {
      allowlistTieuDe: 'Truy cập theo dải tin cậy',
      ipCuaBan: 'IP của bạn:',
      tuDongTieuDe: 'Chặn tạm tự động',
      baoCaoTieuDe: 'IP ngoài dải tin cậy trong {{so}} giờ qua',
      chuaCoDaiGhiChu: 'Chưa có dải tin cậy nên chưa IP nào bị coi là ngoài dải.',
      cotIp: 'Địa chỉ IP',
      cotSoLan: 'Số lần',
      cotNguoiDung: 'Người dùng',
      cotLanCuoi: 'Lần cuối',
      soNguoiDung: '{{so}} người dùng',
      themVaoAllowlist: 'Thêm vào dải tin cậy',
      daCoTrongAllowlist: 'Đã có trong dải tin cậy',
      ghiChuTuBaoCao: 'Thêm từ báo cáo IP ngoài dải tin cậy',
      baoCaoRong: 'Không có truy cập nào từ ngoài dải tin cậy',
      baoCaoRongMo: 'Mọi truy cập trong khoảng này đều từ dải tin cậy.',
      loi: 'Không tải được báo cáo: {{loi}}',
    },
    // Ô sửa dải tin cậy theo dòng (ADR-0098).
    daiTinCay: {
      viDu: '113.185.44.0/24 # Văn phòng Hà Nội\n2001:db8:10::/48 # VPN\n203.0.113.7 # Máy chủ tích hợp',
      huongDanThayCa: 'Ô chứa cả danh sách; lưu là thay toàn bộ, qua nút Lưu ở cuối trang.',
      huongDanDan: 'Có thể dán danh sách đã xuất trước đó.',
      saoChep: 'Sao chép danh sách',
      taiVe: 'Tải danh sách (.txt)',
      daSaoChep: 'Đã sao chép {{so}} dòng.',
      khongSaoChepDuoc: 'Trình duyệt không cho sao chép. Dùng Tải danh sách (.txt).',
      ipTrongDai: 'Trong dải tin cậy',
      ipNgoaiDai: 'Ngoài dải tin cậy',
    },
    // Ô nhập danh sách IP theo dòng, dùng chung cho dải tin cậy và blocklist (ADR-0098).
    dsIp: {
      huongDanTieuDe: 'Cách nhập',
      huongDanMotDong: 'Mỗi dòng một địa chỉ IP hoặc một dải CIDR, IPv4 hoặc IPv6.',
      huongDanGhiChu: 'Ghi chú đặt sau dấu # ở cuối dòng. Dòng bắt đầu bằng # được bỏ qua.',
      huongDanMotDiaChi: 'IP không kèm /tiền tố là đúng một địa chỉ.',
      viDuTieuDe: 'Ví dụ',
      soDong: '{{so}} dòng hợp lệ · tối đa {{max}}',
      loiDinhDang: 'Dòng {{dong}}: “{{giaTri}}” không phải địa chỉ IP hay dải CIDR. Viết dạng 203.0.113.7 hoặc 10.0.0.0/24.',
      loiTienTo: 'Dòng {{dong}}: phần sau dấu / của “{{giaTri}}” sai. IPv4 từ 0 đến 32, IPv6 từ 0 đến 128.',
      loiTrung: 'Dòng {{dong}}: {{cidr}} trùng với dòng {{dongTruoc}}. Xoá một trong hai dòng.',
      loiTatCa: 'Dòng {{dong}}: {{cidr}} phủ mọi địa chỉ. Nhập một dải hẹp hơn.',
      loiQuaSo: 'Dòng {{dong}}: vượt quá giới hạn {{max}} dòng. Gộp các địa chỉ gần nhau thành một dải.',
      loiGhiChuDai: 'Dòng {{dong}}: ghi chú dài quá {{max}} ký tự. Rút gọn ghi chú.',
      loiChuaIpCuaBan: 'Dòng {{dong}}: dải này chứa IP bạn đang dùng ({{ip}}) — chặn sẽ khoá chính bạn. Bỏ dòng này hoặc nhập dải hẹp hơn.',
      loiDaCo: 'Dòng {{dong}}: {{cidr}} đã có trong danh sách chặn.',
      loiMayChu: 'Dòng {{dong}}: {{loi}}',
      goiYDai: 'Dòng {{dong}}: {{ip}} là đúng một địa chỉ. Nếu muốn cả dải, viết {{cidr}}.',
    },
    // Blocklist IP có hạn của tổ chức (ADR-0090 mục 3); phần dùng chung với blocklist nền tảng cũng ở đây.
    chan: {
      tieuDe: 'IP bị chặn',
      canhBao: 'Chặn có hiệu lực ngay, không cần Lưu, kể cả với IP trong dải tin cậy.',
      oNhap: 'Chặn thêm địa chỉ',
      viDu: '198.51.100.23 # Dò mật khẩu\n45.67.0.0/16 # Quét cổng',
      huongDanLyDo: 'Ghi chú sau # là lý do chặn; bỏ trống thì ghi “Chặn thủ công”.',
      huongDanTuKhoa: 'Không chặn được dải chứa IP bạn đang dùng.',
      lyDoMacDinh: 'Chặn thủ công',
      nhanChan: 'Chặn địa chỉ',
      nhanChanSo: 'Chặn {{so}} địa chỉ',
      daChanSo: 'Đã chặn {{so}} địa chỉ.',
      cotNguon: 'Nguồn',
      thuCong: 'Thủ công',
      rong: 'Chưa chặn IP nào',
      rongMo: 'Chặn một IP hay một dải IP khi thấy truy cập đáng ngờ trong nhật ký. IP đăng nhập sai nhiều lần sẽ tự được chặn tạm.',
      xacNhanGoThan: 'Truy cập từ dải này lại được xét theo dải tin cậy như bình thường. Muốn chặn lại phải thêm dòng mới.',
      goSomTuDong: 'Dòng này do hệ thống tự chặn khi IP đăng nhập sai nhiều lần. Gỡ sớm nếu đó là người trong tổ chức gõ nhầm mật khẩu.',
      cotThoiHan: 'Thời hạn',
      vinhVien: 'Vĩnh viễn',
      conPhut: 'Còn {{so}} phút',
      conGio: 'Còn {{so}} giờ',
      conNgay: 'Còn {{so}} ngày',
      hetLuc: 'Hết hạn lúc {{luc}}',
      tuDong: 'Tự động',
      tuDongMo: 'Hệ thống chặn tạm vì IP này đăng nhập sai nhiều lần',
      oThoiHan: 'Chặn trong',
      thoiHanVinhVienGoiY: 'Chỉ hết chặn khi có người gỡ dòng này.',
    },
    // Thời hạn chọn khi chặn tay (ADR-0090 mục 3.1). Tách khỏi `chan` vì catalog chỉ lồng ba cấp.
    chanThoiHan: {
      '1h': '1 giờ',
      '24h': '24 giờ',
      '7d': '7 ngày',
      'vinh-vien': 'Vĩnh viễn',
    },
    dangNhap: {
      mfaTieuDe: 'Xác thực hai lớp',
      matKhauTieuDe: 'Chính sách mật khẩu',
      emailTieuDe: 'Email đăng nhập',
      emailNgoaiDomainCanhBao: 'Người dùng có thể dùng email cá nhân như gmail; mọi người sẽ bắt buộc xác thực hai lớp.',
    },
    // Tài khoản bị brute force detection khoá tạm (ADR-0059 mục 5.3).
    khoaTam: {
      tieuDe: 'Tài khoản đang bị khoá tạm',
      bangCaption: 'Tài khoản đang bị khoá tạm do đăng nhập sai',
      cotNguoiDung: 'Người dùng',
      cotSoLanSai: 'Số lần sai',
      cotLanSaiCuoi: 'Lần sai gần nhất',
      cotIp: 'IP lần sai gần nhất',
      chuaDocDuocEmail: 'Không đọc được email',
      moKhoa: 'Mở khoá tài khoản',
      xnTieuDe: 'Mở khoá tài khoản của {{ten}}?',
      xnThan: '{{ten}} đăng nhập lại được ngay, bộ đếm {{so}} lần sai về 0. Không hoàn tác được: nếu có người đang dò mật khẩu, họ cũng được thử lại từ đầu.',
      xnGhiChu: 'Tài khoản đã bị khoá ở màn Cấu hình người dùng thì vẫn bị khoá.',
      daMoKhoa: 'Đã mở khoá tài khoản của {{ten}}.',
      rong: 'Không có tài khoản nào đang bị khoá tạm',
      rongMo: 'Tài khoản đăng nhập sai quá số lần cho phép sẽ hiện ở đây.',
      chuaThay: 'Chưa thấy tài khoản nào bị khoá tạm trong phần đã tra',
      chuaThayMo: 'Danh sách được tra dần theo từng nhóm người dùng. Tra tiếp để xem phần còn lại.',
      traTiep: 'Tra tiếp',
      dangHien: '{{so}} tài khoản đang bị khoá tạm',
      dangHienCoThem: 'Đang hiện {{so}} tài khoản, còn người dùng chưa tra',
      loi: 'Không tải được danh sách tài khoản bị khoá tạm: {{loi}}',
      loiTraTiep: 'Không tra tiếp được: {{loi}}',
    },
    nhatKy: {
      canhBaoTieuDe: 'Người nhận cảnh báo bảo mật',
      vanHanhTieuDe: 'Ghi nhật ký và gỡ lỗi',
    },
    // Xác thực hai lớp theo ba mức (ADR-0047) ở thẻ Đăng nhập của cổng tenant.
    mfa: {
      tieuDe: 'Xác thực hai lớp',
      mucNhan: 'Ai phải nhập mã OTP khi đăng nhập',
      tomTatKhong: 'Không bắt buộc',
      tomTatNguoi: '{{so}} người phải nhập mã OTP',
      tomTatCaToChuc: 'Cả tổ chức phải nhập mã OTP',
      chuaCaiUngDung: 'Ai chưa cài ứng dụng xác thực sẽ phải cài ở lần đăng nhập sau.',
      tatCaNhan: 'Tất cả mọi người trong tổ chức',
      tatCaMo: 'Kể cả người được thêm sau',
      goiYChonTatCa: 'Người được thêm sau sẽ không bị bắt buộc. Chọn “Tất cả” để áp cho cả tổ chức.',
      caToChucKhongSua:
        'Quản trị nền tảng chưa cho tổ chức tự bắt buộc cho tất cả mọi người; vẫn chọn được từng người.',
      caToChucBiEp:
        'Quản trị nền tảng đã bắt buộc xác thực hai lớp cho cả tổ chức: mọi người phải nhập mã OTP khi đăng nhập. Muốn đổi thì liên hệ quản trị nền tảng.',
      caToChucBiEpEmail: 'Bắt buộc vì tổ chức cho phép email ngoài domain đã xác minh. Muốn bỏ bắt buộc thì bật lại Chỉ nhận email thuộc domain đã xác minh ở trên.',
      dangXuatNgay: 'Đăng xuất mọi người ngay để áp dụng',
      dangXuatNgayMo:
        'Mọi người, kể cả bạn, phải đăng nhập lại kèm mã OTP ngay sau khi lưu.',
      dangXuatNguoiChon: 'Đăng xuất ngay người vừa được chọn để áp dụng',
      dangXuatNguoiChonMo: 'Người vừa được chọn phải đăng nhập lại kèm mã OTP ngay sau khi lưu.',
      apDungDanMo:
        'Phiên đang mở vẫn dùng được tới khi hết hạn, chậm nhất 8 giờ; lần đăng nhập sau mới phải nhập mã OTP.',
      timNhan: 'Tìm người dùng',
      timPlaceholder: 'Tìm theo tên hoặc email',
      daChon: 'Đã chọn {{so}} người',
      daChonLoc: 'Đã chọn {{so}} người, đang hiện {{hien}}',
      loiNguoi: '{{ten}}: {{loi}}',
      loiTaiDanhSach: 'Không tải được danh sách người phải xác thực hai lớp: {{loi}}',
      loiTaiNguoi: 'Không tải được danh sách người dùng: {{loi}}',
      dangTaiNguoi: 'Đang tải danh sách người dùng',
      rongNguoi: 'Tổ chức chưa có người dùng nào',
      rongNguoiMo: 'Mời người dùng ở Cấu hình người dùng, rồi quay lại đây để chọn.',
      denCauHinhNguoiDung: 'Đến Cấu hình người dùng',
      rongLoc: 'Không có ai khớp “{{tuKhoa}}”',
      luuNuaChung:
        'Đã lưu danh sách người được chọn nhưng chưa lưu được mức cả tổ chức. Lựa chọn của bạn vẫn giữ nguyên; xử lý lỗi bên dưới rồi lưu lại.',
      xungDot:
        'Có người vừa lưu cài đặt bảo mật của tổ chức. Tải bản mới rồi lưu lại; lựa chọn của bạn không mất.',
      nutLuu: 'Lưu xác thực hai lớp',
      nutLuuVaDangXuat: 'Lưu và đăng xuất mọi người',
      nutLuuVaDangXuatNguoiChon: 'Lưu và đăng xuất người vừa được chọn',
      boThayDoi: 'Bỏ thay đổi',
      daLuu: 'Đã lưu cài đặt xác thực hai lớp.',
      daLuuVaDangXuat: 'Đã lưu cài đặt xác thực hai lớp và đăng xuất mọi người.',
      daLuuVaDangXuatNguoiChon: 'Đã lưu cài đặt xác thực hai lớp và đăng xuất người vừa được chọn.',
    },
    hauQua: {
      tieuDe: 'Sau khi lưu',
      moRongDan:
        'Mở rộng ra cả tổ chức: người chưa được chọn cũng phải nhập mã OTP, áp dụng ở lần đăng nhập sau của từng người, chậm nhất sau 8 giờ.',
      moRongNgay:
        'Mở rộng ra cả tổ chức: mọi người, kể cả bạn, bị đăng xuất ngay và phải đăng nhập lại kèm mã OTP.',
      moRongSoDan:
        'Mở rộng ra cả tổ chức: thêm {{so}} người chưa được chọn phải nhập mã OTP, áp dụng ở lần đăng nhập sau của từng người, chậm nhất sau 8 giờ.',
      moRongSoNgay:
        'Mở rộng ra cả tổ chức: mọi người, kể cả bạn, bị đăng xuất ngay và phải đăng nhập lại kèm mã OTP; {{so}} người trong số đó trước đây chưa phải nhập.',
      thuHep:
        'Thu hẹp về {{giu}} người được chọn: họ vẫn phải nhập mã OTP, những người còn lại chỉ cần mật khẩu từ lần đăng nhập sau. Không ai bị đăng xuất.',
      thuHepSo:
        'Thu hẹp về {{giu}} người được chọn: họ vẫn phải nhập mã OTP, {{thoi}} người còn lại chỉ cần mật khẩu từ lần đăng nhập sau. Không ai bị đăng xuất.',
      themNguoiDan:
        '{{so}} người vừa được chọn phải nhập mã OTP, áp dụng ở lần đăng nhập sau của từng người, chậm nhất sau 8 giờ. Ai chưa cài ứng dụng xác thực sẽ phải cài.',
      themNguoiNgay: '{{so}} người vừa được chọn bị đăng xuất ngay và phải đăng nhập lại kèm mã OTP.',
      boNguoi: '{{so}} người được bỏ chọn chỉ cần mật khẩu từ lần đăng nhập sau.',
      banBiDangXuat: 'Bạn có trong số người vừa được chọn nên cũng bị đăng xuất.',
    },
    xacNhan: {
      tieuDe: 'Lưu cài đặt xác thực hai lớp?',
      mfaBatNgay:
        'Bắt buộc xác thực hai lớp: mọi người, kể cả bạn, bị đăng xuất ngay và phải đăng nhập lại kèm mã OTP.',
      mfaBatDan:
        'Bắt buộc xác thực hai lớp: áp dụng ở lần đăng nhập sau của từng người, chậm nhất sau 8 giờ.',
      mfaTat: 'Bỏ bắt buộc xác thực hai lớp: mọi người đăng nhập chỉ cần mật khẩu.',
      hieuLucNgay: 'Thay đổi có hiệu lực ngay khi lưu.',
      khongHoanTac: 'Thay đổi có hiệu lực ngay khi lưu. Việc đăng xuất không hoàn tác được.',
    },
  },

  // Quy tắc cảnh báo audit `audit.alert_rules` (ADR-0059 mục 4.1).
  quyTacCanhBao: {
    tieuDe: 'Quy tắc cảnh báo',
    them: 'Thêm quy tắc',
    sua: 'Sửa quy tắc',
    xoa: 'Xoá quy tắc',
    capNhat: 'Cập nhật quy tắc',
    themTieuDe: 'Thêm quy tắc cảnh báo',
    suaTieuDe: 'Sửa quy tắc "{{ten}}"',
    nganMo: 'Quy tắc đi vào phần đang sửa của cài đặt; bấm Lưu ở thanh dưới cùng để áp dụng.',
    caption: 'Các quy tắc của {{ten}}',
    cotTen: 'Quy tắc',
    cotDieuKien: 'Điều kiện',
    cotNguong: 'Ngưỡng',
    cotMuc: 'Mức',
    cotBat: 'Bật',
    batTatVoi: 'Bật quy tắc {{ten}}',
    demToiDa: '{{so}}/{{toiDa}} quy tắc',
    rong: 'Chưa có quy tắc cảnh báo nào',
    rongMo: 'Thêm quy tắc để được báo khi có chuỗi sự kiện bất thường, ví dụ một người dùng bị từ chối nhiều lần trong vài phút.',
    xoaTieuDe: 'Xoá quy tắc "{{ten}}"?',
    xoaThan: 'Sau khi lưu cài đặt, hệ thống ngừng đánh giá quy tắc này và không tạo cảnh báo mới từ nó; cảnh báo đã kích hoạt vẫn giữ. Trước khi lưu, bấm Hoàn tác ở dòng Quy tắc cảnh báo để lấy lại.',
    loiTruong: '{{truong}}: {{cau}}',
    nhomChung: 'Thông tin chung',
    nhomDieuKien: 'Sự kiện được đếm',
    nhomNguong: 'Ngưỡng kích hoạt',
    tenViDu: 'Nhiều lần bị từ chối',
    idTuSinh: 'Sinh từ tên, không đổi được sau khi thêm.',
    idKhongDoi: 'Mã không đổi được: cảnh báo đã kích hoạt tham chiếu quy tắc bằng mã này.',
    moiQuyetDinh: 'Mọi quyết định',
    actionsGoiY: 'Dấu * khớp mọi chuỗi, ví dụ user.*. Để trống là mọi action.',
    actionsViDu: 'GET, user.* — gõ rồi nhấn Enter',
    resourceGoiY: 'Chỉ đếm resource bắt đầu bằng chuỗi này. Để trống là mọi resource.',
    resourceViDu: '/api/v1/',
    sourcesGoiY: 'Không chọn nguồn nào là đếm mọi nguồn.',
    errorCodesGoiY: 'Mã lỗi viết hoa. Để trống là không lọc theo mã lỗi.',
    errorCodesViDu: 'PERMISSION_DENIED',
    toiDaMuc: 'Tối đa {{so}} mục.',
    donViGiay: 'giây',
    donViLan: 'lần',
    bang: '= {{giaTri}}',
    xemTruoc: 'Kích hoạt khi đủ {{tomTat}}. Đếm: {{dieuKien}}.',
    nguongTomTat: '{{so}} lần / {{cuaSo}} {{nhom}}',
    truong: {
      id: 'Mã quy tắc',
      name: 'Tên quy tắc',
      enabled: 'Bật quy tắc',
      decision: 'Quyết định',
      actions: 'Action',
      resource_prefix: 'Tiền tố resource',
      sources: 'Nguồn',
      error_codes: 'Mã lỗi',
      group_by: 'Đếm riêng theo',
      threshold: 'Ngưỡng',
      window_seconds: 'Khoảng thời gian đếm',
      cooldown_seconds: 'Thời gian chờ trước khi báo lại',
      severity: 'Mức cảnh báo',
    },
    quyetDinh: { ALLOW: 'Được cho phép', DENY: 'Bị từ chối' },
    nguon: { gateway: 'Gateway', service: 'Service', keycloak: 'Keycloak' },
    nhom: { user: 'Người dùng', ip: 'IP', tenant: 'Cả tenant' },
    nhomTheo: { user: 'theo người dùng', ip: 'theo IP', tenant: 'trên cả tenant' },
    thoiLuong: { giay: '{{so}} giây', phut: '{{so}} phút', gio: '{{so}} giờ', ngay: '{{so}} ngày' },
    tomTat: {
      action: 'action {{ds}}',
      resource: 'resource bắt đầu bằng {{tienTo}}',
      nguon: 'nguồn {{ds}}',
      maLoi: 'mã lỗi {{ds}}',
      moiSuKien: 'Mọi sự kiện nhật ký',
    },
    loi: {
      bat_buoc: 'Nhập giá trị cho ô này.',
      qua_dai: 'Rút gọn còn tối đa {{max}} ký tự.',
      id_sai: 'Đặt tên có ít nhất một chữ cái hoặc chữ số để sinh mã.',
      id_trung: 'Mã {{giaTri}} đã có ở quy tắc khác. Đổi tên quy tắc.',
      khong_phai_so_nguyen: 'Nhập một số nguyên, không có phần thập phân.',
      nho_hon_min: 'Nhập giá trị từ {{min}} trở lên.',
      lon_hon_max: 'Nhập giá trị không quá {{max}}.',
      qua_so_muc: 'Bỏ bớt mục: tối đa {{max}} mục.',
      muc_sai: 'Sửa "{{giaTri}}" cho đúng định dạng của ô này.',
      ngoai_danh_sach: 'Chọn một giá trị trong danh sách.',
    },
  },
  // Cảnh báo đã kích hoạt — audit-service.
  canhBaoAudit: {
    tieuDe: 'Cảnh báo đã kích hoạt',
    caption: 'Cảnh báo audit đã kích hoạt',
    cotThoiDiem: 'Thời điểm',
    cotQuyTac: 'Quy tắc',
    cotMuc: 'Mức',
    cotNhom: 'Đối tượng',
    cotSoLan: 'Số lần',
    cotCuaSo: 'Khoảng thời gian',
    soLanTrenNguong: '{{so}} / {{nguong}}',
    tuDen: '{{tu}} – {{den}}',
    trangThai: { OPEN: 'Chưa xem', ACKNOWLEDGED: 'Đã xem' },
    danhDauDaXem: 'Đánh dấu đã xem',
    daXacNhan: 'Đã đánh dấu cảnh báo "{{ten}}" là đã xem.',
    loiXacNhan: 'Không đánh dấu được cảnh báo "{{ten}}": {{loi}}',
    taiLai: 'Tải lại danh sách',
    loi: 'Không tải được cảnh báo: {{loi}}',
    loiTaiThem: 'Không tải thêm được: {{loi}}',
    locMuc: 'Lọc theo mức',
    locTrangThai: 'Lọc theo trạng thái',
    moiMuc: 'Mọi mức',
    tatCa: 'Tất cả',
    rong: 'Chưa có cảnh báo nào',
    rongMo: 'Khi một quy tắc đạt ngưỡng, cảnh báo hiện ở đây và được gửi tới người nhận cảnh báo audit.',
    rongLoc: 'Không có cảnh báo khớp bộ lọc',
  },

  // ─────────── Nhật ký thao tác — §8.2 của đặc tả, audit-service ───────────
  nhatKy: {
    moTaMan: 'Ai đã làm gì, trên đối tượng nào, lúc nào và kết quả ra sao. Mặc định hiện 24 giờ qua.',
    moTaManNenTang: 'Nhật ký thao tác và sử dụng AI của các tenant. Mỗi lần xem đều được ghi vết.',
    chonTenant: 'Chọn tenant',
    chuaChonTenant: 'Chọn một tenant để xem nhật ký',
    chuaChonTenantMo: 'Nhật ký được tra theo từng tenant, tối đa 90 ngày mỗi lần.',
    loiTenant: 'Không tải được danh sách tenant: {{loi}}',
    caption: 'Nhật ký thao tác',
    cot: {
      thoiDiem: 'Thời điểm',
      nguoi: 'Người thực hiện',
      thaoTac: 'Thao tác',
      ketQua: 'Kết quả',
    },
    ketQua: { ALLOW: 'Cho phép', DENY: 'Từ chối' },
    moc: {
      hom_nay: 'Hôm nay',
      '24_gio': '24 giờ qua',
      '7_ngay': '7 ngày qua',
      '30_ngay': '30 ngày qua',
      tuy_chon: 'Tuỳ chọn',
    },
    loc: {
      khoang: 'Khoảng thời gian',
      nguoi: 'Người dùng',
      nguoiGoiY: 'Tìm theo tên hoặc email',
      nhomNguoiDung: 'Người dùng của tenant',
      nhomNhanSu: 'Nhân sự nền tảng',
      dangTaiNguoi: 'Đang tải danh sách người dùng...',
      khongCoNguoi: 'Không có người dùng nào khớp',
      loiNguoi: 'Không tải được danh sách người dùng: {{loi}}',
      thaoTac: 'Thao tác',
      nhomThaoTac: 'Nhóm thao tác',
      chiTiet: 'Chi tiết',
      chiTietMo: 'Chọn từng thao tác',
      daChonChiTiet: 'Đã chọn {{so}} thao tác',
      timThaoTac: 'Tìm thao tác',
      boChonChiTiet: 'Bỏ chọn tất cả',
      khongCoThaoTac: 'Không có thao tác nào khớp',
      ketQua: 'Kết quả',
      chonKhoang: 'Chọn đủ thời điểm bắt đầu và kết thúc.',
      nguoc: 'Chọn thời điểm kết thúc sau thời điểm bắt đầu.',
      quaDai: 'Thu hẹp khoảng còn tối đa {{so}} ngày.',
    },
    hanhDong: {
      'auth.login_error': 'Đăng nhập sai',
      'auth.user_temporarily_locked': 'Tài khoản bị khoá tạm vì đăng nhập sai nhiều lần',
      USER_INVITED: 'Mời người dùng',
      USER_INVITE_RESENT: 'Gửi lại lời mời',
      USER_INVITE_REJECTED: 'Không mời được email này',
      USER_PROFILE_UPDATED: 'Sửa hồ sơ người dùng',
      USER_DEPARTMENT_CHANGED: 'Đổi phòng ban của người dùng',
      USER_ROLES_CHANGED: 'Gán vai trò cho người dùng',
      USER_DISABLED: 'Khoá người dùng',
      USER_ENABLED: 'Mở khoá người dùng',
      USER_MFA_REQUIRED_ENABLED: 'Bắt buộc xác thực hai lớp cho người dùng',
      USER_MFA_REQUIRED_DISABLED: 'Bỏ bắt buộc xác thực hai lớp cho người dùng',
      USER_PASSWORD_RESET_SENT: 'Gửi email đặt lại mật khẩu',
      USER_LOGIN_UNLOCKED: 'Mở khoá đăng nhập',
      USER_MFA_RESET: 'Đặt lại xác thực hai lớp',
      USER_BRUTE_FORCE_UNLOCKED: 'Gỡ khoá tạm vì đăng nhập sai',
      ROLE_CREATED: 'Tạo vai trò',
      ROLE_UPDATED: 'Sửa vai trò',
      ROLE_PERMISSIONS_CHANGED: 'Đổi quyền của vai trò',
      ROLE_DELETED: 'Xoá vai trò',
      DEPARTMENT_CREATED: 'Tạo phòng ban',
      DEPARTMENT_UPDATED: 'Sửa phòng ban',
      DEPARTMENT_DELETED: 'Xoá phòng ban',
      PLATFORM_STAFF_REQUESTED: 'Đề nghị tạo nhân sự nền tảng',
      PLATFORM_STAFF_APPROVED: 'Duyệt nhân sự nền tảng',
      PLATFORM_STAFF_UPDATED: 'Sửa nhân sự nền tảng',
      PLATFORM_STAFF_ROLE_CHANGED: 'Đổi nhóm tài khoản của nhân sự nền tảng',
      PLATFORM_STAFF_ROLES_CHANGED: 'Gán vai trò nền tảng cho nhân sự',
      PLATFORM_STAFF_DISABLED: 'Khoá nhân sự nền tảng',
      PLATFORM_STAFF_ENABLED: 'Mở khoá nhân sự nền tảng',
      PLATFORM_STAFF_INVITE_SENT: 'Gửi email mời nhân sự nền tảng',
      PLATFORM_STAFF_INVITE_RESENT: 'Gửi lại email mời nhân sự nền tảng',
      PLATFORM_ROLE_CREATED: 'Tạo vai trò nền tảng',
      PLATFORM_ROLE_UPDATED: 'Sửa vai trò nền tảng',
      PLATFORM_ROLE_PERMISSIONS_CHANGED: 'Đổi quyền của vai trò nền tảng',
      PLATFORM_ROLE_DELETED: 'Xoá vai trò nền tảng',
      KEYCLOAK_USER_CREATED: 'Tạo tài khoản đăng nhập',
      KEYCLOAK_USER_DELETED: 'Xoá tài khoản đăng nhập',
      KEYCLOAK_USER_DISABLED: 'Khoá tài khoản đăng nhập',
      KEYCLOAK_USER_ENABLED: 'Mở khoá tài khoản đăng nhập',
      KEYCLOAK_USER_BRUTE_FORCE_CLEARED: 'Gỡ khoá tạm của tài khoản đăng nhập',
      KEYCLOAK_USER_MFA_REQUIRED: 'Bật bắt buộc OTP cho tài khoản',
      KEYCLOAK_USER_MFA_NOT_REQUIRED: 'Tắt bắt buộc OTP cho tài khoản',
      KEYCLOAK_INVITE_SENT: 'Gửi email mời đặt mật khẩu',
      KEYCLOAK_SESSIONS_REVOKED: 'Đăng xuất mọi phiên của người dùng',
      KEYCLOAK_OTP_RESET: 'Xoá OTP của tài khoản',
      KEYCLOAK_LOGIN_UNLOCKED: 'Mở khoá đăng nhập của tài khoản',
      KEYCLOAK_PASSWORD_RESET_SENT: 'Gửi email đặt lại mật khẩu của tài khoản',
      PERMISSION_CHECK: 'Kiểm quyền nội bộ',
      TENANT_CREATED: 'Tạo tenant',
      TENANT_VIEWED: 'Xem chi tiết tenant',
      TENANT_UPDATED: 'Sửa tenant',
      TENANT_APPROVED: 'Duyệt tenant',
      TENANT_SUSPENDED: 'Tạm ngưng tenant',
      TENANT_RESUMED: 'Mở lại tenant',
      TENANT_ONBOARDING_FINISHED: 'Hoàn tất onboarding',
      TENANT_ONBOARDING_REVERTED: 'Hoàn tác onboarding',
      KEYCLOAK_TENANT_GROUP_CREATED: 'Tạo group tenant ở Keycloak',
      TENANT_DOMAIN_ADDED: 'Thêm domain',
      TENANT_DOMAIN_CHECKED: 'Kiểm tra xác minh domain',
      TENANT_DOMAIN_APPROVED: 'Duyệt domain',
      'tenant.contact.created': 'Thêm đầu mối liên hệ',
      'tenant.contact.updated': 'Sửa đầu mối liên hệ',
      'tenant.contact.deleted': 'Xoá đầu mối liên hệ',
      'security.policy.updated': 'Đổi cài đặt bảo mật',
      'security.ip_blocklist.added': 'Thêm IP vào danh sách chặn',
      'security.ip_blocklist.removed': 'Bỏ IP khỏi danh sách chặn',
      'ip.monitor': 'Truy cập ngoài allowlist (chế độ Thử)',
      'api_key.created': 'Tạo khoá API',
      'api_key.revoked': 'Thu hồi khoá API',
      KEYCLOAK_TENANT_MFA_ENABLED: 'Bật bắt buộc OTP cho cả tổ chức',
      KEYCLOAK_TENANT_MFA_DISABLED: 'Tắt bắt buộc OTP cho cả tổ chức',
      KEYCLOAK_TENANT_SESSIONS_REVOKED: 'Đăng xuất mọi phiên của tổ chức',
      KEYCLOAK_TENANT_PASSWORD_POLICY_SYNCED: 'Đồng bộ chính sách mật khẩu',
      TENANT_SESSIONS_REVOKED_MFA_APPLY_NOW: 'Huỷ phiên để áp dụng xác thực hai lớp ngay',
      'license.issue': 'Phát hành license',
      'license.revoke': 'Thu hồi license',
      'license.request.create': 'Tạo yêu cầu license',
      'license.request.approve': 'Duyệt yêu cầu license',
      'license.request.reject': 'Từ chối yêu cầu license',
      'license.request.cancel': 'Huỷ yêu cầu license',
      'license.installation.register': 'Đăng ký bản cài on-premise',
      'license.download_ticket': 'Cấp vé tải license',
      'license.download': 'Tải license',
      'license.keyring.upload': 'Tải lên bộ khoá ký license',
      'license.local.license_imported': 'Nạp license',
      'license.local.license_rejected': 'Từ chối license không hợp lệ',
      'license.local.keyring_imported': 'Nạp bộ khoá ký',
      'license.local.keyring_rejected': 'Từ chối bộ khoá ký không hợp lệ',
      'license.local.revocation_imported': 'Nạp danh sách thu hồi',
      'license.local.revocation_rejected': 'Từ chối danh sách thu hồi không hợp lệ',
      'license.local.clock_rollback': 'Phát hiện đồng hồ máy chủ bị lùi',
      'license.local.license_status': 'Đổi trạng thái license',
      MODULE_INSTALLED: 'Cài module',
      MODULE_UNINSTALLED: 'Gỡ module',
      MODULE_UPGRADED: 'Nâng cấp module',
      MODULE_ROLLED_BACK: 'Hạ phiên bản module',
      MODULE_DELETED: 'Xoá module khỏi Marketplace',
      'audit.query': 'Tra cứu nhật ký',
      'audit.export': 'Xuất nhật ký',
      'audit.archive.list': 'Xem danh sách lưu trữ lạnh',
      'audit.archive.download': 'Tải bản lưu trữ lạnh',
      'audit.archive.completed': 'Lưu trữ lạnh xong một tháng',
      'audit.archive.partition_dropped': 'Xoá bảng nóng đã lưu trữ',
      'audit.archive.failed': 'Lưu trữ lạnh lỗi',
      'audit.alert.triggered': 'Cảnh báo nhật ký kích hoạt',
      'audit.alert.acknowledged': 'Đánh dấu đã xem cảnh báo',
      'audit.trash.deleted': 'Chuyển nhật ký vào thùng rác',
      'audit.trash.restored': 'Khôi phục nhật ký từ thùng rác',
      'audit.trash.purged': 'Xoá vĩnh viễn nhật ký hết hạn',
      'ops.log_level.changed': 'Đổi mức log',
      'notification.sent': 'Gửi thông báo',
      'llm.goi': 'Gọi model AI',
      AI_MODEL_CREATED: 'Thêm model AI',
      AI_MODEL_UPDATED: 'Sửa model AI',
      TENANT_AI_MODELS_UPDATED: 'Đổi model AI của tenant',
    },
    goiApi: 'Gọi API ({{phuongThuc}})',
    nhom: {
      dang_nhap: 'Đăng nhập',
      nguoi_dung: 'Người dùng & vai trò',
      tenant: 'Tenant',
      bao_mat: 'Bảo mật',
      license_module: 'License & module',
      truy_cap_api: 'Truy cập API',
      van_hanh: 'Nhật ký & vận hành',
      ai: 'AI',
    },
    hanhDongLa: 'Thao tác chưa có trong danh mục của phiên bản này ({{ma}})',
    nguon: { GATEWAY: 'Cổng vào', SERVICE: 'Nghiệp vụ', SDK: 'Nghiệp vụ (SDK)', KEYCLOAK: 'Đăng nhập (Keycloak)' },
    heThong: 'Hệ thống',
    chiNguoiNay: 'Chỉ xem thao tác của người này',
    moChiTiet: 'Mở chi tiết thao tác',
    co: 'Có',
    khong: 'Không',
    rongThat: 'Không có thao tác nào trong khoảng này',
    rongThatMo: 'Thử chọn khoảng thời gian dài hơn.',
    xem30Ngay: 'Xem 30 ngày qua',
    rongLoc: 'Không có thao tác nào khớp bộ lọc',
    hetBanGhi: 'Đã hiện hết {{so}} bản ghi của bộ lọc này.',
    loi: 'Không tải được nhật ký: {{loi}}',
    chiTiet: {
      tieuDe: 'Chi tiết thao tác',
      tomTat: 'Tóm tắt',
      doiTuong: 'Đối tượng',
      lyDo: 'Lý do / mã lỗi',
      ip: 'Địa chỉ IP',
      nguonGhi: 'Nguồn ghi',
      thayDoi: 'Thay đổi',
      khongDoi: 'Thao tác này không đổi dữ liệu.',
      duLieuKyThuat: 'Dữ liệu kỹ thuật',
      maSuKien: 'Mã sự kiện',
      maBanGhi: 'Mã bản ghi',
      maNguoi: 'Mã người dùng',
    },
  },

  // Lưu trữ lạnh (ADR-0059 mục 4.2)
  luuTruLanh: {
    theThaoTac: 'Nhật ký thao tác',
    the: 'Lưu trữ lạnh',
    moTa: 'Nhật ký quá 12 tháng được chuyển theo từng tháng sang object storage dạng JSON Lines nén, kèm manifest để kiểm chuỗi băm, và giữ 5 năm. Mỗi lần xem hay tải đều được ghi vết.',
    caption: 'Bản lưu trữ lạnh của nhật ký thao tác, mới nhất trước',
    cot: {
      thang: 'Tháng',
      trangThai: 'Trạng thái',
      soDong: 'Số dòng',
      dungLuong: 'Dung lượng',
      giuToi: 'Giữ tới',
      loi: 'Lỗi gần nhất',
    },
    trangThai: {
      PENDING: 'Chờ xuất',
      EXPORTING: 'Đang xuất',
      UPLOADED: 'Đã lưu, chờ tách',
      DETACHED: 'Đã tách khỏi bảng',
      DROPPED: 'Đã xoá bảng nóng',
      FAILED: 'Lỗi, sẽ thử lại',
    },
    soLanThu: 'Đã thử {{so}} lần',
    soTenant: '{{so}} tenant',
    taiDuLieu: 'Tải tệp dữ liệu',
    taiManifest: 'Tải manifest',
    rong: 'Chưa có bản lưu trữ lạnh nào',
    rongMo: 'Job lưu trữ chạy hằng ngày và chỉ chuyển nhật ký quá 12 tháng, nên hệ thống mới chạy thì danh sách còn trống.',
    hetBanGhi: 'Đã hiện hết {{so}} bản lưu trữ.',
    loi: 'Không tải được danh sách lưu trữ lạnh: {{loi}}',
  },

  // Thùng rác nhật ký (ADR-0089)
  thungRac: {
    the: 'Thùng rác',
    moTa: 'Nhật ký đã xoá nằm ở đây 30 ngày và khôi phục được; hết hạn thì bị xoá vĩnh viễn. Sổ này chỉ ghi thêm, kể cả lần khôi phục và lần xoá vĩnh viễn, nên không xoá được.',
    caption: 'Các lần xoá nhật ký, mới nhất trước',
    cot: {
      khoang: 'Khoảng đã xoá',
      tenant: 'Tenant',
      soBanGhi: 'Số bản ghi',
      lyDo: 'Lý do',
      thoiDiem: 'Thời điểm / người xoá',
      conLai: 'Còn khôi phục được',
      trangThai: 'Trạng thái',
    },
    donVi: { DAY: 'Ngày', WEEK: 'Tuần', MONTH: 'Tháng' },
    trangThai: { ACTIVE: 'Trong thùng rác', RESTORED: 'Đã khôi phục', PURGED: 'Đã xoá vĩnh viễn' },
    khoangNgay: '{{tu}} – {{den}}',
    conNgay: 'Còn {{so}} ngày',
    conMotNgay: 'Còn 1 ngày',
    choXoa: 'Chờ xoá vĩnh viễn',
    daXoaVinhVien: 'Đã xoá {{so}} bản ghi',
    khoiPhuc: 'Khôi phục',
    khoiPhucTieuDe: 'Khôi phục nhật ký đã xoá?',
    khoiPhucThan: '{{so}} bản ghi của {{tenant}} từ {{tu}} đến hết {{den}} sẽ hiện lại trong tra cứu và tệp xuất. Lần khôi phục được ghi vào sổ thùng rác.',
    khoiPhucThanNgay: '{{so}} bản ghi của {{tenant}} ngày {{ngay}} sẽ hiện lại trong tra cứu và tệp xuất. Lần khôi phục được ghi vào sổ thùng rác.',
    khoiPhucNut: 'Khôi phục {{so}} bản ghi',
    dangKhoiPhuc: 'Đang khôi phục...',
    daKhoiPhuc: 'Đã khôi phục {{so}} bản ghi nhật ký.',
    rong: 'Thùng rác đang trống',
    rongMo: 'Nhật ký xoá theo ngày, tuần hoặc tháng ở thẻ Nhật ký thao tác sẽ nằm ở đây 30 ngày.',
    rongLoc: '{{tenant}} chưa có lần xoá nhật ký nào',
    dangLoc: 'Chỉ hiện các lần xoá của {{tenant}}, theo tenant đang chọn ở thẻ Nhật ký thao tác.',
    loi: 'Không tải được thùng rác: {{loi}}',
    hetBanGhi: 'Đã hiện hết {{so}} lần xoá.',
    nutXoa: 'Xoá nhật ký',
    tieuDe: 'Xoá nhật ký của {{tenant}}?',
    donViNhan: 'Xoá theo',
    chon: { DAY: 'Ngày cần xoá', WEEK: 'Tuần cần xoá', MONTH: 'Tháng cần xoá' },
    khoang: 'Từ {{tu}} đến hết {{den}}, giờ Việt Nam.',
    dinhDangTuan: 'Tuần {{so}}, {{nam}}',
    lyDo: 'Lý do xoá',
    lyDoGoiY: 'Ví dụ: dữ liệu thử nghiệm của đợt UAT tháng 10',
    lyDoThieu: 'Nhập lý do xoá để lưu vào sổ thùng rác.',
    lyDoNgan: 'Viết lý do dài ít nhất {{so}} ký tự.',
    lyDoDai: 'Rút lý do còn tối đa {{so}} ký tự.',
    chonThoiDiem: 'Chọn thời điểm để xem số bản ghi sẽ xoá.',
    dangDem: 'Đang đếm bản ghi...',
    soSeXoa: '{{so}} bản ghi sẽ vào thùng rác.',
    khongCoBanGhi: 'Khoảng này không còn bản ghi nào để xoá. Chọn khoảng khác.',
    loiDem: 'Không xoá được khoảng này: {{loi}}',
    hauQua: 'Bản ghi trong khoảng đã chọn sẽ vào thùng rác và bị ẩn khỏi tra cứu, tệp xuất ngay. Khôi phục được trong 30 ngày ở thẻ Thùng rác; sau đó bị xoá vĩnh viễn, không hoàn tác được. Bản ghi về chính việc xoá và khôi phục luôn được giữ.',
    nutXacNhan: 'Xoá {{so}} bản ghi',
    dangXoa: 'Đang xoá...',
    daXoa: 'Đã chuyển {{so}} bản ghi vào thùng rác.',
  },

  // ─────────── Đầu mối liên hệ của tenant — ADR-0059 mục 6a.3 ───────────
  lienHe: {
    tieuDe: 'Đầu mối liên hệ',
    mo: 'Người nhận thông báo của tenant theo từng loại. Với tenant on-premise, đây là kênh duy nhất báo license sắp hết hạn.',
    caption: 'Đầu mối liên hệ của tenant',
    cotTen: 'Họ tên',
    cotLoai: 'Loại',
    cotThongBao: 'Nhận thông báo',
    cotCapNhat: 'Cập nhật',
    chinh: 'Đầu mối chính',
    khongNhan: 'Không nhận thông báo',
    loai: {
      ADMIN: 'Quản trị',
      CONTRACT_OWNER: 'Phụ trách hợp đồng',
      TECHNICAL: 'Kỹ thuật',
      SECURITY: 'Bảo mật',
      BILLING: 'Thanh toán',
    },
    themDauMoi: 'Thêm đầu mối',
    luuDauMoi: 'Lưu đầu mối',
    suaDauMoi: 'Sửa đầu mối',
    xoaDauMoi: 'Xoá đầu mối',
    dialogThem: 'Thêm đầu mối liên hệ',
    dialogSua: 'Sửa đầu mối {{ten}}',
    fLoai: 'Loại đầu mối',
    fTen: 'Họ tên hoặc tên bộ phận',
    fTenTrong: 'Nhập họ tên hoặc tên bộ phận.',
    fEmail: 'Email',
    fEmailGoiY: 'Nên dùng hộp thư nhóm để người nghỉ việc không làm rơi thông báo.',
    fEmailViDu: 'baomat@congty.vn',
    fEmailTrong: 'Nhập địa chỉ email.',
    fEmailSai: 'Nhập địa chỉ email đầy đủ, ví dụ baomat@congty.vn.',
    fEmailNhanThu: 'Email nhận thư (nếu khác)',
    fDienThoai: 'Điện thoại',
    fNhanThongBao: 'Nhận thông báo',
    fNhanThongBaoGoiY: 'Bỏ trống nếu đầu mối này chỉ để liên hệ.',
    fChinh: 'Đầu mối chính của loại này',
    fChinhMo: 'Mỗi loại chỉ một đầu mối chính; chọn ở đây sẽ bỏ chọn đầu mối chính cũ.',
    rong: 'Chưa có đầu mối liên hệ',
    rongMo: 'Thêm ít nhất một đầu mối để thông báo bảo mật và license tới được tenant.',
    loi: 'Không tải được đầu mối: {{loi}}',
    xacNhanXoaTieuDe: 'Xoá đầu mối {{ten}}?',
    xacNhanXoaThan: 'Đầu mối này thôi nhận các thông báo: {{thongBao}}. Không hoàn tác được.',
    daThem: 'Đã thêm đầu mối.',
    daCapNhat: 'Đã lưu đầu mối.',
    daXoa: 'Đã xoá đầu mối.',
  },

  // ─────────── Thông báo và người nhận — ADR-0059 mục 6a ───────────
  thongBao: {
    nguoiNhanTieuDe: 'Người nhận theo loại thông báo',
    guiThu: 'Gửi thử',
    guiThuDungBanDaLuu: 'Gửi thử dùng người nhận đã lưu, chưa gồm phần đang sửa.',
    daGuiThu: 'Đã gửi thông báo thử tới {{so}} người nhận.',
    guiThuChuaCoNguoiNhan: 'Loại này chưa có người nhận: thông báo thử chỉ hiện trong cổng quản trị.',
    guiThuLoi: 'Gửi thử không thành công. Kiểm tra kênh gửi rồi thử lại.',
    ganDayTieuDe: 'Thông báo gần đây',
    cotThoiDiem: 'Thời điểm',
    cotMucDo: 'Mức độ',
    cotNoiDung: 'Nội dung',
    cotNguoiNhan: 'Người nhận',
    soNguoiNhan: '{{so}} người nhận',
    trangThai: {
      SENT: 'Đã gửi',
      NO_RECIPIENT: 'Chưa có người nhận',
      FAILED: 'Gửi lỗi',
    },
    rong: 'Chưa có thông báo nào',
    rongMo: 'Thông báo từ cảnh báo hạ tầng, luật audit và job license sẽ hiện ở đây.',
    loi: 'Không tải được thông báo: {{loi}}',
  },

  // ─────────── Chính sách nền tảng — ADR-0059 mục 8 ───────────
  chinhSach: {
    the: {
      goi: 'Theo gói',
      nenTang: 'Nền tảng',
      matKhau: 'Mật khẩu',
      blocklist: 'IP bị chặn',
      thongBao: 'Thông báo',
    },
    goi: {
      chon: 'Gói đang xem',
      coNhap: '{{goi}} (chưa lưu)',
      tieuDe: 'Chính sách gói {{goi}}',
    },
    nenTang: {
      truyCapTieuDe: 'Xác thực và giới hạn',
      auditTieuDe: 'Nhật ký và phát hiện dò mật khẩu',
      lienHeTieuDe: 'Đầu mối liên hệ',
    },
    matKhau: {
      tieuDe: 'Mức sàn chính sách mật khẩu',
    },
    dongBo: {
      SYNCED: 'Đã đồng bộ Keycloak',
      PENDING: 'Đang chờ đồng bộ',
      DISABLED: 'Chưa đồng bộ vào Keycloak',
      PENDING_MO: 'Giá trị đã lưu nhưng Keycloak chưa nhận. Thường xong trong vài phút; nếu không, xem nhật ký của tenant-service.',
      DISABLED_MO: 'Đồng bộ realm đang tắt: giá trị chỉ lưu ở nền tảng. Vận hành cần áp bằng tệp realm cho tới khi bật client platform-realm-config.',
    },
    blocklist: {
      tieuDe: 'IP bị chặn toàn nền tảng',
      canhBao: 'Chặn có hiệu lực ngay ở mọi tenant và cả cổng quản trị.',
      cotLyDo: 'Lý do',
      cotTao: 'Người chặn',
      go: 'Gỡ chặn',
      nhanGo: 'Gỡ chặn {{cidr}}',
      xacNhanGoTieuDe: 'Gỡ chặn {{cidr}}?',
      xacNhanGoThan: 'Truy cập từ dải này lại đi qua các bước kiểm của tenant như bình thường. Không hoàn tác được; muốn chặn lại phải thêm dòng mới.',
      daGo: 'Đã gỡ chặn {{cidr}}.',
      rong: 'Chưa chặn dải IP nào',
      rongMo: 'Dải IP chặn ở đây áp cho mọi tenant. Chặn từng tenant thì dùng tab Bảo mật của tenant đó.',
      loi: 'Không tải được danh sách chặn: {{loi}}',
    },
  },

  nguoiDung: {
    // Màn "Cấu hình người dùng" của cổng tenant (G2-D1-1, G2-D1-2).
    trangThai: {
      ACTIVE: 'Đang hoạt động',
      INVITED: 'Đã mời',
      DISABLED: 'Đã khoá',
    },
    tatCaTrangThai: 'Tất cả trạng thái',
    bangCaption: 'Danh sách người dùng của tổ chức',
    cotNguoiDung: 'Người dùng',
    cotVaiTro: 'Vai trò',
    cotCapNhat: 'Cập nhật lúc',
    ban: '(bạn)',
    chuaDocDuocEmail: 'Không đọc được email',
    loiDanhSach: 'Không tải được danh sách người dùng: {{loi}}',
    loiTaiThem: 'Không tải được thêm: {{loi}}',
    rongThat: 'Tổ chức chưa có người dùng nào',
    rongThatMo: 'Mời đồng nghiệp bằng email công ty. Họ nhận liên kết đặt mật khẩu, có hạn 72 giờ.',
    rongLoc: 'Không có người dùng nào ở trạng thái này',
    moiNguoiDauTien: 'Mời người dùng đầu tiên',
    dangHien: '{{so}} người dùng',
    dangHienCoThem: 'Đang hiện {{so}} người dùng, còn nữa',
    taiThem: 'Tải thêm',
    // Thao tác trên dòng.
    suaThongTin: 'Sửa thông tin',
    guiLaiLoiMoi: 'Gửi lại lời mời',
    ganVaiTro: 'Gán vai trò',
    khoaTaiKhoan: 'Khoá tài khoản',
    moKhoaTaiKhoan: 'Mở khoá tài khoản',
    // Hộp mời.
    moiNguoiDung: 'Mời người dùng',
    moiTieuDe: 'Mời người dùng',
    nhacNgoaiDomain: 'Email thuộc {{domain}}, ngoài domain đã xác minh. Kiểm tra kỹ: người nhận sẽ vào được tổ chức.',
    fEmail: 'Email đăng nhập',
    fEmailGoiY: 'Phải thuộc domain đã xác minh của tổ chức.',
    fEmailTrong: 'Nhập email của người cần mời.',
    fEmailSai: 'Email chưa đúng định dạng. Nhập dạng ten@congty.vn.',
    fEmailKhongSua: 'Email là tên đăng nhập nên không sửa được.',
    // Email nhận thư tách khỏi email đăng nhập (ADR-0101).
    fEmailNhanThu: 'Email nhận thư (nếu khác)',
    nhanThu: 'Nhận thư: {{email}}',
    doiEmailNhanThu: 'Đổi email nhận thư',
    xnDoiEmailTieuDe: 'Đổi email nhận thư của {{ten}}?',
    xnDoiEmailThan:
      'Thư đặt lại mật khẩu và mọi thông báo sẽ gửi tới địa chỉ mới. Người này bị đăng xuất khỏi mọi phiên và phải xác minh địa chỉ mới.',
    fEmailNhanThuMoi: 'Email nhận thư mới',
    fEmailNhanThuTrong: 'Nhập email nhận thư mới.',
    fEmailNhanThuTrung: 'Đây đã là email nhận thư hiện tại.',
    fEmailNhanThuKhongDung: 'Không dùng được email này. Nhập địa chỉ khác.',
    daDoiEmailNhanThu: 'Đã đổi email nhận thư của {{ten}}.',
    daDoiChuaGuiXacMinh:
      'Đã đổi email nhận thư của {{ten}} nhưng chưa gửi được thư xác minh. Người này phải xác minh ở lần đăng nhập sau.',
    fPhongBan: 'Phòng ban',
    fPhongBanTrong: 'Chưa thuộc phòng ban nào',
    fPhongBanLoi: 'Không tải được danh sách phòng ban: {{loi}}',
    fPhongBanKhongCo: 'Không có phòng ban nào khớp',
    cotPhongBan: 'Phòng ban',
    chuaCoPhongBan: 'Chưa có',
    phongBanKhongRo: 'Không rõ',
    fTenHienThi: 'Tên hiển thị',
    fTenTrong: 'Nhập tên hiển thị.',
    fQuaDai: 'Dài quá {{so}} ký tự. Rút gọn lại.',
    // Ô tích "Quản trị tổ chức" là role lớp 1; ô "Vai trò" là vai trò lớp 2 của tổ chức.
    fQuanTriMo: 'Toàn quyền trong tổ chức, không cần gán vai trò',
    fQuanTriLuuY: 'Phải cài ứng dụng xác thực ở lần đăng nhập đầu. Không đổi được sau khi mời.',
    fVaiTro: 'Vai trò',
    fVaiTroChon: 'Chọn vai trò',
    fVaiTroKhongKhop: 'Không có vai trò nào khớp',
    fVaiTroRong: 'Tổ chức chưa có vai trò nào. Có thể mời trước, tạo vai trò rồi gán sau.',
    fVaiTroLoiTai: 'Không tải được danh sách vai trò. Mở lại ô để thử lại, hoặc mời trước rồi gán vai trò sau.',
    chuaGanVaiTro: 'Chưa gán vai trò',
    vaiTroKhongRo: 'Không rõ',
    guiLoiMoi: 'Gửi lời mời',
    dangGuiLoiMoi: 'Đang gửi...',
    // Hộp sửa.
    suaTieuDe: 'Sửa thông tin của {{ten}}',
    luuThongTin: 'Lưu thông tin',
    // Hộp xác nhận khoá.
    xnKhoaTieuDe: 'Khoá tài khoản của {{ten}}?',
    xnKhoaThan:
      'Người này bị đăng xuất ngay khỏi mọi phiên và không đăng nhập được cho tới khi một quản trị mở khoá. Hồ sơ và dữ liệu vẫn được giữ.',
    xnKhoaThanDaMoi:
      'Liên kết trong email mời ngừng dùng được, người này không đăng nhập được cho tới khi một quản trị mở khoá. Hồ sơ vẫn được giữ.',
    // Toast.
    daGuiLoiMoi: 'Đã gửi lời mời tới {{email}}.',
    daTaoChuaGuiEmail:
      'Đã thêm {{email}} nhưng chưa gửi được email mời. Dùng "Gửi lại lời mời" để thử lại.',
    chuaGanDuocVaiTro: 'Đã mời {{email}} nhưng chưa gán được vai trò: {{loi}}',
    ganLaiVaiTro: 'Gán lại vai trò',
    daGuiLaiLoiMoi: 'Đã gửi lại lời mời tới {{email}}. Hôm nay còn gửi lại được {{so}} lần.',
    daGuiLaiLoiMoiHetLuot: 'Đã gửi lại lời mời tới {{email}}. Đây là lần gửi lại cuối trong hôm nay.',
    hetLuotGuiLai: 'Đã gửi lại lời mời 5 lần hôm nay cho người này. Thử lại từ 0 giờ ngày mai.',
    daLuu: 'Đã lưu thông tin của {{ten}}.',
    daKhoa: 'Đã khoá tài khoản của {{ten}}.',
    daMoKhoa: 'Đã mở khoá tài khoản của {{ten}}.',
    // Xác thực hai lớp của từng người (ADR-0015, mức "người được chọn").
    mfa: {
      cot: 'Xác thực hai lớp',
      batBuoc: 'Bắt buộc',
      daCai: 'Đã cài',
      chuaCai: 'Chưa cài',
      khongRo: 'Không rõ',
      batBuocMuc: 'Bắt buộc xác thực hai lớp',
      boBatBuocMuc: 'Bỏ bắt buộc xác thực hai lớp',
      xnTieuDe: 'Bắt buộc {{ten}} xác thực hai lớp?',
      xnThan:
        'Người này bị đăng xuất ngay khỏi mọi phiên. Lần đăng nhập sau phải nhập mã OTP từ ứng dụng xác thực; ai chưa cài ứng dụng thì phải cài trước khi vào được.',
      xnThanChuaCai:
        'Người này chưa cài ứng dụng xác thực. Họ bị đăng xuất ngay khỏi mọi phiên, và ở lần đăng nhập sau phải cài ứng dụng rồi nhập mã OTP mới vào được.',
      xnThanBan:
        'Bạn bị đăng xuất ngay khỏi mọi phiên và phải nhập mã OTP ở lần đăng nhập sau. Nếu chưa cài ứng dụng xác thực thì phải cài trước khi vào được.',
      daBatBuoc: 'Đã bắt buộc {{ten}} xác thực hai lớp.',
      daBoBatBuoc: 'Đã bỏ bắt buộc xác thực hai lớp với {{ten}}.',
    },
    // Lấy lại tài khoản: đặt lại mật khẩu qua email, gỡ khoá tạm, đặt lại xác thực hai lớp.
    layLai: {
      tamKhoa: 'Tạm khoá đăng nhập',
      guiDatLaiMatKhau: 'Gửi email đặt lại mật khẩu',
      moKhoaDangNhap: 'Mở khoá đăng nhập',
      moKhoaDangNhapMo: 'Bị khoá tạm vì nhập sai mật khẩu nhiều lần',
      datLaiMfa: 'Đặt lại xác thực hai lớp',
      // Hộp xác nhận gửi email đặt lại mật khẩu.
      xnDatLaiTieuDe: 'Gửi email đặt lại mật khẩu cho {{ten}}?',
      xnDatLaiThan:
        'Thư gửi tới {{email}}, kèm liên kết để người này tự đặt mật khẩu mới. Liên kết có hạn 1 giờ. Mật khẩu hiện tại vẫn dùng được cho tới khi họ đặt mật khẩu mới.',
      loiXungDot:
        'Người này chưa nhận lời mời hoặc vừa bị khoá nên không gửi được. Danh sách đã tải lại: người chưa nhận lời mời thì dùng "Gửi lại lời mời", người bị khoá thì mở khoá tài khoản trước.',
      loiQuaNhieu:
        'Hôm nay đã gửi đủ 5 email đặt lại mật khẩu cho người này, mức tối đa mỗi ngày. Nhắc họ kiểm tra hộp thư (cả thư mục spam), hoặc thử lại vào ngày mai.',
      loiMayChuEmail:
        'Máy chủ email đang lỗi nên chưa gửi được thư. Thử lại sau ít phút; vẫn lỗi thì báo đội vận hành nền tảng.',
      // Hộp xác nhận đặt lại xác thực hai lớp.
      xnMfaTieuDe: 'Đặt lại xác thực hai lớp của {{ten}}?',
      xnMfaThan:
        'Ứng dụng xác thực và mã khôi phục của người này bị xoá, họ bị đăng xuất ngay khỏi mọi phiên. Lần đăng nhập sau chỉ cần mật khẩu, cho tới khi họ tự cài lại ứng dụng xác thực. Không hoàn tác được.',
      xnMfaThanBatBuoc:
        'Ứng dụng xác thực và mã khôi phục của người này bị xoá, họ bị đăng xuất ngay khỏi mọi phiên. Vì đang bị bắt buộc xác thực hai lớp, lần đăng nhập sau họ phải cài lại ứng dụng xác thực mới vào được. Không hoàn tác được.',
      // Toast.
      daGuiDatLai: 'Đã gửi email đặt lại mật khẩu tới {{email}}.',
      daMoKhoaDangNhap: 'Đã mở khoá đăng nhập cho {{ten}}.',
      daDatLaiMfa: 'Đã đặt lại xác thực hai lớp của {{ten}}.',
    },
  },
  coCauToChuc: {
    // Màn "Cơ cấu tổ chức" của cổng tenant (G2-D1-17, ADR-0014, ADR-0067).
    bangCaption: 'Cây phòng ban của tổ chức',
    timNhan: 'Tìm phòng ban',
    timPlaceholder: 'Tìm theo tên phòng ban',
    moRongHet: 'Mở rộng tất cả',
    thuGonHet: 'Thu gọn tất cả',
    cotPhongBan: 'Phòng ban',
    cotNguoiDung: 'Người dùng',
    cotPhongCon: 'Phòng ban con',
    cotTaoLuc: 'Tạo lúc',
    cap: 'Cấp {{so}}',
    chuaCoAi: 'Chưa có ai',
    loiDanhSach: 'Không tải được cây phòng ban: {{loi}}',
    rongThat: 'Tổ chức chưa có phòng ban nào',
    rongThatMo:
      'Dựng cơ cấu từ phòng ban cấp cao nhất, ví dụ Khối Tài chính, rồi thêm các phòng ban con bên dưới.',
    rongLoc: 'Không có phòng ban nào khớp “{{tuKhoa}}”',
    themPhongBan: 'Thêm phòng ban',
    themPhongBanDauTien: 'Thêm phòng ban đầu tiên',
    themPhongBanCon: 'Thêm phòng ban con',
    suaPhongBan: 'Sửa phòng ban',
    xoaPhongBan: 'Xoá phòng ban',
    chuaXoaMoTa: 'Còn người dùng hoặc phòng ban con',
    dangHien: '{{so}} phòng ban',
    dangHienLoc: 'Đang hiện {{so}} trong {{tong}} phòng ban',
    // Hộp thêm, sửa.
    taoTieuDe: 'Thêm phòng ban',
    taoConTieuDe: 'Thêm phòng ban con của "{{ten}}"',
    suaTieuDe: 'Sửa phòng ban "{{ten}}"',
    dangTaiChiTiet: 'Đang tải phòng ban...',
    loiChiTiet: 'Không tải được phòng ban: {{loi}}',
    xungDot: 'Phòng ban vừa được người khác sửa, tên hiện tại là "{{ten}}". Kiểm tra lại rồi lưu lần nữa.',
    fTen: 'Tên phòng ban',
    fTenPlaceholder: 'Ví dụ: Phòng Kế toán',
    fTenTrong: 'Nhập tên phòng ban.',
    fTenDai: 'Dài quá {{so}} ký tự. Rút gọn lại.',
    fCha: 'Phòng ban cha',
    fChaTrong: 'Không có, đây là phòng ban cấp cao nhất',
    fChaGoiY: 'Để trống thì đây là phòng ban cấp cao nhất. Cây có tối đa {{so}} cấp.',
    fChaQuaSau: 'Chọn phòng ban này thì cây vượt {{so}} cấp',
    fChaQuaSauNgan: 'Vượt {{so}} cấp',
    fChaKhongCo: 'Không có phòng ban nào khớp',
    luuTao: 'Thêm phòng ban',
    luuSua: 'Lưu thay đổi',
    daTao: 'Đã thêm phòng ban "{{ten}}".',
    daLuu: 'Đã lưu phòng ban "{{ten}}".',
    daXoa: 'Đã xoá phòng ban "{{ten}}".',
    // Hộp xoá.
    xnXoaTieuDe: 'Xoá phòng ban "{{ten}}"?',
    xnXoaThan: 'Phòng ban bị xoá khỏi cơ cấu tổ chức. Không hoàn tác được.',
    chuaXoaTieuDe: 'Chưa xoá được phòng ban "{{ten}}"',
    chuaXoaNguoi: 'Còn {{so}} người dùng thuộc phòng ban này. Chuyển họ sang phòng ban khác ở Cấu hình người dùng.',
    chuaXoaCon: 'Còn {{so}} phòng ban con. Chuyển hoặc xoá các phòng ban con trước.',
  },

  vaiTroLop2: {
    // Màn "Cấu hình vai trò" của cổng tenant và ngăn "Gán vai trò" ở màn người dùng (G2-D1-4).
    bangCaption: 'Danh sách vai trò của tổ chức',
    timNhan: 'Tìm vai trò',
    timPlaceholder: 'Tìm theo mã hoặc tên vai trò',
    cotVaiTro: 'Vai trò',
    cotQuyen: 'Quyền',
    cotNguoiDung: 'Người dùng',
    cotTaoLuc: 'Tạo lúc',
    soQuyen: '{{so}} quyền',
    themModule: '+{{so}} module',
    chuaCoQuyen: 'Chưa có quyền',
    chuaAiDung: 'Chưa ai dùng',
    loiDanhSach: 'Không tải được danh sách vai trò: {{loi}}',
    rongThat: 'Tổ chức chưa có vai trò nào',
    rongThatMo:
      'Vai trò gom các quyền theo việc người dùng làm, ví dụ Kế toán viên hay Duyệt hồ sơ. Tạo vai trò rồi gán cho người dùng ở Cấu hình người dùng.',
    rongLoc: 'Không có vai trò nào khớp “{{tuKhoa}}”',
    taoVaiTroDauTien: 'Tạo vai trò đầu tiên',
    dangHien: '{{so}} vai trò',
    dangHienLoc: '{{so}} trên {{tong}} vai trò',
    // Thao tác trên dòng.
    phanQuyen: 'Phân quyền',
    doiTen: 'Đổi tên',
    xoaVaiTro: 'Xoá vai trò',
    // Hộp tạo.
    taoVaiTro: 'Tạo vai trò',
    taoTieuDe: 'Tạo vai trò',
    dangTao: 'Đang tạo...',
    fTen: 'Tên vai trò',
    fTenGoiY: 'Tên người dùng thấy, ví dụ Kế toán trưởng.',
    fTenTrong: 'Nhập tên vai trò.',
    fMa: 'Mã vai trò',
    fMaTuSinh: 'Tự sinh từ tên. Không đổi được sau khi tạo.',
    fMaChoTen: 'Gõ tên để sinh mã.',
    fTenKhongSinhMa: 'Tên cần có ít nhất một chữ cái hoặc chữ số để sinh mã.',
    fTrungMa: 'Đã có vai trò mang mã {{ma}}. Đặt tên khác.',
    fMaKhongSua: 'Mã vai trò không đổi được sau khi tạo.',
    fQuaDai: 'Dài quá {{so}} ký tự. Rút gọn lại.',
    fQuyen: 'Quyền (tuỳ chọn)',
    fQuyenGoiY: 'Để trống thì phân quyền sau bằng mục Phân quyền ở dòng của vai trò.',
    daChonSoQuyen: 'Đã chọn {{so}} quyền',
    // Hộp đổi tên.
    doiTenTieuDe: 'Đổi tên vai trò {{ten}}',
    luuTen: 'Lưu tên',
    // Ngăn phân quyền.
    phanQuyenTieuDe: 'Phân quyền cho {{ten}}',
    phanQuyenMo: 'Chọn quyền theo từng module. Tích ô ở đầu module để chọn hoặc bỏ cả module. Vai trò:',
    luuPhanQuyen: 'Lưu phân quyền',
    quyenNgoaiDanhMuc:
      'Vai trò đang giữ {{so}} quyền của module đã gỡ khỏi tổ chức. Lưu phân quyền sẽ bỏ các quyền này:',
    dangTaiQuyen: 'Đang tải danh mục quyền',
    loiDanhMucQuyen: 'Không tải được danh mục quyền: {{loi}}',
    danhMucRong: 'Chưa có quyền nào để gán',
    danhMucRongMo:
      'Tổ chức chưa cài module nào. Quyền hiện ở đây khi đội vận hành nền tảng cài module cho tổ chức.',
    // Nhóm quyền do nền tảng cấp, không thuộc module nào (ADR-0104).
    quyenNenTang: {
      nhom: 'Quản lý module',
      xem: 'Xem danh sách module',
      nop: 'Nộp module',
      cai: 'Cài, nâng cấp và gỡ module',
    },
    // Hộp xác nhận xoá.
    xnXoaTieuDe: 'Xoá vai trò {{ten}}?',
    xnXoaThanCoNguoi:
      '{{so}} người đang có vai trò này sẽ mất ngay mọi quyền mà vai trò này cấp. Xoá rồi không khôi phục được.',
    xnXoaThanChuaAi: 'Chưa ai có vai trò này. Xoá rồi không khôi phục được.',
    // Ngăn gán vai trò cho người dùng.
    ganTieuDe: 'Gán vai trò cho {{ten}}',
    ganMo: 'Người dùng có mọi quyền của các vai trò được chọn.',
    ganLegend: 'Vai trò của người dùng',
    ganXungDot:
      'Vai trò của {{ten}} vừa được một quản trị khác thay đổi. Đã tải bản mới nhất — xem lại lựa chọn rồi lưu lại.',
    ganRong: 'Tổ chức chưa có vai trò nào',
    ganRongMo: 'Tạo vai trò ở Cấu hình vai trò trước, rồi quay lại đây để gán.',
    denCauHinhVaiTro: 'Đến Cấu hình vai trò',
    dangTaiVaiTro: 'Đang tải vai trò',
    loiTaiVaiTroNguoi: 'Không tải được vai trò của người dùng: {{loi}}',
    daChonSoVaiTro: 'Đã chọn {{so}} vai trò',
    luuVaiTro: 'Lưu vai trò',
    // Toast.
    daTao: 'Đã tạo vai trò {{ten}}.',
    daDoiTen: 'Đã đổi tên vai trò thành {{ten}}.',
    daLuuQuyen: 'Đã lưu phân quyền của {{ten}}.',
    daXoa: 'Đã xoá vai trò {{ten}}.',
    daGanVaiTro: 'Đã lưu vai trò của {{ten}}.',
  },
  // ─────────── Vai trò nền tảng — ADR-0074, G2-D1-19 ───────────
  // Vai trò của nhân sự FoxAI vận hành nền tảng, cổng `nen-tang`. Khác `vaiTroLop2` ở trên: đó là
  // vai trò lớp 2 của một tổ chức khách. Chữ ở đây nói "nhân sự", không nói "người dùng".
  vaiTroNenTang: {
    bangCaption: 'Danh sách vai trò của nhân sự nền tảng',
    timNhan: 'Tìm vai trò nền tảng',
    timPlaceholder: 'Tìm theo mã hoặc tên vai trò',

    // Cột bảng.
    cotVaiTro: 'Vai trò',
    cotQuyen: 'Quyền',
    cotNhanSu: 'Nhân sự',
    cotTaoLuc: 'Tạo lúc',
    nhanHeThong: 'Hệ thống',
    chuaCoQuyen: 'Chưa có quyền',
    chuaAiDung: 'Chưa ai giữ',
    soQuyen: '{{so}} quyền',
    themPhanHe: '+{{so}} phân hệ',
    dangHien: 'Đang hiện {{so}} vai trò',
    dangHienLoc: 'Đang hiện {{so}} trong {{tong}} vai trò',

    // Bốn trạng thái của bảng.
    rongThat: 'Chưa có vai trò nền tảng nào',
    rongThatMo: 'Tạo vai trò để gom quyền theo việc, rồi gán cho nhân sự nền tảng.',
    rongLoc: 'Không có vai trò nào khớp “{{tuKhoa}}”',
    loiDanhSach: 'Không tải được danh sách vai trò: {{loi}}',

    // Thao tác.
    taoVaiTro: 'Tạo vai trò',
    taoVaiTroDauTien: 'Tạo vai trò đầu tiên',
    taoTieuDe: 'Tạo vai trò nền tảng',
    dangTao: 'Đang tạo...',
    doiTen: 'Đổi tên',
    doiTenTieuDe: 'Đổi tên vai trò {{ten}}',
    luuTen: 'Lưu tên',
    phanQuyen: 'Phân quyền',
    phanQuyenTieuDe: 'Phân quyền cho {{ten}}',
    phanQuyenMo: 'Chọn quyền cho vai trò',
    luuPhanQuyen: 'Lưu phân quyền',
    xoaVaiTro: 'Xoá vai trò',

    // Trường của form.
    fTen: 'Tên vai trò',
    fTenGoiY: 'Tên hiển thị, ví dụ Vận hành hạ tầng.',
    fTenTrong: 'Nhập tên vai trò.',
    fMa: 'Mã vai trò',
    fMaTuSinh: 'Tự sinh từ tên. Không đổi được sau khi tạo: mọi chỗ kiểm quyền đọc theo mã này.',
    fMaChoTen: 'Gõ tên để sinh mã.',
    fTenKhongSinhMa: 'Tên cần có ít nhất một chữ cái hoặc chữ số để sinh mã.',
    fTrungMa: 'Đã có vai trò mang mã {{ma}}. Đặt tên khác.',
    fMaKhongSua: 'Mã không đổi được sau khi tạo: mọi chỗ kiểm quyền đọc theo mã này.',
    fQuaDai: 'Tối đa {{so}} ký tự.',
    fQuyen: 'Quyền (tuỳ chọn)',
    fQuyenGoiY: 'Chọn ngay, hoặc để trống rồi phân quyền sau ở menu của dòng.',

    // Ngăn chọn quyền.
    dangTaiQuyen: 'Đang tải danh mục quyền',
    loiDanhMucQuyen: 'Không tải được danh mục quyền: {{loi}}',
    danhMucRong: 'Máy chủ trả danh mục quyền rỗng. Đây là lỗi máy chủ, không phải thiếu cấu hình.',
    daChonSoQuyen: 'Đã chọn {{so}} quyền',
    heThongChiXem: 'Vai trò hệ thống giữ làm đường lùi khi cấu hình sai, nên chỉ xem được tập quyền. Tạo vai trò mới nếu cần tập quyền khác.',
    quyenNgoaiDanhMuc: '{{so}} quyền của vai trò này không còn trong danh mục nên sẽ bị bỏ khi lưu:',

    // Nhãn nhóm quyền. Mã nhóm do backend trả; bảng này đổi thành chữ người đọc.
    nhomTenant: 'Tổ chức khách',
    nhomGoi: 'Gói dịch vụ',
    nhomLicense: 'License',
    nhomViec: 'Hàng đợi việc',
    nhomAudit: 'Nhật ký',
    nhomChinhSach: 'Chính sách nền tảng',
    nhomBaoMat: 'Bảo mật tổ chức',
    nhomLienHe: 'Đầu mối liên hệ',
    nhomNhanSu: 'Nhân sự nền tảng',
    nhomVaiTro: 'Vai trò nền tảng',
    nhomKhoaApi: 'Khoá API',
    nhomModelAi: 'Model AI',
    nhomModule: 'Module',

    // Hộp xác nhận xoá. Thân nói hậu quả và tính không hoàn tác.
    xnXoaTieuDe: 'Xoá vai trò {{ten}}?',
    xnXoaThanChuaAi: 'Chưa ai giữ vai trò này nên không ai mất quyền. Xoá rồi không hoàn tác được.',
    xnXoaThanConNguoiGiu: 'Vai trò này còn {{so}} người đang giữ nên chưa xoá được. Gỡ vai trò khỏi những người đó ở màn Người dùng rồi xoá lại, để thấy rõ ai sẽ mất quyền.',
    xnXoaThanHeThong: 'Vai trò hệ thống giữ làm đường lùi khi cấu hình sai nên không xoá được. Tạo vai trò mới nếu cần tập quyền khác.',

    // Ngăn gán vai trò cho nhân sự (mở từ màn Người dùng).
    ganTieuDe: 'Vai trò nền tảng của {{ten}}',
    ganMo: 'Đã gán vai trò thì quyền của người này đúng bằng tập quyền của các vai trò được chọn. Bỏ hết ô tích thì họ quay về quyền mặc định của nhóm tài khoản.',
    ganLegend: 'Chọn vai trò nền tảng',
    ganXungDot: 'Vai trò của {{ten}} vừa được một quản trị khác thay đổi. Danh sách đã tải lại theo bản mới; xem lại rồi lưu.',
    ganRong: 'Chưa có vai trò nền tảng nào để gán',
    ganRongMo: 'Tạo vai trò ở màn Vai trò nền tảng, rồi quay lại gán cho người này.',
    denManVaiTro: 'Tới màn Vai trò nền tảng',
    dangTaiVaiTro: 'Đang tải vai trò',
    loiTaiVaiTroNhanSu: 'Không tải được vai trò: {{loi}}',
    daChonSoVaiTro: 'Đã chọn {{so}} vai trò',
    luuVaiTro: 'Lưu vai trò',

    // Toast.
    daTao: 'Đã tạo vai trò {{ten}}.',
    daDoiTen: 'Đã đổi tên vai trò thành {{ten}}.',
    daLuuQuyen: 'Đã lưu phân quyền của {{ten}}.',
    daXoa: 'Đã xoá vai trò {{ten}}.',
    daGanVaiTro: 'Đã lưu vai trò của {{ten}}.',
  },
  // ─────────── Plugin của tổ chức — đặc tả chức năng module của tenant mục 7, ADR-0117 ───────────
  // ─────────── Khoá API của tổ chức — ADR-0068, G2-D1-18 ───────────
  khoaApi: {
    bangCaption: 'Danh sách khoá API của tổ chức',
    timNhan: 'Tìm khoá API',
    timPlaceholder: 'Tìm theo tên hoặc tiền tố khoá',
    locTrangThai: 'Lọc theo trạng thái',
    tatCaTrangThai: 'Mọi trạng thái',
    cotKhoa: 'Khoá',
    cotQuyen: 'Quyền',
    cotTrangThai: 'Trạng thái',
    cotHetHan: 'Hết hạn',
    cotDungLanCuoi: 'Dùng lần cuối',
    cotTaoLuc: 'Tạo lúc',
    tienToHien: '{{tienTo}}…',
    soQuyen: '{{so}} quyền',
    themModule: '+{{so}} module',
    khongHetHan: 'Không hết hạn',
    chuaDung: 'Chưa dùng lần nào',
    ttHoatDong: 'Đang hoạt động',
    ttHetHan: 'Hết hạn',
    ttDaThuHoi: 'Đã thu hồi',
    loiDanhSach: 'Không tải được danh sách khoá API: {{loi}}',
    rongThat: 'Tổ chức chưa có khoá API nào',
    rongThatMo:
      'Tạo một khoá cho mỗi hệ thống bên ngoài cần gọi API của tổ chức, để thu hồi riêng từng khoá khi cần.',
    rongLoc: 'Không có khoá API nào khớp bộ lọc',
    taoKhoaDauTien: 'Tạo khoá API đầu tiên',
    dangHien: '{{so}} khoá',
    dangHienLoc: '{{so}} trên {{tong}} khoá',
    // Hộp tạo.
    taoKhoa: 'Tạo khoá API',
    taoTieuDe: 'Tạo khoá API',
    dangTao: 'Đang tạo...',
    fTen: 'Tên hệ thống dùng khoá',
    fTenGoiY: 'Ví dụ: Cổng dịch vụ công, Phần mềm văn thư.',
    fTenTrong: 'Nhập tên hệ thống dùng khoá.',
    fTenDai: 'Dài quá {{so}} ký tự. Rút gọn lại.',
    fHan: 'Hạn dùng',
    fHanGoiY: 'Khoá hết hạn ngày {{ngay}}.',
    fHanKhongGoiY: 'Khoá dùng tới khi thu hồi. Nên đặt hạn để giới hạn thiệt hại nếu khoá bị lộ.',
    han30: '30 ngày',
    han90: '90 ngày',
    han180: '180 ngày',
    han365: '1 năm',
    hanKhong: 'Không hết hạn',
    fQuyen: 'Quyền',
    fQuyenGoiY: 'Chỉ chọn quyền hệ thống bên ngoài thật sự cần. Khoá chỉ gọi được API của module, không gọi được API quản trị.',
    fQuyenTrong: 'Chọn ít nhất một quyền.',
    daChonSoQuyen: 'Đã chọn {{so}} quyền',
    loiTheoO: 'Chưa tạo được khoá. Sửa các chỗ được đánh dấu rồi tạo lại.',
    // Hộp hiện bí mật.
    biMatTieuDe: 'Khoá API của {{ten}}',
    biMatCanhBao:
      'Khoá chỉ hiện một lần này. Chép và lưu vào nơi an toàn của hệ thống bên ngoài — máy chủ chỉ giữ bản băm nên không lấy lại được. Mất khoá thì thu hồi và tạo khoá mới.',
    biMatNhan: 'Khoá API',
    biMatGoiY: 'Gửi khoá ở header X-Api-Key khi gọi API của module.',
    saoChep: 'Sao chép',
    daChep: 'Đã chép',
    daChepToast: 'Đã chép khoá API.',
    khongChepDuoc: 'Trình duyệt không cho sao chép. Chọn khoá trong ô rồi chép tay.',
    daLuuKhoa: 'Tôi đã lưu khoá',
    // Thu hồi.
    thuHoiKhoa: 'Thu hồi khoá',
    dangThuHoi: 'Đang thu hồi...',
    xnTieuDe: 'Thu hồi khoá "{{ten}}"?',
    xnThan:
      'Hệ thống dùng khoá {{tienTo}}… sẽ bị từ chối sau vài giây. Thu hồi rồi không khôi phục được — cần thì tạo khoá mới.',
    daThuHoi: 'Đã thu hồi khoá "{{ten}}".',
    loiKhongCon: 'Khoá này không còn trong tổ chức, có thể vừa bị thu hồi ở phiên khác. Danh sách khoá đã được tải lại.',
  },
  // ─────────── Mức log tạm thời — ADR-0059 mục 3 ───────────
  mucLog: {
    locService: 'Lọc theo service',
    moiService: 'Mọi service',
    gioiHan: 'Tối đa {{so}} mức mỗi service, thời hạn tối đa {{phut}} phút.',
    datMuc: 'Đặt mức log',
    suaMuc: 'Sửa mức',
    xoaMuc: 'Xoá mức log',
    bangCaption: 'Mức log tạm thời đang có hiệu lực',
    cotService: 'Service',
    cotLogger: 'Logger',
    cotMuc: 'Mức',
    cotConLai: 'Còn lại',
    cotLyDo: 'Lý do',
    cotCapNhat: 'Cập nhật',
    heThong: 'Hệ thống',
    toiKhiXoa: 'Tới khi xoá',
    daHetHan: 'Đã hết hạn',
    gioPhut: '{{gio}} giờ {{phut}} phút',
    hetHanLuc: 'Hết hạn lúc {{luc}}',
    soPhut: '{{so}} phút',
    rong: 'Chưa có mức log tạm thời nào',
    rongMo: 'Mọi service đang chạy theo mức log trong cấu hình gốc. Khi cần tra sự cố, đặt DEBUG có thời hạn cho đúng logger.',
    rongLoc: '{{service}} không có mức log tạm thời nào',
    loiTai: 'Không tải được mức log: {{loi}}',
    loiDanhMuc: 'Không tải được danh sách service: {{loi}}',
    dangTaiDanhMuc: 'Đang tải danh sách service...',
    dialogDat: 'Đặt mức log tạm thời',
    dialogSua: 'Sửa mức log của {{logger}}',
    fService: 'Service',
    fLogger: 'Logger',
    fLoggerGoiY: 'Chọn từ gợi ý hoặc gõ tên gói, tên lớp. ROOT là mọi logger.',
    fLoggerViDu: 'vn.foxai.platform.tenant',
    fMuc: 'Mức',
    fMucGoiY: 'DEBUG và TRACE ghi rất nhiều log nên luôn có thời hạn.',
    fThoiHan: 'Thời hạn',
    fThoiHanGoiY: 'Tính từ lúc lưu, tối đa {{phut}} phút. Hết hạn thì logger về mức cấu hình gốc.',
    fLyDo: 'Lý do',
    fLyDoGoiY: 'Ghi sự cố đang tra hoặc mã phiếu, để người sau biết khi nào xoá được.',
    fLyDoViDu: 'Tra lỗi gửi thư thông báo cho tenant Hoà Bình, phiếu HT-2291',
    nutDatTrong: 'Đặt {{muc}} trong {{phut}} phút',
    nutDatToiKhiXoa: 'Đặt {{muc}} tới khi xoá',
    daDat: 'Đã đặt {{logger}} ở mức {{muc}}.',
    daXoa: 'Đã xoá mức log của {{logger}}.',
    xnXoaTieuDe: 'Xoá mức log của {{logger}}?',
    xnXoaThan: 'Logger {{logger}} của {{service}} sẽ về mức cấu hình gốc trên mọi instance trong vài giây. Không hoàn tác được; muốn đặt lại phải tạo mức mới.',
    loi: {
      serviceTrong: 'Chọn service cần đổi mức log.',
      loggerTrong: 'Nhập tên logger, ví dụ vn.foxai.platform.tenant hoặc ROOT.',
      loggerQuaDai: 'Tên logger tối đa {{so}} ký tự.',
      loggerSaiMau: 'Tên logger là tên gói hoặc tên lớp Java, các đoạn nối bằng dấu chấm (ví dụ org.hibernate.SQL), hoặc ROOT.',
      tatBaoVe: 'Không tắt được log của {{logger}}: logger này chạm logger audit hoặc bảo mật cần giữ. Chọn ERROR nếu muốn giảm log.',
      thoiHanBatBuoc: '{{muc}} cần thời hạn để không ghi log chi tiết mãi. Chọn một thời hạn.',
      thoiHanQuaTran: 'Thời hạn tối đa {{phut}} phút. Chọn thời hạn ngắn hơn.',
      lyDoTrong: 'Ghi lý do đổi mức log.',
      lyDoQuaDai: 'Lý do tối đa {{so}} ký tự.',
      xungDot: 'Người khác vừa đổi mức log này. Danh sách đã tải lại — đóng hộp rồi thao tác lại trên dòng mới nhất.',
    },
  },
  nhanSuNenTang: {
    // Màn "Người dùng" của nhóm quản trị nền tảng (ADR-0054, đầu việc G2-D1-3).
    // Khác nhánh `nguoiDung`: đó là người dùng của một tenant, đây là nhân sự FoxAI.
    batBuoc: 'Bắt buộc',
    bangCaption: 'Danh sách tài khoản nhân sự nền tảng',
    cotNhanSu: 'Nhân sự',
    cotVaiTro: 'Nhóm tài khoản',
    cotVaiTroNenTang: 'Vai trò',
    chuaGanVaiTro: 'Chưa gán',
    themVaiTro: '+{{so}}',
    cotMfa: 'Xác thực hai lớp',
    cotTao: 'Tạo lúc',
    ban: '(bạn)',
    tatCaTrangThai: 'Tất cả trạng thái',
    trangThai: {
      CHO_DUYET: 'Chờ duyệt',
      ACTIVE: 'Đang hoạt động',
      DISABLED: 'Đã khoá',
    },
    vaiTro: {
      PLATFORM_ADMIN: 'Quản trị nền tảng',
      AUDITOR: 'Kiểm toán',
      VENDOR: 'Đối tác',
    },
    tamKhoa: 'Tạm khoá đăng nhập',
    daCaiMfa: 'Đã cài',
    chuaCaiMfa: 'Chưa cài',
    khongRoMfa: 'Không rõ',
    // Trạng thái vùng dữ liệu.
    rongThat: 'Chưa có tài khoản nhân sự nền tảng nào',
    rongThatMo: 'Tài khoản đầu tiên tạo bằng script bootstrap. Từ người thứ hai trở đi thì đề nghị ở đây, và một quản trị nền tảng khác duyệt.',
    rongLoc: 'Không có tài khoản nào ở trạng thái này',
    loiDanhSach: 'Không tải được danh sách nhân sự nền tảng: {{loi}}',
    loiTaiThem: 'Không tải thêm được: {{loi}}',
    taiThem: 'Tải thêm',
    dangHien: '{{so}} tài khoản',
    dangHienCoThem: '{{so}} tài khoản đầu',
    // Thao tác.
    deNghi: 'Đề nghị tài khoản',
    suaThongTin: 'Sửa họ tên',
    duyet: 'Duyệt tài khoản',
    doiVaiTro: 'Đổi nhóm tài khoản',
    ganVaiTroNenTang: 'Gán vai trò nền tảng',
    khoaTaiKhoan: 'Khoá tài khoản',
    moKhoaTaiKhoan: 'Mở khoá tài khoản',
    khongDuyetDuoc: {
      'da-duyet': 'Tài khoản này đã được duyệt.',
      'tu-de-nghi': 'Bạn là người đề nghị tài khoản này, nên cần một quản trị nền tảng khác duyệt.',
    },
    guiLaiEmail: 'Gửi lại email đặt mật khẩu',
    // Hộp đề nghị.
    deNghiTieuDe: 'Đề nghị tài khoản nhân sự nền tảng',
    deNghiMo: 'Tài khoản tạo ra ở trạng thái chờ duyệt: chưa có vai trò và chưa đăng nhập được. Một quản trị nền tảng khác phải duyệt thì nó mới hoạt động.',
    nhanEmail: 'Email',
    goiYEmail: 'ten.nhanvien@ops.foxai.com.vn',
    moTaEmail: 'Cũng là tên đăng nhập. Không đổi được sau khi tạo.',
    moTaEmailDanhRieng: 'Quản trị nền tảng và kiểm toán phải dùng email thuộc domain dành riêng của nền tảng.',
    nhanHoTen: 'Họ tên',
    goiYHoTen: 'Nguyễn Văn An',
    nhanVaiTro: 'Nhóm tài khoản',
    moTaVaiTroThieu: 'Chưa chọn nhóm tài khoản thì không đề nghị được: bước duyệt cần biết gán nhóm nào.',
    moTaNhomTaiKhoan: 'Quyết người này vào được cổng nào. Vai trò cụ thể (quyền làm gì) gán sau khi duyệt.',
    nhanDeNghi: 'Đề nghị tài khoản',
    // Hộp duyệt.
    duyetTieuDe: 'Duyệt tài khoản này?',
    duyetThan: 'Gán vai trò {{vaiTro}} cho {{ten}} và bật tài khoản. Người này sẽ đăng nhập được, và phải cài xác thực hai lớp ở lần đăng nhập đầu.',
    nhanDuyet: 'Duyệt tài khoản',
    // Hộp khoá.
    khoaTieuDe: 'Khoá tài khoản này?',
    khoaThan: '{{ten}} bị đăng xuất ngay và không đăng nhập lại được. Mở khoá sau này không khôi phục các phiên đã mở.',
    nhanKhoa: 'Khoá tài khoản',
    // Hộp đổi vai trò.
    doiVaiTroTieuDe: 'Đổi nhóm tài khoản của người này?',
    doiVaiTroThan: 'Quyền của {{ten}} đổi ngay ở yêu cầu kế tiếp. Người này có thể đang làm việc dở.',
    nhanDoiVaiTro: 'Đổi nhóm tài khoản',
    // Hộp sửa.
    suaTieuDe: 'Sửa họ tên',
    suaMoEmail: 'Email không đổi được: nó nằm trong mọi bản ghi nhật ký.',
    // Toast.
    daDeNghi: 'Đã đề nghị tài khoản {{email}}. Chờ một quản trị nền tảng khác duyệt.',
    daDuyet: 'Đã duyệt tài khoản của {{ten}}.',
    daGuiLaiEmail: 'Đã gửi lại email đặt mật khẩu cho {{ten}}.',
    daLuu: 'Đã lưu họ tên của {{ten}}.',
    daDoiVaiTro: 'Đã đổi nhóm tài khoản của {{ten}}.',
    daKhoa: 'Đã khoá tài khoản của {{ten}}.',
    daMoKhoa: 'Đã mở khoá tài khoản của {{ten}}.',
    // Lỗi.
    loi: {
      emailDaCo: 'Email này đã có tài khoản nền tảng.',
      domainCuaTenant: 'Domain của email này đã ánh xạ về một tenant. Tài khoản nền tảng phải dùng domain riêng.',
      domainKhongDanhRieng: 'Vai trò này chỉ dùng được email thuộc domain dành riêng của nền tảng.',
      adminCuoiCung: 'Đây là quản trị nền tảng cuối cùng đang hoạt động. Duyệt hoặc mở một quản trị nền tảng khác trước.',
      tuDuyet: 'Người duyệt phải khác người đề nghị. Nhờ một quản trị nền tảng khác duyệt tài khoản này.',
      khac: 'Không thực hiện được: {{loi}}',
    },
  },

  // ─────────── Tri thức — module tri-thuc, ADR-0075 ───────────
  triThuc: {
    chonBo: 'Chọn bộ tri thức',
    dangTai: 'Đang tải...',
    loiTaiThem: 'Không tải thêm được: {{loi}}',
    loiTaiXuong: 'Không tải xuống được “{{ten}}”: {{loi}}',
    loi: {
      danhSachBo: 'Không tải được danh sách bộ tri thức: {{loi}}',
      phongBan: 'Không tải được danh sách phòng ban: {{loi}}',
      tepDoc: 'Tệp .doc không đọc được. Mở bằng Word, lưu thành .docx rồi tải lại.',
      tepKhongNhan: 'Loại tệp này chưa được nhận. Dùng PDF, Word (.docx), Excel, PowerPoint, TXT hoặc ảnh.',
      tepQuaLon: 'Tệp lớn hơn {{so}} MB. Tách nhỏ hoặc nén lại rồi tải lên.',
      tepRong: 'Tệp rỗng. Chọn lại đúng tệp.',
    },
    // Trạng thái nạp tệp vào index — bảng tông ở `modules/tri-thuc/constants.ts`.
    nap: {
      CHUA_INDEX: 'Chưa nạp',
      CHO_INDEX: 'Chờ nạp',
      DANG_INDEX: 'Đang nạp',
      DA_INDEX: 'Đã nạp',
      LOI: 'Nạp lỗi',
      ket: 'Kẹt',
      chiThongTin: 'Chỉ có thông tin, chưa kèm tệp',
      soLanThu: 'Đã thử {{so}} lần',
    },
    loaiTep: {
      WORD: 'Word',
      EXCEL: 'Excel',
      PDF: 'PDF',
      IMAGE: 'Ảnh',
      POWERPOINT: 'PowerPoint',
      TEXT: 'Văn bản',
    },
    trangThaiTaiLieu: {
      DRAFT: 'Nháp',
      REVIEW: 'Chờ duyệt',
      APPROVED: 'Đã duyệt',
      ARCHIVED: 'Lưu trữ',
    },
    // Mục trong hàng đợi của màn Upload tài liệu.
    trangThaiMuc: {
      cho: 'Chờ gửi',
      dang: 'Đang gửi...',
      xong: 'Đã gửi',
      loi: 'Lỗi',
    },
    cot: {
      ten: 'Tên',
      loai: 'Loại',
      kichThuoc: 'Kích thước',
      ngayTai: 'Ngày tải lên',
      hieuLuc: 'Hiệu lực',
      trangThaiNap: 'Trạng thái nạp',
      phanQuyen: 'Phân quyền',
    },
    thongKe: {
      tongBo: 'Bộ tri thức',
      tongBoSub: 'đang có trong tổ chức',
      tongTep: 'Tệp tri thức',
      tongTepSub: 'trong đó {{so}} tệp PDF',
      phongBan: 'Phòng ban sử dụng',
      phongBanSub: 'được dùng ít nhất một bộ',
      capNhat: 'Cập nhật gần nhất',
      capNhatSub: 'lần sửa bộ hay tệp gần nhất',
      loi: 'Không tải được số liệu tri thức: {{loi}}',
    },
    // Màn Quản lý tri thức và hộp tạo/sửa bộ.
    kho: {
      moTa: 'Bộ tri thức gom các tệp mà trợ lý AI dùng để trả lời. Mỗi bộ có quy tắc cắt chunk và phòng ban được dùng riêng.',
      danhSach: 'Danh sách bộ tri thức',
      timKiem: 'Tìm theo tên hoặc mã bộ',
      tatCaPhongBan: 'Tất cả phòng ban',
      locPhongBan: 'Lọc theo phòng ban',
      loiDanhSach: 'Không tải được danh sách bộ tri thức: {{loi}}',
      rongThat: 'Chưa có bộ tri thức nào',
      rongThatMo: 'Tạo bộ đầu tiên rồi tải tệp vào để trợ lý AI dùng.',
      rongLoc: 'Không có bộ tri thức nào khớp bộ lọc',
      taoBoDauTien: 'Tạo bộ tri thức đầu tiên',
      dangHien: 'Đang hiện {{so}} bộ',
      dangHienCoThem: 'Đang hiện {{so}} bộ, còn bộ chưa tải',
      taoBo: 'Tạo bộ tri thức',
      moBo: 'Mở bộ tri thức',
      suaBo: 'Sửa bộ tri thức',
      xoaBo: 'Xoá bộ tri thức',
      luuBo: 'Lưu bộ tri thức',
      chuaCoMoTa: 'Chưa có mô tả.',
      quanLy: 'Quản lý:',
      duocDung: 'Dùng được:',
      chuaPhongBanNao: 'Chưa phòng ban nào',
      chuaNoiIndex: 'Chưa nối được index',
      loaiVaSo: '{{loai}} {{so}}',
      soTep: '{{so}} tệp',
      capNhatNgay: 'Cập nhật {{ngay}}',
      taoTieuDe: 'Tạo bộ tri thức',
      suaTieuDe: 'Sửa bộ tri thức “{{ten}}”',
      fMa: 'Mã bộ',
      fMaGoiY: 'Chữ hoa, số, dấu - và _, tối đa 20 ký tự. Không đổi được sau khi tạo.',
      fTen: 'Tên bộ',
      fMoTa: 'Mô tả',
      fQuanLy: 'Phòng ban quản lý',
      fDuocDung: 'Phòng ban được dùng',
      fDuocDungGoiY: 'Trợ lý chỉ trả lời bằng tệp của bộ cho người thuộc các phòng ban này. Để trống thì chưa ai dùng được.',
      chonPhongBan: 'Chọn phòng ban',
      moiPhongBan: 'Chọn một hay nhiều phòng ban',
      fAnNgoaiHieuLuc: 'Ẩn tệp ngoài khoảng hiệu lực',
      fAnNgoaiHieuLucMo: 'Trợ lý bỏ qua tệp đã hết hoặc chưa tới hiệu lực.',
      chunkTieuDe: 'Quy tắc cắt chunk',
      chunkMo: 'Áp cho mọi tệp nạp vào bộ này. Chỉ đặt được lúc tạo bộ.',
      chunkChiLucTao: 'Quy tắc cắt chunk chỉ đặt lúc tạo bộ, không sửa được.',
      chunkTuDong: 'Tự động',
      chunkThuCong: 'Thủ công',
      chunkTuDongMo: 'Hệ thống tự chọn cách cắt, hợp với hầu hết tài liệu.',
      chunkThuCongMo: 'Tự đặt kích thước chunk và độ chồng lấn.',
      chunkThuCongTomTat: 'Thủ công: chunk {{kichThuoc}} ký tự, chồng lấn {{chongLan}} ký tự',
      fKichThuoc: 'Kích thước chunk (ký tự)',
      fChongLan: 'Chồng lấn (ký tự)',
      chunkGoiY:
        'Chunk to giữ trọn ý hơn nhưng lấy về ít đoạn hơn; chunk nhỏ thì ngược lại. Điều khoản dài nên để 1.200–2.000; hỏi đáp ngắn nên để 400–700.',
      fCauTruc: 'Nhờ AI dựng tiêu đề trước khi cắt',
      fCauTrucMo: 'Có ích với tệp .docx không đặt tiêu đề: cắt theo mục thay vì theo độ dài.',
      loiMaTrong: 'Nhập mã bộ.',
      loiMaDai: 'Mã bộ tối đa 20 ký tự.',
      loiMaSai: 'Mã chỉ gồm chữ hoa, số, dấu - và _.',
      loiTenTrong: 'Nhập tên bộ.',
      loiQuanLyTrong: 'Chọn phòng ban quản lý.',
      loiKichThuocThieu: 'Nhập kích thước chunk.',
      loiKichThuocNgoai: 'Kích thước chunk phải từ {{min}} đến {{max}} ký tự.',
      loiChongLanThieu: 'Nhập độ chồng lấn.',
      loiChongLanNgoai: 'Độ chồng lấn phải từ {{minCl}} đến {{maxCl}} ký tự.',
      loiChongLanQuaLon: 'Độ chồng lấn phải nhỏ hơn kích thước chunk.',
      daTao: 'Đã tạo bộ tri thức “{{ten}}”.',
      daLuu: 'Đã lưu bộ tri thức “{{ten}}”.',
      daXoa: 'Đã xoá bộ tri thức “{{ten}}”.',
      xoaTieuDe: 'Xoá bộ tri thức này?',
      xoaThan:
        'Bộ “{{ten}}” bị xoá hẳn cùng {{so}} tệp, mọi thư mục và tài liệu kiểm duyệt của nó. Trợ lý thôi trả lời bằng các tệp này. Không hoàn tác được.',
    },
    // Màn chi tiết một bộ.
    chiTiet: {
      phuDe: '{{ma}} · Quản lý bởi {{phongBan}}',
      khongDoc: 'Bộ tri thức',
      khongThay: 'Không tìm thấy bộ tri thức này',
      khongThayMo: 'Bộ có thể đã bị xoá. Quay lại danh sách để chọn bộ khác.',
      loi: 'Không tải được bộ tri thức: {{loi}}',
      chuaNoiIndex:
        'Chưa tạo được nơi lưu tìm kiếm cho bộ này nên tệp tải lên chưa nạp được. Báo đội vận hành kèm lỗi lần gần nhất: {{loi}}',
      noiDung: 'Thư mục và tệp của bộ',
      timKiem: 'Tìm thư mục, tệp...',
      moiLoai: 'Mọi loại tệp',
      dangTimCaBo: 'Đang tìm trong cả bộ.',
      duongDan: 'Đường dẫn thư mục',
      bangCaption: 'Thư mục và tệp trong {{ten}}',
      rongLoc: 'Không có thư mục hay tệp nào khớp bộ lọc',
      thuMucTrong: 'Thư mục này đang trống',
      boTrong: 'Bộ tri thức này chưa có tệp nào',
      trongMo: 'Tải tệp lên để trợ lý AI dùng được.',
    },
    thuMuc: {
      gocBo: 'Gốc bộ',
      loai: 'Thư mục',
      mo: 'Mở thư mục',
      doiTen: 'Đổi tên thư mục',
      xoa: 'Xoá thư mục',
      tao: 'Tạo thư mục',
      taoTieuDe: 'Tạo thư mục',
      doiTenTieuDe: 'Đổi tên thư mục “{{ten}}”',
      fTen: 'Tên thư mục',
      fTenViDu: 'VD: Hợp đồng 2026',
      namTrong: 'Thư mục mới nằm trong {{noi}}.',
      loiTenTrong: 'Nhập tên thư mục.',
      loiDoc: 'Không tải được cây thư mục: {{loi}}',
      daTao: 'Đã tạo thư mục “{{ten}}”.',
      daDoiTen: 'Đã đổi tên thư mục thành “{{ten}}”.',
      daXoa: 'Đã xoá thư mục “{{ten}}”.',
      xoaTieuDe: 'Xoá thư mục này?',
      xoaThan:
        'Thư mục “{{ten}}” và mọi thư mục con bị xoá. Tệp bên trong không mất mà chuyển về gốc bộ. Không hoàn tác được.',
    },
    tep: {
      taiLenTieuDe: 'Tải tệp lên',
      taiVao: 'Tệp vào {{noi}} và được nạp cho trợ lý ngay, không qua kiểm duyệt.',
      keoTha: 'Kéo thả tệp vào đây, hoặc bấm để chọn',
      keoThaMo: 'PDF, Word (.docx), Excel, PowerPoint, TXT hoặc ảnh; tối đa {{so}} MB mỗi tệp.',
      khongNhan: 'Các tệp sau không được thêm:',
      tepVaLyDo: '{{ten}}: {{lyDo}}',
      daChon: 'Đã chọn {{so}} tệp',
      boTep: 'Bỏ tệp {{ten}}',
      loiChuaChon: 'Chọn ít nhất một tệp.',
      chuaVaoThuMuc:
        'Đã tải lên {{ten}} nhưng chưa chuyển được vào thư mục, tệp đang ở gốc bộ. Dùng “Chuyển thư mục” thay vì tải lại, để không thành hai bản.',
      taiLen: 'Tải lên',
      taiLenSo: 'Tải lên {{so}} tệp',
      dangTaiLen: 'Đang tải lên...',
      fHieuLucTu: 'Hiệu lực từ ngày',
      fHieuLucDen: 'Hiệu lực đến ngày',
      fHieuLucDenGoiY: 'Bỏ trống: còn hiệu lực vô thời hạn.',
      chonNgay: 'Chọn ngày',
      khongThoiHan: 'Không thời hạn',
      khoangHieuLuc: '{{tu}} → {{den}}',
      hetHieuLuc: 'Hết hiệu lực',
      chuaToiHieuLuc: 'Chưa tới hiệu lực',
      loiThieuTu: 'Chọn ngày bắt đầu hiệu lực.',
      loiDenKhongSauTu: 'Ngày hết hiệu lực phải sau ngày bắt đầu.',
      hieuLucTieuDe: 'Sửa hiệu lực',
      hieuLucGoiY: 'Bỏ trống cả hai ngày: tệp áp dụng không giới hạn thời gian.',
      luuHieuLuc: 'Lưu hiệu lực',
      suaHieuLuc: 'Sửa hiệu lực',
      taiXuong: 'Tải xuống',
      dongBoLai: 'Đồng bộ lại',
      dongBoLaiMo: 'Nạp lại tệp cho trợ lý',
      chuyenThuMuc: 'Chuyển thư mục',
      chuyenThuMucTieuDe: 'Chuyển tệp sang thư mục khác',
      fThuMucDich: 'Thư mục đích',
      chuyenBo: 'Chuyển sang bộ khác',
      chuyenBoTieuDe: 'Chuyển tệp sang bộ tri thức khác',
      fBoDich: 'Bộ tri thức đích',
      loiChuaChonBo: 'Chọn bộ tri thức đích.',
      khongCoBoKhac: 'Không còn bộ tri thức nào khác.',
      chuyenBoHauQua:
        'Tệp ra khỏi thư mục hiện tại, về gốc của bộ mới và được nạp lại theo quy tắc chunk của bộ đó. Trợ lý của bộ này thôi dùng tệp.',
      xoa: 'Xoá tệp',
      xoaTieuDe: 'Xoá tệp này?',
      xoaThan: 'Tệp “{{ten}}” bị xoá khỏi bộ, khỏi nơi lưu và khỏi index; trợ lý thôi dùng nó. Không hoàn tác được.',
      loiDanhSach: 'Không tải được danh sách tệp: {{loi}}',
      dangHienCoThem: 'Đang hiện {{so}} tệp, còn tệp chưa tải',
      daTaiLen: 'Đã tải lên “{{ten}}”. Tệp đang chờ nạp.',
      daTaiLenNhieu: 'Đã tải lên {{so}} tệp. Các tệp đang chờ nạp.',
      daLuuHieuLuc: 'Đã lưu hiệu lực của “{{ten}}”.',
      daChuyenThuMuc: 'Đã chuyển “{{ten}}” sang thư mục mới.',
      daChuyenBo: 'Đã chuyển “{{ten}}” sang bộ “{{bo}}”.',
      daBoKhoiBo: 'Đã bỏ “{{ten}}” khỏi bộ tri thức.',
      daXepDongBo: 'Đã xếp “{{ten}}” vào hàng đợi nạp lại.',
      loiDongBo: 'Không đồng bộ lại được “{{ten}}”: {{loi}}',
      daXoa: 'Đã xoá tệp “{{ten}}”.',
    },
    // Hộp Quyền đọc của tệp và thư mục, cột Phân quyền (ADR-0125).
    quyen: {
      quyenDoc: 'Quyền đọc',
      tieuDe: 'Quyền đọc “{{ten}}”',
      kieu: 'Kiểu quyền đọc',
      theoBo: 'Theo bộ',
      theoThuMuc: 'Theo thư mục {{ten}}',
      theoThuMucKhongTen: 'Theo thư mục',
      theoBoTriThuc: 'Theo bộ tri thức',
      chonRieng: 'Chọn riêng',
      layTuThuMuc: 'Lấy từ thư mục {{ten}}.',
      fPhongBan: 'Phòng ban',
      fNguoi: 'Người',
      khongAiDoc: 'Không ai đọc được',
      canhBaoRong: 'Chưa chọn ai nên không ai đọc được.',
      tim: 'Tìm phòng ban hoặc người đã tải',
      cay: 'Phòng ban và người',
      rongTim: 'Không thấy phòng ban hoặc người phù hợp',
      chuaCoPhongBan: 'Tổ chức chưa có phòng ban nào.',
      daChon: 'Đã chọn',
      chuaChonAi: 'Chưa chọn ai.',
      bo: 'Bỏ {{ten}}',
      phongBanKhongRo: 'Phòng ban không rõ tên',
      khongThuocPhong: 'Không thuộc phòng ban nào',
      motPhongBan: '1 phòng ban',
      soPhongBan: '{{so}} phòng ban',
      motNguoi: '1 người',
      soNguoi: '{{so}} người',
      motTep: '1 tệp',
      soTep: '{{so}} tệp',
      motThuMucCon: '1 thư mục con',
      soThuMucCon: '{{so}} thư mục con',
      conRieng: '{{ds}} đang chọn riêng không bị ảnh hưởng.',
      dangTai: 'Đang tải...',
      dangTaiPhong: 'Đang tải người của phòng...',
      luu: 'Lưu quyền đọc',
      daLuu: 'Đã lưu quyền đọc “{{ten}}”.',
      loiDoc: 'Không tải được quyền đọc: {{loi}}',
      loiTaiPhong: 'Không tải được người của {{phong}}: {{loi}}',
    },
    // Tài liệu kiểm duyệt: khung chi tiết, hộp thao tác, hộp phiên bản.
    taiLieu: {
      bo: 'Bộ:',
      phienBan: 'Phiên bản:',
      tep: 'Tệp:',
      ngayTai: 'Ngày tải lên:',
      capNhat: 'Cập nhật:',
      xetLuc: 'Xét lần cuối:',
      khongKemTep: 'Không kèm tệp',
      tienTrinh: 'Tiến trình duyệt',
      ghiChuXet: 'Ghi chú của lần xét gần nhất',
      ghiChuXetLuc: 'Ghi chú của lần xét ngày {{ngay}}',
      tomTat: 'Tóm tắt nội dung',
      chuaCoTomTat: 'Chưa có tóm tắt.',
      lichSu: 'Lịch sử phiên bản',
      chuaCoLichSu: 'Chưa có lịch sử phiên bản.',
      khongGhiChu: 'Không có ghi chú',
      khongThay: 'Không tìm thấy tài liệu này. Có thể nó đã bị xoá cùng bộ tri thức.',
      loiChiTiet: 'Không tải được tài liệu: {{loi}}',
      taiXuong: 'Tải xuống',
      taoPhienBan: 'Tạo phiên bản mới',
      phienBanTieuDe: 'Tạo phiên bản mới cho “{{ten}}”',
      phienBanMo:
        'Phiên bản đang là {{phienBan}}. Tạo phiên bản mới thì số phiên bản tăng và tài liệu về nháp, phải gửi duyệt lại.',
      fThayDoi: 'Thay đổi trong phiên bản này',
      fTomTat: 'Tóm tắt nội dung',
      loiThayDoiTrong: 'Ghi lại đã thay đổi gì.',
      fGhiChu: 'Ghi chú cho người soạn',
      fGhiChuTuyChon: 'Không bắt buộc.',
      fGhiChuViDu: 'Mô tả cụ thể những gì cần chỉnh sửa...',
      loiGhiChuTrong: 'Ghi rõ cần sửa gì để người soạn biết.',
      guiDuyet: 'Gửi duyệt',
      duyet: 'Duyệt tài liệu',
      traNhap: 'Trả về nháp',
      yeuCauSua: 'Yêu cầu sửa',
      luuTru: 'Lưu trữ tài liệu',
      quayLai: 'Quay lại trạng thái trước',
      duyetTieuDe: 'Duyệt tài liệu này?',
      duyetThan: '“{{ten}}” thành một tệp trong bộ “{{bo}}” và vào hàng đợi nạp; trợ lý dùng được sau khi nạp xong.',
      traNhapTieuDe: 'Trả tài liệu này về nháp?',
      traNhapThan: '“{{ten}}” về nháp. Người soạn sửa rồi gửi duyệt lại.',
      yeuCauSuaTieuDe: 'Yêu cầu sửa tài liệu này?',
      yeuCauSuaThan: '“{{ten}}” về nháp kèm ghi chú dưới đây để người soạn sửa.',
      luuTruTieuDe: 'Lưu trữ tài liệu này?',
      luuTruThan:
        '“{{ten}}” chuyển sang lưu trữ. Tệp đã vào bộ “{{bo}}” vẫn giữ nguyên; xoá tệp ở màn bộ tri thức nếu không muốn trợ lý dùng nữa.',
      quayLaiTieuDe: 'Đưa tài liệu về trạng thái trước?',
      quayLaiThan: '“{{ten}}” về trạng thái {{trangThai}}. Tệp đã vào bộ tri thức (nếu có) vẫn giữ nguyên.',
      daGuiDuyet: 'Đã gửi duyệt “{{ten}}”.',
      daDuyet: 'Đã duyệt “{{ten}}”. Tệp đang chờ nạp.',
      daTraNhap: 'Đã trả “{{ten}}” về nháp.',
      daYeuCauSua: 'Đã yêu cầu sửa “{{ten}}”.',
      daLuuTru: 'Đã lưu trữ “{{ten}}”.',
      daQuayLai: 'Đã đưa “{{ten}}” về trạng thái trước.',
      daTaoPhienBan: 'Đã tạo phiên bản {{phienBan}} của “{{ten}}”.',
    },
    // Màn Kiểm duyệt & Phê duyệt.
    duyet: {
      tieuDe: 'Kiểm duyệt & Phê duyệt tri thức',
      moTa: 'Duyệt tài liệu trước khi chúng thành tệp trong bộ tri thức, và theo dõi phiên bản của từng tài liệu.',
      taiTaiLieu: 'Tải tài liệu lên',
      danhSach: 'Danh sách tài liệu',
      chiTiet: 'Chi tiết tài liệu',
      timKiem: 'Tìm theo tiêu đề',
      moiTrangThai: 'Mọi trạng thái',
      loiDanhSach: 'Không tải được danh sách tài liệu: {{loi}}',
      rongThat: 'Chưa có tài liệu nào',
      rongThatMo: 'Tài liệu tải lên ở màn Upload tài liệu sẽ hiện ở đây để duyệt.',
      rongLoc: 'Không có tài liệu nào khớp bộ lọc',
      dangHien: 'Đang hiện {{so}} tài liệu',
      dangHienCoThem: 'Đang hiện {{so}} tài liệu, còn nữa',
      chuaChon: 'Chọn một tài liệu để xem',
      chuaChonMo: 'Chi tiết, lịch sử phiên bản và các thao tác duyệt hiện ở đây.',
    },
    // Màn Upload tài liệu.
    upload: {
      moTa: 'Tài liệu tải lên ở đây vào luồng kiểm duyệt dưới dạng nháp. Được duyệt thì tài liệu thành tệp trong bộ tri thức.',
      moKiemDuyet: 'Mở màn kiểm duyệt',
      khongCoQuyen: 'Tài khoản này chưa có quyền tải tài liệu lên. Nhờ quản trị tổ chức cấp quyền tạo tài liệu tri thức.',
      anThongBao: 'Ẩn thông báo',
      hangDoi: 'Hàng đợi tải lên',
      boDaXong: 'Bỏ {{so}} mục đã gửi',
      datBoTatCa: 'Đặt một bộ cho mọi mục',
      guiTatCa: 'Gửi {{so}} tài liệu',
      guiMuc: 'Gửi',
      boKhoiHang: 'Bỏ {{ten}} khỏi hàng đợi',
      rong: 'Hàng đợi đang trống',
      rongMo: 'Kéo thả tệp vào vùng phía trên để thêm vào hàng đợi.',
      chuaCoBo: 'Tổ chức chưa có bộ tri thức nào. Tạo bộ ở màn Quản lý tri thức trước khi gửi.',
      fBo: 'Bộ tri thức',
      fTieuDe: 'Tiêu đề',
      fTomTat: 'Tóm tắt nội dung',
      fTomTatGoiY: 'Không bắt buộc. Người duyệt đọc phần này trước khi mở tệp.',
      loiChuaChonBo: 'Chọn bộ tri thức.',
      loiTieuDeTrong: 'Nhập tiêu đề tài liệu.',
      loiTieuDeDai: 'Tiêu đề tối đa {{so}} ký tự.',
      daTaiLen: 'Đã gửi {{so}} tài liệu vào luồng kiểm duyệt.',
    },
  },

  // ─────────── Cấu hình xử lý văn bản (module van-ban, GN-D2-3 → GN-D2-5) ───────────
  vanBanChung: {
    dong: 'Đóng',
    luuThayDoi: 'Lưu thay đổi',
    themMoi: 'Thêm mới',
  },
  vanBanDonVi: {
    tieuDe: 'Danh sách, vai trò các cấp',
    moTa: 'Quản lý danh sách cơ quan, phòng ban, cá nhân cùng chức năng, nhiệm vụ và điểm đánh giá phục vụ AI phân công văn bản',
    timTheo: 'Tìm theo tên, nhiệm vụ...',
    tatCaLoai: 'Tất cả loại',
    locLoai: 'Lọc theo loại',
    thongKe: {
      tong: 'Tổng số',
      coQuan: 'Cơ quan',
      phongBan: 'Phòng ban',
      caNhan: 'Cá nhân',
    },
    cot: {
      ten: 'Tên',
      loai: 'Loại',
      nhiemVu: 'Chức năng và nhiệm vụ chính',
      phuTrach: 'Người phụ trách',
      trucThuoc: 'Trực thuộc',
      diem: 'Điểm đánh giá',
    },
    loai: {
      CO_QUAN: 'Cơ quan',
      PHONG_BAN: 'Phòng ban',
      CA_NHAN: 'Cá nhân',
    },
    bangCaption: 'Danh sách cơ quan, phòng ban, cá nhân',
    rongThat: 'Chưa có cơ quan, phòng ban hay cá nhân nào',
    rongThatMo: 'Thêm các cấp để AI dựa vào chức năng, nhiệm vụ khi phân công văn bản.',
    rongLoc: 'Không có đơn vị nào khớp bộ lọc',
    loiDanhSach: 'Không tải được danh sách: {{loi}}',
    dialogThem: 'Thêm cơ quan / phòng ban / cá nhân',
    dialogSua: 'Sửa thông tin cấp',
    fTen: 'Tên',
    fTenGoiY: 'Tên cơ quan / phòng ban / cá nhân',
    fLoai: 'Loại',
    fDiem: 'Điểm đánh giá (0 - 10)',
    fPhuTrach: 'Người phụ trách',
    fPhuTrachGoiY: 'Họ tên người đứng đầu cơ quan / phòng ban',
    fTrucThuoc: 'Trực thuộc',
    khongTrucThuoc: '— Không trực thuộc —',
    fNhiemVu: 'Chức năng và nhiệm vụ chính',
    fNhiemVuGoiY: 'Mô tả chức năng, nhiệm vụ chính...',
    loiTenTrong: 'Nhập tên cơ quan, phòng ban hoặc cá nhân.',
    loiNhiemVuTrong: 'Nhập chức năng và nhiệm vụ chính.',
    loiDiemSai: 'Điểm đánh giá phải từ 0 đến 10.',
    sua: 'Sửa',
    xoa: 'Xoá',
    xacNhanXoa: 'Xoá {{loai}} "{{ten}}"?',
    xacNhanXoaMo: 'Các đơn vị trực thuộc sẽ không còn cấp trên. Thao tác này không hoàn tác được.',
    nutXoa: 'Xoá {{loai}}',
    daThem: 'Đã thêm mới.',
    daLuu: 'Đã lưu thay đổi.',
    daXoa: 'Đã xoá.',
  },
  vanBanMau: {
    tieuDe: 'Danh sách mẫu văn bản',
    moTa: 'Quản lý các mẫu văn bản hành chính theo thể thức Nghị định 30/2020/NĐ-CP, dùng cho chức năng Soạn thảo văn bản',
    them: 'Thêm mẫu văn bản',
    timTheo: 'Tìm mẫu văn bản...',
    tatCaNguon: 'Tất cả nguồn',
    locNguon: 'Lọc theo nguồn',
    nguonNd30: 'Nghị định 30/2020/NĐ-CP',
    nguonTuyChinh: 'Tuỳ chỉnh',
    thongKe: {
      tong: 'Tổng số mẫu',
      nd30: 'Mẫu theo Nghị định 30',
      tuyChinh: 'Mẫu tuỳ chỉnh',
      dangDung: 'Đang sử dụng',
    },
    cot: {
      ten: 'Tên mẫu văn bản',
      kyHieu: 'Ký hiệu',
      nguon: 'Nguồn',
    },
    trangThai: {
      DANG_DUNG: 'Đang dùng',
      NGUNG_DUNG: 'Ngừng dùng',
    },
    kyHieuMau: 'Số: …/{{ma}}-…',
    bangCaption: 'Danh sách mẫu văn bản',
    rongThat: 'Chưa có mẫu văn bản nào',
    rongThatMo: 'Thêm mẫu tuỳ chỉnh để dùng ở màn Soạn thảo văn bản.',
    rongLoc: 'Không có mẫu văn bản nào khớp bộ lọc',
    loiDanhSach: 'Không tải được danh sách mẫu: {{loi}}',
    xem: 'Xem mẫu',
    sua: 'Sửa mẫu',
    xoa: 'Xoá mẫu',
    xacNhanXoa: 'Xoá mẫu "{{ten}}"?',
    xacNhanXoaNd30: 'Đây là mẫu gốc theo Nghị định 30/2020/NĐ-CP. Mẫu bị xoá khỏi tổ chức của bạn và không hoàn tác được.',
    xacNhanXoaMo: 'Thao tác này không hoàn tác được.',
    xemTieuDe: 'Xem mẫu văn bản: {{ten}}',
    dungDeSoan: 'Dùng mẫu này để soạn thảo',
    tTenMau: 'Tên mẫu',
    tKyHieu: 'Ký hiệu văn bản',
    tNguon: 'Nguồn',
    tMoTa: 'Mô tả',
    cacTruong: 'Các trường thông tin ({{so}})',
    aiDien: 'AI điền',
    boCucTieuDe: 'Bố cục thể thức',
    boCucNguon: '(Điều 8, Nghị định 30/2020/NĐ-CP)',
    boCuc: {
      '1': 'Quốc hiệu và Tiêu ngữ',
      '2': 'Tên cơ quan, tổ chức ban hành văn bản',
      '3': 'Số, ký hiệu của văn bản',
      '4': 'Địa danh và thời gian ban hành văn bản',
      '5': 'Tên loại và trích yếu nội dung văn bản',
      '6': 'Nội dung văn bản',
      '7': 'Chức vụ, họ tên và chữ ký của người có thẩm quyền',
      '8': 'Dấu, chữ ký số của cơ quan, tổ chức',
      '9': 'Nơi nhận',
    },
    xemTruocNoiDung: 'Xem trước nội dung mẫu',
    xemTruocAi: 'Bản xem trước do AI tạo',
    xemTruocTep: 'Xem trước mẫu (từ tệp: {{tep}})',
    taoXemTruoc: 'Tạo xem trước AI',
    dangTaoXemTruoc: 'Đang tạo...',
    taoLai: 'Tạo lại',
    chuaCoNoiDung: 'Mẫu này chưa gắn nội dung tĩnh. Bấm "Tạo xem trước AI" để sinh văn bản minh hoạ theo thể thức Nghị định 30, hoặc dựng nội dung ở màn Soạn thảo văn bản.',
    chuaCoNoiDungKhongAi: 'Mẫu này chưa gắn nội dung tĩnh. Bản cài này chưa bật AI tạo bản xem trước; dựng nội dung ở màn Soạn thảo văn bản.',
    daTaoXemTruoc: 'AI đã tạo bản xem trước cho mẫu văn bản.',
    daThem: 'Đã thêm mẫu văn bản mới.',
    daLuu: 'Đã lưu thay đổi mẫu văn bản.',
    daXoa: 'Đã xoá mẫu văn bản.',
    kieu: {
      TEXT: 'Văn bản',
      TEXTAREA: 'Đoạn văn bản',
      DATE: 'Ngày tháng',
      NUMBER: 'Số',
      SELECT: 'Danh sách chọn',
    },
  },
  vanBanMauForm: {
    dialogThem: 'Thêm mẫu văn bản',
    dialogSua: 'Sửa mẫu văn bản',
    nutThem: 'Thêm mẫu',
    thongTinChung: 'Thông tin chung',
    fTen: 'Tên mẫu văn bản',
    fTenGoiY: 'VD: Công điện, Phiếu chuyển, Bản ghi nhớ...',
    fKyHieu: 'Mã ký hiệu',
    fKyHieuGoiY: 'VD: CĐ, PC...',
    fTrangThai: 'Trạng thái',
    fMoTa: 'Mô tả công dụng của mẫu',
    fMoTaGoiY: 'Mô tả mục đích, phạm vi áp dụng của mẫu...',
    loiTenTrong: 'Nhập tên mẫu văn bản.',
    theThuc: 'Thể thức văn bản (Điều 8, Nghị định 30/2020/NĐ-CP)',
    theThucMo: 'Dựng phần đầu (cơ quan ban hành, số, ký hiệu, địa danh) và phần ký của văn bản. Thay đổi ở đây hiện ngay trong bản xem trước bên phải.',
    canGiua: 'Quốc hiệu, Tiêu ngữ căn giữa',
    canGiuaMo: '(dùng cho đơn, tờ khai, không có cơ quan)',
    fCqch: 'Cơ quan chủ quản (cấp trên)',
    fCqchGoiY: 'VD: BỘ Y TẾ',
    fCq: 'Tên cơ quan, tổ chức ban hành',
    fCqGoiY: 'VD: SỞ Y TẾ HÀ NỘI',
    fSo: 'Số văn bản',
    fSoGoiY: 'VD: 1234',
    fKyHieuCq: 'Ký hiệu (viết tắt cơ quan)',
    fKyHieuCqGoiY: 'VD: SYT',
    fDd: 'Địa danh',
    fDdGoiY: 'VD: Hà Nội',
    fCv: 'Chức vụ người ký',
    fCvGoiY: 'VD: GIÁM ĐỐC',
    fKy: 'Họ và tên người ký',
    fKyGoiY: 'VD: Nguyễn Văn A',
    fNn: 'Nơi nhận',
    fNnGoiY: 'Mỗi nơi nhận một dòng, VD:\nNhư trên;\nUBND các quận, huyện;\nLưu: VT.',
    truong: 'Các trường thông tin',
    truongMo: 'Khai báo trường, rồi bấm hoặc kéo chip vào văn bản bên phải.',
    themTruong: 'Thêm trường',
    chuaCoTruong: 'Chưa có trường thông tin nào.',
    chuaCoTruongMo: 'Bấm "Thêm trường" để bắt đầu.',
    fTenTruong: 'Tên trường {{so}}',
    fTenTruongGoiY: 'Tên trường (VD: Kính gửi)',
    fKieuTruong: 'Kiểu trường {{so}}',
    fGoiYAi: 'Mô tả, gợi ý cho AI (không bắt buộc)',
    fLuaChon: 'Các lựa chọn, cách nhau bằng dấu phẩy (VD: Phép năm, Nghỉ ốm)',
    batBuoc: 'Bắt buộc nhập',
    aiTuDien: 'AI tự điền từ văn bản nguồn',
    aiTuDienMo: 'Bật: AI tự điền từ văn bản nguồn khi soạn thảo. Tắt: người dùng tự nhập (VD: chữ ký).',
    xoaTruong: 'Xoá trường {{so}}',
    luuYNd30: 'Mẫu gốc theo Nghị định 30/2020/NĐ-CP: chỉ nên sửa mô tả và thể thức.',
    xemTruoc: 'Bản xem trước, soạn như Word',
    daChinhTay: 'Đã chỉnh tay',
    dungLai: 'Dựng lại',
    dungLaiMo: 'Dựng lại văn bản theo cấu hình',
    chenTruong: 'Chèn trường:',
    chenTruongRong: 'khai báo trường ở cột trái để bấm hoặc kéo chèn vào văn bản.',
    chenTruongMo: 'Bấm để chèn tại con trỏ, hoặc kéo thả vào bản xem trước',
    vungDau: 'Phần đầu văn bản',
    vungThan: 'Thân văn bản',
    vungKy: 'Phần ký văn bản',
    goiYThan: 'Soạn nội dung mẫu tại đây. Bấm hoặc kéo thả các trường thông tin để chèn vào văn bản.',
  },
  vanBanCc: {
    thanhCongCu: 'Định dạng văn bản',
    dam: 'Đậm',
    nghieng: 'Nghiêng',
    gachChan: 'Gạch chân',
    canTrai: 'Canh trái',
    canGiua: 'Canh giữa',
    canPhai: 'Canh phải',
    canDeu: 'Canh đều',
    dsCham: 'Danh sách chấm',
    dsSo: 'Danh sách số',
    coChu: 'Cỡ chữ',
    co: {
      '2': 'Nhỏ',
      '3': 'Thường',
      '4': 'Lớn',
      '5': 'Rất lớn',
      '6': 'Tiêu đề',
    },
    hoanTac: 'Hoàn tác',
    lamLai: 'Làm lại',
    xoaDinhDang: 'Xoá định dạng',
  },
  vanBanTichHop: {
    tieuDe: 'Cấu hình tích hợp & Webhook',
    moTa: 'Cấp API key cho hệ thống bên ngoài (QLVB&ĐH, Cổng DVC, Email công vụ…) đẩy văn bản vào FoxAI để tóm tắt tự động, rồi nhận kết quả trả về qua webhook',
    them: 'Thêm tích hợp',
    timTheo: 'Tìm theo tên cấu hình...',
    tatCaNguon: 'Tất cả nguồn',
    locNguon: 'Lọc theo nguồn',
    thongKe: {
      tong: 'Tổng cấu hình',
      hoatDong: 'Đang hoạt động',
      coWebhook: 'Có webhook',
    },
    cot: {
      ten: 'Tên cấu hình',
      nguon: 'Nguồn',
      webhook: 'Webhook trả kết quả',
    },
    chuaCauHinh: 'Chưa cấu hình',
    dangHoatDong: 'Đang hoạt động',
    daTat: 'Đã tắt',
    bangCaption: 'Danh sách cấu hình tích hợp',
    rongThat: 'Chưa có cấu hình tích hợp nào',
    rongThatMo: 'Thêm tích hợp để cấp API key cho hệ thống bên ngoài đẩy văn bản vào FoxAI.',
    rongLoc: 'Không có cấu hình nào khớp bộ lọc',
    loiDanhSach: 'Không tải được danh sách cấu hình: {{loi}}',
    quanLy: 'Quản lý (API key, webhook, nhật ký)',
    sua: 'Sửa cấu hình',
    xoa: 'Xoá cấu hình',
    xacNhanXoa: 'Xoá cấu hình "{{ten}}"?',
    xacNhanXoaMo: 'API key bị vô hiệu ngay và toàn bộ lịch sử webhook liên quan cũng bị xoá. Thao tác này không hoàn tác được.',
    daLuu: 'Đã lưu thay đổi cấu hình tích hợp.',
    daXoa: 'Đã xoá cấu hình tích hợp.',
    daSaoChep: 'Đã sao chép.',
    daSaoChepKhoa: 'Đã sao chép API key.',
    daSaoChepLenh: 'Đã sao chép lệnh mẫu.',
    khongSaoChep: 'Không sao chép được, hãy chọn và sao chép thủ công.',
    nguon: {
      QLVB: 'Hệ thống QLVB & ĐH',
      CONG_DVC: 'Cổng dịch vụ công',
      EMAIL: 'Email công vụ',
      UPLOAD: 'Upload thủ công',
      OTHER: 'Nguồn khác',
    },
    suKien: {
      AFTER_SUMMARIZE: 'Sau khi tóm tắt văn bản',
      AFTER_COMPOSE: 'Sau khi soạn thảo văn bản',
      AFTER_OCR: 'Sau khi OCR chứng từ',
      ON_ERROR: 'Khi xử lý lỗi',
    },
    xacThuc: {
      NONE: 'Không xác thực',
      API_KEY: 'API Key (X-Api-Key)',
      BEARER: 'Bearer Token',
    },
  },
  vanBanTichHopForm: {
    dialogThem: 'Thêm cấu hình tích hợp',
    dialogSua: 'Sửa cấu hình tích hợp',
    nutTao: 'Tạo và cấp API key',
    fTen: 'Tên cấu hình',
    fTenGoiY: 'VD: Hệ thống QLVB&ĐH',
    fNguon: 'Nhãn nguồn',
    fLoaiVb: 'Loại văn bản mặc định',
    fTuDong: 'Tự động tóm tắt',
    fTuDongMo: 'Tóm tắt ngay khi tiếp nhận',
    phanTiepNhan: 'Tiếp nhận văn bản (đối tác gọi vào)',
    fXacThucDauVao: 'Kiểu xác thực đối tác',
    xacThucVao: {
      API_KEY: 'API Key (X-Api-Key)',
      BEARER: 'Bearer Token',
    },
    fDinhDang: 'Định dạng dữ liệu',
    dinhDang: {
      MULTIPART: 'Multipart (kèm tệp)',
      JSON: 'JSON (chỉ văn bản)',
    },
    tiepNhanMo: 'Đường tiếp nhận luôn là POST /inbound; máy chủ nhận cả hai kiểu xác thực và cả hai định dạng, cấu hình này để đối chiếu khi hiển thị.',
    phanWebhook: 'Webhook trả kết quả',
    fUrl: 'Webhook URL',
    fPhuongThuc: 'Phương thức',
    fXacThuc: 'Xác thực',
    xacThucNgan: {
      NONE: 'Không',
      API_KEY: 'API Key',
      BEARER: 'Bearer',
    },
    fThuLai: 'Số lần thử lại',
    fToken: 'Token / API key gửi kèm',
    fTokenSua: 'Token / API key gửi kèm (bỏ trống để giữ nguyên)',
    fSuKien: 'Sự kiện kích hoạt gửi webhook',
    fKichHoat: 'Kích hoạt cấu hình này',
    loiTenTrong: 'Nhập tên cấu hình tích hợp.',
    loiThuLaiSai: 'Số lần thử lại webhook phải từ 0 đến 10.',
    loiUrlSai: 'Webhook URL phải bắt đầu bằng http:// hoặc https://',
    loiTokenTrong: 'Nhập token hoặc API key cho xác thực webhook.',
  },
  vanBanKhoa: {
    tieuDe: 'API key cho "{{ten}}"',
    canhBao: 'API key chỉ hiện một lần. Hãy sao chép và lưu ngay: đóng hộp thoại này là không xem lại được, chỉ cấp lại được key mới.',
    saoChep: 'Sao chép',
    daLuuKhoa: 'Tôi đã lưu key',
  },
  vanBanQl: {
    tieuDe: 'Quản lý tích hợp: {{ten}}',
    iNguon: 'Nhãn nguồn',
    iLoaiVb: 'Loại văn bản mặc định',
    iTuDong: 'Tự động tóm tắt',
    co: 'Có',
    khong: 'Không',
    iTrangThai: 'Trạng thái',
    phanKhoa: 'API key đối tác',
    capLai: 'Cấp lại key',
    khoaMo: 'Key được lưu dạng hash SHA-256, không khôi phục được. Cấp lại sẽ vô hiệu key cũ ngay.',
    xacNhanCapLai: 'Cấp API key mới cho "{{ten}}"?',
    xacNhanCapLaiMo: 'Key cũ bị vô hiệu ngay, đối tác phải cập nhật key mới.',
    nutCapLai: 'Cấp lại API key',
    phanKetNoi: 'Cách kết nối để gửi văn bản vào',
    ketNoiMo: 'Hệ thống bên ngoài gọi endpoint dưới đây để đẩy văn bản vào FoxAI, xác thực bằng API key của cấu hình này (thay <API_KEY> bằng key đã lưu). Văn bản nhận về vào danh sách Tóm tắt văn bản với nhãn nguồn "{{nguon}}".',
    chuaMoInbound: 'Đường tiếp nhận văn bản từ đối tác chưa mở ở Gateway của platform. Thông tin dưới đây để chuẩn bị tích hợp.',
    iEndpoint: 'Địa chỉ (endpoint)',
    iDinhDang: 'Định dạng dữ liệu',
    dinhDangDai: {
      JSON: 'JSON (chỉ nội dung văn bản)',
      MULTIPART: 'Multipart (kèm tệp đính kèm)',
    },
    iHeader: 'Header xác thực',
    viDu: 'Ví dụ lệnh gọi',
    truongMo: 'Trường: title (bắt buộc) · content hoặc file (bắt buộc một trong hai) · doc_type, doc_id, auto_summarize (tuỳ chọn). Phản hồi 202 kèm summarization_id.',
    iUrl: 'URL:',
    iXacThuc: 'Xác thực:',
    thuLaiToiDa: '{{xacThuc}} · thử lại tối đa {{so}} lần',
    kichHoatKhi: 'Kích hoạt khi:',
    guiThu: 'Gửi thử webhook',
    xemNhatKy: 'Xem nhật ký gửi',
    guiThanhCong: 'Gửi thành công, HTTP {{ma}}',
    guiThatBai: 'Gửi thất bại: {{loi}}',
    chuaCoWebhook: 'Cấu hình này chưa có webhook. Bấm "Sửa cấu hình" để thêm URL nhận kết quả.',
    nhatKyRong: 'Chưa có lần gửi webhook nào.',
    nkCaption: 'Nhật ký gửi webhook',
    nkSuKien: 'Sự kiện',
    nkLan: 'Lần',
    nkKetQua: 'Kết quả',
    nkThoiGian: 'Thời gian',
  },

  // ───────────────────────── Báo cáo doanh nghiệp (module bao-cao, ADR-0081) ─────────────────────────
  baoCaoDoanhNghiep: {
    phanHe: {
      overview: 'Tổng quan',
      finance: 'Tài chính',
      projects: 'Dự án',
      sales: 'Kinh doanh',
      people: 'Nhân sự',
    },
    phanHeMoTa: 'Số liệu ứng dụng di động FOXAI hiển thị ở phân hệ này. Lưu là ghi đè toàn bộ số liệu phân hệ của nguồn đang xem.',
    khongCoQuyen: {
      tieuDe: 'Bạn chưa được xem mục này',
      moTa: 'Cần quyền {{quyen}}. Nhờ quản trị tổ chức cấp quyền rồi mở lại trang.',
    },
    loiTaiPhanHe: 'Không tải được số liệu phân hệ: {{loi}}',
    chuaNhap: 'Phân hệ {{ten}} chưa có số liệu ở nguồn này. Nhập các khối bên dưới rồi bấm Lưu số liệu để tạo.',
    chuaNhapChiXem: 'Phân hệ {{ten}} chưa có số liệu ở nguồn này.',
    chiXem: 'Bạn chỉ có quyền xem phân hệ này, không sửa được số liệu.',
    nguonDaNgungBanner: 'Nguồn “{{ten}}” đã ngừng hoạt động: xem được nhưng không ghi thêm được. Bật lại nguồn ở màn Nguồn dữ liệu.',
    conLoi: 'Còn {{so}} ô cần sửa trước khi lưu. Lỗi hiện ngay dưới từng ô.',
    khongLuuDuoc: 'Chưa lưu được: {{loi}}',
    daLuuPhanHe: 'Đã lưu số liệu {{ten}}.',
    luuSoLieu: 'Lưu số liệu',
    boThayDoi: 'Bỏ thay đổi',
    thongTinChung: 'Thông tin chung',
    tieuDePhanHe: 'Tiêu đề phân hệ',
    tieuDeKhoiTaiLieu: 'Tiêu đề khối tài liệu',
    tieuDeKhoiTaiLieuGoiY: 'Để trống thì ứng dụng dùng tiêu đề mặc định.',
    soTaiLieuPhanHe: 'Tài liệu thuộc phân hệ:',
    capNhatLuc: 'Cập nhật lúc:',
    ghiChuGiaTri: 'Nhập chuỗi đã định dạng theo quy ước Việt (“8,42 tỷ”, “+12,4%”). Ứng dụng hiển thị nguyên văn, không tự quy đổi.',
    ghiChuXuHuong: 'Chọn riêng, không suy từ dấu của biến động: “-4,1%” ở Công nợ là tin tốt.',
    ghiChuSpark: 'Ít nhất 2 điểm, cùng đơn vị với giá trị, điểm cuối khớp giá trị ở trên. Ngăn cách bằng dấu phẩy (7.1, 8.42) hoặc dấu chấm phẩy (7,1; 8,42).',
    hero: {
      tieuDe: 'Thẻ chủ đạo',
      nhan: 'Nhãn',
      giaTri: 'Giá trị',
      chuThich: 'Chú thích',
      delta: 'Biến động',
      xuHuong: 'Xu hướng',
      spark: 'Đường xu hướng',
      sparkGoiY: '7.1, 7.9, 8.42',
      them: 'Thêm thẻ chủ đạo',
      go: 'Gỡ thẻ chủ đạo',
      trong: 'Chưa có thẻ chủ đạo. Ứng dụng sẽ ẩn khối này.',
    },
    chiSo: {
      tieuDe: 'Ô chỉ số',
      them: 'Thêm chỉ số',
      ma: 'Mã',
      nhan: 'Nhãn',
      giaTri: 'Giá trị',
      mauNhan: 'Màu nhấn',
      bieuTuong: 'Biểu tượng',
      chuaDatTen: 'Chỉ số chưa đặt tên',
      trong: 'Chưa có ô chỉ số nào. Ứng dụng sẽ ẩn khối này.',
      go: 'Gỡ chỉ số {{ten}}',
    },
    xepHang: {
      tieuDe: 'Bảng xếp hạng',
      tenBang: 'Tiêu đề bảng',
      them: 'Thêm bảng xếp hạng',
      themDong: 'Thêm dòng',
      trong: 'Chưa có bảng xếp hạng. Ứng dụng sẽ ẩn khối này.',
      trongDong: 'Bảng chưa có dòng nào.',
      go: 'Gỡ bảng xếp hạng',
      goDong: 'Gỡ dòng {{ten}}',
    },
    xuHuongGiaTri: {
      up: 'Tăng',
      down: 'Giảm',
      flat: 'Đi ngang',
    },
    mauNhan: {
      primary: 'Màu chủ đạo',
      success: 'Xanh lá',
      warning: 'Hổ phách',
      danger: 'Đỏ',
      violet: 'Tím',
      teal: 'Xanh mòng két',
      sky: 'Xanh da trời',
      orange: 'Cam',
    },
    bieuTuong: {
      banknote: 'Tờ tiền',
      receipt: 'Hoá đơn',
      droplet: 'Giọt nước',
      'trending-up': 'Mũi tên đi lên',
      wallet: 'Ví tiền',
      'chart-column': 'Biểu đồ cột',
      folder: 'Thư mục',
      'clipboard-list': 'Bảng kê',
      calendar: 'Lịch',
      users: 'Nhóm người',
      'user-check': 'Người đã duyệt',
      briefcase: 'Cặp tài liệu',
      target: 'Bia ngắm',
      handshake: 'Bắt tay',
      package: 'Kiện hàng',
      'file-text': 'Trang văn bản',
    },
    danhMuc: {
      finance: 'Tài chính',
      projects: 'Dự án',
      sales: 'Kinh doanh',
      people: 'Nhân sự',
      other: 'Khác',
    },
    loaiTep: {
      pdf: 'PDF',
      web: 'Trang web',
      document: 'Tài liệu Office',
      unknown: 'Không rõ',
    },
    loi: {
      batBuoc: 'Nhập nội dung cho ô này.',
      maTrung: 'Mã này đã dùng cho một chỉ số khác. Đặt mã khác.',
      sparkSai: 'Có điểm không phải số. Chỉ nhập số, ngăn cách bằng dấu phẩy hoặc dấu chấm phẩy.',
      sparkThieu: 'Nhập ít nhất 2 điểm.',
      urlSai: 'Nhập đường dẫn đầy đủ, bắt đầu bằng http:// hoặc https://.',
      chuaChonTep: 'Chọn một tệp để tải lên.',
      tepKhongNhan: 'Chỉ nhận tệp PDF, Word (.docx), Excel (.xlsx) và PowerPoint (.pptx).',
      tepQuaLon: 'Tệp vượt quá 50 MB. Chia nhỏ hoặc nén tệp rồi chọn lại.',
      tepRong: 'Tệp rỗng. Chọn tệp khác.',
      nguonDaNgung: 'Nguồn này đã ngừng hoạt động nên không ghi thêm được. Bật lại nguồn ở màn Nguồn dữ liệu rồi thử lại.',
      nguonMacDinhKhoa: 'Không ngừng được nguồn mặc định, và không đặt được nguồn đã ngừng làm mặc định. Đổi nguồn mặc định hoặc bật lại nguồn trước.',
      thuMucConNoiDung: 'Thư mục còn thư mục con hoặc tài liệu. Chuyển chúng đi rồi xoá lại.',
      thuMucChuTrinh: 'Không chuyển được thư mục vào chính nó hoặc thư mục con của nó. Chọn chỗ khác.',
    },
    taiLieu: {
      moTa: 'Tài liệu báo cáo ứng dụng di động hiển thị ở từng phân hệ, theo danh mục. Thư mục chỉ để sắp xếp.',
      them: 'Thêm tài liệu',
      themDauTien: 'Thêm tài liệu đầu tiên',
      themTieuDe: 'Thêm tài liệu',
      suaTieuDe: 'Sửa tài liệu',
      sua: 'Sửa tài liệu',
      luuThayDoi: 'Lưu thay đổi',
      daThem: 'Đã thêm tài liệu “{{ten}}”.',
      daLuu: 'Đã lưu tài liệu “{{ten}}”.',
      tim: 'Tìm theo tiêu đề, tóm tắt',
      tatCaDanhMuc: 'Mọi danh mục',
      locDanhMuc: 'Lọc theo danh mục',
      dangHien: 'Đang hiện {{hien}} trong {{tong}} tài liệu',
      caption: 'Tài liệu báo cáo trong {{noi}}',
      trong: 'Nguồn này chưa có tài liệu nào',
      trongMoTa: 'Tải tệp báo cáo lên hoặc thêm liên kết để ứng dụng di động hiển thị.',
      rongLoc: 'Không có tài liệu khớp bộ lọc',
      loiTai: 'Không tải được danh sách tài liệu: {{loi}}',
      loiTaiThem: 'Không tải thêm được: {{loi}}',
      cotTieuDe: 'Tiêu đề',
      cotDanhMuc: 'Danh mục',
      cotLoai: 'Loại',
      cotCapNhat: 'Cập nhật',
      daChon: 'Đã chọn {{so}} tài liệu',
      boChon: 'Bỏ chọn',
      chuyenSo: 'Chuyển {{so}} tài liệu',
      chuyenThuMuc: 'Chuyển thư mục',
      chuyenTieuDe: 'Chuyển {{so}} tài liệu sang thư mục khác?',
      chuyenToi: 'Chuyển tới',
      daChuyen: 'Đã chuyển {{so}} tài liệu.',
      chuyenDoDang: 'Đã chuyển {{xong}}/{{tong}} tài liệu thì dừng: {{loi}}',
      moTep: 'Mở tệp',
      moLienKet: 'Mở liên kết',
      go: 'Gỡ khỏi ứng dụng',
      goTieuDe: 'Gỡ “{{ten}}” khỏi ứng dụng di động?',
      goThan: 'Người dùng ứng dụng không còn thấy tài liệu này, và nó biến mất khỏi danh sách ở đây. Tệp đã tải lên vẫn được giữ trên máy chủ. Màn này chưa có cách hiện lại tài liệu đã gỡ.',
      daGo: 'Đã gỡ “{{ten}}” khỏi ứng dụng.',
      noiDung: 'Nội dung tài liệu',
      tepTaiLen: 'Tải tệp lên',
      lienKetNgoai: 'Liên kết ngoài',
      tep: 'Tệp',
      tepGoiY: 'PDF hoặc Office (.docx, .xlsx, .pptx), tối đa 50 MB. Ứng dụng mở tệp qua đường dẫn tạm có hạn 5 phút.',
      chonTep: 'Chọn tệp',
      chonTepKhac: 'Chọn tệp khác',
      seThayTep: 'Sẽ thay tệp cũ khi lưu',
      url: 'Đường dẫn',
      urlGoiY: 'https://files.congty.vn/bao-cao-q3.pdf',
      ghiChuUrl: 'Đường dẫn đầy đủ, bắt đầu bằng http:// hoặc https://. Link http chỉ mở được trong mạng nội bộ.',
      ghiChuUrlBoTep: 'Lưu liên kết này thì tệp đang gắn bị bỏ khỏi tài liệu.',
      danhMucGoiY: 'Quyết định phân hệ nào của ứng dụng hiện tài liệu.',
      tomTat: 'Tóm tắt',
      meta: 'Dòng mô tả',
      ghiChuMeta: 'Chuỗi để đọc, ví dụ “PDF · 2,4 MB”.',
    },
    thuMuc: {
      tieuDe: 'Thư mục',
      tatCa: 'Tất cả tài liệu',
      chuaPhanLoai: 'Chưa phân loại',
      ngoaiCung: 'Ngoài cùng (không có thư mục cha)',
      chonThuMuc: 'Chọn thư mục',
      chuaCoThuMuc: 'Chưa có thư mục nào. Tạo thư mục để sắp xếp tài liệu.',
      loiTai: 'Không tải được cây thư mục: {{loi}}',
      moRong: 'Mở rộng {{ten}}',
      thuGon: 'Thu gọn {{ten}}',
      them: 'Thêm thư mục',
      themCon: 'Thêm thư mục con',
      themTieuDe: 'Thêm thư mục',
      themConTieuDe: 'Thêm thư mục con',
      taoTrong: 'Tạo trong: {{ten}}',
      taoThuMuc: 'Tạo thư mục',
      daTao: 'Đã tạo thư mục “{{ten}}”.',
      ten: 'Tên thư mục',
      tenGoiY: 'Báo cáo tháng 10',
      doiTen: 'Đổi tên',
      doiTenTieuDe: 'Đổi tên thư mục',
      daDoiTen: 'Đã đổi tên thư mục thành “{{ten}}”.',
      chuyen: 'Chuyển tới thư mục khác',
      chuyenTieuDe: 'Chuyển thư mục “{{ten}}”',
      chuyenVao: 'Thư mục cha mới',
      chuyenNut: 'Chuyển thư mục',
      daChuyen: 'Đã chuyển thư mục “{{ten}}”.',
      xoa: 'Xoá thư mục',
      xoaTieuDe: 'Xoá thư mục “{{ten}}”?',
      xoaThan: 'Chỉ xoá được thư mục rỗng. Thao tác này không hoàn tác được.',
      daXoa: 'Đã xoá thư mục “{{ten}}”.',
      trong: 'Thư mục này chưa có tài liệu nào',
      trongMoTa: 'Thêm tài liệu vào đây, hoặc chọn tài liệu ở thư mục khác rồi chuyển sang.',
      chuaPhanLoaiTrong: 'Mọi tài liệu đã được xếp vào thư mục',
      chuaPhanLoaiTrongMoTa: 'Tài liệu thêm mới mà không chọn thư mục sẽ nằm ở đây.',
      keCaThuMucCon: 'Kèm thư mục con',
    },
    nguon: {
      moTa: 'Mỗi nguồn giữ bộ số liệu và tài liệu riêng. Ứng dụng di động đọc nguồn mặc định.',
      chon: 'Nguồn đang xem',
      tenMacDinh: '{{ten}} (mặc định)',
      tenDaNgung: '{{ten}} — đã ngừng',
      loiTai: 'Không tải được danh sách nguồn: {{loi}}',
      rong: 'Chưa có nguồn dữ liệu nào',
      rongMoTa: 'Tạo một nguồn để nhập số liệu, hoặc để hệ thống ngoài đẩy số liệu vào.',
      them: 'Thêm nguồn',
      themDauTien: 'Thêm nguồn đầu tiên',
      themTieuDe: 'Thêm nguồn dữ liệu',
      suaTieuDe: 'Sửa nguồn dữ liệu',
      sua: 'Sửa nguồn',
      luu: 'Lưu thay đổi',
      daThem: 'Đã thêm nguồn “{{ten}}”.',
      daLuu: 'Đã lưu nguồn “{{ten}}”.',
      cotTen: 'Nguồn',
      cotTaiLieu: 'Tài liệu',
      cotCachNhap: 'Cách nhập',
      cotCapNhat: 'Cập nhật',
      nhapTay: 'Nhập tay',
      heThongNgoai: 'Hệ thống ngoài',
      hoatDong: 'Đang hoạt động',
      dungHoatDong: 'Đã ngừng',
      macDinh: 'Mặc định',
      ten: 'Tên hiển thị',
      tenGoiY: 'Phần mềm kế toán Misa',
      tenKhongSinhDuocMa: 'Tên phải có ít nhất một chữ cái hoặc chữ số.',
      maSeLa: 'Mã nguồn sẽ là',
      maLa: 'Mã nguồn',
      maKhongDoiDuoc: 'Mã sinh từ tên, đi trong đường dẫn của ứng dụng và cấu hình của hệ thống ngoài. Tạo xong thì không đổi được.',
      moTaTruong: 'Mô tả',
      laNhapTay: 'Nhập số liệu bằng tay trên màn này (bỏ chọn nếu hệ thống ngoài đẩy vào)',
      datMacDinh: 'Đặt làm mặc định',
      batLaiTruoc: 'Bật lại nguồn trước',
      macDinhTieuDe: 'Đặt “{{ten}}” làm nguồn mặc định?',
      macDinhThan: 'Ứng dụng di động sẽ hiển thị số liệu và tài liệu của nguồn này cho mọi người dùng, thay cho nguồn mặc định hiện tại.',
      daDatMacDinh: 'Đã đặt “{{ten}}” làm nguồn mặc định.',
      ngung: 'Ngừng hoạt động',
      ngungTieuDe: 'Ngừng hoạt động nguồn “{{ten}}”?',
      ngungThan: 'Số liệu và tài liệu của nguồn vẫn xem được, nhưng không ai ghi thêm được, kể cả hệ thống ngoài. Bật lại được bất cứ lúc nào.',
      daNgung: 'Đã ngừng nguồn “{{ten}}”.',
      batLai: 'Bật lại',
      daBatLai: 'Đã bật lại nguồn “{{ten}}”.',
    },
    tichHop: {
      tieuDe: 'Tích hợp hệ thống ngoài',
      moTa: 'Phần mềm kế toán, ERP hay hệ thống khác đẩy số liệu vào bằng khoá API của tổ chức. Mỗi lời gọi chọn nguồn bằng tham số source.',
      diaChiGoc: 'Địa chỉ gốc',
      xacThuc: 'Xác thực',
      chonNguon: 'Chọn nguồn',
      chonNguonMo: 'Thêm ?source=<mã nguồn> vào mỗi lời gọi; bỏ trống là nguồn mặc định.',
      dayPhanHe: 'Ghi đè số liệu một phân hệ (overview, finance, projects, sales, people).',
      dayTaiLieu: 'Đẩy một lô 1–200 tài liệu; đẩy lại cùng externalId là cập nhật, không tạo trùng.',
      doiSoat: 'Đọc lại để đối soát, phân trang bằng nextCursor.',
      goTaiLieu: 'Gỡ một tài liệu khỏi ứng dụng.',
      phamVi: 'Khoá đồng bộ từ phần mềm kế toán thường cần các phạm vi:',
      moKhoaApi: 'Tạo khoá API ở màn Khoá API',
      nhoQuanTri: 'Nhờ quản trị tổ chức tạo khoá ở màn Cấu hình hệ thống › Khoá API.',
    },
  },
  /**
   * Module eKYC (Định danh eKYC), dựng theo bộ bàn giao `docs/eKYC/`.
   *
   * Chữ ở đây phải nói đúng ba khoảng cách mà cả phân hệ xoay quanh — viết lẫn là nói sai với người
   * đang quyết định về một con người:
   *
   * - "chưa kiểm được" (`ERROR`) khác "đã kiểm và không khớp" (`FAIL`);
   * - "đối tác tắt" (`SKIPPED`) khác "chưa chạy";
   * - "không đo được" (yếu tố `null`) khác "đo ra 0".
   *
   * Giữ nguyên tiếng Anh: `eKYC`, `OCR`, `MRZ`, `webhook`, `model`, và mọi mã (`RC_*`, `HR-*`,
   * `E_*`, `LOGIC_*`) — mã là **hợp đồng** với đối tác và cơ quan quản lý, dịch là đổi hợp đồng.
   */
  ekyc: {
    // ───── Tiêu đề và mô tả từng màn ─────
    loiSoLieu: 'Không tải được số liệu tổng quan: {{loi}}',
    loiTrienKhai: 'Không đọc được cấu hình triển khai: {{loi}}',
    loiDanhSach: 'Không tải được danh sách hồ sơ: {{loi}}',
    loiChiTiet: 'Không tải được hồ sơ: {{loi}}',
    loiNhatKy: 'Không tải được nhật ký: {{loi}}',
    loiTaiThem: 'Không tải thêm được: {{loi}}',
    tongQuanTieuDe: 'Tổng quan định danh',
    tongQuanMo: 'Số liệu {{so}} ngày gần nhất.',
    hoSoTieuDe: 'Hồ sơ định danh',
    hoSoMo: 'Hồ sơ của mọi đối tác. Mỗi lần mở đều được ghi vào nhật ký.',
    chiTietTieuDe: 'Hồ sơ {{ma}}',
    chiTietMo: 'Kết quả từng hạng mục và căn cứ của quyết định.',

    // ───── Màn Định danh người dùng (tại quầy) ─────
    dinhDanh: {
      ungDung: 'Ứng dụng',
      quyTrinh: 'Quy trình định danh',
      quyTrinhMacDinh: '{{ten}} (mặc định)',
      quyTrinhMacDinhHeThong: 'Ứng dụng chưa có quy trình nào, phiên chạy theo mặc định của hệ thống.',
      tieuDe: 'Định danh người dùng',
      moTa: 'Thực hiện toàn bộ luồng định danh ngay trên màn này.',
      loaiGiayTo: 'Loại giấy tờ',
      maHoSo: 'Mã hồ sơ của tổ chức',
      maHoSoGoiY: 'Ví dụ: số hợp đồng, mã khách hàng',
      maHoSoMo: 'Để tìm lại hồ sơ này sau. Không nhập tên hay số giấy tờ của khách.',
      luong: 'Luồng định danh sẽ chạy',
      banThucHien: 'Bạn thực hiện',
      heThongTuChay: 'Hệ thống tự chạy',
      buocDongY: 'Đồng ý xử lý dữ liệu',
      buocDongYMo: 'Khách đọc và chấp nhận điều khoản trước khi gửi bất kỳ ảnh nào.',
      buocMatTruoc: 'Chụp mặt trước',
      buocMatTruocMo: 'Không có mặt trước thì không có gì để định danh.',
      buocMatSau: 'Chụp mặt sau',
      buocMatSauMo: 'CCCD gắn chip có dữ liệu ở mặt sau. Hộ chiếu thì không cần.',
      buocChanDung: 'Chụp ảnh chân dung',
      buocChanDungMo: 'Để đối chiếu với ảnh trên giấy tờ.',
      chuaChay: 'Chưa chạy ở bản này',
      chuaChayMo: 'Các bước tự động chưa bật, nên hồ sơ nộp lên sẽ chờ một người thẩm định.',
      dongYTieuDe: 'Đồng ý xử lý dữ liệu cá nhân',
      dongYMo: 'Để định danh bạn, chúng tôi cần xử lý ảnh giấy tờ và ảnh khuôn mặt của bạn.',
      dongYY1: 'Ảnh giấy tờ và ảnh khuôn mặt được lưu trong suốt thời gian hồ sơ định danh của bạn còn hiệu lực.',
      dongYY2: 'Dữ liệu chỉ dùng để định danh và phòng chống gian lận, không dùng cho mục đích khác và không chia sẻ ra ngoài.',
      dongYY3: 'Bạn có quyền yêu cầu xem lại hoặc xoá dữ liệu của mình bất cứ lúc nào — liên hệ nơi bạn đang giao dịch.',
      dongYNut: 'Đồng ý và bắt đầu',
      khongDongYNut: 'Tôi không đồng ý',
      dangGhiNhan: 'Đang ghi nhận...',
      khachTuBam: 'Mời khách tự bấm: bản ghi đồng ý phải là lựa chọn của chính khách.',
      tuChoiTieuDe: 'Đã ghi nhận khách không đồng ý',
      tuChoiMo: 'Không ảnh nào được thu. Bản ghi từ chối vẫn được lưu làm bằng chứng.',
      anhTieuDe: 'Ảnh của khách',
      anhMo: 'Ảnh JPEG hoặc PNG, cạnh ngắn ít nhất 480 px, tối đa 10 MB.',
      dangTaiAnh: 'Đang tải lên và kiểm tra ảnh...',
      daNhanAnh: 'Đã nhận · {{rong}} × {{cao}} px',
      batBuoc: 'Bắt buộc',
      nopNut: 'Nộp hồ sơ',
      dangNop: 'Đang nộp...',
      conThieu: 'Còn thiếu {{so}} ảnh bắt buộc.',
      daNop: 'Đã nộp hồ sơ, đang chờ thẩm định.',
      huyNut: 'Huỷ phiên',
      huyTieuDe: 'Huỷ phiên định danh này?',
      huyThan: 'Ảnh đã tải lên sẽ không được dùng cho hồ sơ nào. Không hoàn tác được.',
      huyXacNhan: 'Huỷ phiên định danh',
      daHuy: 'Đã huỷ phiên định danh.',
      dinhDanhKhac: 'Định danh người khác',
      khongQuyenTao: 'Tài khoản của bạn xem được hồ sơ nhưng không mở được phiên định danh mới. Liên hệ quản trị tổ chức nếu bạn cần định danh khách.',
    },

    // ───── Màn Tổng quan ─────
    tongHoSo: 'Tổng hồ sơ',
    tongHoSoSub: 'Trong {{so}} ngày gần nhất',
    dangChoNguoi: 'Đang chờ người',
    dangChoNguoiSub: 'Bấm để mở hàng chờ',
    tiLeTuDong: 'Tỉ lệ tự động',
    tiLeTuDongSub: 'Trên số hồ sơ đã xong',
    thoiGianXuLy: 'Thời gian xử lý',
    thoiGianXuLySub: 'Trung bình mỗi hồ sơ',
    lyDoHayGap: 'Lý do hay gặp',
    lyDoHayGapRong: 'Chưa hồ sơ nào mang mã lý do.',
    theoTrangThai: 'Theo trạng thái',
    theoTrangThaiRong: 'Chưa có hồ sơ nào.',

    // Thẻ toàn vẹn nhật ký. "Chưa kiểm được" KHÔNG phải "chuỗi hỏng" — ở v3 đường này luôn 403.
    toanVen: 'Toàn vẹn nhật ký',
    toanVenOk: 'Nguyên vẹn',
    toanVenOkMo: 'Đã kiểm {{so}} dòng, chuỗi băm liền mạch.',
    toanVenGay: 'Chuỗi băm đứt',
    toanVenGayMo: 'Đứt từ dòng số {{so}}. Báo quản trị hệ thống để đối chiếu bản sao lưu.',
    toanVenChua: 'Chưa kiểm được',
    toanVenChuaMo: 'Chưa đọc được kết quả kiểm. Đây không phải dấu hiệu nhật ký bị sửa — nhiều khả năng đường kiểm chưa được khai quyền ở gateway.',

    // Thẻ triển khai (`02` §7.2). Nó tồn tại để LÀM HIỆN RA việc ai đang kiểm quyền.
    trienKhai: 'Kết nối của service định danh',
    trienKhaiNguoiDung: 'Người dùng',
    trienKhaiDuLieu: 'Dữ liệu',
    trienKhaiPhanQuyen: 'Phân quyền quản trị',
    trienKhaiBocTach: 'Bóc tách giấy tờ',
    trienKhaiGateway: 'Do API Gateway kiểm',
    trienKhaiTuKiem: 'Service tự kiểm',
    trienKhaiCanhBao: 'Không có gateway phía trước — mọi tài khoản đăng nhập được đều có toàn quyền trên dữ liệu định danh.',
    cheDo: {
      NATIVE: 'Chạy cùng Native',
      STANDALONE: 'Chạy độc lập',
      MIXED: 'Cấu hình hỗn hợp',
    },

    // ───── Màn Hồ sơ định danh ─────
    timHoSo: 'Tìm theo mã hồ sơ của đối tác',
    cotHoSo: 'Hồ sơ',
    cotQuyetDinh: 'Quyết định',
    cotRuiRo: 'Rủi ro',
    cotTaoLuc: 'Tạo lúc',
    dangTaiThem: 'Đang tải...',
    bangHoSo: 'Danh sách hồ sơ định danh',
    taiThem: 'Tải thêm',
    rong: 'Chưa có hồ sơ định danh nào',
    rongMo: 'Hồ sơ sẽ hiện ở đây khi đối tác mở phiên định danh qua API, hoặc khi nhân viên định danh khách tại quầy.',
    rongLoc: 'Không có hồ sơ nào khớp',
    xemChiTiet: 'Xem hồ sơ',
    // Thao tác trên một dòng. "Xoá" ở đây là xoá DỮ LIỆU CÁ NHÂN: dòng hồ sơ ở lại làm bằng chứng đã xoá.
    huyHoSo: 'Huỷ hồ sơ',
    xoaDuLieu: 'Xoá dữ liệu cá nhân',
    xoaPhaiHuyTruoc: 'Hồ sơ đang chạy, huỷ hồ sơ trước rồi mới xoá được',
    huyHoiTieuDe: 'Huỷ hồ sơ {{ma}}?',
    huyHoiThan: 'Khách không làm tiếp được hồ sơ này; muốn định danh lại phải mở hồ sơ mới. Không hoàn tác được.',
    daHuy: 'Đã huỷ hồ sơ {{ma}}.',
    xoaHoiTieuDe: 'Xoá dữ liệu cá nhân của hồ sơ {{ma}}?',
    xoaHoiThan: 'Ảnh, dữ liệu đọc từ giấy tờ, mã hồ sơ của đối tác, thiết bị và IP trong nhật ký bị xoá vĩnh viễn; hồ sơ biến khỏi danh sách. Chỉ giữ bản ghi đồng ý và mốc thời gian làm bằng chứng đã xoá (Nghị định 13/2023/NĐ-CP). Không hoàn tác được.',
    daXoa: 'Đã xoá dữ liệu cá nhân của hồ sơ {{ma}}.',

    tab: {
      tatCa: 'Tất cả',
      choNguoi: 'Chờ người',
      dangChay: 'Đang chạy',
      dat: 'Đạt',
      tuChoi: 'Từ chối',
      roi: 'Rơi',
    },

    // ───── Màn Chi tiết ─────
    khongTimThay: 'Không tìm thấy hồ sơ này',
    khongTimThayMo: 'Hồ sơ có thể đã bị xoá theo yêu cầu của chủ thể dữ liệu, hoặc mã hồ sơ không đúng.',
    hangMuc: 'Năm hạng mục kiểm tra',
    canCu: 'Căn cứ quyết định',
    yeuToRuiRo: 'Yếu tố rủi ro',
    maLyDo: 'Mã lý do',
    luatPhuQuyet: 'Luật phủ quyết đã kích hoạt',
    luatPhuQuyetRong: 'Không luật phủ quyết nào kích hoạt.',
    diemRuiRo: 'Điểm rủi ro',
    chinhSach: 'Chính sách áp dụng',
    thoiGian: 'Thời gian xử lý',
    soLanChup: 'Số lần chụp chân dung',
    soLanChupCon: '{{da}} lần, còn {{con}} lượt',
    soLanChupHet: '{{da}} lần, đã hết lượt',

    // Quyết định chưa có: đường hết hạn mức AI cố ý để trống (`01` §4.6).
    chuaCoQuyetDinh: 'Chưa có quyết định',
    chuaCoQuyetDinhMo: 'Phiên dừng trước khi đọc được giấy tờ, nên hệ thống không đưa ra kết luận nào về người này.',

    // Banner máy chủ hỏng (`07` §22.10).
    loiMayChu: 'Hồ sơ dừng vì lỗi máy chủ',
    loiMayChuMo: 'Chưa phép kiểm nào chạy xong. Đây không phải dấu hiệu về người nộp hồ sơ — xử lý xong sự cố thì mời khách định danh lại.',

    // Banner chưa vào sổ (`07` §23.1) — banner THƯỜNG TRÚ, không phải toast.
    chuaVaoSo: 'Hồ sơ đã duyệt nhưng chưa vào sổ định danh',
    chuaVaoSoMo: 'Người này nằm ngoài phép kiểm trùng và vẫn đăng ký lại được bằng giấy tờ cũ. Kiểm tra lại nguồn bóc tách giấy tờ trước khi tin vào kết quả duyệt.',

    // Vùng dữ liệu cá nhân và ảnh — mở theo yêu cầu, mỗi lần mở để lại vết.
    duLieuGiayTo: 'Dữ liệu trên giấy tờ',
    moDuLieu: 'Mở dữ liệu giấy tờ',
    moDuLieuMo: 'Mở dữ liệu cá nhân sẽ ghi một dòng nhật ký kèm tên bạn.',
    duLieuDaXoa: 'Dữ liệu cá nhân của hồ sơ này đã được xoá theo hạn lưu.',
    chuaBocTach: 'Chưa có dữ liệu bóc từ giấy tờ: bước đọc giấy tờ chưa chạy ở bản này. Đối chiếu trực tiếp trên ảnh.',
    anhHoSo: 'Ảnh đã nộp',
    moAnh: 'Mở {{ten}}',
    moAnhMo: 'Mỗi lần mở ảnh sẽ ghi một dòng nhật ký kèm tên bạn.',
    anhLoi: 'Không mở được ảnh. Thử lại, hoặc kiểm tra quyền xem ảnh của tài khoản.',
    mayBocRa: 'Máy bóc ra',
    khachSua: 'Khách đã sửa',
    doTinCay: 'Độ tin cậy bóc tách: {{so}}',
    doTinCayToanTaiLieu: 'Tính cho cả tài liệu, không theo từng trường.',

    truong: {
      idNumber: 'Số định danh',
      oldIdNumber: 'Số CMND cũ',
      fullName: 'Họ và tên',
      dob: 'Ngày sinh',
      gender: 'Giới tính',
      issueDate: 'Ngày cấp',
      expiryDate: 'Ngày hết hạn',
      issuePlace: 'Nơi cấp',
      residence: 'Nơi thường trú',
      nationality: 'Quốc tịch',
      mrz: 'Dải MRZ',
    },

    gioiTinh: {
      NAM: 'Nam',
      NU: 'Nữ',
      KHAC: 'Khác',
    },

    // Nhật ký.
    nhatKy: 'Nhật ký hồ sơ',
    nhatKyRong: 'Chưa có dòng nhật ký nào.',
    nhatKyCotLuc: 'Lúc',
    nhatKyCotHanhDong: 'Hành động',
    nhatKyCotNguoi: 'Người thực hiện',
    nhatKyChuaNoi: 'Chưa nối chuỗi',
    nhatKyChuaNoiMo: 'Dòng này chưa được job nối vào chuỗi băm (chạy 5 phút một lần). Dòng vẫn đọc được, chỉ là chưa chống được sửa lén.',
    bangNhatKy: 'Nhật ký thao tác trên hồ sơ định danh',

    // ───── Nhãn chung ─────
    khongCoTinHieu: 'Không có tín hiệu',
    khongCoTinHieuMo: 'Hạng mục này không chạy, nên không có số liệu để tính — khác với đo ra 0.',
    chuaCoPhanHe: 'Chưa có phân hệ',
    nguongLa: 'Ngưỡng {{so}}',
    vuotNguong: 'Vượt ngưỡng {{so}}',
    duoiNguong: 'Thiếu {{so}} so với ngưỡng',
    vanDe: 'Vấn đề ghi nhận',
    phienBanModel: 'Model {{ten}}',

    trangThai: {
      CREATED: 'Vừa tạo',
      CONSENT_REQUIRED: 'Chờ đồng ý',
      DOCUMENT_REQUIRED: 'Chờ ảnh giấy tờ',
      OCR_PROCESSING: 'Đang bóc tách',
      FRAUD_CHECKING: 'Đang kiểm giả mạo',
      INFO_CONFIRMATION: 'Chờ khách xác nhận',
      SELFIE_REQUIRED: 'Chờ ảnh chân dung',
      LIVENESS_PROCESSING: 'Đang kiểm thực thể sống',
      VERIFICATION_PROCESSING: 'Đang đối chiếu',
      VERIFIED: 'Đạt',
      REJECTED: 'Từ chối',
      MANUAL_REVIEW: 'Chờ thẩm định',
      ADDITIONAL_REQUIRED: 'Chờ bổ sung',
      EXPIRED: 'Hết hạn',
      CANCELLED: 'Khách huỷ',
      ERROR: 'Lỗi hệ thống',
      DELETED: 'Đã xoá',
    },

    quyetDinh: {
      PASS: 'Đạt',
      REVIEW: 'Cần xem',
      REJECT: 'Từ chối',
    },

    ruiRo: {
      LOW: 'Thấp',
      MEDIUM: 'Trung bình',
      HIGH: 'Cao',
    },

    ketLuan: {
      PASS: 'Đạt',
      REVIEW: 'Cần xem',
      FAIL: 'Không đạt',
      SKIPPED: 'Đã tắt',
      ERROR: 'Chưa chạy được',
    },

    mucVanDe: {
      LOW: 'Nhẹ',
      MEDIUM: 'Vừa',
      HIGH: 'Nặng',
    },

    tenHangMuc: {
      sanity: 'Chất lượng ảnh',
      logic: 'Đối chiếu dữ liệu',
      fraud: 'Dấu vết giả mạo',
      liveness: 'Thực thể sống',
      faceMatch: 'Đối chiếu khuôn mặt',
    },

    hangMucMo: {
      sanity: 'Ảnh giấy tờ có đủ rõ để đọc không.',
      logic: 'Dữ liệu bóc ra có hợp lệ và khớp nhau không.',
      fraud: 'Có dấu vết chỉnh sửa hay chụp lại màn hình không.',
      liveness: 'Người trước camera có phải người sống không.',
      faceMatch: 'Mặt trên giấy tờ và mặt trước camera có là một người không.',
    },

    yeuTo: {
      fraud: 'Dấu vết giả mạo',
      liveness: 'Thực thể sống',
      pad: 'Chống giả mạo chuyên biệt',
      face: 'Đối chiếu khuôn mặt',
      document: 'Chất lượng giấy tờ',
      ocr: 'Độ tin cậy bóc tách',
      device: 'Thiết bị',
      authority: 'Đối soát cơ quan',
      behavior: 'Hành vi',
    },

    loaiGiayTo: {
      VIETNAM_CCCD: 'Căn cước / CCCD gắn chip',
      VIETNAM_CCCD_BARCODE: 'CCCD mã vạch',
      VIETNAM_CMND: 'CMND 9 số',
      VIETNAM_PASSPORT: 'Hộ chiếu',
      VIETNAM_DRIVER_LICENSE: 'Giấy phép lái xe',
      LAOS_ID: 'Thẻ căn cước Lào',
    },

    anh: {
      DOCUMENT_FRONT: 'ảnh mặt trước',
      DOCUMENT_BACK: 'ảnh mặt sau',
      SELFIE: 'ảnh chân dung',
    },

    // ───── Quy trình định danh (ADR-0085 mục 3) ─────
    // ───── Chụp ảnh ở màn Định danh (camera trong trang + chọn ảnh có sẵn) ─────
    chupAnh: {
      moCamera: 'Mở camera',
      chupLaiCamera: 'Chụp lại bằng camera',
      chonCoSan: 'Chọn ảnh có sẵn',
      chonCoSanCua: 'Chọn ảnh có sẵn cho {{ten}}',
      chup: 'Chụp',
      dong: 'Đóng camera',
      khungCamera: 'Khung camera để chụp {{ten}}',
      anhVuaChup: 'Ảnh {{ten}} vừa chụp',
      chuaNhan: 'Ảnh này chưa được nhận.',
      meoTieuDeGiayTo: 'Chụp giấy tờ',
      meoGiayTo1: 'Đặt giấy tờ trên mặt phẳng, nền tối và trơn.',
      meoGiayTo2: 'Lấy đủ bốn góc, không cắt mép, không che bằng ngón tay.',
      meoGiayTo3: 'Tránh loá: đừng chụp dưới đèn chiếu thẳng hoặc qua lớp bọc nhựa.',
      meoGiayTo4: 'Chụp trực tiếp giấy tờ thật — ảnh chụp lại từ màn hình sẽ bị phát hiện.',
      meoTieuDeChanDung: 'Chụp ảnh chân dung',
      meoChanDung1: 'Đưa mặt vào giữa khung bầu dục, chiếm gần hết chiều cao khung.',
      meoChanDung2: 'Nhìn thẳng vào camera, giữ đầu ngay ngắn.',
      meoChanDung3: 'Bỏ khẩu trang, mũ và kính râm. Ánh sáng chiếu từ phía trước.',
      cameraYeu: 'Camera này cho ảnh nhỏ hơn {{min}} px — ảnh chụp ra có thể bị từ chối. Nên chọn ảnh có sẵn từ máy ảnh khác.',
      quaNho: 'Ảnh {{rong}}×{{cao}} px quá nhỏ (cạnh ngắn cần từ {{min}} px). Hãy chụp lại gần hơn, hoặc chọn ảnh gốc chưa bị thu nhỏ.',
      loiKhongBaoMat: 'Trình duyệt chỉ cho mở camera trên kết nối bảo mật (HTTPS hoặc localhost). Trang này đang chạy qua HTTP nên không mở được — hãy chọn ảnh có sẵn, hoặc mở trang bằng HTTPS.',
      loiTuChoi: 'Bạn đã chặn quyền camera. Bấm biểu tượng khoá hoặc camera trên thanh địa chỉ để cho phép, rồi thử lại.',
      loiKhongCoThietBi: 'Không tìm thấy camera trên thiết bị này. Hãy chọn ảnh có sẵn.',
      loiDangBan: 'Camera đang được ứng dụng khác dùng. Đóng ứng dụng đó rồi thử lại.',
      loiKhac: 'Không mở được camera. Hãy chọn ảnh có sẵn.',
    },
    mucNopLai: {
      DOCUMENT_FRONT: 'Mặt trước',
      DOCUMENT_BACK: 'Mặt sau',
      SELFIE: 'Chân dung',
      SELFIE_VIDEO: 'Video chân dung',
    },

    thamDinh: {
      raQuyetDinh: 'Ra quyết định',
      ketQuaGanNhat: 'Kết quả thẩm định gần nhất',
      lyDo: 'Lý do',
      lyDoGoiY: 'Người khiếu nại sau này sẽ đọc dòng này. Bắt buộc khi từ chối.',
      lyDoMau: 'Ví dụ: ảnh chân dung không khớp ảnh trên giấy tờ',
      canLyDo: 'Ghi lý do từ chối trước khi từ chối.',
      canNopLai: 'Cần khách nộp lại',
      canNopLaiGoiY: 'Chỉ dùng khi yêu cầu bổ sung. Chọn đúng thứ khách phải chụp lại.',
      canMuc: 'Chọn ít nhất một mục khách cần nộp lại.',
      duyet: 'Duyệt hồ sơ',
      tuChoi: 'Từ chối hồ sơ',
      yeuCauBoSung: 'Yêu cầu bổ sung',
      duyetHoi: 'Duyệt hồ sơ định danh này?',
      duyetThan: 'Hồ sơ chuyển sang Đã duyệt và đối tác nhận kết quả. Đây là trạng thái kết thúc, không hoàn tác được.',
      tuChoiHoi: 'Từ chối hồ sơ định danh này?',
      tuChoiThan: 'Hồ sơ chuyển sang Từ chối kèm lý do bạn ghi, và đối tác nhận kết quả. Đây là trạng thái kết thúc, không hoàn tác được.',
      boSungHoi: 'Yêu cầu khách nộp bổ sung?',
      boSungThan: 'Khách được yêu cầu nộp lại: {{muc}}. Hồ sơ mở lại để nhận ảnh mới và quay về hàng chờ duyệt khi khách nộp.',
      daDuyet: 'Đã duyệt hồ sơ.',
      daTuChoi: 'Đã từ chối hồ sơ.',
      daYeuCauBoSung: 'Đã gửi yêu cầu bổ sung.',
      khongGhiLyDo: 'Người duyệt không ghi lý do.',
      lyDoDaXoa: 'Lý do đã xoá cùng dữ liệu cá nhân của hồ sơ.',
    },
    buoc: {
      dong_y: 'Đồng ý xử lý dữ liệu',
      mat_truoc: 'Chụp mặt trước',
      mat_sau: 'Chụp mặt sau',
      chan_dung: 'Chụp ảnh chân dung',
      nfc: 'Đọc chip NFC',
      chat_luong_anh: 'Kiểm chất lượng ảnh',
      boc_tach: 'Bóc tách thông tin',
      mrz_logic: 'Kiểm QR/MRZ và logic',
      khach_xac_nhan: 'Khách xác nhận thông tin',
      gia_mao: 'Phát hiện giả mạo',
      thuc_the_song: 'Kiểm thực thể sống',
      doi_chieu_mat: 'Đối chiếu khuôn mặt',
      pad: 'PAD chống mặt nạ',
      kiem_trung: 'Kiểm trùng định danh',
      doi_soat_co_quan: 'Đối soát cơ quan cấp',
      kiem_thiet_bi: 'Kiểm thiết bị',
      cham_diem: 'Chấm điểm rủi ro',
      duyet_tay: 'Duyệt tay',
    },

    nhomBuoc: {
      thuThap: 'Thu thập dữ liệu',
      giayTo: 'Xử lý giấy tờ',
      khuonMat: 'Xác thực khuôn mặt',
      doiSoat: 'Đối soát',
      quyetDinh: 'Quyết định',
    },

    quyTrinh: {
      tieuDe: 'Quy trình định danh',
      moTaMan: 'Bật, tắt từng bước của luồng eKYC và đặt chính sách, lưu thành quy trình có tên rồi áp khi mở phiên.',
      chonUngDung: 'Chọn ứng dụng',
      danhSach: 'Danh sách quy trình',
      taoQuyTrinh: 'Tạo quy trình',
      dangTao: 'Đang tạo...',
      luuQuyTrinh: 'Lưu quy trình',
      boThayDoi: 'Bỏ thay đổi',
      quyTrinhMoi: 'Quy trình mới (chưa lưu)',
      macDinh: 'Mặc định',
      dangTat: 'Đang tắt',
      khongHopLe: 'Không còn hợp lệ',
      phienBan: 'Phiên bản {{so}}',
      ten: 'Tên quy trình',
      ma: 'Mã',
      maTuSinh: 'Tự sinh từ tên, không đổi được sau khi tạo.',
      maKhongDoi: 'Không đổi được sau khi tạo.',
      maChoTen: 'Gõ tên để sinh mã',
      tenKhongSinhMa: 'Tên cần có ít nhất một chữ hoặc số để sinh mã.',
      moTa: 'Mô tả',
      cacBuoc: 'Các bước',
      thuTuMayChu: 'Bước chạy theo thứ tự trên, do máy chủ cưỡng chế. Bước chưa khả dụng sẽ tự chạy theo mặc định khi phân hệ của nó có.',
      luonBat: 'Luôn bật',
      tuBat: 'Tự bật vì bước khác cần',
      canBuoc: 'Cần bật: {{ten}}',
      chuaKhaDung: 'Chưa khả dụng',
      chuaKhaDungSeChay: 'Chưa khả dụng · tự chạy khi phân hệ có',
      thieuPhuThuoc: 'Thiếu bước phụ thuộc',
      nguoiDungTat: 'Đã tắt',
      chinhSach: 'Chính sách',
      luuY: 'Lưu ý',
      conLoi: 'Còn lỗi, sửa các ô báo đỏ trước khi lưu.',
      biCu: 'Quy trình vừa được người khác lưu. Tải bản mới nhất rồi sửa lại — thay đổi chưa lưu ở đây sẽ mất.',
      taiBanMoi: 'Tải bản mới nhất',
      daTao: 'Đã tạo quy trình.',
      daLuu: 'Đã lưu thay đổi.',
      daBat: 'Đã bật quy trình.',
      daTat: 'Đã tắt quy trình.',
      daDatMacDinh: 'Đã đặt {{ten}} làm quy trình mặc định.',
      daXoa: 'Đã xoá quy trình.',
      datMacDinh: 'Đặt làm mặc định',
      tatQuyTrinh: 'Tắt quy trình',
      tatQuyTrinhMo: 'Không mở phiên mới theo quy trình này nữa.',
      batQuyTrinh: 'Bật quy trình',
      xoaQuyTrinh: 'Xoá quy trình',
      xoaHoi: 'Xoá quy trình {{ten}}?',
      xoaThan: 'Phiên đã chạy theo quy trình này vẫn giữ nguyên bản chụp chính sách của nó. Không hoàn tác được.',
      loiTai: 'Không tải được cấu hình quy trình: {{loi}}',
      chuaCoUngDung: 'Chưa có ứng dụng nào',
      chuaCoUngDungMo: 'Quy trình thuộc về một ứng dụng. Tạo ứng dụng trước, rồi quay lại đây cấu hình.',
      moUngDung: 'Mở Ứng dụng & khoá API',
      rong: 'Ứng dụng này chưa có quy trình',
      rongMo: 'Phiên mở theo ứng dụng này đang chạy theo mặc định của hệ thống. Tạo quy trình để tuỳ chỉnh bước và chính sách.',
    },

    chinhSachQt: {
      chuaTacDung: 'Một số tham số chỉ có tác dụng khi bước tương ứng khả dụng (thực thể sống, bóc tách, đối soát). Máy chủ vẫn lưu và chụp chúng vào phiên.',
      nguongTieuDe: 'Ngưỡng quyết định',
      nguongMo: 'Áp lên điểm rủi ro từ 0 đến 1: thấp hơn ngưỡng đạt thì duyệt, từ ngưỡng từ chối trở lên thì từ chối, ở giữa thì chờ người xem.',
      nguongDat: 'Ngưỡng đạt',
      nguongDatGoiY: 'Ví dụ 0,30',
      nguongTuChoi: 'Ngưỡng từ chối',
      nguongTuChoiGoiY: 'Lớn hơn ngưỡng đạt, ví dụ 0,70',
      nguongMat: 'Ngưỡng đối chiếu khuôn mặt',
      nguongMatGoiY: 'Từ 0,50 đến 0,95',
      trongSoTieuDe: 'Trọng số rủi ro',
      trongSoMo: 'Chín yếu tố, tổng phải bằng 1. Yếu tố chưa đo được không bị tính là 0.',
      tongTrongSo: 'Tổng: {{tong}}',
      livenessTieuDe: 'Thực thể sống',
      cheDoLiveness: 'Chế độ',
      soDongTac: 'Số động tác mỗi lượt',
      soLanLiveness: 'Số lần thử tối đa',
      boDongTac: 'Bộ động tác',
      thuTuNgauNhien: 'Xáo thứ tự động tác mỗi lượt',
      khiHetLuotLiveness: 'Khi hết lượt thử thực thể sống',
      giayToTieuDe: 'Giấy tờ',
      nguonOcr: 'Nguồn bóc tách',
      nguonOcrGoiY: 'Khoá API của nhà cung cấp đặt ở máy chủ, không nhập ở đây.',
      soLanChupLai: 'Số lần chụp lại tối đa',
      tuoiToiThieu: 'Tuổi tối thiểu',
      tuoiToiThieuGoiY: 'Để trống nếu không giới hạn.',
      truongTraVe: 'Trường giấy tờ trả cho đối tác',
      khiThieuCoQuan: 'Khi không đối soát được với cơ quan cấp',
      webhookTieuDe: 'Webhook',
      guiWebhook: 'Gửi webhook khi hồ sơ có kết quả',
      guiWebhookMo: 'Gửi tới callback URL của phiên, không có thì tới webhook URL của ứng dụng. Tắt thì không có sự kiện nào được ghi.',
    },

    cheDoLiveness: {
      PASSIVE: 'Thụ động (một ảnh)',
      ACTIVE: 'Chủ động (làm động tác)',
      HYBRID: 'Kết hợp',
    },

    dongTac: {
      TURN_LEFT: 'Quay trái',
      TURN_RIGHT: 'Quay phải',
      LOOK_UP: 'Nhìn lên',
      LOOK_DOWN: 'Nhìn xuống',
      MOVE_CLOSER: 'Lại gần camera',
    },

    nguonOcr: {
      macDinh: 'Theo cấu hình chung của máy chủ',
      service: 'Dịch vụ OCR của nền tảng',
      gemini: 'Google Gemini',
      openai: 'OpenAI',
      local: 'Model chạy tại chỗ',
    },

    khiThieu: {
      MANUAL_REVIEW: 'Chuyển người duyệt',
      REJECT: 'Từ chối',
    },

    truongGiayTo: {
      idNumber: 'Số định danh',
      oldIdNumber: 'Số CMND cũ',
      fullName: 'Họ và tên',
      dob: 'Ngày sinh',
      gender: 'Giới tính',
      issueDate: 'Ngày cấp',
      expiryDate: 'Ngày hết hạn',
      issuePlace: 'Nơi cấp',
      residence: 'Nơi thường trú',
      nationality: 'Quốc tịch',
      mrz: 'Dòng MRZ',
    },

    // ───── Ứng dụng & khoá API (ADR-0085 mục 1–2) ─────
    trangThaiGiao: {
      PENDING: 'Đang thử lại',
      SENT: 'Đã gửi',
      FAILED: 'Hết lượt thử',
    },

    ungDung: {
      tieuDe: 'Ứng dụng & khoá API',
      moTaMan: 'Mỗi ứng dụng là một kênh định danh của đối tác: webhook, bí mật ký webhook và quy trình riêng.',
      bang: 'Danh sách ứng dụng',
      taoUngDung: 'Tạo ứng dụng',
      dangTao: 'Đang tạo...',
      luuUngDung: 'Lưu ứng dụng',
      taoTieuDe: 'Tạo ứng dụng',
      suaTieuDe: 'Sửa ứng dụng {{ten}}',
      ten: 'Tên ứng dụng',
      canTen: 'Nhập tên ứng dụng.',
      ma: 'Mã',
      maKhongDoi: 'Không đổi được: mã nằm trong bí mật ký webhook.',
      trungMa: 'Đã có ứng dụng mang mã {{ma}}. Đổi tên để sinh mã khác.',
      webhookUrl: 'Webhook URL',
      webhookUrlGoiY: 'URL https của đối tác nhận kết quả. Để trống nếu chưa dùng webhook.',
      cotUngDung: 'Ứng dụng',
      cotWebhook: 'Webhook',
      cotQuyTrinh: 'Quy trình mặc định',
      cotPhien30: 'Phiên 30 ngày',
      dangDung: 'Đang dùng',
      daTatNhan: 'Đã tắt',
      chuaDatWebhook: 'Chưa đặt webhook',
      biMatChuaCap: 'Chưa cấp bí mật ký',
      biMatBan: 'Bí mật ký · lần cấp thứ {{so}}',
      soLuotLoi: '{{so}} lượt gửi lỗi',
      soQuyTrinh: '{{so}} quy trình',
      phienCuoi: 'Gần nhất {{luc}}',
      chuaCoPhien: 'Chưa có phiên',
      xemSoLieu: 'Xem số liệu và webhook',
      xemSoLieuCua: 'Xem số liệu và webhook của {{ten}}',
      moQuyTrinh: 'Mở quy trình định danh',
      sua: 'Sửa ứng dụng',
      capBiMat: 'Cấp bí mật ký webhook',
      xoayBiMat: 'Xoay bí mật ký webhook',
      bat: 'Bật ứng dụng',
      tat: 'Tắt ứng dụng',
      tatUngDung: 'Tắt ứng dụng',
      daLuu: 'Đã lưu thay đổi.',
      daBat: 'Đã bật {{ten}}.',
      daTat: 'Đã tắt {{ten}}.',
      xoayHoi: 'Xoay bí mật ký webhook của {{ten}}?',
      xoayThan: 'Bí mật cũ hết hiệu lực ngay, kể cả với webhook đang chờ gửi lại. Đối tác phải cập nhật bí mật mới trước khi kiểm được chữ ký. Không hoàn tác được.',
      tatHoi: 'Tắt ứng dụng {{ten}}?',
      tatThan: 'Ứng dụng không nhận phiên mới nữa; đối tác gọi sẽ nhận lỗi 403. Phiên đang dở vẫn chạy tiếp và vẫn gửi webhook. Bật lại được bất cứ lúc nào.',
      chuaCoKhoaGoc: 'Máy chủ chưa cấu hình khoá gốc nên chưa cấp được bí mật ký webhook và chưa gửi webhook. Báo người vận hành đặt EKYC_KHOA_GOC_FILE.',
      chuaCoKhoaGocNgan: 'Máy chủ chưa có khoá gốc.',
      chuaCoKhoaGocKhiTao: 'Đã tạo ứng dụng. Máy chủ chưa có khoá gốc nên chưa cấp bí mật ký webhook; cấp sau ở menu của dòng khi người vận hành cấu hình xong.',
      biMatTieuDe: 'Bí mật ký webhook của {{ten}}',
      biMatCanhBao: 'Bí mật chỉ hiện một lần. Máy chủ không lưu và không trả lại nó — đóng hộp này là không xem lại được, chỉ còn cách xoay bí mật mới.',
      biMatNhan: 'Bí mật ký webhook',
      biMatGoiY: 'Lần cấp thứ {{so}}. Đối tác dùng nó để kiểm header X-Ekyc-Signature.',
      saoChep: 'Sao chép',
      daChep: 'Đã chép',
      daChepBiMat: 'Đã chép bí mật vào bộ nhớ tạm.',
      khongChepDuoc: 'Không chép tự động được. Chọn chữ trong ô rồi chép tay.',
      daLuuBiMat: 'Tôi đã lưu bí mật',
      rong: 'Chưa có ứng dụng nào',
      rongMo: 'Tạo ứng dụng cho từng đối tác hoặc kênh định danh để tách webhook và quy trình của họ.',
      loiTai: 'Không tải được danh sách ứng dụng: {{loi}}',
      soLieuTieuDe: 'Số liệu 30 ngày của {{ten}}',
      loiSoLieu: 'Không tải được số liệu: {{loi}}',
      tongPhien: 'Tổng phiên',
      daDuyet: 'Đã duyệt',
      tuChoi: 'Từ chối',
      choDuyet: 'Chờ người duyệt',
      roi: 'Rơi',
      dangLam: 'Đang làm',
      webhookDaGui: 'Webhook đã gửi',
      webhookChoVaLoi: '{{cho}} đang chờ · {{loi}} lỗi',
      luotGiaoTieuDe: 'Lượt gửi webhook gần đây',
      bangLuotGiao: 'Lượt gửi webhook',
      loiLuotGiao: 'Không tải được lượt gửi webhook: {{loi}}',
      chuaCoLuotGiao: 'Chưa có lượt gửi webhook nào',
      chuaCoLuotGiaoMo: 'Webhook gửi khi hồ sơ có kết quả và quy trình bật "Gửi webhook".',
      chuaDatWebhookMo: 'Ứng dụng chưa đặt webhook URL. Sửa ứng dụng để đặt.',
      cotSuKien: 'Sự kiện',
      cotLanCuoi: 'Lần thử gần nhất',
      cotTaoLuc: 'Tạo lúc',
      khongToiDich: 'Không tới được đích',
      lanThu: '{{so}} lần thử',
      thuLaiLuc: 'Thử lại lúc {{luc}}',
      guiLai: 'Gửi lại',
      guiLaiMo: 'Thử gửi thêm đúng một lần.',
      daXepGuiLai: 'Đã xếp lượt gửi lại.',
      tichHopTieuDe: 'Đối tác tích hợp thế nào',
      tichHopKhoaApi: 'Đối tác gọi API bằng khoá API của tổ chức, gửi ở header X-Api-Key, và chỉ định ứng dụng bằng ungDungId khi mở phiên. Cấp cho khoá các quyền:',
      quyenMoPhien: 'mở phiên định danh',
      quyenGuiAnh: 'ghi đồng ý, gửi ảnh, nộp hồ sơ',
      quyenDocKetQua: 'đọc trạng thái và kết quả',
      quyenDocPii: 'đọc số giấy tờ đầy đủ và ảnh — chỉ cấp khi thật cần',
      moKhoaApi: 'Mở màn Khoá API',
      tichHopWebhook: 'Webhook ký bằng HMAC-SHA256 trên timestamp và thân thô. Phía nhận kiểm như sau:',
      tichHopThuLai: 'Phản hồi khác 2xx (kể cả 3xx) là thất bại. Máy chủ thử tối đa 9 lần, giãn dần tới 24 giờ; dùng header X-Ekyc-Delivery để bỏ lượt trùng.',
    },
  },
  /**
   * Module `tham-dinh` (Thẩm định AI), ADR-0080.
   *
   * Bốn màn Tổng quan, Hồ sơ, Phê duyệt, Tích hợp API gọi route backend chưa mở (đợt sau của ADR-0080
   * mục 9), nên chúng có chuỗi riêng nói rõ "chưa mở" thay vì mượn câu lỗi chung — người dùng cần biết
   * đây là tính năng chưa tới lượt, không phải hệ thống đang hỏng.
   */
  thamDinh: {
    // Tiêu đề và mô tả từng màn.
    tongQuanTieuDe: 'Tổng quan thẩm định',
    tongQuanMo: 'Tình hình thẩm định hồ sơ trong 30 ngày gần nhất.',
    hoSoTieuDe: 'Hồ sơ thẩm định',
    hoSoMo: 'Hồ sơ đã nộp để chấm theo bộ tiêu chí.',
    pheDuyetTieuDe: 'Phê duyệt thẩm định',
    pheDuyetMo: 'Hồ sơ đang chờ chữ ký của bạn.',
    tieuChiTieuDe: 'Thư viện tiêu chí',
    tieuChiMo: 'Lĩnh vực, loại hồ sơ và bộ tiêu chí dùng để chấm.',
    tichHopTieuDe: 'Tích hợp API',
    tichHopMo: 'Khoá API để hệ thống khác nộp hồ sơ vào thẩm định.',

    // Tab của màn Thư viện tiêu chí.
    tabBo: 'Bộ tiêu chí',
    tabLinhVuc: 'Lĩnh vực',
    tabLoaiHoSo: 'Loại hồ sơ',

    // Màn chưa mở route.
    chuaMoTieuDe: 'Phần này chưa mở',
    // Nhân sự nền tảng xem chéo giao diện tenant (ADR-UI-0012 mục 9): không phải lỗi, nên không mời
    // thử lại — họ cần đổi đường, không cần bấm lại.
    xemCheoTieuDe: 'Bạn đang xem giao diện của tenant',
    xemCheoMo: 'Tài khoản nhân sự nền tảng không thuộc tenant nào nên màn này không có dữ liệu. Muốn xem dữ liệu thật thì đăng nhập bằng tài khoản của một tenant đã bật dịch vụ Thẩm định AI.',
    chuaMoMo: 'Màn hình đã dựng xong, còn API của phân hệ sẽ mở ở đợt sau. Khi đó màn tự chạy, không cần cập nhật gì thêm.',

    // Cây thư viện (cột trái), theo giao diện v3.
    cay: {
      nhan: 'Cây thư viện tiêu chí',
      tim: 'Tìm lĩnh vực, loại hồ sơ, bộ tiêu chí',
      nhap: 'Nháp',
      rong: 'Thư viện còn trống',
      rongMo: 'Bắt đầu bằng một lĩnh vực, rồi thêm loại hồ sơ và bộ tiêu chí trong đó.',
      rongLoc: 'Không có mục nào khớp',
      themLinhVuc: 'Thêm lĩnh vực',
      // Lý do mục Xoá bị chặn, hiện ngay dưới nhãn trong menu — khớp đúng điều kiện backend trả 409.
      conLoaiHoSo: 'Còn {{so}} loại hồ sơ bên trong, xoá chúng trước',
      conBo: 'Còn {{so}} bộ tiêu chí bên trong, xoá hoặc lưu trữ chúng trước',
      daPhatHanh: 'Bộ đã phát hành không xoá được, hãy chuyển vào lưu trữ',
    },

    // Trình soạn bộ tiêu chí (cột giữa).
    soan: {
      chuaChonBo: 'Chưa chọn bộ tiêu chí',
      chuaChonBoMo: 'Bấm một bộ tiêu chí ở cây bên trái để mở ra soạn.',
      dangApDung: 'Đang áp dụng v{{so}}',
      chuaPhatHanh: 'Chưa phát hành',
      suaGanNhat: 'Sửa gần nhất: {{luc}}',
      phatHanh: 'Phát hành v{{so}}',
      canTrongSo: 'AI cân trọng số',
      canKetQua: 'AI đề xuất đổi {so} trọng số',
      canModel: 'Đề xuất của {model}',
      canApDung: 'Áp vào bản nháp',
      canKhongDoi: 'AI không đề xuất đổi trọng số nào.',
      chayThu: 'Chạy thử',
      chayThuDangCham: 'Đang chạy thử bộ tiêu chí...',
      chayThuTieuDe: 'Chạy thử bộ tiêu chí trên hồ sơ mẫu',
      chayThuMo: 'Chấm theo bản nháp đang soạn, không theo phiên bản đã phát hành. Lượt chạy thử không vào danh sách hồ sơ.',
      chayThuChonTep: 'Chọn hồ sơ mẫu',
      chayThuBatDau: 'Chạy thử',
      chayThuXemKetQua: 'Xem kết quả chạy thử',
      goiY: 'AI gợi ý tiêu chí',
      goiYTieuDe: 'AI gợi ý tiêu chí từ nguồn pháp luật',
      goiYMoTa: 'Loại hồ sơ cần thẩm định',
      goiYMoTaMo: 'Mô tả này được gửi tới công cụ tra cứu web. Đừng nhập thông tin của một hồ sơ cụ thể.',
      goiYMoTaVd: 'Hồ sơ vay vốn doanh nghiệp vừa và nhỏ',
      goiYChonTep: 'Chọn hồ sơ mẫu',
      goiYTepMo: 'Tuỳ chọn. Tệp mẫu chỉ dùng để AI biết hồ sơ thực tế gồm những gì — tệp không được gửi ra công cụ tra cứu web.',
      goiYBatDau: 'Bắt đầu gợi ý',
      goiYDangChay: 'Đang gợi ý tiêu chí...',
      goiYKetQua: 'AI gợi ý {so} tiêu chí, dựng bởi {model}',
      goiYTuLieu: 'Nguồn đã tra cứu',
      goiYThayCanh: 'Áp dụng sẽ THAY toàn bộ cây tiêu chí đang soạn. Bản nháp chỉ đổi trên màn, bạn vẫn phải bấm Lưu.',
      goiYApDung: 'Áp {so} tiêu chí vào bản nháp',
      loaiTru: 'loại trừ',
      chuaPhatHanhDuoc: 'Chưa phát hành được',
      nguong: 'Ngưỡng kết luận',
      nguongDat: 'Ngưỡng đạt',
      nguongDatDieuKien: 'Ngưỡng đạt có điều kiện',
      nguongMo: 'Thấp hơn là Không đạt. Vi phạm một tiêu chí bắt buộc cũng là Không đạt, bất kể điểm.',
      thanhTyTrong: 'Tỉ trọng các nhóm sau chuẩn hoá (tổng 100%).',
      tenNhomMoi: 'Tên nhóm mới, Enter để thêm',
      themNhom: 'Thêm nhóm',
      tenTieuChiMoi: 'Tên tiêu chí mới, Enter để thêm',
      themTieuChi: 'Thêm tiêu chí',
      batBuoc: 'Tiêu chí bắt buộc',
      trongSoNhom: 'Trọng số nhóm {{ten}}',
      trongSoTieuChi: 'Trọng số tiêu chí {{ten}}',
      chuyenLen: 'Chuyển {{ten}} lên trên',
      chuyenXuong: 'Chuyển {{ten}} xuống dưới',
      xoaMuc: 'Xoá {{ten}}',
      daLuu: 'Mọi thay đổi đã được lưu',
      chuaLuu: 'Có thay đổi chưa lưu',
      hoanTac: 'Hoàn tác',
    },

    // Chi tiết một tiêu chí (cột phải).
    chiTiet: {
      tieuDe: 'Chi tiết tiêu chí',
      chuaChon: 'Chưa chọn tiêu chí',
      chuaChonMo: 'Bấm vào một tiêu chí ở khung giữa để sửa kiểu chấm, hướng dẫn cho AI và căn cứ.',
      ten: 'Tên tiêu chí',
      kieu: 'Kiểu chấm',
      kieuMo: 'Quyết định AI phán quyết thế nào và điểm quy đổi ra sao.',
      thangToiDa: 'Thang điểm tối đa',
      mucNghiemTrong: 'Mức nghiêm trọng',
      loaiTru: 'Tiêu chí bắt buộc',
      loaiTruMo: 'Trượt tiêu chí này thì cả hồ sơ Không đạt, bất kể tổng điểm.',
      huongDan: 'Hướng dẫn chấm cho AI',
      huongDanMo: 'Câu này đi thẳng vào lời nhắc chấm. Viết rõ cái gì tính là đạt, cái gì không.',
      viDuDat: 'Ví dụ đạt',
      viDuMo: 'Ví dụ neo phán quyết của AI, nên trích từ hồ sơ thật.',
      viDuKhongDat: 'Ví dụ không đạt',
      canCu: 'Căn cứ pháp luật',
      canCuMo: 'AI chỉ được trích dẫn từ những nguồn này khi tra cứu.',
      canCuTen: 'Số hiệu và tên văn bản',
      canCuDieu: 'Điều, khoản',
      canCuUrl: 'Đường dẫn',
      themCanCu: 'Thêm căn cứ',
      xoaCanCu: 'Xoá căn cứ',
    },

    // Phát hành phiên bản.
    phatHanh: {
      tieuDe: 'Phát hành phiên bản v{{so}}?',
      than: 'Bản v{{so}} được chụp lại và không sửa được nữa. Hồ sơ chấm theo bản này về sau vẫn tra lại được đúng bản đã dùng. Bản nháp vẫn sửa tiếp được bình thường.',
      ghiChu: 'Ghi chú phiên bản',
      ghiChuMo: 'Lần này đổi gì so với bản trước — người đọc kết quả chấm cũ sẽ cần biết.',
      xacNhan: 'Phát hành v{{so}}',
    },

    // Sáu kiểu tiêu chí (đặc tả mục 5.2).
    kieu: {
      co_khong: 'Có / không',
      thang_diem: 'Thang điểm',
      doi_chieu_gia_tri: 'Đối chiếu giá trị',
      thanh_phan_ho_so: 'Thành phần hồ sơ',
      doi_chieu_cheo: 'Đối chiếu chéo',
      tu_do: 'Tự do',
    },

    // Bộ tiêu chí.
    bo: {
      caption: 'Danh sách bộ tiêu chí',
      tim: 'Tìm theo tên bộ',
      themMoi: 'Thêm bộ tiêu chí',
      rong: 'Chưa có bộ tiêu chí nào',
      rongMo: 'Bộ tiêu chí quyết định hồ sơ được chấm theo thang nào. Tạo bộ đầu tiên để bắt đầu thẩm định.',
      rongLoc: 'Không có bộ tiêu chí nào khớp bộ lọc',
      cotTen: 'Tên bộ',
      cotLoaiHoSo: 'Loại hồ sơ',
      cotTrangThai: 'Trạng thái',
      cotPhienBan: 'Phiên bản',
      cotCapDuyet: 'Cấp duyệt',
      cotCapNhat: 'Cập nhật lúc',
      chuaPhatHanh: 'Chưa phát hành',
      banSo: 'Bản {{so}}',
      khongCapDuyet: 'Không qua duyệt',
      soCapDuyet: '{{so}} cấp',
      sua: 'Sửa bộ tiêu chí',
      xoa: 'Xoá bộ tiêu chí',
      luuTru: 'Chuyển vào lưu trữ',
      dungLai: 'Dùng lại bộ này',
      phatHanh: 'Phát hành phiên bản',
      daTao: 'Đã tạo bộ tiêu chí {{ten}}.',
      daLuu: 'Đã lưu bộ tiêu chí.',
      daLuuNhap: 'Đã lưu bản nháp.',
      daXoa: 'Đã xoá bộ tiêu chí {{ten}}.',
      daPhatHanh: 'Đã phát hành phiên bản mới.',
      formTaoTieuDe: 'Thêm bộ tiêu chí',
      formSuaTieuDe: 'Sửa bộ tiêu chí',
      nhanTen: 'Tên bộ',
      nhanMoTa: 'Mô tả',
      nhanLoaiHoSo: 'Loại hồ sơ',
      moLoaiHoSo: 'Bộ tiêu chí thuộc về một loại hồ sơ và không chuyển sang loại khác được sau khi tạo.',
      chonLoaiHoSo: 'Chọn loại hồ sơ',
      xoaHoiTieuDe: 'Xoá bộ tiêu chí này?',
      xoaHoiThan: 'Bộ "{{ten}}" sẽ bị xoá khỏi thư viện. Không hoàn tác được. Bộ đã phát hành thì không xoá được — hãy chuyển vào lưu trữ.',
      xoaXacNhan: 'Xoá bộ tiêu chí',
    },

    // Lĩnh vực.
    linhVuc: {
      caption: 'Danh sách lĩnh vực',
      tim: 'Tìm theo tên hoặc mã',
      themMoi: 'Thêm lĩnh vực',
      rong: 'Chưa có lĩnh vực nào',
      rongMo: 'Lĩnh vực gom các loại hồ sơ cùng ngành và khai nguồn pháp luật được phép tra cứu.',
      rongLoc: 'Không có lĩnh vực nào khớp bộ lọc',
      cotMa: 'Mã',
      cotTen: 'Tên lĩnh vực',
      cotNguon: 'Nguồn ưu tiên',
      cotSoLoai: 'Loại hồ sơ',
      khongNguon: 'Chưa khai nguồn',
      themNguon: 'thêm {{so}}',
      sua: 'Sửa lĩnh vực',
      xoa: 'Xoá lĩnh vực',
      daTao: 'Đã tạo lĩnh vực {{ten}}.',
      daLuu: 'Đã lưu lĩnh vực.',
      daXoa: 'Đã xoá lĩnh vực {{ten}}.',
      formTaoTieuDe: 'Thêm lĩnh vực',
      formSuaTieuDe: 'Sửa lĩnh vực',
      nhanMa: 'Mã lĩnh vực',
      moMa: 'Chữ thường, số, gạch ngang và gạch dưới. Không đổi được sau khi tạo.',
      nhanTen: 'Tên lĩnh vực',
      nhanMoTa: 'Mô tả',
      nhanNguon: 'Nguồn ưu tiên',
      moNguon: 'Tên miền được phép tra căn cứ pháp luật, mỗi dòng một miền. Ví dụ: vbpl.vn. Để trống thì AI tra từ mọi nguồn.',
      xoaHoiTieuDe: 'Xoá lĩnh vực này?',
      xoaHoiThan: 'Lĩnh vực "{{ten}}" sẽ bị xoá. Không hoàn tác được. Lĩnh vực còn loại hồ sơ thì không xoá được.',
      xoaXacNhan: 'Xoá lĩnh vực',
    },

    // Loại hồ sơ.
    loaiHoSo: {
      caption: 'Danh sách loại hồ sơ',
      tim: 'Tìm theo tên loại hồ sơ',
      themMoi: 'Thêm loại hồ sơ',
      rong: 'Chưa có loại hồ sơ nào',
      rongMo: 'Loại hồ sơ nói một bộ hồ sơ gồm những giấy tờ nào. Cần có lĩnh vực trước khi thêm.',
      rongLoc: 'Không có loại hồ sơ nào khớp bộ lọc',
      rongChuaCoLinhVuc: 'Hãy tạo lĩnh vực trước',
      rongChuaCoLinhVucMo: 'Mỗi loại hồ sơ thuộc về một lĩnh vực, nên phải có lĩnh vực trước đã.',
      cotTen: 'Tên loại hồ sơ',
      cotLinhVuc: 'Lĩnh vực',
      cotThanhPhan: 'Thành phần mặc định',
      cotSoBo: 'Bộ tiêu chí',
      khongThanhPhan: 'Chưa khai thành phần',
      soGiayTo: '{{so}} giấy tờ',
      locTheoLinhVuc: 'Lọc theo lĩnh vực',
      moiLinhVuc: 'Mọi lĩnh vực',
      sua: 'Sửa loại hồ sơ',
      xoa: 'Xoá loại hồ sơ',
      daTao: 'Đã tạo loại hồ sơ {{ten}}.',
      daLuu: 'Đã lưu loại hồ sơ.',
      daXoa: 'Đã xoá loại hồ sơ {{ten}}.',
      formTaoTieuDe: 'Thêm loại hồ sơ',
      formSuaTieuDe: 'Sửa loại hồ sơ',
      nhanLinhVuc: 'Lĩnh vực',
      moLinhVuc: 'Không chuyển sang lĩnh vực khác được sau khi tạo, vì căn cứ pháp luật tra theo nguồn của lĩnh vực.',
      chonLinhVuc: 'Chọn lĩnh vực',
      nhanTen: 'Tên loại hồ sơ',
      nhanMoTa: 'Mô tả',
      nhanThanhPhan: 'Thành phần mặc định',
      moThanhPhan: 'Tên các giấy tờ cần có, mỗi dòng một giấy tờ.',
      xoaHoiTieuDe: 'Xoá loại hồ sơ này?',
      xoaHoiThan: 'Loại hồ sơ "{{ten}}" sẽ bị xoá. Không hoàn tác được. Loại hồ sơ còn bộ tiêu chí thì không xoá được.',
      xoaXacNhan: 'Xoá loại hồ sơ',
    },

    // Hồ sơ thẩm định.
    phien: {
      caption: 'Danh sách hồ sơ thẩm định',
      tim: 'Tìm theo tiêu đề hồ sơ',
      rong: 'Chưa có hồ sơ nào được thẩm định',
      rongMo: 'Hồ sơ nộp vào đây sẽ được chấm theo bộ tiêu chí đã phát hành.',
      rongLoc: 'Không có hồ sơ nào khớp bộ lọc',
      cotTieuDe: 'Hồ sơ',
      cotBo: 'Bộ tiêu chí',
      cotTrangThai: 'Trạng thái',
      cotKetLuan: 'Kết luận',
      cotDiem: 'Điểm',
      cotNguoiTao: 'Người nộp',
      cotCapNhat: 'Cập nhật lúc',
      locTrangThai: 'Lọc theo trạng thái',
      moiTrangThai: 'Mọi trạng thái',
      dangXuLy: 'Đang xử lý',
      tabTatCa: 'Tất cả',
      xem: 'Xem chi tiết',
      xoa: 'Xoá hồ sơ',
      daNop: 'Đã nộp hồ sơ {{ten}}.',
      daXoa: 'Đã xoá hồ sơ {{ten}}.',
      xoaHoiTieuDe: 'Xoá hồ sơ này?',
      xoaHoiThan: 'Hồ sơ "{{ten}}" cùng mọi tệp và kết quả chấm sẽ bị xoá. Không hoàn tác được.',
      xoaXacNhan: 'Xoá hồ sơ',
      daXem: '{{daXem}}/{{canXem}} đã xem',
    },

    // Phê duyệt.
    hangCho: {
      caption: 'Hồ sơ chờ phê duyệt',
      rong: 'Không có hồ sơ nào chờ bạn duyệt',
      rongMo: 'Hồ sơ được trình lên sẽ xuất hiện ở đây.',
      cotTieuDe: 'Hồ sơ',
      cotBo: 'Bộ tiêu chí',
      cotKetLuan: 'Kết luận',
      cotDiem: 'Điểm',
      cotCap: 'Cấp duyệt',
      cotNguoiTrinh: 'Người trình',
      cotTrinhLuc: 'Trình lúc',
      capSo: 'Cấp {{so}}',
    },

    // Rà soát và phê duyệt (Mốc 6).
    raSoat: {
      dangTai: 'Đang tải hồ sơ...',
      veDanhSach: 'Danh sách hồ sơ',
      ketQua: 'Kết quả chấm',
      ketLuan: 'Kết luận',
      tep: 'Tệp hồ sơ',
      soKyTu: '{{so}} ký tự',
      chuaCham: 'Chưa có kết quả chấm',
      chuaChamMo: 'Hồ sơ đang chờ AI chấm. Kết quả hiện ở đây khi chấm xong.',
      chamLoi: 'Không chấm được hồ sơ',
      chamLai: 'Chấm lại',
      daDayChamLai: 'Đã đẩy hồ sơ vào hàng đợi chấm lại.',
      daNopLai: 'Đã nộp lại hồ sơ.',
      nopLai: 'Nộp lại hồ sơ',
      nopLaiMo: 'Tải lên bộ tệp đã sửa. Hồ sơ cũ vẫn còn trong lịch sử để đối chiếu.',
      soSanh: 'So với lần nộp trước',
      soSanhLan: 'Lần nộp {sau} so với lần {truoc}',
      soSanhDiem: 'Điểm: {truoc} → {sau}',
      soSanhThayDoi: 'Tiêu chí đổi phán quyết ({so})',
      soSanhKhongDoi: 'Không có tiêu chí nào đổi phán quyết.',
      soSanhVanBan: 'Khác biệt trong hồ sơ',
      soSanhGap: '{so} dòng giống nhau',
      soSanhBiCat: 'Phần khác biệt quá dài nên đã cắt — hãy mở hai hồ sơ để đối chiếu đầy đủ.',
      lanNop: 'Lần nộp {so}',
      taiBienBan: 'Tải biên bản',
      daTaiBienBan: 'Đã tải biên bản.',
      danhGia: 'Bản đánh giá của AI',
      danhGiaMo: 'Trợ lí AI tổng hợp kết quả chấm thành một bản đánh giá để bạn đọc nhanh.',
      danhGiaBoi: '{model} viết, lần {lan}',
      danhGiaChuaCo: 'Chưa có bản đánh giá nào cho hồ sơ này.',
      danhGiaViet: 'Viết bản đánh giá',
      danhGiaVietLai: 'Viết lại',
      danhGiaDangViet: 'Đang viết bản đánh giá...',
      danhGiaLoi: 'Không viết được bản đánh giá',
      danhGiaBoSot: 'Bản đánh giá bỏ sót {so} tiêu chí đáng nói',
      danhGiaRong: 'Bản đánh giá rỗng',
      danhGiaRongMo: 'Mô hình đã trả lời nhưng không có nội dung. Hãy bấm Viết lại.',
      canXem: 'Cần xem',
      daGhiDe: 'Đã sửa',
      bangChung: 'Trích dẫn từ hồ sơ',
      khongBangChung: 'AI không đưa trích dẫn nào cho tiêu chí này.',
      goiYSua: 'Việc cần sửa',
      apSua: 'Áp gợi ý sửa',
      apSuaMo: 'Hệ thống dựng lại hồ sơ theo các gợi ý bạn đã đồng ý, rồi chấm lại thành một lần nộp mới.',
      apSuaDangChay: 'Đang áp gợi ý sửa...',
      apSuaSoDongY: 'Đã đồng ý {so} gợi ý',
      apSuaChuaDongY: 'Chưa đồng ý gợi ý nào. Đánh dấu đồng ý ở từng tiêu chí rồi bấm lại.',
      apSuaXong: 'Đã dựng lần nộp mới',
      apSuaMoHoSoMoi: 'Mở hồ sơ mới',
      apSuaBoQua: '{so} thao tác không áp được',
      apSuaLoi: 'Không áp được gợi ý sửa',
      troLy: 'Hỏi trợ lí về hồ sơ',
      troLyMo: 'Trợ lí đọc hồ sơ và kết quả chấm để trả lời. Lịch sử hỏi chỉ bạn thấy.',
      troLyOHoi: 'Hỏi về hồ sơ này',
      troLyVd: 'Hồ sơ có nêu thời hạn bảo hành không?',
      troLyGui: 'Hỏi',
      troLyDung: 'Dừng',
      troLyKhongMoDuoc: 'Không mở được luồng trả lời của trợ lí, vui lòng thử lại.',
      troLyTrichDan: 'Trích dẫn',
      troLyTrangSo: 'trang {so}',
      troLyLichSu: 'Các lượt hỏi trước',
      troLyChuaHoi: 'Bạn chưa hỏi gì về hồ sơ này.',
      troLyDangChay: 'Lượt hỏi này bị ngắt giữa chừng.',
      goiYSuaDongY: 'Đồng ý sửa',
      goiYSuaTuChoi: 'Từ chối',
      goiYSuaBoQuyet: 'Bỏ quyết định',
      ghiDe: 'Phán quyết',
      theoAi: 'Theo AI: {{pq}}',
      ghiChu: 'Ghi chú của bạn',
      danhDauDaXem: 'Đánh dấu đã xem',
      trang: 'trang {{so}}',
      conPhaiXem: 'Còn {{con}}/{{tong}} tiêu chí cần xem',
      chuaXemHet: 'Còn {{so}} tiêu chí cần xem trước khi hoàn tất. Mở từng dòng được đánh dấu để xem.',
      hoanTat: 'Hoàn tất rà soát',
      daHoanTat: 'Đã hoàn tất hồ sơ.',
      daTrinh: 'Đã trình hồ sơ lên cấp phê duyệt.',
      pheDuyet: 'Phê duyệt',
      pheDuyetTieuDe: 'Phê duyệt hồ sơ này?',
      pheDuyetThan: 'Hồ sơ chuyển sang cấp duyệt tiếp theo, hoặc hoàn tất nếu đây là cấp cuối.',
      daPheDuyet: 'Đã phê duyệt hồ sơ.',
      traLai: 'Trả lại',
      traLaiTieuDe: 'Trả lại hồ sơ này?',
      traLaiThan: 'Hồ sơ về bước rà soát để người thẩm định sửa. Mọi cấp đã duyệt ở vòng này phải duyệt lại từ đầu.',
      daTraLai: 'Đã trả lại hồ sơ.',
      yKien: 'Ý kiến',
      yKienBatBuoc: 'Ghi rõ cần sửa gì — người thẩm định đọc câu này để biết làm tiếp.',
      yKienTuyChon: 'Không bắt buộc.',
    },

    // Phán quyết một tiêu chí (đặc tả mục 2.1).
    phanQuyet: {
      dat: 'Đạt',
      mot_phan: 'Đạt một phần',
      khong_dat: 'Không đạt',
      khong_ap_dung: 'Không áp dụng',
      khong_du_can_cu: 'Chưa đủ căn cứ',
    },
    // Nộp hồ sơ.
    nop: {
      nutMoi: 'Thẩm định hồ sơ mới',
      tieuDe: 'Thẩm định hồ sơ mới',
      bo: 'Bộ tiêu chí',
      boMo: 'Chỉ chọn được bộ đã phát hành — hồ sơ ghim vào một bản cố định để về sau tra lại đúng bản đã dùng.',
      chonBo: 'Chọn bộ tiêu chí',
      chuaCoBo: 'Chưa có bộ tiêu chí nào được phát hành. Hãy vào Thư viện tiêu chí, soạn và phát hành một bộ trước.',
      tieuDeHoSo: 'Tên hồ sơ',
      maThamChieu: 'Mã tham chiếu',
      maThamChieuMo: 'Mã của hệ thống bên bạn, để đối chiếu về sau. Không bắt buộc.',
      tep: 'Tệp hồ sơ',
      tepMo: 'Nhận {{duoi}}. PDF phải là bản gốc có lớp văn bản; bản quét cần chuyển qua Nhận dạng OCR trước.',
      chonTep: 'Chọn tệp',
      boTep: 'Bỏ tệp {{ten}}',
      tepQuaLon: 'Tệp "{{ten}}" vượt 50 MB. Hãy tách nhỏ hoặc nén lại rồi tải lên.',
      xacNhan: 'Nộp hồ sơ',
      dangNop: 'Đang đọc tệp...',
    },
    // Tài liệu tích hợp, hiện ngay trên màn như v3.
    // Tài liệu tích hợp, hiện ngay trên màn như v3.
    tichHopTaiLieu: {
      goiApi: 'Gọi API',
      goiApiMo: 'Mọi lời gọi gửi khoá trong header X-Api-Key. Hệ thống ngoài chỉ đọc được hồ sơ do chính nó gửi qua API.',
      webhook: 'Webhook',
      webhookMo: 'Thân yêu cầu là JSON {suKien, phienId, maThamChieu, trangThai, diem, ketLuan, luc}. Hãy kiểm tra chữ ký ở header X-FoxAI-Signature trước khi tin dùng. Gửi lỗi sẽ được thử lại tối đa 5 lần, khoảng cách tăng dần.',
      suKienChamXong: 'AI chấm xong, hồ sơ chờ người thẩm định duyệt.',
      suKienHoanTat: 'Hồ sơ hoàn tất, sau mọi cấp phê duyệt.',
      suKienLoi: 'Không chấm được hồ sơ.',
    },
    // Tích hợp API.
    khoa: {
      caption: 'Danh sách khoá API',
      cotWebhookKetQua: 'Lần gửi webhook cuối',
      chuaGuiWebhook: 'Chưa gửi',
      thuWebhook: 'Thử webhook',
      themMoi: 'Tạo khoá API',
      rong: 'Chưa có khoá API nào',
      rongMo: 'Khoá API cho phép hệ thống khác nộp hồ sơ vào thẩm định mà không qua giao diện.',
      cotTen: 'Tên khoá',
      cotTienTo: 'Tiền tố',
      cotTrangThai: 'Trạng thái',
      cotWebhook: 'Webhook',
      cotDungCuoi: 'Dùng lần cuối',
      chuaDung: 'Chưa dùng lần nào',
      khongWebhook: 'Chưa khai',
      sao: 'Sao chép',
      daChep: 'Đã sao chép',
      taoTieuDe: 'Tạo khoá API',
      ten: 'Tên khoá',
      tenMo: 'Đặt theo hệ thống dùng khoá, ví dụ "Cổng dịch vụ công" — mỗi hệ thống một khoá để thu hồi riêng.',
      webhookUrl: 'Địa chỉ webhook',
      webhookUrlMo: 'Nơi nhận kết quả thẩm định. Để trống thì hệ thống ngoài tự hỏi lại bằng API.',
      taoXacNhan: 'Tạo khoá',
      daTaoTieuDe: 'Đã tạo khoá API',
      chiHienMotLan: 'Khoá và bí mật webhook chỉ hiện lần này. Hệ thống chỉ lưu bản băm nên không xem lại được — hãy chép và cất ngay. Mất thì thu hồi khoá rồi tạo khoá mới.',
      khoaApi: 'Khoá API',
      biMatWebhook: 'Bí mật ký webhook',
      biMatWebhookMo: 'Dùng để kiểm chữ ký ở header X-FoxAI-Signature.',
      daChepXong: 'Tôi đã chép xong',
      thuHoiHoiTieuDe: 'Thu hồi khoá này?',
      thuHoiHoiThan: 'Khoá "{{ten}}" sẽ không gọi được nữa, ngay lập tức. Hồ sơ đã gửi bằng khoá này vẫn giữ nguyên và vẫn truy được nguồn. Không hoàn tác được.',
      thuHoiXacNhan: 'Thu hồi khoá',
      daThuHoi: 'Đã thu hồi khoá {{ten}}.',
      thuHoi: 'Thu hồi khoá',
    },

    // Số liệu màn Tổng quan.
    soLieu: {
      dangCham: 'Đang chấm',
      choDuyet: 'Chờ thẩm định viên',
      choPheDuyet: 'Chờ phê duyệt',
      loi: 'Lỗi',
      hoanTat30Ngay: 'Hoàn tất 30 ngày',
      tyLeDat: 'Tỉ lệ đạt',
      tieuChiHayTruot: 'Tiêu chí hay trượt',
      tieuChiHayTruotMo: 'Trong 90 ngày gần nhất.',
      ganDay: 'Hồ sơ gần đây',
      soLanTruot: '{{so}} lần',
      cotSoLan: 'Số lần trượt',
      cotTyLeTruot: 'Tỉ lệ trượt',
      dangChamMo: 'AI đang đọc và chấm',
      choDuyetMo: 'Cần người thẩm định xem',
      hoanTat: 'Hoàn tất',
      hoanTatMo: 'trong 30 ngày',
      tyLeDatMo: '30 ngày · {{dieuKien}} có điều kiện · {{khongDat}} không đạt',
      choDuyetKhoiMo: 'Hồ sơ chờ lâu nhất được xếp trên cùng.',
      khongChoDuyet: 'Không có hồ sơ nào chờ duyệt.',
      khongTieuChiTruot: 'Chưa có tiêu chí nào bị đánh giá không đạt.',
      cotTieuChi: 'Mã tiêu chí',
      phienGanDay: 'Phiên gần đây',
      xemTatCa: 'Xem tất cả',
      chuaCoLinhVuc: 'Chưa khai lĩnh vực nào',
      chuaCoLinhVucMo: 'Thẩm định cần ít nhất một lĩnh vực và một bộ tiêu chí đã phát hành.',
      chuaCoBoPhatHanh: 'Chưa có bộ tiêu chí nào được phát hành',
      chuaCoBoPhatHanhMo: 'Bộ tiêu chí phải phát hành thì mới dùng để chấm được.',
      sangThuVien: 'Mở thư viện tiêu chí',
    },

    // Nhãn giá trị liệt kê.
    trangThaiBo: {
      hoat_dong: 'Đang dùng',
      luu_tru: 'Lưu trữ',
    },
    trangThaiPhien: {
      cho_xu_ly: 'Chờ xử lý',
      dang_doc: 'Đang đọc',
      dang_cham: 'Đang chấm',
      cho_duyet: 'Chờ thẩm định viên',
      cho_phe_duyet: 'Chờ phê duyệt',
      hoan_tat: 'Hoàn tất',
      loi: 'Lỗi',
    },
    ketLuan: {
      dat: 'Đạt',
      dat_co_dieu_kien: 'Đạt có điều kiện',
      khong_dat: 'Không đạt',
      chua_du_du_lieu: 'Chưa đủ dữ liệu',
    },
    mucNghiemTrong: {
      thap: 'Thấp',
      trung_binh: 'Trung bình',
      cao: 'Cao',
      nghiem_trong: 'Nghiêm trọng',
    },
    trangThaiKhoa: {
      hoat_dong: 'Đang dùng',
      thu_hoi: 'Đã thu hồi',
    },
  },

  // Xử lý văn bản (ADR-0084): mỗi màn một nhánh
  vanBanTomTat: {
    tieuDe: 'Tóm tắt văn bản',
    moTa: 'Tự động tóm tắt nội dung văn bản bằng AI. Bấm vào một văn bản để xem so sánh bản gốc và bản tóm tắt.',
    them: 'Thêm văn bản',
    daThem: 'Đã thêm văn bản.',
    daLuuTomTat: 'Đã lưu bản tóm tắt.',
    daXoa: 'Đã xoá văn bản.',
    thatBai: 'Tóm tắt thất bại. Bấm "AI tóm tắt" để thử lại.',
    thongKe: {
      tong: 'Tổng văn bản',
      daTomTat: 'Đã tóm tắt',
      chuaTomTat: 'Chưa tóm tắt',
      tyLe: 'Tỷ lệ rút gọn TB',
    },
    timKiem: 'Tìm kiếm văn bản...',
    tatCaLoai: 'Tất cả loại văn bản',
    locLoai: 'Lọc theo loại văn bản',
    tatCaTrangThai: 'Tất cả trạng thái',
    locTrangThai: 'Lọc theo trạng thái',
    daTomTat: 'Đã tóm tắt',
    chuaTomTat: 'Chưa tóm tắt',
    loiDanhSach: 'Không tải được danh sách văn bản: {{loi}}',
    bangCaption: 'Danh sách văn bản cần tóm tắt',
    rongLoc: 'Không có văn bản nào phù hợp',
    rongThat: 'Chưa có văn bản nào',
    rongThatMo: 'Tải văn bản lên hoặc dán nội dung để AI tóm tắt.',
    cot: {
      ten: 'Tên văn bản',
      loai: 'Loại',
      ngayTao: 'Ngày tạo',
      nguoiTao: 'Người tạo',
    },
    nguon: {
      QLVB: 'QLVB & ĐH',
      CONG_DVC: 'Cổng DVC',
      EMAIL: 'Email công vụ',
      OTHER: 'Nguồn khác',
    },
    maDoiTac: 'Mã đối tác: {{ma}}',
    kyTuGoc: '{{so}} ký tự gốc',
    daRutGon: 'Đã rút gọn {{so}}%',
    xemSoSanh: 'Xem so sánh bản gốc / tóm tắt',
    suaBanTomTat: 'Sửa bản tóm tắt',
    xuatDoc: 'Xuất bản tóm tắt (.doc)',
    soanTuBanNay: 'Soạn thảo văn bản từ bản này',
    aiPhanCong: 'AI phân công nhiệm vụ',
    xoa: 'Xoá văn bản',
    aiTomTat: 'AI tóm tắt',
    dangTomTatNgan: 'Đang tóm tắt...',
    xacNhanXoa: 'Xoá văn bản "{{ten}}"?',
    xacNhanXoaMo: 'Văn bản gốc, bản tóm tắt và tệp đính kèm bị xoá vĩnh viễn, không hoàn tác được.',
    soSanhTieuDe: 'So sánh văn bản gốc & bản tóm tắt',
    loiChiTiet: 'Không tải được văn bản: {{loi}}',
    phanCongXong: 'Phân công thành công.',
    phanCongXongMo: 'AI đã tạo {{so}} nhiệm vụ từ bản tóm tắt, giao cho các phòng ban, cá nhân phù hợp.',
    rutGonSoVoiGoc: 'Bản tóm tắt rút gọn {{so}}% so với bản gốc',
    vanBanGoc: 'Văn bản gốc',
    banTomTatAi: 'Bản tóm tắt AI',
    soKyTu: '{{so}} ký tự',
    dangChinhSua: 'Đang chỉnh sửa',
    dangTomTat: 'AI đang tóm tắt văn bản này...',
    chuaDuocTomTat: 'Văn bản này chưa được tóm tắt',
    aiTomTatNgay: 'AI tóm tắt ngay',
    tomTatTrong: 'Nội dung tóm tắt không được để trống.',
    luuTomTat: 'Lưu bản tóm tắt',
    suaTomTat: 'Sửa tóm tắt',
    xuatTomTat: 'Xuất văn bản tóm tắt',
    themTieuDe: 'Thêm văn bản mới',
    oTen: 'Tên văn bản',
    oTenViDu: 'VD: Công văn về việc...',
    oLoai: 'Loại văn bản',
    oTep: 'Tải tệp lên (PDF, DOCX, TXT, MD — hệ thống tự trích xuất nội dung)',
    oNoiDung: 'Hoặc dán nội dung văn bản (bỏ trống nếu đã tải tệp PDF/DOCX)',
    oNoiDungViDu: 'Dán nội dung văn bản gốc vào đây...',
    tuDongTomTat: 'Tự động tóm tắt bằng AI ngay sau khi thêm',
    loiThieuTen: 'Nhập tên văn bản.',
    loiThieuNoiDung: 'Tải tệp lên hoặc dán nội dung văn bản.',
  },
  vanBanSoanThao: {
    tieuDe: 'Soạn thảo văn bản',
    moTa: 'Soạn thảo văn bản hành chính theo mẫu thể thức Nghị định 30/2020/NĐ-CP bằng AI',
    thietLap: 'Thiết lập soạn thảo',
    oNguon: 'Văn bản gốc (nguồn nội dung)',
    oNguonTuyChon: 'Văn bản gốc (nguồn nội dung, tuỳ chọn)',
    tuTomTat: 'Chọn từ Tóm tắt văn bản',
    taiMoi: 'Tải văn bản mới',
    chonVanBanGoc: '— Chọn văn bản gốc —',
    chuaCoVanBanGoc: 'Chưa có văn bản nào đã tóm tắt',
    dangDocNoiDung: 'Đang đọc nội dung tệp...',
    goTep: 'Gỡ văn bản đã tải',
    chuaTaiTep: 'Chưa có văn bản nào được tải lên',
    trichDoan: 'Trích đoạn:',
    oMau: 'Mẫu văn bản — Nghị định 30/2020/NĐ-CP',
    chonMau: '— Chọn mẫu văn bản —',
    mauKhongCo: 'Mẫu văn bản này đã ngưng dùng hoặc không tồn tại. Chọn mẫu khác.',
    cheDoDien: 'Điền theo mẫu',
    cheDoAi: 'AI soạn theo thể thức NĐ30',
    theThuc: 'Thông tin thể thức',
    theThucTuyChon: '(tuỳ chọn — để trống nếu không cần khung)',
    theThucTuMau: 'lấy từ mẫu',
    tt: {
      cqch: 'Cơ quan chủ quản',
      cq: 'Tên cơ quan ban hành',
      so: 'Số văn bản',
      kyhieu: 'Ký hiệu cơ quan',
      dd: 'Địa danh',
      ky: 'Người ký',
      cv: 'Chức vụ',
      noinhan: 'Nơi nhận',
    },
    noiNhanViDu: 'Mỗi nơi nhận một dòng, VD:\n- Như trên;\n- Lưu: VT.',
    noiDungDien: 'Nội dung cần điền theo mẫu',
    aiDien: 'AI điền',
    nhanAiDienDuoc: '{{nhan}} (AI điền được)',
    nhanTuNhap: '{{nhan}} (tự nhập)',
    chon: '— Chọn —',
    dangXuLy: 'Đang xử lý...',
    dangDocTepNgan: 'Đang đọc tệp...',
    taoHoanChinh: 'Tạo văn bản hoàn chỉnh',
    aiSoan: 'AI xử lý — Soạn thảo văn bản',
    vanBanSauSoan: 'Văn bản sau khi soạn thảo',
    xongSua: 'Hoàn tất sửa',
    suaVanBan: 'Sửa văn bản',
    saoChep: 'Sao chép',
    xuatTep: 'Xuất tệp',
    rongHuongDan:
      'Chọn mẫu văn bản ở bên trái. Mẫu điền theo mẫu xem trước ngay khi gõ; mẫu AI soạn cần chọn văn bản gốc rồi bấm nút xử lý.',
    loiDinhDang: 'Định dạng không hỗ trợ. Chỉ nhận PDF, DOCX, TXT, MD.',
    loiQuaLon: 'Tệp vượt quá 20MB. Chọn tệp nhỏ hơn.',
    loiKhongDocDuoc: 'Không đọc được nội dung từ tệp. PDF scan cần bản nhận dạng được chữ.',
    dangDocTep: 'Đang đọc nội dung tệp, đợi trong giây lát.',
    canNguonDeAiDien: 'Chọn văn bản gốc để AI gợi ý giá trị các trường.',
    daAiDien: 'AI đã gợi ý giá trị các trường, có thể chỉnh lại.',
    chuaChonMau: 'Chọn mẫu văn bản trước khi xử lý.',
    canNguon: 'Chọn văn bản gốc có nội dung đủ dài để soạn thảo.',
    thieuTheThuc: 'Nhập đủ thông tin thể thức: {{ds}}',
    thieuTruong: 'Điền các trường bắt buộc: {{ds}}',
    conTruongChuaDien: 'Văn bản còn trường chưa điền: {{ds}}',
    daSaoChep: 'Đã sao chép nội dung văn bản.',
  },
  vanBanPhanCong: {
    tieuDe: 'Phân công nhiệm vụ',
    moTa: 'AI phân công nhiệm vụ theo phòng ban, cá nhân từ văn bản tóm tắt hoặc tệp tải lên — dựa trên Danh sách, vai trò các cấp',
    nutThemTay: 'Thêm nhiệm vụ thủ công',
    nutTaiTep: 'Tải tệp lên & AI phân công',
    nutTuTomTat: 'Từ Tóm tắt văn bản',
    thongKe: {
      tong: 'Tổng nhiệm vụ',
      phongBan: 'Giao phòng ban',
      caNhan: 'Giao cá nhân',
      hoanThanh: 'Hoàn thành',
    },
    timTheo: 'Tìm theo nhiệm vụ, văn bản, người nhận...',
    tatCaDoiTuong: 'Tất cả đối tượng',
    locDoiTuong: 'Lọc theo đối tượng được phân công',
    tatCaTrangThai: 'Tất cả trạng thái',
    locTrangThai: 'Lọc theo trạng thái',
    loiDanhSach: 'Không tải được danh sách nhiệm vụ: {{loi}}',
    bangCaption: 'Nhiệm vụ đã phân công',
    rongLoc: 'Không có nhiệm vụ nào khớp bộ lọc',
    rongThat: 'Chưa có nhiệm vụ nào',
    rongThatMo: 'Thêm nhiệm vụ thủ công, tải tệp lên để AI phân công, hoặc mở một văn bản ở màn Tóm tắt văn bản',
    cot: {
      nhiemVu: 'Nhiệm vụ được phân công',
      doiTuong: 'Đối tượng được phân công',
      vanBanNguon: 'Văn bản nguồn',
      dinhKem: 'Tệp đính kèm',
      han: 'Hạn xử lý',
    },
    xemTep: 'Xem {{ten}}',
    taiTep: 'Tải tệp về',
    taiTepTen: 'Tải {{ten}} về',
    sua: 'Sửa nhiệm vụ',
    xoa: 'Xoá nhiệm vụ',
    xacNhanXoa: 'Xoá nhiệm vụ đã giao cho {{ten}}?',
    xacNhanXoaMo: 'Nhiệm vụ và tệp đính kèm riêng của nó bị xoá hẳn, không hoàn tác được.',
    nutXoa: 'Xoá nhiệm vụ',
    daThem: 'Đã thêm nhiệm vụ',
    daCapNhat: 'Đã cập nhật nhiệm vụ',
    daXoa: 'Đã xoá nhiệm vụ',
    trangThai: {
      MOI_PHAN_CONG: 'Mới phân công',
      DANG_THUC_HIEN: 'Đang thực hiện',
      HOAN_THANH: 'Hoàn thành',
    },
    uuTien: {
      AUTO: 'Tự động (AI quyết định)',
      PHONG_BAN: 'Ưu tiên phòng ban',
      CA_NHAN: 'Ưu tiên cá nhân',
    },
    loi: {
      tepQuaLon: 'Tệp vượt quá {{mb}} MB. Chọn tệp nhỏ hơn.',
      thieuNoiDung: 'Nhập nội dung nhiệm vụ.',
      thieuDoiTuong: 'Chọn đối tượng nhận nhiệm vụ.',
      dinhDang: 'Định dạng không hỗ trợ. Chọn tệp PDF, DOCX, TXT hoặc MD.',
    },
    form: {
      tieuDeThem: 'Thêm nhiệm vụ thủ công',
      tieuDeSua: 'Sửa nhiệm vụ',
      nutThem: 'Thêm nhiệm vụ',
      noiDung: 'Nội dung nhiệm vụ',
      noiDungGoiY: 'Mô tả chi tiết nhiệm vụ cần giao...',
      vanBanLienQuan: 'Văn bản / tài liệu liên quan',
      vanBanLienQuanGoiY: 'Tên văn bản hoặc số hiệu (tuỳ chọn)',
      dinhKem: 'Đính kèm tệp',
      chonTep: 'Chọn tệp đính kèm',
      doiTep: 'Đổi tệp đính kèm',
      boTep: 'Bỏ tệp {{ten}}',
      dangDinhKem: 'Đang đính kèm tệp này. Chọn tệp mới sẽ thay thế.',
      loaiDoiTuong: 'Loại đối tượng',
      hanXuLy: 'Hạn xử lý',
      coQuanNhan: 'Cơ quan nhận nhiệm vụ',
      chonCoQuan: 'Chọn cơ quan...',
      phongBanNhan: 'Phòng ban nhận nhiệm vụ',
      chonPhongBan: 'Chọn phòng ban...',
      caNhanNhan: 'Cá nhân nhận nhiệm vụ',
      chonCaNhan: 'Chọn cá nhân...',
      chuaCoDonVi: 'Chưa có dữ liệu trong Danh sách, vai trò các cấp',
    },
    tep: {
      tieuDe: 'Tải tệp lên & AI phân công nhiệm vụ',
      tep: 'Tệp văn bản',
      keoTha: 'Kéo thả hoặc bấm để chọn tệp văn bản',
      dinhDang: 'PDF · DOCX · TXT · MD, tối đa 20 MB',
      dangTrich: 'Đang trích xuất nội dung...',
      trichDoan: 'Trích đoạn nội dung:',
      khongTrichDuoc: 'Đã chọn tệp "{{ten}}". Hệ thống sẽ trích xuất nội dung khi phân công.',
      tuyChon: 'Tuỳ chọn phân công',
      uuTien: 'Ưu tiên giao cho',
      hanMacDinh: 'Hạn xử lý mặc định',
      soNgay: '{{so}} ngày',
      ghiChu: 'Mô tả / ghi chú thêm',
      ghiChuGoiY: 'VD: Văn bản cấp bách, ưu tiên phòng CNTT...',
      nutChay: 'AI phân công từ tệp',
      dangPhanCong: 'Đang phân công...',
      dangTaiLen: 'Đang tải tệp lên...',
      aiDangXuLy: 'AI đang phân tích & phân công...',
      daTao: 'AI đã tạo {{so}} nhiệm vụ từ tệp "{{ten}}".',
      khongTaoDuoc: 'AI không tạo được nhiệm vụ nào từ tệp này.',
      thatBai: 'AI phân công thất bại. Thử lại sau ít phút.',
      hetGio: 'Quá thời gian chờ phân công. Tải lại danh sách sau ít phút.',
    },
  },
  vanBanNhiemVu: {
    breadcrumbNhom: 'Xử lý văn bản',
    tieuDe: 'Quản lý nhiệm vụ',
    moTa: 'Phân công, phân rã nhiệm vụ con nhiều cấp và giám sát tiến độ xử lý văn bản hành chính',
    giaoMoi: 'Giao nhiệm vụ mới',
    phanCongAi: 'Phân công bằng AI',
    trangThai: {
      MOI_PHAN_CONG: 'Chưa bắt đầu',
      DANG_THUC_HIEN: 'Đang thực hiện',
      HOAN_THANH: 'Hoàn thành',
      QUA_HAN: 'Quá hạn',
      SAP_DEN_HAN: 'Sắp đến hạn',
    },
    vaiTro: {
      CHU_TRI: 'Chủ trì',
      PHOI_HOP: 'Phối hợp',
      XEM_DE_BIET: 'Xem để biết',
    },
    phamVi: {
      nhan: 'Phạm vi',
      mine: 'Việc của tôi',
      assigned: 'Tôi giao',
      all: 'Toàn đơn vị',
      moiVaiTro: 'Mọi vai trò của tôi',
    },
    khoang: {
      nhan: 'Thời gian giao',
      thangNay: 'Tháng này',
      quyNay: 'Quý này',
      namNay: 'Năm nay',
      tuNgay: 'Từ ngày',
      denNgay: 'Đến ngày',
    },
    kpi: {
      tong: 'Tổng nhiệm vụ',
      tongMo: 'Gồm cả nhiệm vụ con',
      tyLe: '{{phanTram}}% tổng',
      sapDenHanMo: 'Hạn trong {{so}} ngày tới',
      locMo: 'Bấm để lọc danh sách, bấm lại để bỏ lọc',
      loi: 'Không tải được số liệu thống kê.',
    },
    thongKe: {
      nut: 'Thống kê tiến độ',
      nutMo: 'Tỷ lệ trạng thái và phân bổ theo đơn vị chủ trì',
      tyLe: 'Tỷ lệ trạng thái',
      tyLeAria: 'Thanh tỷ lệ trạng thái nhiệm vụ',
      theoDonVi: 'Theo đơn vị chủ trì',
      theoDonViRong: 'Chưa có nhiệm vụ trong phạm vi này',
    },
    ds: {
      tieuDe: 'Danh sách nhiệm vụ',
      soChinh: '{{so}} nhiệm vụ chính',
      sapXepMo: 'Xếp theo mức gấp: quá hạn, sắp đến hạn lên trước',
      tim: 'Tìm theo nội dung, văn bản, người thực hiện...',
      moiTrangThai: 'Tất cả trạng thái',
      moiDonVi: 'Tất cả đơn vị chủ trì',
      canLam: 'Cần làm',
      daXong: 'Đã hoàn thành',
      cotNhiemVu: 'Nhiệm vụ',
      cotHan: 'Hạn xử lý',
      cotDonVi: 'Chủ trì · người thực hiện',
      cotTrangThai: 'Trạng thái',
      tienDoCon: '{{xong}}/{{tong}} việc con',
      tienDoPhanTram: '{{phanTram}}%',
      chuaCoDonVi: 'Chưa có đơn vị',
      themNguoi: '+{{so}}',
      moCon: 'Mở nhiệm vụ con',
      thuCon: 'Thu gọn nhiệm vụ con',
      xemChiTiet: 'Xem chi tiết: {{ten}}',
      giaoCon: 'Giao nhiệm vụ con',
      themNguoiThucHien: 'Thêm người thực hiện',
      sua: 'Sửa nhiệm vụ',
      xoa: 'Xoá nhiệm vụ',
      doiTrangThai: 'Đổi trạng thái: {{trangThai}}',
      xongBiChan: 'Hoàn thành (còn {{so}} việc con)',
      aiPhanRa: 'AI phân rã',
      rongTieuDe: 'Chưa có nhiệm vụ nào',
      rongMo: 'Bấm "Giao nhiệm vụ mới" để phân công việc đầu tiên.',
      rongCuaToiTieuDe: 'Bạn chưa có nhiệm vụ nào',
      rongCuaToiMo: 'Nhiệm vụ bạn được giao chủ trì, phối hợp hoặc xem để biết sẽ hiện ở đây.',
      rongLoc: 'Không có nhiệm vụ khớp bộ lọc',
      loi: 'Không tải được danh sách nhiệm vụ: {{loi}}',
    },
    han: {
      none: 'Không hạn',
      overdue: 'Quá {{so}} ngày',
      today: 'Hạn hôm nay',
      soon: 'Còn {{so}} ngày',
      later: 'Còn {{so}} ngày',
      done: 'Hạn {{ngay}}',
    },
    chiTiet: {
      tieuDe: 'Chi tiết nhiệm vụ',
      thuoc: 'Thuộc nhiệm vụ',
      moTa: 'Mô tả / chỉ đạo',
      chuaMoTa: 'Không có mô tả',
      donViChuTri: 'Đơn vị chủ trì',
      nguoiGiao: 'Người giao',
      ngayGiao: 'Ngày giao',
      xongLuc: 'Hoàn thành lúc',
      tepDinhKem: 'Tệp đính kèm',
      taiTep: 'Tải {{ten}}',
      vanBanLienQuan: 'Văn bản liên quan',
      nguoiThucHien: 'Người thực hiện',
      chuaCoNguoi: 'Chưa có người thực hiện',
      viecCon: 'Nhiệm vụ con ({{xong}}/{{tong}})',
      chuaCoViecCon: 'Chưa phân rã nhiệm vụ con',
      khongRo: 'Không rõ',
      khongTai: 'Không tải được nhiệm vụ: {{loi}}',
    },
    themNhanh: {
      tieuDe: 'Thêm người thực hiện',
      vaiTro: 'Vai trò',
    },
    form: {
      tieuDeTao: 'Giao nhiệm vụ mới',
      tieuDeCon: 'Giao nhiệm vụ con',
      tieuDeSua: 'Sửa nhiệm vụ',
      thuoc: 'Thuộc nhiệm vụ: {{ten}}',
      chaTuCapNhat: 'Trạng thái việc cha tự cập nhật theo các việc con.',
      noiDung: 'Nội dung nhiệm vụ',
      noiDungGoiY: 'VD: Rà soát, khắc phục lỗ hổng bảo mật hệ thống',
      noiDungThieu: 'Nhập nội dung nhiệm vụ',
      moTa: 'Mô tả / chỉ đạo',
      moTaGoiY: 'Yêu cầu cụ thể, kết quả cần đạt...',
      han: 'Hạn xử lý',
      hanGoiY: 'dd/mm/yyyy',
      donVi: 'Đơn vị chủ trì',
      donViGoiY: 'Chọn đơn vị...',
      nguoi: 'Người thực hiện',
      nguoiMo: 'Cần ít nhất một người chủ trì. Người phối hợp và xem để biết là tuỳ chọn.',
      nguoiThieu: 'Chọn ít nhất một người chủ trì',
      timNguoi: 'Tìm người theo tên...',
      khongThayNguoi: 'Không tìm thấy người dùng',
      boNguoi: 'Bỏ {{ten}}',
      chuaChonNguoi: 'Chưa chọn người thực hiện',
      tep: 'Tệp đính kèm',
      chonTep: 'Chọn tệp',
      tepHienTai: 'Tệp hiện tại: {{ten}}',
      tepMo: 'Tối đa {{so}}MB. Chọn tệp mới sẽ thay tệp cũ.',
      tepQuaLon: 'Tệp đính kèm tối đa {{so}}MB',
      nutTao: 'Giao nhiệm vụ',
      nutSua: 'Lưu thay đổi',
    },
    xoa: {
      tieuDe: 'Xoá nhiệm vụ "{{ten}}"?',
      than: 'Nhiệm vụ bị xoá vĩnh viễn, không hoàn tác được.',
      thanKemCon: 'Nhiệm vụ cùng {{so}} nhiệm vụ con bị xoá vĩnh viễn, không hoàn tác được.',
      nut: 'Xoá nhiệm vụ',
    },
    toast: {
      daGiao: 'Đã giao nhiệm vụ.',
      daLuu: 'Đã lưu nhiệm vụ.',
      daThemNguoi: 'Đã thêm người thực hiện.',
      daDoiTrangThai: 'Đã cập nhật trạng thái.',
      daXoa: 'Đã xoá nhiệm vụ.',
      daLuuTienDo: 'Đã cập nhật tiến độ.',
      daGuiYKien: 'Đã gửi ý kiến.',
      daTaoCon: 'Đã tạo {{so}} việc con.',
    },
    hoatDong: {
      tieuDe: 'Tiến độ & trao đổi',
      tabTienDo: 'Cập nhật tiến độ',
      tabYKien: 'Gửi ý kiến',
      tienDoHienTai: 'Tiến độ hiện tại',
      ghiChuTienDo: 'Kết quả đã làm, vướng mắc (không bắt buộc)',
      tienDoMo: 'Báo tiến độ trên việc chưa bắt đầu sẽ tự chuyển sang Đang thực hiện.',
      yKienGoiY: 'Ý kiến chỉ đạo, trao đổi, đề xuất...',
      dinhKem: 'Đính kèm tệp kết quả',
      boTep: 'Bỏ tệp {{ten}}',
      nutTienDo: 'Lưu tiến độ',
      nutYKien: 'Gửi ý kiến',
      dangGui: 'Đang gửi...',
      yKienThieu: 'Nhập nội dung hoặc đính kèm tệp',
      rong: 'Chưa có hoạt động nào',
      loi: 'Không tải được dòng thời gian',
      heThong: 'Hệ thống',
      themNguoi: 'Thêm {{ten}} ({{vaiTro}})',
      boNguoi: 'Bỏ {{ten}} ({{vaiTro}})',
      doiVaiTro: '{{ten}} → {{vaiTro}}',
    },
    loaiHoatDong: {
      CREATED: 'đã giao nhiệm vụ',
      CHILD_ADDED: 'đã giao nhiệm vụ con "{{ten}}"',
      UPDATED: 'đã sửa {{truong}}',
      PARTICIPANTS_CHANGED: 'đã thay đổi người thực hiện',
      STATUS_CHANGED: 'đã chuyển trạng thái {{tu}} → {{sang}}',
      STATUS_CHANGED_AUTO: 'tự chuyển trạng thái {{tu}} → {{sang}} theo các việc con',
      PROGRESS: 'đã cập nhật tiến độ {{phanTram}}%',
      COMMENT: 'đã gửi ý kiến',
    },
    truongDoi: {
      title: 'nội dung',
      description: 'mô tả',
      deadline: 'hạn xử lý',
      related_document: 'văn bản liên quan',
      lead_org: 'đơn vị chủ trì',
      attachment: 'tệp đính kèm',
    },
    thongBao: {
      tieuDe: 'Thông báo nhiệm vụ',
      moChuong: 'Thông báo nhiệm vụ ({{so}} chưa đọc)',
      rong: 'Chưa có thông báo nào',
      rongMo: 'Cập nhật trên việc bạn tham gia hoặc bạn giao sẽ hiện ở đây.',
      xemTatCa: 'Xem tất cả',
      danhDauDaDoc: 'Đánh dấu đã đọc',
      tieuDeTrang: 'Thông báo',
      moTaTrang: 'Hoạt động mới trên nhiệm vụ bạn tham gia hoặc bạn giao',
      loi: 'Không tải được thông báo',
      chuaDoc: 'Chưa đọc',
    },
    phanRa: {
      tieuDe: 'AI phân rã nhiệm vụ con',
      cha: 'Nhiệm vụ cha: {{ten}}',
      dangGoiY: 'AI đang đọc nhiệm vụ, chỉ đạo và văn bản đính kèm để đề xuất việc con...',
      dangGoiYMo: 'Thường mất 10–40 giây.',
      daDocTep: 'AI đã đọc văn bản đính kèm của nhiệm vụ.',
      khongCoTep: 'Nhiệm vụ không có văn bản đính kèm đọc được — AI chỉ dựa vào nội dung và chỉ đạo.',
      rong: 'AI cho rằng nhiệm vụ này đã đủ cụ thể, không cần phân rã. Bạn vẫn có thể thêm việc con thủ công.',
      loi: 'Không lấy được gợi ý từ AI: {{loi}}',
      goiYLai: 'Gợi ý lại',
      chonViec: 'Tạo việc này',
      noiDungGoiY: 'Nội dung việc con',
      donVi: 'Đơn vị chủ trì',
      han: 'Hạn',
      aiChon: 'AI chọn: {{ten}}',
      aiKhongKhop: 'AI chọn "{{ten}}" — không có trong Vai trò các cấp, hãy chọn tay',
      nguoiChuaKhop: 'Chưa khớp tài khoản "{{ten}}" — chọn người',
      dangKhop: 'Đang khớp tài khoản...',
      boDong: 'Bỏ dòng {{so}}',
      themDong: 'Thêm việc con',
      hanVuot: 'Hạn vượt hạn nhiệm vụ cha ({{ngay}})',
      noiDungThieu: 'Nhập nội dung cho mọi việc được chọn',
      chuaChon: 'Chọn ít nhất một việc để tạo',
      nut: 'Tạo {{so}} việc con',
      aiDonVi: '{{ten}} · Vai trò các cấp',
      aiDonViMo: 'Chưa có trong Cơ cấu tổ chức — việc vẫn được giao cho đơn vị này. Chọn để gắn đơn vị trong cơ cấu.',
    },
  },
  // Trợ lý AI (ADR-0086)
  ...viChung,
  ...viHoi,
  ...viKetNoi,
  ...viKho,
  ...viKhuon,
  ...viBot,
  ...viVanHanh,
  // Nhật ký & Truy vết (ADR-0095)
  ...viNktvChung,
  ...viNktvSuDungAi,
  ...viGiaAi,
  ...viCanhBao,
  ...viBanToken,
  ...viManTokenAi,
  ...viBienBan,
  ...viHanMucAi,
  ...viBanGiao,
  // Quản lý RPA (ADR-0103)
  ...viRpaChung,
  ...viRpaLoi,
  ...viRpaKetNoi,
  ...viRpaWorkflow,
  ...viRpaLichSu,
  ...viRpaTongQuan,
  ...viRpaLicense,
  ...viRpaDanhBa,
  ...viRpaThongBao,
  ...viRpaAgent,
} as const;
